/* quest-one-v2-art.js - QOArt: the procedural pixel-art kit for Quest One v2.
   One palette, one pipeline: shapes are drawn as material + light on a small native grid,
   quantised to the palette with ordered (Bayer 4x4) dithering, outlined 1px in warm black,
   then upscaled nearest-neighbour. Plain ES2017, no dependencies, no network. */
(function (root) {
  'use strict';
  if (!root || root.QOArt) return;
  var doc = root.document;
  var VERSION = '1.0.0';

  /* ---------- small utils ---------- */
  function warn(e) { try { if (root.console) root.console.warn('[QOArt]', (e && e.message) || e); } catch (_) { /* silent */ } }
  function guard(fn, fb) {
    return function () {
      try { return fn.apply(this, arguments); } catch (e) { warn(e); return typeof fb === 'function' ? fb.apply(this, arguments) : fb; }
    };
  }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function hash(x, y, s) {
    var h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul((s | 0) + 1, 1442695041)) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  }
  function vnoise(x, y, s) { /* smooth value noise, 0..1 */
    var xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
    var u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
    var a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
    return lerp(lerp(a, b, u), lerp(c, d, u), v);
  }
  function now() { return (root.performance && root.performance.now) ? root.performance.now() : Date.now(); }
  function prefersReduced() {
    try { return !!(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }
  function dprCap() { return Math.min(2.5, Math.max(1, root.devicePixelRatio || 1)); }
  function mk(w, h) { var c = doc.createElement('canvas'); c.width = Math.max(1, w | 0); c.height = Math.max(1, h | 0); return c; }
  function ctx2d(c) { var x = c.getContext('2d'); noSmooth(x); return x; }
  function noSmooth(x) {
    if (!x) return;
    x.imageSmoothingEnabled = false;
    x.mozImageSmoothingEnabled = false; x.webkitImageSmoothingEnabled = false; x.msImageSmoothingEnabled = false;
  }
  function pixelated(el) {
    var s = el.style;
    s.imageRendering = 'optimizeSpeed';
    s.imageRendering = '-moz-crisp-edges';
    s.imageRendering = '-webkit-optimize-contrast';
    s.imageRendering = 'crisp-edges';
    s.imageRendering = 'pixelated';
    s.msInterpolationMode = 'nearest-neighbor';
  }
  var raf = root.requestAnimationFrame ? root.requestAnimationFrame.bind(root) : function (f) { return root.setTimeout(function () { f(now()); }, 33); };
  var caf = root.cancelAnimationFrame ? root.cancelAnimationFrame.bind(root) : root.clearTimeout.bind(root);
  var BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
  function bay(x, y) { return (BAYER[((y & 3) << 2) | (x & 3)] + 0.5) / 16; }
  function easeInOut(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
  function easeOut(t) { return 1 - Math.pow(1 - t, 3); }

  /* ---------- the one palette ---------- */
  var PAL = [
    ['ol', '#1a1210'], ['k1', '#2b1d18'],
    ['lt1', '#40291e'], ['lt2', '#5c3b27'], ['lt3', '#7f5436'], ['lt4', '#a8744c'], ['sk5', '#cf9d72'], ['sk6', '#ecc9a0'],
    ['pa', '#e8e0d0'], ['pa2', '#b8ad98'], ['mu', '#8b8178'], ['hb', '#5b534d'],
    ['w1', '#24212b'], ['w2', '#35313d'], ['w3', '#4a4553'],
    ['i1', '#3a3e47'], ['i2', '#5f646e'], ['i3', '#8d939c'], ['i4', '#c9ced4'],
    ['g1', '#5a3e14'], ['g2', '#8e6a24'], ['g3', '#c9a84c'], ['g4', '#e8c674'], ['g5', '#f4d68a'], ['hw', '#fff6dc'],
    ['m0', '#08080e'], ['m1', '#131a28'], ['m2', '#23304a'], ['m3', '#5d7394'], ['m4', '#9fb7d9'], ['m5', '#d6e4f5'],
    ['e1', '#7a2a14'], ['e2', '#b8461f'], ['e3', '#e0764f'], ['e4', '#f5a45e'],
    ['b1', '#4d0f14'], ['b2', '#8c1a22'], ['b3', '#ef4444'],
    ['v1', '#26381c'], ['v2', '#4b6b2c'], ['v3', '#8aa846'],
    /* the mythic light of FramedFantasy (#ef94ff purple, #b3f5ee pale teal) and the tones that lead into them;
       kept last so every older colour keeps its index */
    ['y1', '#3a2150'], ['y2', '#8a52b0'], ['y3', '#ef94ff'], ['y4', '#b3f5ee'], ['y5', '#4f9c9c']
  ];
  var PI = {}, PHEX = [], P32 = new Uint32Array(PAL.length), PRGB = [];
  PAL.forEach(function (p, i) {
    PI[p[0]] = i; PHEX.push(p[1]);
    var r = parseInt(p[1].slice(1, 3), 16), g = parseInt(p[1].slice(3, 5), 16), b = parseInt(p[1].slice(5, 7), 16);
    PRGB.push([r, g, b]);
    P32[i] = ((255 << 24) | (b << 16) | (g << 8) | r) >>> 0;
  });
  function pc(k) { return typeof k === 'number' ? k : (PI[k] != null ? PI[k] : 0); }

  /* material ramps, dark -> light, as palette keys */
  var RAMP_SRC = {
    gold: ['g1', 'g2', 'g3', 'g4', 'g5'],
    brass: ['k1', 'g1', 'g2', 'g3', 'g4'],
    iron: ['i1', 'i2', 'i3', 'i4'],
    steel: ['w1', 'i1', 'i2', 'i3', 'i4'],
    wood: ['k1', 'lt1', 'lt2', 'lt3', 'lt4'],
    pine: ['lt2', 'lt3', 'lt4', 'sk5', 'sk6'],
    skin: ['lt2', 'lt3', 'lt4', 'sk5', 'sk6'],
    hair: ['w2', 'hb', 'mu', 'pa2'],
    wool: ['w1', 'w2', 'w3', 'hb'],
    linen: ['hb', 'mu', 'pa2', 'pa'],
    moon: ['m1', 'm2', 'm3', 'm4', 'm5'],
    ember: ['e1', 'e2', 'e3', 'e4', 'g5'],
    blood: ['b1', 'b2', 'b3', 'e4'],
    green: ['v1', 'v2', 'v3', 'g4'],
    leaf: ['k1', 'v1', 'v2', 'v3'],
    holy: ['g3', 'g4', 'g5', 'hw'],
    night: ['m0', 'm1', 'm2'],
    shadow: ['ol', 'w1', 'w2', 'w3'],
    dark: ['ol', 'k1', 'lt1'],
    /* cold (moonlit) variants used when the mask slips */
    skinC: ['k1', 'lt1', 'lt2', 'lt3', 'lt4'],
    hairC: ['w1', 'w2', 'hb', 'i3'],
    woolC: ['m0', 'm1', 'w1', 'm2'],
    woodC: ['m0', 'ol', 'k1', 'lt1', 'lt2'],
    linenC: ['m2', 'i1', 'i2', 'm3'],
    leatherC: ['m0', 'ol', 'k1', 'lt1', 'i1'],
    goldC: ['k1', 'g1', 'g2', 'i2', 'i3'],
    /* drained (fallen / illusion failing) */
    skinD: ['k1', 'lt1', 'lt2', 'lt3', 'mu'],
    hairD: ['w1', 'w2', 'hb', 'mu'],
    woolD: ['m0', 'm1', 'm2', 'w2'],
    woodD: ['m0', 'm1', 'w1', 'w2', 'i1'],
    /* the tavern at night */
    timber: ['m0', 'ol', 'k1', 'lt1', 'lt2'],
    plaster: ['m1', 'm2', 'i1', 'i2', 'm3'],
    slate: ['m0', 'm1', 'w1', 'm2', 'm3'],
    stone: ['m0', 'm1', 'i1', 'i2', 'm3'],
    win: ['e1', 'e2', 'e3', 'g4', 'g5', 'hw']
  };
  var RNAMES = Object.keys(RAMP_SRC), RID = {}, RAMPS = [];
  RNAMES.forEach(function (n, i) { RID[n] = i; RAMPS.push(RAMP_SRC[n].map(pc)); });

  /* ---------- geometry and light helpers ---------- */
  function norm3(x, y, z) { var l = Math.sqrt(x * x + y * y + z * z) || 1; return [x / l, y / l, z / l]; }
  var LT = norm3(-0.55, -0.7, 0.5);            /* icons: light from top-left */
  function xf(cx, cy, ang) { var c = Math.cos(ang), s = Math.sin(ang); return function (a, b) { return [cx + a * c - b * s, cy + a * s + b * c]; }; }
  function inv(cx, cy, ang) { var c = Math.cos(ang), s = Math.sin(ang); return function (x, y) { var dx = x - cx, dy = y - cy; return [dx * c + dy * s, -dx * s + dy * c]; }; }
  function mapPts(T, pts) { return pts.map(function (p) { return T(p[0], p[1]); }); }
  function gSph(cx, cy, rx, ry, amt, L) {
    L = L || LT;
    return function (x, y) {
      var nx = (x - cx) / rx, ny = (y - cy) / ry, d = nx * nx + ny * ny; if (d > 1) { var q = Math.sqrt(d); nx /= q; ny /= q; d = 1; }
      var nz = Math.sqrt(1 - d);
      return amt * ((nx * L[0] + ny * L[1] + nz * L[2]) - 0.45);
    };
  }
  function gLin(ax, ay, bx, by, amt) {
    var dx = bx - ax, dy = by - ay, d2 = dx * dx + dy * dy || 1;
    return function (x, y) { var t = ((x - ax) * dx + (y - ay) * dy) / d2; return amt * (0.5 - clamp(t, 0, 1)); };
  }
  function gCyl(ax, ay, bx, by, r, amt, L) {
    L = L || LT;
    var dx = bx - ax, dy = by - ay, len = Math.sqrt(dx * dx + dy * dy) || 1, px = -dy / len, py = dx / len;
    return function (x, y) {
      var s = clamp(((x - ax) * px + (y - ay) * py) / r, -1, 1), nz = Math.sqrt(1 - s * s);
      return amt * ((px * s * L[0] + py * s * L[1] + nz * L[2]) - 0.45);
    };
  }
  function gRidge(ax, ay, bx, by, amt, L) { /* a blade's two facets */
    L = L || LT;
    var dx = bx - ax, dy = by - ay, len = Math.sqrt(dx * dx + dy * dy) || 1, px = -dy / len, py = dx / len;
    var lit = (px * L[0] + py * L[1]) > 0 ? 1 : -1;
    return function (x, y) { var s = (x - ax) * px + (y - ay) * py; return (s >= 0 ? lit : -lit) * amt; };
  }
  function gAdd() { var fs = Array.prototype.slice.call(arguments).filter(Boolean); return function (x, y) { var s = 0; for (var i = 0; i < fs.length; i++) s += fs[i](x, y); return s; }; }
  function pip(pts) {
    return function (x, y) {
      var c = false;
      for (var i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        var xi = pts[i][0], yi = pts[i][1], xj = pts[j][0], yj = pts[j][1];
        if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) c = !c;
      }
      return c;
    };
  }
  function bbox(pts, pad) {
    var x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
    pts.forEach(function (p) { if (p[0] < x0) x0 = p[0]; if (p[1] < y0) y0 = p[1]; if (p[0] > x1) x1 = p[0]; if (p[1] > y1) y1 = p[1]; });
    pad = pad || 1; return [x0 - pad, y0 - pad, x1 + pad, y1 + pad];
  }
  /* quadratic path: [x,y] points and ['q',cx,cy,x,y] curves flattened to a polygon */
  function path(list) {
    var out = [], cx = 0, cy = 0;
    list.forEach(function (p) {
      if (p[0] === 'q') {
        for (var i = 1; i <= 8; i++) {
          var t = i / 8, u = 1 - t;
          out.push([u * u * cx + 2 * u * t * p[1] + t * t * p[3], u * u * cy + 2 * u * t * p[2] + t * t * p[4]]);
        }
        cx = p[3]; cy = p[4];
      } else { out.push([p[0], p[1]]); cx = p[0]; cy = p[1]; }
    });
    return out;
  }

  /* ---------- the sprite buffer: material + light per pixel ---------- */
  function Spr(w, h) {
    this.w = w; this.h = h; var n = w * h;
    this.rp = new Int16Array(n); this.rp.fill(-1);
    this.lm = new Float32Array(n);
    this.fx = new Int16Array(n); this.fx.fill(-1);
    this.id = new Int16Array(n);
    this.ly = new Uint8Array(n);
    this.no = new Uint8Array(n);        /* 1 = not an outline source (light, glow) */
    this.dz = new Float32Array(n);      /* per pixel dither sharpness, 0 = default */
    this.nid = 0; this.layer = 0; this.pxs = [];
  }
  Spr.prototype.shape = function (test, bb, o) {
    o = o || {};
    var W = this.w, H = this.h;
    var x0 = Math.max(0, Math.floor(bb[0])), y0 = Math.max(0, Math.floor(bb[1]));
    var x1 = Math.min(W, Math.ceil(bb[2])), y1 = Math.min(H, Math.ceil(bb[3]));
    var bw = x1 - x0, bh = y1 - y0; if (bw <= 0 || bh <= 0) return 0;
    var mw = bw + 2, m = new Uint8Array(mw * (bh + 2)), x, y, i, k;
    var on = o.on == null ? null : (Array.isArray(o.on) ? o.on : [o.on]);
    for (y = y0; y < y1; y++) for (x = x0; x < x1; x++) {
      if (!test(x + 0.5, y + 0.5)) continue;
      i = y * W + x;
      if (on) { if (on[0] === 'any') { if (this.rp[i] < 0) continue; } else if (on.indexOf(this.id[i]) < 0) continue; }
      if (o.under && this.rp[i] >= 0) continue;
      m[(y - y0 + 1) * mw + (x - x0 + 1)] = 1;
    }
    var id = ++this.nid, rid = o.m != null ? RID[o.m] : 0, base = o.l == null ? 0.5 : o.l;
    if (rid == null) rid = 0;
    var ld = o.ld || [-0.6, -0.8], lx = ld[0], lyy = ld[1];
    var fxc = o.fx != null ? pc(o.fx) : -1, olc = o.olc != null ? pc(o.olc) : 0;
    for (y = y0; y < y1; y++) for (x = x0; x < x1; x++) {
      k = (y - y0 + 1) * mw + (x - x0 + 1);
      if (!m[k]) continue;
      i = y * W + x;
      if (o.erase) { this.rp[i] = -1; this.fx[i] = -1; this.no[i] = 0; continue; }
      var up = !m[k - mw], dn = !m[k + mw], lf = !m[k - 1], rt = !m[k + 1];
      var L = base;
      if (o.g) L += o.g(x + 0.5, y + 0.5);
      if (o.bev) L += o.bev * ((up ? -lyy : 0) + (dn ? lyy : 0) + (lf ? -lx : 0) + (rt ? lx : 0));
      if (o.tex) L += o.tex(x, y);
      this.rp[i] = rid; this.lm[i] = L; this.id[i] = id; this.ly[i] = o.layer != null ? o.layer : this.layer;
      this.no[i] = o.noOl ? 1 : 0; this.dz[i] = o.dz || 0;
      /* inner outline: only where this part sits over an earlier part; bare silhouette
         edges are left to the outer outline pass so outlines never double up */
      var sep = false;
      if (o.ol && (up || dn || lf || rt)) {
        if (o.ol === 2) sep = true;
        else {
          if (up && y > 0 && this.rp[i - W] >= 0 && !this.no[i - W] && !(m[k - mw] === 0 && this.id[i - W] === id)) sep = true;
          if (dn && y < H - 1 && this.rp[i + W] >= 0 && !this.no[i + W]) sep = true;
          if (lf && x > 0 && this.rp[i - 1] >= 0 && !this.no[i - 1]) sep = true;
          if (rt && x < W - 1 && this.rp[i + 1] >= 0 && !this.no[i + 1]) sep = true;
        }
      }
      this.fx[i] = sep ? olc : fxc;
    }
    return id;
  };
  Spr.prototype.ell = function (cx, cy, rx, ry, o) {
    return this.shape(function (x, y) { var a = (x - cx) / rx, b = (y - cy) / ry; return a * a + b * b <= 1; }, [cx - rx - 1, cy - ry - 1, cx + rx + 1, cy + ry + 1], o);
  };
  Spr.prototype.rell = function (cx, cy, rx, ry, ang, o) {
    var c = Math.cos(ang), s = Math.sin(ang), R = Math.max(rx, ry) + 1;
    return this.shape(function (x, y) { var dx = x - cx, dy = y - cy, a = (dx * c + dy * s) / rx, b = (-dx * s + dy * c) / ry; return a * a + b * b <= 1; }, [cx - R, cy - R, cx + R, cy + R], o);
  };
  Spr.prototype.poly = function (pts, o) { return this.shape(pip(pts), bbox(pts), o); };
  Spr.prototype.rect = function (x, y, w, h, o) { return this.shape(function () { return true; }, [x, y, x + w, y + h], o); };
  Spr.prototype.ring = function (cx, cy, r0, r1, o) {
    return this.shape(function (x, y) { var d = Math.sqrt((x - cx) * (x - cx) + (y - cy) * (y - cy)); return d >= r0 && d <= r1; }, [cx - r1 - 1, cy - r1 - 1, cx + r1 + 1, cy + r1 + 1], o);
  };
  Spr.prototype.seg = function (ax, ay, bx, by, r0, r1, o) {
    var dx = bx - ax, dy = by - ay, d2 = dx * dx + dy * dy || 1e-6, R = Math.max(r0, r1) + 1;
    return this.shape(function (x, y) {
      var t = clamp(((x - ax) * dx + (y - ay) * dy) / d2, 0, 1), qx = ax + t * dx - x, qy = ay + t * dy - y, r = r0 + (r1 - r0) * t;
      return qx * qx + qy * qy <= r * r;
    }, [Math.min(ax, bx) - R, Math.min(ay, by) - R, Math.max(ax, bx) + R, Math.max(ay, by) + R], o);
  };
  /* a stroke along a polyline (thin lines: strands, strings, cracks) */
  Spr.prototype.line = function (pts, r, o) {
    var self = this, ids = [];
    for (var i = 0; i < pts.length - 1; i++) ids.push(self.seg(pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1], r, r, o));
    return ids;
  };
  /* hand-placed pixel after quantising: c = palette key, or 'ramp:level' (resolved through the state's ramp map) */
  Spr.prototype.px = function (x, y, c, layer) { this.pxs.push([x | 0, y | 0, c, layer == null ? this.layer : layer]); };
  Spr.prototype.pxl = function (list, c, layer) { for (var i = 0; i < list.length; i += 2) this.px(list[i], list[i + 1], c, layer); };

  function rampOf(rid, map) { var n = RNAMES[rid]; if (map && map[n]) n = map[n]; return RAMPS[RID[n]] || RAMPS[rid]; }
  function resolve(c, map) {
    if (typeof c === 'number') return c;
    var j = c.indexOf(':');
    if (j < 0) return pc(c);
    var name = c.slice(0, j), lv = +c.slice(j + 1); if (map && map[name]) name = map[name];
    var r = RAMPS[RID[name]]; if (!r) return 0;
    return r[clamp(lv < 0 ? r.length + lv : lv, 0, r.length - 1)];
  }
  /* quantise: returns one Int16Array of palette indices (-1 = clear) per layer */
  Spr.prototype.render = function (opt) {
    opt = opt || {};
    var W = this.w, H = this.h, n = W * H, map = opt.map || null, dz0 = opt.dz || 2.2, nl = opt.layers || 1;
    var outs = [], l, i, x, y;
    for (l = 0; l < nl; l++) { var a = new Int16Array(n); a.fill(-1); outs.push(a); }
    var lyr = new Uint8Array(n);
    for (i = 0; i < n; i++) {
      var r = this.rp[i]; if (r < 0) continue;
      l = Math.min(nl - 1, this.ly[i]); lyr[i] = l;
      if (this.fx[i] >= 0) { outs[l][i] = this.fx[i]; continue; }
      var ramp = rampOf(r, map), len = ramp.length;
      var v = clamp(this.lm[i], 0, 0.999) * (len - 1) + (opt.bias || 0), lv = Math.floor(v), f = v - lv;
      var dz = this.dz[i] || dz0;
      f = (f - 0.5) * dz + 0.5;
      x = i % W; y = (i / W) | 0;
      if (f > bay(x, y)) lv++;
      outs[l][i] = ramp[clamp(lv, 0, len - 1)];
    }
    if (opt.rim) applyRim(this, outs, opt.rim, map);
    for (var p = 0; p < this.pxs.length; p++) {
      var q = this.pxs[p]; if (q[0] < 0 || q[1] < 0 || q[0] >= W || q[1] >= H) continue;
      var li = Math.min(nl - 1, q[3]);
      outs[li][q[1] * W + q[0]] = q[2] === null ? -1 : resolve(q[2], map);
    }
    if (opt.outline !== false) for (l = 0; l < nl; l++) outline(outs[l], W, H, this, opt.olc != null ? pc(opt.olc) : 0);
    return outs;
  };
  function outline(a, W, H, S, c) {
    var add = [];
    for (var y = 0; y < H; y++) for (var x = 0; x < W; x++) {
      var i = y * W + x; if (a[i] >= 0) continue;
      var hit = false;
      if (x > 0 && a[i - 1] >= 0 && !S.no[i - 1]) hit = true;
      else if (x < W - 1 && a[i + 1] >= 0 && !S.no[i + 1]) hit = true;
      else if (y > 0 && a[i - W] >= 0 && !S.no[i - W]) hit = true;
      else if (y < H - 1 && a[i + W] >= 0 && !S.no[i + W]) hit = true;
      if (hit) add.push(i);
    }
    for (var k = 0; k < add.length; k++) a[add[k]] = c;
  }
  /* rim light: silhouette pixels whose neighbour toward the light is empty take the rim colour */
  function applyRim(S, outs, rim, map) {
    var W = S.w, H = S.h, a = outs[rim.layer || 0], dx = rim.dx, dy = rim.dy;
    var c1 = resolve(rim.c, map), c2 = rim.c2 ? resolve(rim.c2, map) : -1, ids = rim.ids || null;
    var hit = new Uint8Array(W * H);
    for (var y = 0; y < H; y++) for (var x = 0; x < W; x++) {
      var i = y * W + x; if (a[i] < 0 || S.no[i]) continue;
      if (ids && ids.indexOf(S.id[i]) < 0) continue;
      if (rim.y0 != null && (y < rim.y0 || y > rim.y1)) continue;
      var nx = x + dx, ny = y + dy;
      var empty = nx < 0 || ny < 0 || nx >= W || ny >= H || a[ny * W + nx] < 0;
      if (empty) hit[i] = 1;
    }
    for (var j = 0; j < W * H; j++) {
      if (hit[j]) { a[j] = c1; continue; }
      if (c2 >= 0) {
        var px = j % W, py = (j / W) | 0, bx = px + dx, by = py + dy;
        if (a[j] >= 0 && bx >= 0 && by >= 0 && bx < W && by < H && hit[by * W + bx] && bay(px, py) < (rim.d2 == null ? 0.5 : rim.d2)) a[j] = c2;
      }
    }
  }
  function idxToU32(idx) { var u = new Uint32Array(idx.length); for (var i = 0; i < idx.length; i++) u[i] = idx[i] < 0 ? 0 : P32[idx[i]]; return u; }
  function u32ToCanvas(u, w, h) {
    var c = mk(w, h), x = ctx2d(c), im = x.createImageData(w, h);
    new Uint32Array(im.data.buffer).set(u); x.putImageData(im, 0, 0); return c;
  }
  function upscale(src, k) {
    var c = mk(src.width * k, src.height * k), x = ctx2d(c);
    x.drawImage(src, 0, 0, c.width, c.height); return c;
  }
  /* soft pixel halo: dithered accent pixels fading with distance from the silhouette */
  function halo(idx, W, H, ramp, radius) {
    var dist = new Float32Array(W * H), i, x, y, R = radius;
    for (i = 0; i < W * H; i++) dist[i] = idx[i] >= 0 ? 0 : 99;
    for (var it = 0; it < R; it++) {
      for (y = 0; y < H; y++) for (x = 0; x < W; x++) {
        i = y * W + x; if (dist[i] === 0) continue;
        var m = dist[i];
        if (x > 0) m = Math.min(m, dist[i - 1] + 1); if (x < W - 1) m = Math.min(m, dist[i + 1] + 1);
        if (y > 0) m = Math.min(m, dist[i - W] + 1); if (y < H - 1) m = Math.min(m, dist[i + W] + 1);
        if (x > 0 && y > 0) m = Math.min(m, dist[i - W - 1] + 1.41);
        if (x < W - 1 && y < H - 1) m = Math.min(m, dist[i + W + 1] + 1.41);
        if (x > 0 && y < H - 1) m = Math.min(m, dist[i + W - 1] + 1.41);
        if (x < W - 1 && y > 0) m = Math.min(m, dist[i - W + 1] + 1.41);
        dist[i] = m;
      }
    }
    var out = new Int16Array(idx);
    for (y = 0; y < H; y++) for (x = 0; x < W; x++) {
      i = y * W + x; var d = dist[i]; if (d === 0 || d > R) continue;
      var t = 1 - (d - 1) / R;           /* 1 at the outline, 0 at the edge */
      if (Math.pow(t, 1.6) * 0.72 > bay(x, y)) out[i] = ramp[clamp(Math.floor(t * t * (ramp.length - 0.01)), 0, ramp.length - 1)];
    }
    return out;
  }

  /* =====================================================================
     1. CLASS ICONS - 32x32, lit from the top-left, gold/metal + one accent
     ===================================================================== */
  var CLASSES = [
    ['barbarian', 'Barbarian', 'ember'], ['bard', 'Bard', 'blood'], ['cleric', 'Cleric', 'holy'], ['druid', 'Druid', 'green'],
    ['fighter', 'Fighter', 'blood'], ['monk', 'Monk', 'holy'], ['paladin', 'Paladin', 'moon'], ['ranger', 'Ranger', 'green'],
    ['rogue', 'Rogue', 'moon'], ['sorcerer', 'Sorcerer', 'ember'], ['warlock', 'Warlock', 'green'], ['wizard', 'Wizard', 'moon'],
    ['artificer', 'Artificer', 'moon'], ['bloodhunter', 'Blood Hunter', 'blood'], ['other', 'Adventurer', 'gold']
  ];
  var CLASS_BY = {}; CLASSES.forEach(function (c) { CLASS_BY[c[0]] = c; });
  var ALIASES = [
    ['blood hunter', 'bloodhunter'], ['bloodhunter', 'bloodhunter'], ['hemocraft', 'bloodhunter'],
    ['barbarian', 'barbarian'], ['berserker', 'barbarian'], ['barb', 'barbarian'],
    ['bard', 'bard'], ['minstrel', 'bard'], ['skald', 'bard'],
    ['cleric', 'cleric'], ['priest', 'cleric'], ['priestess', 'cleric'], ['acolyte', 'cleric'],
    ['druid', 'druid'], ['circle of', 'druid'], ['shaman', 'druid'],
    ['fighter', 'fighter'], ['warrior', 'fighter'], ['eldritch knight', 'fighter'], ['battle master', 'fighter'], ['champion', 'fighter'], ['samurai', 'fighter'], ['knight', 'fighter'], ['gladiator', 'fighter'],
    ['monk', 'monk'], ['way of', 'monk'],
    ['paladin', 'paladin'], ['oath of', 'paladin'], ['crusader', 'paladin'],
    ['ranger', 'ranger'], ['hunter', 'ranger'], ['gloom stalker', 'ranger'], ['beast master', 'ranger'], ['archer', 'ranger'],
    ['rogue', 'rogue'], ['rouge', 'rogue'], ['thief', 'rogue'], ['assassin', 'rogue'], ['arcane trickster', 'rogue'], ['swashbuckler', 'rogue'],
    ['sorcerer', 'sorcerer'], ['sorceror', 'sorcerer'], ['sorceress', 'sorcerer'],
    ['warlock', 'warlock'], ['hexblade', 'warlock'], ['witch', 'warlock'],
    ['wizard', 'wizard'], ['mage', 'wizard'], ['bladesinger', 'wizard'], ['necromancer', 'wizard'], ['evoker', 'wizard'], ['illusionist', 'wizard'], ['magus', 'wizard'],
    ['artificer', 'artificer'], ['artificier', 'artificer'], ['tinker', 'artificer'], ['alchemist', 'artificer'], ['armorer', 'artificer'], ['artillerist', 'artificer']
  ];
  function lev(a, b) {
    if (Math.abs(a.length - b.length) > 2) return 9;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      for (j = 1; j <= b.length; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = cur;
    }
    return prev[b.length];
  }
  var keyCache = {};
  function classKey(s) {
    var raw = String(s == null ? '' : s);
    if (keyCache[raw]) return keyCache[raw];
    var t = ' ' + raw.toLowerCase().replace(/[^a-z]+/g, ' ').trim() + ' ';
    var best = null, bestAt = 1e9;
    ALIASES.forEach(function (a) {
      var at = t.indexOf(a[0]);
      if (at >= 0) {
        /* a word must start at a word boundary ("barb" inside "rhubarb" does not count) */
        if (t.charAt(at - 1) !== ' ') return;
        if (a[0].length <= 4 && t.charAt(at + a[0].length) !== ' ') return;
        if (at < bestAt || (at === bestAt && best && a[0].length > best[0].length)) { best = a; bestAt = at; }
      }
    });
    var key = best ? best[1] : null;
    if (!key) {                                  /* typos: nearest class word within 2 edits */
      var words = t.trim().split(' '), bd = 3;
      words.forEach(function (w) {
        if (w.length < 4) return;
        CLASSES.forEach(function (c) { if (c[0] === 'other') return; var d = lev(w, c[0]); if (d < bd && d <= (w.length > 6 ? 2 : 1)) { bd = d; key = c[0]; } });
      });
    }
    key = key || 'other';
    keyCache[raw] = key;
    return key;
  }

  var ICON = {};
  function P(p) { return [Math.floor(p[0]), Math.floor(p[1])]; }
  /* Barbarian: a double-bit greataxe with an ember grip wrap */
  ICON.barbarian = function (S, A) {
    var ang = Math.atan2(-18, 11), hx = 17.4, hy = 11.4, T = xf(hx, hy, ang), Lc = inv(hx, hy, ang);
    var b0 = T(-20.8, 0), b1 = T(6.5, 0);
    S.seg(b0[0], b0[1], b1[0], b1[1], 1.6, 1.6, { m: 'wood', l: 0.62, g: gCyl(b0[0], b0[1], b1[0], b1[1], 1.6, 0.55), ol: 1 });
    var w0 = T(-19, 0), w1 = T(-10.5, 0);
    S.seg(w0[0], w0[1], w1[0], w1[1], 1.95, 1.95, { m: A, l: 0.5, g: gCyl(w0[0], w0[1], w1[0], w1[1], 1.95, 0.5), ol: 1,
      tex: function (x, y) { var a = Lc(x + 0.5, y + 0.5)[0]; return (Math.floor(a * 0.95 + 20) % 2) ? 0.16 : -0.16; } });
    var p = T(-21.2, 0); S.ell(p[0], p[1], 1.9, 1.9, { m: 'gold', l: 0.6, g: gSph(p[0], p[1], 1.9, 1.9, 0.6), ol: 1 });
    var bl = [[-1.4, 1.2], [-3, 3.1], [-5.8, 4.8], [-7.8, 5.5], [-8.2, 7.6], [-6, 9.2], [-2.3, 10], [2.3, 10], [6, 9.2], [8.2, 7.6], [7.8, 5.5], [5.8, 4.8], [3, 3.1], [1.4, 1.2]];
    [1, -1].forEach(function (sd) {
      var pts = mapPts(T, bl.map(function (q) { return [q[0], q[1] * sd]; }));
      S.poly(pts, { m: 'steel', l: 0.5, bev: 0.14, ol: 1,
        g: function (x, y) { var q = Lc(x, y), e = Math.abs(q[1]) / 10; return 0.5 * e * e - 0.05 + (q[0] > 0 ? 0.08 : -0.06) + (sd < 0 ? 0.06 : -0.04); } });
      /* sharpened edge: a bright band just inside each crescent */
      S.shape(function (x, y) { var q = Lc(x, y), a = q[0], b = q[1] * sd; if (b < 6.4) return false; var edge = 10 - Math.pow(Math.abs(a) / 8.2, 2.2) * 3.0; return b > edge - 1.3 && b < edge; },
        bbox(mapPts(T, [[-9, 5 * sd], [9, 5 * sd], [-9, 11 * sd], [9, 11 * sd]])), { m: 'iron', l: sd < 0 ? 0.99 : 0.8, on: 'any' });
    });
    S.poly(mapPts(T, [[-3.2, -2.4], [3.2, -2.4], [3.2, 2.4], [-3.2, 2.4]]), { m: 'gold', l: 0.55, bev: 0.22, ol: 1 });
    S.poly(mapPts(T, [[6, -1.2], [10.6, 0], [6, 1.2]]), { m: 'steel', l: 0.7, bev: 0.2, ol: 1 });
    var r1 = P(T(0, -1.1)); S.px(r1[0], r1[1], 'g5');
  };
  /* Bard: a pear-bodied lute with a rosette and a crimson ribbon */
  ICON.bard = function (S, A) {
    var ang = -0.93, cx = 11.6, cy = 21.2, T = xf(cx, cy, ang), Lc = inv(cx, cy, ang);
    var n0 = T(8, 0), n1 = T(19.6, 0);
    S.seg(n0[0], n0[1], n1[0], n1[1], 1.6, 1.4, { m: 'wood', l: 0.5, g: gCyl(n0[0], n0[1], n1[0], n1[1], 1.5, 0.5), ol: 1 });
    var pb0 = T(19.2, 0), pb1 = T(23.4, 2.1);
    S.seg(pb0[0], pb0[1], pb1[0], pb1[1], 1.55, 1.25, { m: 'wood', l: 0.32, g: gCyl(pb0[0], pb0[1], pb1[0], pb1[1], 1.4, 0.4), ol: 1 });
    var W8 = function (a) { if (a < -9.6 || a > 10) return -1; if (a < -2.4) return 7.3 * Math.sqrt(Math.max(0, 1 - Math.pow((a + 2.4) / 7.3, 2))); return 7.3 - 5.5 * Math.pow((a + 2.4) / 12.4, 1.25); };
    var body = function (x, y) { var q = Lc(x, y), w = W8(q[0]); return w > 0 && Math.abs(q[1]) <= w; };
    S.shape(body, [0, 6, 26, 32], { m: 'pine', l: 0.56, g: gSph(cx - 1, cy + 1, 8.5, 8.5, 0.55), ol: 1 });
    S.shape(function (x, y) { if (!body(x, y)) return false; return !(body(x - 1.2, y) && body(x + 1.2, y) && body(x, y - 1.2) && body(x, y + 1.2)); }, [0, 6, 26, 32], { m: 'wood', l: 0.42, on: 'any' });
    var h = T(1.2, 0);
    S.ring(h[0], h[1], 1.9, 3.1, { m: 'gold', l: 0.6, g: gSph(h[0], h[1], 3.1, 3.1, 0.5), on: 'any' });
    S.ell(h[0], h[1], 1.95, 1.95, { fx: 'ol', on: 'any' });
    S.poly(mapPts(T, [[-7, -3.4], [-5.6, -3.4], [-5.6, 3.4], [-7, 3.4]]), { m: 'wood', l: 0.18, bev: 0.2, on: 'any' });
    [-0.8, 0.8].forEach(function (b) { var s0 = T(-6.3, b), s1 = T(19.4, b * 0.55); S.seg(s0[0], s0[1], s1[0], s1[1], 0.45, 0.45, { m: 'linen', l: 0.72, noOl: 1, on: 'any' }); });
    /* a crimson bow tied where the pegbox bends, one tail streaming */
    var k0 = T(18.6, 0);
    S.poly(path([[k0[0], k0[1]], ['q', k0[0] + 5.5, k0[1] + 1.5, k0[0] + 5.4, k0[1] + 7.2], ['q', k0[0] + 4.8, k0[1] + 11, k0[0] + 7.8, k0[1] + 14.2], [k0[0] + 4.4, k0[1] + 13.8], ['q', k0[0] + 2.2, k0[1] + 9.4, k0[0] + 3, k0[1] + 5.6], ['q', k0[0] + 2.6, k0[1] + 2.6, k0[0], k0[1] + 1.6]]), { m: A, l: 0.6, g: gLin(k0[0], k0[1], k0[0] + 6, k0[1] + 14, 0.45), bev: 0.18, ol: 1 });
    S.ell(k0[0] + 0.6, k0[1] + 0.8, 1.7, 1.5, { m: A, l: 0.72, ol: 1 });
    [[20.4, -1.7], [22, -1.4], [19.8, 2.1], [21.6, 2.6]].forEach(function (q) { var pp = P(T(q[0], q[1] + (q[1] < 0 ? -0.5 : 0.7))); S.px(pp[0], pp[1], 'g4'); });
  };
  /* Cleric: a radiant sun pendant on a short chain */
  ICON.cleric = function (S, A) {
    var cx = 16, cy = 18;
    for (var k = 0; k < 8; k++) {
      if (k === 6) continue;
      var a = k * Math.PI / 4, long = k % 2 === 0, r0 = 9, r1 = long ? 14.8 : 13.2;
      var c = Math.cos(a), s = Math.sin(a);
      S.seg(cx + c * r0, cy + s * r0, cx + c * r1, cy + s * r1, long ? 1.7 : 1.35, 0.45, { m: A, l: 0.78, g: gLin(cx + c * r0, cy + s * r0, cx + c * r1, cy + s * r1, 0.35), ol: 1 });
    }
    S.ring(16, 4.4, 1.3, 2.5, { m: 'gold', l: 0.62, bev: 0.2, ol: 1 });
    S.seg(16, 6.4, 16, 9.2, 1.0, 1.0, { m: 'gold', l: 0.45, bev: 0.2, ol: 1 });
    S.ell(cx, cy, 9.3, 9.3, { m: 'gold', l: 0.5, g: gSph(cx, cy, 9.3, 9.3, 0.75), bev: 0.12, ol: 1 });
    S.ell(cx, cy, 6.7, 6.7, { m: 'shadow', l: 0.3, g: gSph(cx + 2, cy + 2, 7, 7, -0.3), on: 'any' });
    S.ring(cx, cy, 6.3, 7.1, { m: 'gold', l: 0.18, on: 'any' });
    var st = [];
    for (var j = 0; j < 16; j++) { var aa = j * Math.PI / 8 - Math.PI / 2, rr = j % 2 === 0 ? (j % 4 === 0 ? 6.3 : 4.4) : 1.8; st.push([cx + Math.cos(aa) * rr, cy + Math.sin(aa) * rr]); }
    S.poly(st, { m: A, l: 0.62, g: gSph(cx - 1, cy - 1, 6, 6, 0.5), on: 'any' });
    S.ell(cx, cy, 1.6, 1.6, { m: A, l: 1 });
    S.px(15, 17, 'hw'); S.px(10, 13, 'g5'); S.px(11, 12, 'g5'); S.px(9, 14, 'g4');
  };
  /* Druid: an oak sprig with lobed leaves and a gilded acorn */
  ICON.druid = function (S, A) {
    var tw = [[4.6, 29.4], [8.8, 24.6], [13.4, 19.6], [17.4, 14.8], [20.8, 10], [23.2, 6]];
    for (var i = 0; i < tw.length - 1; i++) S.seg(tw[i][0], tw[i][1], tw[i + 1][0], tw[i + 1][1], 1.45 - i * 0.13, 1.32 - i * 0.13, { m: 'wood', l: 0.6, g: gCyl(tw[i][0], tw[i][1], tw[i + 1][0], tw[i + 1][1], 1.3, 0.5), ol: 1 });
    function leaf(bx, by, ang, len, wid, lit) {
      var T = inv(bx, by, ang);
      var test = function (x, y) {
        var q = T(x, y), u = q[0] / len, v = Math.abs(q[1]);
        if (u < 0 || u > 1) return false;
        var lobes = 0.52 + 0.48 * Math.abs(Math.cos(u * Math.PI * 3.1 + 0.2));
        var w = wid * Math.pow(Math.sin(Math.PI * Math.min(1, u * 1.03)), 0.7) * lobes;
        return v <= w + 0.2;
      };
      var R = len + 2;
      var id = S.shape(test, [bx - R, by - R, bx + R, by + R], { m: 'leaf', l: 0.6 + lit, bev: 0.2, ol: 1,
        g: function (x, y) { var q = T(x, y); return (q[1] < 0 ? 0.1 : -0.1) + 0.16 * (0.5 - q[0] / len); } });
      S.shape(function (x, y) { var q = T(x, y); return q[0] > 1 && q[0] < len * 0.82 && Math.abs(q[1]) < 0.55; }, [bx - R, by - R, bx + R, by + R], { m: A, l: 0.7, on: id });
      return id;
    }
    leaf(12.4, 20.6, -2.3, 12.4, 5.2, 0.06);
    leaf(18.2, 13.8, -0.3, 12.6, 4.8, 0);
    leaf(22, 8, -2.0, 7.4, 3.3, 0.08);
    S.seg(15.4, 17.8, 17.6, 20.6, 0.75, 0.75, { m: 'wood', l: 0.45, ol: 1 });
    S.ell(18.9, 25.4, 4, 4.6, { m: 'gold', l: 0.52, g: gSph(18.9, 25.4, 4, 4.6, 0.8), ol: 1 });
    S.ell(18.8, 21.5, 4.8, 2.7, { m: 'wood', l: 0.5, g: gSph(18.8, 21.1, 4.8, 2.7, 0.5), ol: 1,
      tex: function (x, y) { return ((x + y) % 2 === 0) ? 0.2 : -0.1; } });
    S.px(17, 24, 'hw'); S.px(17, 25, 'g5'); S.px(16, 25, 'g4'); S.px(19, 19, 'lt4');
  };
  /* Fighter: a broad longsword over a crimson heater shield */
  ICON.fighter = function (S, A) {
    var cxs = 17.4;
    var sh = function (x, y) { if (y < 4.5 || y > 29.5) return false; var w = y < 14.5 ? 10 : 10 * Math.sqrt(Math.max(0, 1 - Math.pow((y - 14.5) / 15, 1.6))); return Math.abs(x - cxs) <= w; };
    var shin = function (x, y) { return sh(x - 1.8, y) && sh(x + 1.8, y) && sh(x, y - 1.8) && sh(x, y + 2.2); };
    S.shape(sh, [5, 3, 30, 31], { m: 'iron', l: 0.58, g: gSph(15, 12, 12, 16, 0.6), bev: 0.22, ol: 1 });
    S.shape(shin, [5, 3, 30, 31], { m: A, l: 0.5, g: gSph(15, 11, 11, 14, 0.85), ol: 1 });
    S.shape(function (x, y) { return shin(x, y) && Math.abs(x - cxs) < 1.1; }, [5, 3, 30, 31], { m: 'gold', l: 0.55, on: 'any' });
    [[10, 7], [25, 7], [10, 16], [25, 16]].forEach(function (p) { S.px(p[0], p[1], 'i4'); });
    var tip = [26.6, 3.2], gd = [11.2, 20.6];
    S.seg(gd[0], gd[1], tip[0], tip[1], 2.2, 0.6, { m: 'steel', l: 0.68, g: gRidge(gd[0], gd[1], tip[0], tip[1], 0.2), ol: 1 });
    S.seg(gd[0] + 1.6, gd[1] - 1.8, tip[0] - 4.2, tip[1] + 4.6, 0.4, 0.4, { m: 'iron', l: 0.25, on: 'any' });
    S.seg(7.8, 17.2, 14.6, 24.4, 1.3, 1.3, { m: 'gold', l: 0.55, g: gCyl(7.8, 17.2, 14.6, 24.4, 1.3, 0.6), ol: 1 });
    S.seg(10.4, 21.8, 6.8, 25.6, 1.15, 1.15, { m: 'wood', l: 0.45, g: gCyl(10.4, 21.8, 6.8, 25.6, 1.1, 0.5), ol: 1, tex: function (x, y) { return ((x + y) % 2) ? 0.12 : -0.1; } });
    S.ell(5.6, 27, 2.1, 2.1, { m: 'gold', l: 0.58, g: gSph(5.6, 27, 2.1, 2.1, 0.7), ol: 1 });
    S.px(5, 26, 'g5'); S.px(23, 7, 'i4'); S.px(24, 6, 'hw');
  };
  /* Monk: a raised open palm with ki in its centre, linen wraps and prayer beads */
  ICON.monk = function (S, A) {
    S.ring(16, 14.6, 11.4, 13.4, { m: A, l: 0.45, noOl: 1, g: gLin(6, 3, 26, 26, 0.6), tex: function (x, y) { return bay(x, y) > 0.62 ? -3 : 0; } });
    var fing = [[11.4, 15, 10.4, 6.6, 1.65], [14.6, 14, 14.4, 4.4, 1.7], [17.9, 14, 18.3, 5.3, 1.65], [21, 15.4, 22.2, 9, 1.45]];
    fing.forEach(function (f) { S.seg(f[0], f[1], f[2], f[3], f[4], f[4] - 0.15, { m: 'skin', l: 0.62, ol: 1, g: gCyl(f[0], f[1], f[2], f[3], f[4], 0.5) }); });
    S.seg(11.6, 21.6, 6.2, 15.4, 1.95, 1.5, { m: 'skin', l: 0.6, ol: 1, g: gCyl(11.6, 21.6, 6.2, 15.4, 1.9, 0.5) });
    var palm = path([[9.6, 13.6], ['q', 16, 12.2, 22.6, 14.2], ['q', 23.2, 20.5, 20.6, 24.6], [11.6, 24.6], ['q', 9, 19.5, 9.6, 13.6]]);
    S.poly(palm, { m: 'skin', l: 0.55, g: gSph(15.6, 17.8, 8, 7, 0.45), bev: 0.1, ol: 1 });
    S.ell(16, 19, 2.6, 2.4, { m: A, l: 0.5, on: 'any', g: gSph(16, 19, 2.6, 2.4, -0.4) });
    S.ell(16, 19, 1.3, 1.2, { m: A, l: 1, on: 'any' });
    S.rect(11.4, 23.4, 9.6, 7.4, { m: 'linen', l: 0.62, g: gLin(11, 24, 21, 30, 0.35), bev: 0.14, ol: 1, tex: function (x, y) { return ((y + (x > 15 ? 1 : 0)) % 3 === 0) ? -0.28 : 0.04; } });
    var arc = []; for (var i = 0; i <= 6; i++) { var t = i / 6, an = Math.PI * (0.96 - 0.92 * t); arc.push([16.2 + Math.cos(an) * 7.4, 26.6 + Math.sin(an) * 3.4]); }
    arc.forEach(function (p, j) { var gd = j % 3 === 0; S.ell(p[0], p[1], 1.3, 1.3, { m: gd ? 'gold' : 'wood', l: gd ? 0.6 : 0.66, g: gSph(p[0], p[1], 1.3, 1.3, 0.8), ol: 1 }); });
    S.seg(16.2, 30, 16.2, 31.5, 0.9, 0.7, { m: A, l: 0.55, ol: 1 });
    S.ell(16.2, 29.6, 1.7, 1.6, { m: 'gold', l: 0.62, g: gSph(16.2, 29.6, 1.7, 1.6, 0.8), ol: 1 });
    S.px(15, 28, 'hw');
  };
  /* Paladin: a gilded heater shield with a sun sigil on moonlit azure */
  ICON.paladin = function (S, A) {
    var sh = function (x, y) { if (y < 3.5 || y > 30) return false; var w = y < 14 ? 11.4 - Math.max(0, (6 - y)) * 0.3 : 11.4 * Math.sqrt(Math.max(0, 1 - Math.pow((y - 14) / 16, 1.7))); return Math.abs(x - 16) <= w; };
    var shin = function (x, y) { return sh(x - 2, y) && sh(x + 2, y) && sh(x, y - 2) && sh(x, y + 2.3); };
    S.shape(sh, [3, 2, 29, 31], { m: 'gold', l: 0.55, g: gSph(14, 11, 13, 17, 0.6), bev: 0.2, ol: 1 });
    S.shape(shin, [3, 2, 29, 31], { m: A, l: 0.45, g: gSph(13, 10, 12, 15, 0.75), ol: 1 });
    var cx = 16, cy = 15.2, st = [];
    for (var j = 0; j < 24; j++) { var a = j * Math.PI / 12 - Math.PI / 2, r = j % 2 === 0 ? (j % 4 === 0 ? 9.2 : 7.4) : 4.6; st.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]); }
    S.poly(st, { m: 'gold', l: 0.62, g: gSph(cx, cy, 9, 9, 0.45), bev: 0.18, ol: 1 });
    S.ell(cx, cy, 3.9, 3.9, { m: 'holy', l: 0.55, g: gSph(cx, cy, 3.9, 3.9, 0.7), ol: 1 });
    S.px(14, 13, 'hw'); S.px(6, 6, 'g5'); S.px(7, 5, 'g5');
  };
  /* Ranger: a recurve longbow with a nocked, green-fletched arrow */
  ICON.ranger = function (S, A) {
    var ang = -Math.PI / 4, cx = 14.2, cy = 17.8, T = xf(cx, cy, ang);
    var bow = [];
    for (var i = 0; i <= 16; i++) { var t = i / 16, b = lerp(-13.4, 13.4, t), a = -7.2 * Math.sin(Math.PI * t) + (t < 0.1 ? 1.8 * (0.1 - t) / 0.1 : t > 0.9 ? 1.8 * (t - 0.9) / 0.1 : 0); bow.push(T(a, b)); }
    var s0 = bow[0], s1 = bow[16], nock = T(1.4, 0);
    S.seg(s0[0], s0[1], nock[0], nock[1], 0.5, 0.5, { m: 'linen', l: 0.75 });
    S.seg(nock[0], nock[1], s1[0], s1[1], 0.5, 0.5, { m: 'linen', l: 0.75 });
    for (var k = 0; k < bow.length - 1; k++) {
      var d = Math.abs(k + 0.5 - 8), mid = d < 1.6, r0 = mid ? 1.9 : 1.75 - d * 0.07, r1 = r0;
      S.seg(bow[k][0], bow[k][1], bow[k + 1][0], bow[k + 1][1], r0, r1, { m: mid ? 'gold' : 'wood', l: mid ? 0.55 : 0.66, g: gCyl(bow[k][0], bow[k][1], bow[k + 1][0], bow[k + 1][1], 1.6, 0.6), ol: mid ? 1 : 0 });
    }
    var a0 = T(0.6, 0), a1 = T(15.4, 0);
    S.seg(a0[0], a0[1], a1[0], a1[1], 0.85, 0.85, { m: 'pine', l: 0.62, ol: 1 });
    S.poly(mapPts(T, [[14.2, -2.9], [19.8, 0], [14.2, 2.9], [15.4, 0]]), { m: 'steel', l: 0.7, bev: 0.25, ol: 1 });
    [1, -1].forEach(function (sd) { S.poly(mapPts(T, [[1.6, 0.4 * sd], [2.2, 3.1 * sd], [7.4, 2.6 * sd], [8, 0.4 * sd]]), { m: A, l: sd > 0 ? 0.42 : 0.7, bev: 0.15, ol: 1 }); });
    [bow[0], bow[16]].forEach(function (p) { S.ell(p[0], p[1], 1.2, 1.2, { m: 'gold', l: 0.7, ol: 1 }); });
  };
  /* Rogue: a hood's shadow with cold eyes, a dagger held ready in front */
  ICON.rogue = function (S, A) {
    var hood = path([[16, 2.2], ['q', 26.5, 5, 26.5, 17], ['q', 27, 24, 30, 30.5], [2, 30.5], ['q', 5, 24, 5.5, 17], ['q', 5.5, 5, 16, 2.2]]);
    var hin = pip(hood);
    S.poly(hood, { m: 'shadow', l: 0.6, g: gAdd(gSph(13, 11, 15, 19, 0.6), gLin(4, 2, 28, 30, 0.2)), ol: 1 });
    S.shape(function (x, y) { return y > 5 && hin(x, y) && !hin(x - 1.4, y); }, [0, 0, 16, 31], { m: A, l: 0.62, on: 'any' });
    S.shape(function (x, y) { return y > 3 && y < 12 && hin(x, y) && !hin(x - 1.2, y - 1.2); }, [0, 0, 20, 14], { m: A, l: 0.5, on: 'any' });
    S.poly(path([[16, 9], ['q', 22.5, 10, 22, 17.5], ['q', 21, 22.5, 16, 23], ['q', 11, 22.5, 10, 17.5], ['q', 9.5, 10, 16, 9]]), { fx: 'ol' });
    S.pxl([12, 16, 13, 16], 'm4'); S.pxl([18, 16, 19, 16], 'm4'); S.px(12, 16, 'm5'); S.px(19, 16, 'm3');
    var tip = [7, 16.2], gd = [19.4, 25.6];
    S.seg(gd[0], gd[1], tip[0], tip[1], 1.9, 0.4, { m: 'steel', l: 0.74, g: gRidge(gd[0], gd[1], tip[0], tip[1], 0.22), ol: 1 });
    S.seg(17, 28.4, 22, 22.8, 1.15, 1.15, { m: 'gold', l: 0.6, g: gCyl(17, 28.4, 22, 22.8, 1.1, 0.6), ol: 1 });
    S.seg(20.4, 26.6, 24.2, 29.2, 1.15, 1.15, { m: 'wood', l: 0.4, ol: 1 });
    S.ell(25.2, 29.8, 1.4, 1.4, { m: 'gold', l: 0.62, ol: 1 });
    S.px(9, 18, 'm5'); S.px(10, 18, 'm4');
  };
  /* Sorcerer: magic from the blood, an orb of living fire gripped in a gold dragon's claw, crackling with violet arcane
     force */
  ICON.sorcerer = function (S, A) {
    var cx = 16, cy = 13.6, R = 8.9;
    /* the claw's stem and three talons, the front one over the orb, the side ones behind its edges */
    S.poly(path([[13.4, 31.4], [18.6, 31.4], [18.2, 27.6], ['q', 16, 26.4, 13.8, 27.6]]), { m: 'gold', l: 0.46, g: gCyl(13.4, 29.5, 18.6, 29.5, 2.6, 0.6), bev: 0.18, ol: 1 });
    S.rect(12.6, 26, 6.8, 2.2, { m: 'gold', l: 0.62, g: gCyl(12.6, 27, 19.4, 27, 1.1, 0.6), bev: 0.2, ol: 1 });
    [[-1, [13.4, 26.8], [7, 24.2], [5.6, 16.6]], [1, [18.6, 26.8], [25, 24.2], [26.4, 16.6]]].forEach(function (t) {
      var pts = slashPts(t[1], t[2], t[3], 1.9);
      S.poly(pts, { m: 'gold', l: t[0] < 0 ? 0.6 : 0.42, g: gLin(t[1][0], t[1][1], t[3][0], t[3][1], 0.2), bev: 0.16, ol: 1 });
    });
    /* the orb: embers lit from the upper left, a turning band of deeper fire, a violet rim where the arcane light holds it */
    var oid = S.ell(cx, cy, R, R, { m: A, l: 0.48, g: gSph(cx - 2.6, cy - 2.8, R + 1, R + 1, 0.55), dz: 3, ol: 1 });
    var swirl = function (x, y, lo, hi) { var dx = x - cx, dy = y - cy, r = Math.sqrt(dx * dx + dy * dy), a = Math.atan2(dy, dx); var b = ((a + r * 0.5) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2); return r > 2.2 && r < R - 1.2 && b > lo && b < hi; };
    S.shape(function (x, y) { return swirl(x, y, 1.1, 2.1) || swirl(x, y, 4.3, 5.1); }, [cx - R, cy - R, cx + R, cy + R], { m: A, l: 0.12, on: [oid], dz: 5 });
    S.shape(function (x, y) { return swirl(x, y, 2.4, 3.1); }, [cx - R, cy - R, cx + R, cy + R], { m: A, l: 0.86, on: [oid], dz: 5 });
    S.shape(function (x, y) { var dx = x - cx, dy = y - cy, r = Math.sqrt(dx * dx + dy * dy); return r > R - 1.7 && dx + dy > 2.4; }, [cx - R, cy - R, cx + R + 1, cy + R + 1], { fx: 'y2', on: [oid] });
    S.ell(cx - 2.2, cy - 2.4, 3.4, 3.2, { m: 'holy', l: 0.62, g: gSph(cx - 3, cy - 3.2, 3.4, 3.2, 0.5), on: [oid] });
    S.ell(cx - 3.2, cy - 3.6, 1.2, 1.1, { fx: 'hw', on: 'any' });
    /* the front talon over the orb's foot */
    S.poly(slashPts([16, 27], [15.2, 24.2], [16.6, 20.6], 1.7), { m: 'gold', l: 0.66, g: gLin(16, 27, 16, 20, 0.24), bev: 0.18, ol: 1 });
    S.px(16, 21, 'g5'); S.px(6, 18, 'g5'); S.px(13, 27, 'g5');
    /* the crackle: jagged violet arcs leaping off the orb, bright at the kinks */
    [[[9.4, 6.6], [7.8, 5.6], [8.6, 3.8], [6.6, 2.6]], [[22.8, 7.4], [24.8, 6.8], [24.4, 4.8], [26.8, 3.8]], [[24.8, 13.2], [27, 14.2], [26.6, 12.2], [29.4, 11.6]]].forEach(function (ln) {
      for (var i = 1; i < ln.length; i++) S.seg(ln[i - 1][0], ln[i - 1][1], ln[i][0], ln[i][1], 0.5, 0.5, { fx: 'y2' });
      S.px(Math.floor(ln[1][0]), Math.floor(ln[1][1]), 'y3'); S.px(Math.floor(ln[3][0]), Math.floor(ln[3][1]), 'y3');
    });
    dot(S, 4, 9, 'y3'); dot(S, 28, 7, 'y2'); dot(S, 19, 2, 'y3');
    aura(S, cx, cy, R + 4.2, A, 0.5, 0.18, 1.2);
  };
  /* Warlock: a sealed pact tome, strapped shut, clasped with a single watching eye */
  ICON.warlock = function (S, A) {
    S.poly([[9, 6], [27, 6], [28.5, 8], [28.5, 29], [10, 29]], { m: 'linen', l: 0.55, ol: 1, tex: function (x, y) { return (y % 2) ? 0.15 : -0.1; } });
    S.poly([[4.5, 4], [24.5, 4], [25.5, 5], [25.5, 27.5], [4.5, 27.5]], { m: 'wood', l: 0.5, g: gSph(12, 12, 20, 22, 0.55), bev: 0.18, ol: 1 });
    S.rect(4.5, 4, 3.2, 23.5, { m: 'wood', l: 0.24, bev: 0.2, on: 'any', tex: function (x, y) { return (y % 7 === 3) ? 0.4 : 0; } });
    S.rect(9.5, 7.5, 13.5, 17, { fx: 'k1', on: 'any' });
    S.rect(10.5, 8.5, 11.5, 15, { m: 'wood', l: 0.5, on: 'any', g: gSph(14, 12, 12, 14, 0.5) });
    [[4.5, 4, 1, 1], [25.5, 4, -1, 1], [4.5, 27.5, 1, -1], [25.5, 27.5, -1, -1]].forEach(function (c) {
      S.poly([[c[0], c[1]], [c[0] + 5 * c[2], c[1]], [c[0], c[1] + 5 * c[3]]], { m: 'gold', l: 0.58, bev: 0.25, ol: 1 });
    });
    S.rect(15, 13, 16, 5.5, { m: 'wood', l: 0.3, bev: 0.2, ol: 1 });
    var ex = 17.4, ey = 15.6;
    var eye = function (x, y) { var u = (x - ex) / 7.4, v = (y - ey) / 4.2; return Math.abs(v) <= (1 - u * u) && Math.abs(u) <= 1; };
    S.shape(eye, [9, 10, 26, 21], { m: 'gold', l: 0.58, bev: 0.25, g: gSph(ex, ey, 7, 4, 0.5), ol: 1 });
    S.shape(function (x, y) { var u = (x - ex) / 5.2, v = (y - ey) / 2.5; return Math.abs(v) <= (1 - u * u) && Math.abs(u) <= 1; }, [9, 10, 26, 21], { m: 'linen', l: 0.72, on: 'any' });
    S.ell(ex, ey, 2.3, 2.3, { m: A, l: 0.66, g: gSph(ex, ey, 2.3, 2.3, 0.6), on: 'any' });
    S.rect(ex - 0.5, ey - 2, 1, 4, { fx: 'ol', on: 'any' });
    S.px(16, 14, 'hw');
  };
  /* Wizard: a staff with a gold claw holding a moon crystal */
  ICON.wizard = function (S, A) {
    var s0 = [7.4, 30.8], s1 = [17.2, 13.6];
    S.seg(s0[0], s0[1], s1[0], s1[1], 1.45, 1.7, { m: 'wood', l: 0.62, g: gCyl(s0[0], s0[1], s1[0], s1[1], 1.5, 0.5), ol: 1,
      tex: function (x, y) { return hash(x, y, 5) > 0.8 ? -0.2 : 0; } });
    S.seg(9.8, 26.4, 11, 24.2, 1.9, 1.9, { m: 'gold', l: 0.58, g: gCyl(9.8, 26.4, 11, 24.2, 1.9, 0.5), ol: 1 });
    var ang = Math.atan2(s1[1] - s0[1], s1[0] - s0[0]), cx = 20.6, cy = 8.2, T = xf(cx, cy, ang), Lc = inv(cx, cy, ang);
    var cr = mapPts(T, [[-6.2, 0], [-3.2, -4], [4, -4], [8, 0], [4, 4], [-3.2, 4]]);
    S.poly(cr, { m: A, l: 0.52, bev: 0.14, g: function (x, y) { var q = Lc(x, y); if (q[1] < -1.2) return q[0] > 4 ? 0.12 : 0.34; if (q[1] > 1.2) return q[0] > 4 ? -0.24 : -0.1; return q[0] > 4 ? 0.04 : 0.14; } });
    [-1, 1].forEach(function (sd) {
      var a = T(-8.8, 0.9 * sd), b = T(-5.6, 4.6 * sd), c = T(-1.4, 5 * sd);
      S.seg(a[0], a[1], b[0], b[1], 1.15, 1.05, { m: 'gold', l: 0.62, g: gCyl(a[0], a[1], b[0], b[1], 1.1, 0.5), ol: 1 });
      S.seg(b[0], b[1], c[0], c[1], 1.05, 0.65, { m: 'gold', l: 0.56, ol: 1 });
    });
    var cc = T(-8.6, 0); S.ell(cc[0], cc[1], 1.9, 1.9, { m: 'gold', l: 0.55, g: gSph(cc[0], cc[1], 1.9, 1.9, 0.6), ol: 1 });
    var g1 = P(T(-1.8, -2.4)), g2 = P(T(4.6, -1.6)); S.px(g1[0], g1[1], 'm5'); S.px(g2[0], g2[1], 'hw');
    var a1 = T(-3.2, 0), a2 = T(3.8, 0); S.seg(a1[0], a1[1], a2[0], a2[1], 0.45, 0.45, { m: A, l: 0.9, on: 'any' });
    S.pxl([28, 13, 13, 6], 'm4'); S.pxl([28, 1, 27, 2, 29, 2, 28, 3], 'm3'); S.px(28, 2, 'm5');
  };
  /* Artificer: a brass gear with an arcane core, a wrench biting one tooth */
  ICON.artificer = function (S, A) {
    var cx = 13.4, cy = 13.6, N = 8;
    var gear = function (x, y) {
      var dx = x - cx, dy = y - cy, r = Math.sqrt(dx * dx + dy * dy);
      if (r < 3.1) return false; if (r <= 8.6) return true;
      for (var k = 0; k < N; k++) { var a = k * Math.PI * 2 / N + 0.2, ux = Math.cos(a), uy = Math.sin(a), al = dx * ux + dy * uy, pe = Math.abs(-dx * uy + dy * ux); if (al > 0 && al <= 11.8 && pe <= 1.9 + (11.8 - al) * 0.12) return true; }
      return false;
    };
    S.shape(gear, [0, 0, 27, 27], { m: 'gold', l: 0.56, g: gSph(cx, cy, 11, 11, 0.55), bev: 0.22, ol: 1 });
    S.ring(cx, cy, 5.5, 6.3, { m: 'gold', l: 0.22, on: 'any' });
    for (var k = 0; k < 4; k++) { var a = k * Math.PI / 2 + Math.PI / 4 + 0.2; S.ell(cx + Math.cos(a) * 7.3, cy + Math.sin(a) * 7.3, 0.9, 0.9, { fx: 'g1', on: 'any' }); }
    S.ell(cx, cy, 3.3, 3.3, { m: A, l: 0.58, g: gSph(cx, cy, 3.3, 3.3, 0.8), ol: 1 });
    S.px(12, 12, 'm5'); S.px(12, 11, 'hw');
    var e = [28.4, 28.8], h = [20.4, 20.6];
    S.seg(e[0], e[1], h[0], h[1], 1.95, 1.7, { m: 'steel', l: 0.66, g: gCyl(e[0], e[1], h[0], h[1], 1.8, 0.55), ol: 1 });
    var jaw = function (x, y) {
      var dx = x - h[0], dy = y - h[1], d = Math.sqrt(dx * dx + dy * dy); if (d > 4.5) return false;
      var ux = -0.707, uy = -0.707, along = dx * ux + dy * uy, perp = Math.abs(-dx * uy + dy * ux);
      return !(along > -0.4 && perp < 1.6);
    };
    S.shape(jaw, [14, 14, 27, 27], { m: 'steel', l: 0.64, g: gSph(h[0], h[1], 4.5, 4.5, 0.6), bev: 0.18, ol: 1 });
    S.px(18, 19, 'i4'); S.px(27, 27, 'i4');
  };
  /* Blood Hunter: a crimson curved blade shedding a single drop */
  ICON.bloodhunter = function (S, A) {
    var ox = 28.5, oy = 28.8;
    var blade = function (x, y) {
      var dx = x - ox, dy = y - oy, r = Math.sqrt(dx * dx + dy * dy), a = Math.atan2(dy, dx);
      if (a > -1.62 || a < -3.14) return false;
      var t = (-1.62 - a) / 1.52;
      var outer = 22.2, inner = 22.2 - (0.7 + 4.2 * Math.pow(Math.min(1, t * 1.35), 0.7));
      return r <= outer && r >= inner;
    };
    S.shape(blade, [3, 3, 30, 30], { m: A, l: 0.5, ol: 1, g: function (x, y) { var dx = x - ox, dy = y - oy, r = Math.sqrt(dx * dx + dy * dy); return (r > 20.6 ? 0.36 : r > 19.2 ? 0.05 : -0.2); } });
    S.shape(function (x, y) { var dx = x - ox, dy = y - oy, r = Math.sqrt(dx * dx + dy * dy), a = Math.atan2(dy, dx); return a < -1.9 && a > -3.05 && r > 21.1 && r < 22.1; }, [3, 3, 30, 30], { m: 'iron', l: 0.95, on: 'any' });
    S.seg(4.2, 26.2, 10.2, 30.6, 1.2, 1.2, { m: 'gold', l: 0.58, g: gCyl(4.2, 26.2, 10.2, 30.6, 1.2, 0.6), ol: 1 });
    S.seg(6.3, 28.3, 3, 31.2, 1.05, 1.05, { m: 'wood', l: 0.35, ol: 1 });
    S.poly(path([[23.8, 11.4], ['q', 24.2, 15.6, 27, 18.6], ['q', 28.8, 22.4, 25, 23.8], ['q', 20.8, 23.4, 21.8, 19.2], ['q', 23.2, 15.8, 23.8, 11.4]]), { m: 'blood', l: 0.52, g: gSph(24.4, 20, 3.6, 4.2, 0.75), ol: 1 });
    S.px(23, 18, 'e4'); S.px(23, 19, 'b3'); S.px(22, 19, 'e4');
    S.px(20, 7, 'e4'); S.px(19, 7, 'b3');
  };
  /* Other: a gold d20 with the ember-inked 20 */
  ICON.other = function (S, A) {
    var c = [16, 16.2], R = 14.2;
    var V = []; for (var i = 0; i < 6; i++) { var a = -Math.PI / 2 + i * Math.PI / 3; V.push([c[0] + Math.cos(a) * R, c[1] + Math.sin(a) * R]); }
    var T0 = [16, 7.2], TL = [7.8, 21.2], TR = [24.2, 21.2];
    var faces = [
      [[V[0], V[5], T0], 0.84], [[V[0], V[1], T0], 0.62], [[V[5], T0, TL], 0.72], [[V[1], T0, TR], 0.44],
      [[V[5], V[4], TL], 0.55], [[V[1], V[2], TR], 0.26], [[V[4], V[3], TL], 0.34], [[V[2], V[3], TR], 0.12], [[TL, TR, V[3]], 0.22], [[T0, TL, TR], 0.64]
    ];
    faces.forEach(function (f) { S.poly(f[0], { m: 'gold', l: f[1], dz: 1.4 }); });
    var edges = [[T0, TL], [TL, TR], [TR, T0], [T0, V[0]], [T0, V[5]], [T0, V[1]], [TL, V[5]], [TL, V[4]], [TL, V[3]], [TR, V[1]], [TR, V[2]], [TR, V[3]]];
    edges.forEach(function (e, j) { S.seg(e[0][0], e[0][1], e[1][0], e[1][1], 0.42, 0.42, { m: 'gold', l: j < 3 ? 0.98 : 0.3, on: 'any' }); });
    var two = ['111', '001', '111', '100', '111'], zero = ['111', '101', '101', '101', '111'];
    [[two, 12], [zero, 16]].forEach(function (g) { for (var yy = 0; yy < 5; yy++) for (var xx = 0; xx < 3; xx++) if (g[0][yy][xx] === '1') S.px(g[1] + xx, 14 + yy, yy === 0 ? 'e3' : 'e2'); });
    S.px(15, 3, 'hw'); S.px(14, 4, 'g5');
  };

  var iconMasters = {};
  function iconMaster(key, glow) {
    var ck = key + (glow ? '|g' : '');
    if (iconMasters[ck]) return iconMasters[ck];
    var S = new Spr(32, 32), acc = CLASS_BY[key] ? CLASS_BY[key][2] : 'gold';
    (ICON[key] || ICON.other)(S, acc);
    var idx = S.render({ dz: 2.4 })[0];
    if (glow) idx = halo(idx, 32, 32, RAMP_SRC[acc === 'gold' ? 'brass' : acc].map(pc).slice(0, 3), 3);
    var c = u32ToCanvas(idxToU32(idx), 32, 32);
    iconMasters[ck] = c;
    return c;
  }
  function classIcon(name, opts) {
    opts = opts || {};
    var size = Math.max(8, +opts.size || 128), key = classKey(name), dpr = +opts.dpr || dprCap();
    var k = Math.max(1, Math.round(size * dpr / 32));
    var c = mk(32 * k, 32 * k), x = ctx2d(c);
    x.drawImage(iconMaster(key, !!opts.glow), 0, 0, c.width, c.height);
    c.style.width = size + 'px'; c.style.height = size + 'px';
    pixelated(c);
    c.setAttribute('role', 'img'); c.setAttribute('aria-label', (CLASS_BY[key] || CLASS_BY.other)[1] + ' icon');
    c.className = 'qoart-icon qoart-icon--' + key;
    return c;
  }
  var urlCache = {};
  function classIconURL(name, size, opts) {
    var key = classKey(name), glow = !!(opts && opts.glow);
    var k = Math.max(1, Math.ceil((+size || 128) / 32)), ck = key + '|' + k + (glow ? '|g' : '');
    if (urlCache[ck]) return urlCache[ck];
    var u = upscale(iconMaster(key, glow), k).toDataURL('image/png');
    urlCache[ck] = u;
    return u;
  }
  function classList() { return CLASSES.map(function (c) { return { key: c[0], label: c[1] }; }); }

  /* =====================================================================
     1b. CHOICE SYMBOLS - the class icon kit, drawn for text-only choices.
     Same grid, palette, top-left light and outline, rendered and cached by
     iconMaster. p- what sort they are, d- where they are in their story,
     s- how they play the barkeep, t- the fight tactic, i- what they buy at the shop,
     v- the portrait's mood.
     ===================================================================== */
  var SYMBOLS = ['p-endure', 'p-laugh', 'p-tab', 'p-help', 'p-names', 'p-run', 'p-prove', 'p-price',
    'd-active', 'd-retired', 'd-building', 'd-explore', 's-bold', 's-shadow', 's-strategist', 's-silver',
    't-strike', 't-trick', 't-shield',
    'c-alone', 'c-familiar', 'c-beast', 'c-mount', 'c-spirit',
    'k-locket', 'k-coin', 'k-ring', 'k-journal', 'k-letter', 'k-toy',
    'o-plate', 'o-leather', 'o-chain', 'o-robes', 'o-cloak', 'o-common',
    'i-tincture', 'i-charm', 'i-blessing',
    'v-battle', 'v-quiet', 'v-brooding', 'v-cocky', 'v-noble', 'v-wild', 'v-mysterious', 'v-warm',
    'f-wisp', 'f-dry', 'f-shortcut', 'coin-heads', 'coin-tails'];
  /* one lit pixel that casts no outline, and a four-point glint made of them */
  function dot(S, x, y, c) { S.rect(x, y, 1, 1, { fx: c, noOl: 1 }); }
  function glint(S, x, y, r, c0, c1) {
    dot(S, x, y, c0);
    for (var i = 1; i <= r; i++) { var c = i === 1 ? c0 : c1; dot(S, x - i, y, c); dot(S, x + i, y, c); dot(S, x, y - i, c); dot(S, x, y + i, c); }
  }
  /* the Adventurer d20 at any size, in any ramp, with a 1px break where it sits over earlier parts */
  function symD20(S, cx, cy, R, m, ink) {
    var V = [], H = [], i;
    for (i = 0; i < 6; i++) { var a = -Math.PI / 2 + i * Math.PI / 3; V.push([cx + Math.cos(a) * R, cy + Math.sin(a) * R]); H.push([cx + Math.cos(a) * (R + 1.1), cy + Math.sin(a) * (R + 1.1)]); }
    var k = R / 14.2, T0 = [cx, cy - 9 * k], TL = [cx - 8.2 * k, cy + 5 * k], TR = [cx + 8.2 * k, cy + 5 * k];
    S.poly(H, { fx: 'ol', on: 'any' });
    [[[V[0], V[5], T0], 0.84], [[V[0], V[1], T0], 0.62], [[V[5], T0, TL], 0.72], [[V[1], T0, TR], 0.44], [[V[5], V[4], TL], 0.55],
      [[V[1], V[2], TR], 0.26], [[V[4], V[3], TL], 0.34], [[V[2], V[3], TR], 0.12], [[TL, TR, V[3]], 0.22], [[T0, TL, TR], 0.64]].forEach(function (f) { S.poly(f[0], { m: m, l: f[1], dz: 1.4 }); });
    [[T0, TL], [TL, TR], [TR, T0], [T0, V[0]], [T0, V[5]], [T0, V[1]], [TL, V[5]], [TL, V[4]], [TL, V[3]], [TR, V[1]], [TR, V[2]], [TR, V[3]]].forEach(function (e, j) {
      S.seg(e[0][0], e[0][1], e[1][0], e[1][1], 0.42, 0.42, { m: m, l: j < 3 ? 0.98 : 0.3, on: 'any' });
    });
    var two = ['111', '001', '111', '100', '111'], zero = ['111', '101', '101', '101', '111'], gx = Math.floor(cx - 3), gy = Math.floor(cy - 1.8);
    [[two, gx], [zero, gx + 4]].forEach(function (g) { for (var yy = 0; yy < 5; yy++) for (var xx = 0; xx < 3; xx++) if (g[0][yy][xx] === '1') S.px(g[1] + xx, gy + yy, ink); });
  }
  /* a straight sword from pommel p to tip t */
  function symSword(S, p, t, lw) {
    var dx = t[0] - p[0], dy = t[1] - p[1], L = Math.sqrt(dx * dx + dy * dy), ux = dx / L, uy = dy / L;
    var at = function (s, w) { return [p[0] + ux * s - uy * w, p[1] + uy * s + ux * w]; };
    var b0 = at(6.8, 0), f0 = at(9.6, 0), f1 = at(L - 6.4, 0), c0 = at(6.2, -4.4), c1 = at(6.2, 4.4), h0 = at(1.4, 0), h1 = at(5.4, 0);
    S.seg(b0[0], b0[1], t[0], t[1], 1.95, 0.5, { m: 'steel', l: 0.7 + lw, g: gRidge(b0[0], b0[1], t[0], t[1], 0.22), ol: 1 });
    S.seg(f0[0], f0[1], f1[0], f1[1], 0.4, 0.4, { m: 'iron', l: 0.22, on: 'any' });
    S.seg(c0[0], c0[1], c1[0], c1[1], 1.15, 1.15, { m: 'gold', l: 0.56 + lw, g: gCyl(c0[0], c0[1], c1[0], c1[1], 1.1, 0.6), ol: 1 });
    S.seg(h0[0], h0[1], h1[0], h1[1], 1.15, 1.15, { m: 'wood', l: 0.42 + lw, ol: 1, tex: function (x, y) { return ((x + y) % 2) ? 0.14 : -0.1; } });
    S.ell(p[0], p[1], 1.85, 1.85, { m: 'gold', l: 0.6 + lw, g: gSph(p[0] - 0.5, p[1] - 0.5, 1.85, 1.85, 0.7), ol: 1 });
  }

  /* p-endure: an iron anvil on an oak stump - never breaks first */
  ICON['p-endure'] = function (S) {
    S.poly([[5.8, 23.8], [27.2, 23.8], [27.6, 28.4], [29.4, 30.6], [3.6, 30.6], [5.4, 28.4]], { m: 'wood', l: 0.52,
      g: gAdd(gCyl(16.5, 23, 16.5, 31, 11.4, 0.5), function (x, y) { return y < 25.4 ? -0.26 : 0; }),
      tex: function (x, y) { return (x % 3 === 1 ? -0.16 : 0) + (hash(x, y, 7) > 0.85 ? 0.12 : 0); } });
    var anv = path([[1.4, 8.8], ['q', 4.4, 6.8, 10, 6.8], [28.6, 6.8], [29.6, 7.8], [29.6, 11.4], [26.4, 12.6], ['q', 22.4, 13.2, 22.6, 17.2],
      ['q', 22.8, 19.8, 27.4, 20.4], [28.4, 21.2], [28.4, 24.4], [24.2, 24.4], ['q', 18.4, 20.4, 12.6, 24.4], [8.4, 24.4], [8.4, 21.2], [9.4, 20.4],
      ['q', 13.8, 19.8, 14.2, 17.2], ['q', 14.4, 13.4, 11, 12.8], ['q', 5.4, 12.4, 1.4, 8.8]]);
    var ain = pip(anv), id = S.poly(anv, { m: 'steel', l: 0.46, g: gLin(3, 8, 26, 24, 0.7), bev: 0.2, dz: 3, ol: 1 });
    S.shape(function (x, y) { return ain(x, y) && y < 8; }, [0, 5, 31, 10], { m: 'steel', l: 0.99, on: [id] });
    S.shape(function (x, y) { return ain(x, y) && y >= 8 && y < 9; }, [0, 5, 31, 10], { m: 'steel', l: 0.74, g: gLin(2, 8, 30, 8, 0.2), on: [id] });
    S.shape(function (x, y) { return ain(x, y) && y > 12.8 && y < 14.8 && x > 12.5 && x < 25; }, [10, 12, 27, 16], { m: 'steel', l: 0.06, on: [id] });
    S.pxl([23, 7, 24, 7], 'i2'); S.px(19, 7, 'i2'); S.px(6, 7, 'hw'); S.px(12, 7, 'hw');
  };
  /* p-laugh: a face thrown back in a laugh, both eyes squeezed shut, the grin wide open on a row of teeth, a tear of
     laughter at each eye */
  ICON['p-laugh'] = function (S) {
    var cx = 16, cy = 16.8, ang = -0.16, T = xf(cx, cy, ang), Lc = inv(cx, cy, ang), BB = [0, 0, 32, 32], RX = 11.6, RY = 12.4;
    var TT = function (l) { return path(l).map(function (p) { return T(p[0], p[1]); }); };
    var fid = S.rell(cx, cy, RX, RY, ang, { m: 'skin', l: 0.62, g: gSph(cx - 3.4, cy - 4.4, 14, 15, 0.5), bev: 0.12, dz: 5, ol: 1 });
    var face = function (x, y) { var q = Lc(x, y); return (q[0] * q[0]) / (RX * RX) + (q[1] * q[1]) / (RY * RY) <= 1; };
    S.shape(function (x, y) { return face(x, y) && !face(x + 1.2, y + 1.6); }, BB, { m: 'skin', l: 0.26, dz: 5, on: [fid] });
    /* the cheeks pushed up round by the laugh */
    [-5.8, 5.8].forEach(function (u) { S.shape(function (x, y) { var q = Lc(x, y), a = (q[0] - u) / 3, b = (q[1] - 2.2) / 2; return face(x, y) && a * a + b * b <= 1; }, BB, { m: 'skin', l: 0.76, dz: 5, on: [fid] }); });
    var dk = { m: 'wood', l: 0.08, on: [fid] };
    /* the brows lifted, the eyes squeezed shut into upturned arcs, a laugh line out from each */
    [-1, 1].forEach(function (sd) {
      S.line(TT([[sd * 8.2, -6.4], ['q', sd * 5.2, -9.4, sd * 2.2, -7.2]]), 0.7, dk);
      S.line(TT([[sd * 8.2, -1.6], ['q', sd * 5.2, -5, sd * 2.2, -1.8]]), 0.85, dk);
      S.line(TT([[sd * 9.2, -2.6], [sd * 10.4, -3.4]]), 0.45, { m: 'skin', l: 0.2, on: [fid] });
    });
    /* the grin: a wide open D, the top teeth showing, the throat dark behind */
    var top = function (u) { return 2.2 - 0.03 * u * u; };
    var mouth = function (x, y) { var q = Lc(x, y), u = q[0]; if (Math.abs(u) > 7.4) return false; var d = q[1] - top(u); return d >= 0 && d <= 7 * Math.sqrt(Math.max(0, 1 - u * u / 54.8)); };
    var mid = S.shape(mouth, BB, { fx: 'ol', on: [fid] });
    S.shape(function (x, y) { var q = Lc(x, y), a = q[0] / 2.8, b = (q[1] - top(q[0]) - 6) / 1.8; return mouth(x, y) && a * a + b * b <= 1; }, BB, { m: 'blood', l: 0.4, dz: 8, on: [mid] });
    S.shape(function (x, y) { var q = Lc(x, y); return mouth(x, y) && q[1] - top(q[0]) < 1.7 && Math.abs(q[0]) < 6; }, BB, { m: 'linen', l: 0.92, on: [mid] });
    [-2, 2].forEach(function (u) { var p = P(T(u, top(u) + 1)); S.px(p[0], p[1], 'pa2'); });
    /* tears of laughter springing from the eyes' outer corners */
    [[-1, -10.4, 1.2], [1, 10.6, 0.6]].forEach(function (t) {
      var c = T(t[1], t[2]), sd = t[0];
      S.poly([[c[0], c[1] - 2.6], [c[0] + sd * 1.6, c[1] + 0.3], [c[0], c[1] + 2], [c[0] - sd * 1.6, c[1] + 0.3]], { m: 'moon', l: 0.78, g: gSph(c[0] - 0.4, c[1] - 0.4, 1.6, 2, 0.5), ol: 1 });
      S.px(Math.floor(c[0]) - (sd > 0 ? 0 : 0), Math.floor(c[1]) - 1, 'hw');
    });
    S.px(Math.floor(cx - 6.8), Math.floor(cy - 9.2), 'sk6');
  };
  /* p-tab: a foaming tankard, a gold coin slapped down beside it */
  ICON['p-tab'] = function (S) {
    var cyl = gCyl(12.2, 8, 12.2, 31, 8.2, 0.55);
    S.shape(function (x, y) { var a = (x - 19) / 7.2, b = (y - 19) / 7, c = (x - 19) / 3.8, d = (y - 19) / 3.7; return x > 18.5 && a * a + b * b <= 1 && c * c + d * d > 1; }, [17, 11, 28, 27],
      { m: 'steel', l: 0.55, g: gSph(23, 14, 7, 8, 0.5), bev: 0.22, ol: 1 });
    S.poly([[4.8, 10.8], [19.6, 10.8], [20.2, 29.6], [4.2, 29.6]], { m: 'wood', l: 0.56, g: cyl, ol: 1,
      tex: function (x, y) { return ((x - 5) % 4 === 0 ? -0.26 : 0) + (hash(x, y, 11) > 0.87 ? -0.1 : 0); } });
    [[14.2, 0.2], [25, 0.5]].forEach(function (b) { S.rect(4 - b[1], b[0], 16.4 + 2 * b[1], 2.2, { m: 'steel', l: 0.56, g: cyl, bev: 0.22, ol: 1 }); });
    var fb = [[7.2, 9.4, 3.4], [11.6, 7, 4.2], [16.3, 7.8, 3.7], [19.6, 10, 2.6], [5.2, 11.2, 2.1]];
    S.shape(function (x, y) { if (y > 8.6 && y < 12.8 && x > 3.9 && x < 20.9) return true; for (var i = 0; i < fb.length; i++) { var dx = x - fb[i][0], dy = y - fb[i][1]; if (dx * dx + dy * dy <= fb[i][2] * fb[i][2]) return true; } return false; },
      [1, 1, 24, 15], { m: 'linen', l: 0.9, g: gSph(10, 5, 12, 9, 0.38), bev: 0.1, dz: 3, ol: 1, tex: function (x, y) { return hash(x, y, 13) > 0.9 ? -0.2 : 0; } });
    S.seg(5.6, 12, 5.4, 17, 1.3, 1.05, { m: 'linen', l: 0.8, g: gCyl(5.6, 12, 5.4, 17, 1.2, 0.4) });
    var cx = 23.2, cy = 24.8, r = 5.3;
    S.ell(cx, cy, r, r, { m: 'gold', l: 0.56, g: gSph(cx - 1, cy - 1, r, r, 0.7), bev: 0.16, ol: 1 });
    S.ring(cx, cy, 3.2, 4, { m: 'gold', l: 0.28, on: 'any' });
    S.ell(cx, cy, 1.6, 1.6, { m: 'gold', l: 0.82, g: gSph(cx, cy, 1.6, 1.6, 0.5), on: 'any' });
    S.px(20, 21, 'hw'); S.px(8, 6, 'hw'); S.px(9, 5, 'pa');
  };
  /* p-help: a lit lantern held out for whoever needs it */
  ICON['p-help'] = function (S) {
    var cx = 16, fy = 17.2;
    S.ring(cx, 4.2, 1.4, 2.8, { m: 'gold', l: 0.62, bev: 0.2, ol: 1 });
    S.rect(10.8, 9.4, 10.4, 14.4, { m: 'ember', l: 0.55, g: function (x, y) { var dx = (x - cx) / 6, dy = (y - fy) / 8.4; return 0.62 * (1 - Math.min(1, Math.sqrt(dx * dx + dy * dy))) - 0.14; } });
    S.poly(path([[cx, 10.4], ['q', cx + 3.8, 15, cx + 2.4, 18.4], ['q', cx, 20.8, cx - 2.4, 18.4], ['q', cx - 3.8, 15, cx, 10.4]]), { m: 'holy', l: 0.6, g: gLin(cx, 20, cx, 11, 0.36), on: 'any' });
    S.ell(cx, 17.6, 1.1, 1.6, { fx: 'hw', on: 'any' });
    [9.2, 20.6].forEach(function (x0) { S.rect(x0, 9, 2.2, 15, { m: 'steel', l: 0.52, g: gCyl(x0 + 1.1, 9, x0 + 1.1, 24, 1.1, 0.5), ol: 1 }); });
    S.poly([[8.6, 10.6], [23.4, 10.6], [20.6, 6.6], [11.4, 6.6]], { m: 'steel', l: 0.56, g: gLin(9, 6, 23, 11, 0.4), bev: 0.2, ol: 1 });
    S.ell(cx, 6.4, 2.6, 1.4, { m: 'gold', l: 0.6, g: gSph(cx - 1, 6, 2.6, 1.4, 0.6), ol: 1 });
    S.poly([[8.6, 23.2], [23.4, 23.2], [22.2, 26.8], [9.8, 26.8]], { m: 'steel', l: 0.5, g: gLin(9, 23, 23, 27, 0.4), bev: 0.2, ol: 1 });
    S.rect(8.8, 23.2, 14.4, 1.2, { m: 'gold', l: 0.6, on: 'any' });
    S.rect(10.6, 26.6, 10.8, 1.6, { m: 'steel', l: 0.3 });
  };
  /* p-names: a list of names, two struck through in red, pinned by a dagger */
  ICON['p-names'] = function (S) {
    var sid = S.poly(path([[4.4, 5.4], [20.2, 5.4], ['q', 19.2, 16, 20.8, 26.6], [5.4, 26.6], ['q', 3.4, 16, 4.4, 5.4]]),
      { m: 'linen', l: 0.8, g: gLin(4, 5, 21, 27, 0.3), bev: 0.1, dz: 3, ol: 1 });
    [[9.4, [[7, 11.8], [13, 16.8]]], [13, [[7, 10.4], [11.6, 16.4]]], [16.6, [[7, 12.6], [13.8, 17.4]]], [20.2, [[7, 9.8], [11, 15.8]]]].forEach(function (r) {
      r[1].forEach(function (w) { S.rect(w[0], r[0], w[1] - w[0], 1, { m: 'wood', l: 0.15, on: [sid] }); });
    });
    [9.4, 16.6].forEach(function (y) { S.seg(5.6, y + 1.3, 18.2, y - 0.3, 0.7, 0.7, { m: 'blood', l: 0.64, on: 'any' }); });
    [[3, 5.4, 21.6, 5.4], [4, 26.8, 22.6, 26.8]].forEach(function (r) { S.seg(r[0], r[1], r[2], r[3], 2.1, 2.1, { m: 'linen', l: 0.7, g: gCyl(r[0], r[1], r[2], r[3], 2.1, 0.5), dz: 3, ol: 1 }); });
    S.px(21, 5, 'lt3'); S.px(22, 26, 'lt3');
    /* the dagger that pins it up */
    var gd = [24, 7.4], en = [15.4, 16];
    S.seg(gd[0], gd[1], en[0], en[1], 2.1, 1.3, { m: 'steel', l: 0.74, g: gRidge(gd[0], gd[1], en[0], en[1], 0.22), ol: 1 });
    S.seg(gd[0] - 1.2, gd[1] + 1.2, en[0] + 2.2, en[1] - 2.2, 0.4, 0.4, { m: 'iron', l: 0.22, on: 'any' });
    S.pxl([14, 15, 15, 16, 16, 17], 'lt1');
    S.seg(21.8, 5.2, 26.2, 9.6, 1.15, 1.15, { m: 'gold', l: 0.58, g: gCyl(21.8, 5.2, 26.2, 9.6, 1.1, 0.6), ol: 1 });
    S.seg(24.9, 6.5, 27.4, 4, 1.15, 1.15, { m: 'wood', l: 0.42, g: gCyl(24.9, 6.5, 27.4, 4, 1.1, 0.5), ol: 1, tex: function (x, y) { return ((x + y) % 2) ? 0.12 : -0.1; } });
    S.ell(28.4, 3, 1.75, 1.75, { m: 'gold', l: 0.6, g: gSph(28, 2.6, 1.75, 1.75, 0.7), ol: 1 });
    S.px(27, 2, 'g5');
  };
  /* p-run: a worn boot caught mid-stride, heel up and pushing off, speed lines behind and dust kicked up */
  ICON['p-run'] = function (S) {
    var T = xf(17.4, 16.4, 0.2), M = function (list) { return mapPts(T, path(list)); };
    [[1, 7.6, 6.4], [1, 11.6, 4.6], [1.6, 15.6, 6.8]].forEach(function (l) { S.rect(l[0], l[1], l[2], 1, { m: 'linen', l: 0.62, noOl: 1, g: gLin(l[0], 0, l[0] + l[2], 0, -0.5) }); });
    /* the boot in one piece: the shaft, the heel, the vamp curving down to a round toe */
    var uid = S.poly(M([[-5.8, -13.2], [4, -13.2], [3.6, -2.4], ['q', 6.4, -1.4, 9.6, 0.8], ['q', 13, 2.4, 12.8, 6.2], [-6, 6.2], [-5.8, 0]]),
      { m: 'wood', l: 0.62, g: gLin(9, 0, 25, 24, 0.44), bev: 0.2, dz: 3, ol: 1 });
    /* the cuff turned down at the top, a light down the shaft, the seam of the toe cap */
    S.poly(M([[-6.6, -14.2], [4.8, -14.2], [4.5, -10.2], [-6.4, -10.2]]), { m: 'pine', l: 0.48, g: gLin(9, 0, 24, 6, 0.4), bev: 0.2, dz: 3, ol: 1 });
    S.line(M([[6.4, -0.2], ['q', 5.6, 3, 6.6, 6]]), 0.45, { m: 'wood', l: 0.36, on: [uid] });
    S.line(M([[-4.6, -8.4], [-4.8, -4.2]]), 0.45, { m: 'wood', l: 0.84, on: [uid] });
    /* the sole and heel */
    S.poly(M([[-6.2, 6], [12.8, 6], ['q', 13.4, 8, 11.2, 8.6], [-1.6, 8.6], [-1.6, 10], [-6.2, 10]]), { m: 'dark', l: 0.62, bev: 0.2, ol: 1 });
    [-4, -1, 2, 5, 8, 11].forEach(function (u) { var p = P(T(u, 5.3)); S.px(p[0], p[1], 'lt4'); });
    [[8.6, 26.6, 2.3], [4.6, 27.8, 1.8], [1.9, 25.4, 1.1]].forEach(function (d) { S.ell(d[0], d[1], d[2], d[2] * 0.85, { m: 'linen', l: 0.86, g: gSph(d[0] - 0.8, d[1] - 0.8, d[2], d[2], 0.5), dz: 3, ol: 1 }); });
    var tc = P(T(9.6, 1.6)); S.px(tc[0], tc[1], 'lt4'); S.px(tc[0] - 1, tc[1], 'sk5');
  };
  /* p-prove: a crimson banner run up a pole, a gold star on it */
  ICON['p-prove'] = function (S) {
    var top = function (x) { var t = (x - 10) / 19; return 6.2 + 1.6 * Math.sin(t * 5.4 + 0.3) + t * 1.2; };
    var hgt = function (x) { return 12.2 - (x - 10) / 19 * 1.8; };
    var ban = function (x, y) { if (x < 9.4 || x > 29.6) return false; var t0 = top(x), h = hgt(x), mid = t0 + h / 2; if (y < t0 || y > t0 + h) return false; return x < 29.6 - 5.4 * (1 - Math.abs(y - mid) / (h / 2)); };
    S.seg(7.7, 29.4, 9.2, 5, 1.35, 1.2, { m: 'wood', l: 0.62, g: gCyl(7.7, 29.4, 9.2, 5, 1.3, 0.55), ol: 1 });
    var bid = S.shape(ban, [8, 2, 31, 22], { m: 'blood', l: 0.5, g: function (x, y) { var t = (x - 10) / 19; return 0.24 * Math.cos(t * 5.4 + 0.3) - 0.16 * (y - top(x)) / hgt(x); }, bev: 0.12, ol: 1 });
    var sx = 17.4, sy = top(17.4) + hgt(17.4) / 2, st = [];
    for (var j = 0; j < 10; j++) { var a = j * Math.PI / 5 - Math.PI / 2, r = j % 2 ? 1.7 : 4; st.push([sx + Math.cos(a) * r, sy + Math.sin(a) * r]); }
    S.poly(st, { m: 'gold', l: 0.62, g: gSph(sx - 1, sy - 1, 4, 4, 0.5), on: [bid] });
    S.poly([[9.3, 0.6], [10.9, 3.4], [9.3, 5.6], [7.7, 3.4]], { m: 'gold', l: 0.62, g: gLin(8, 1, 11, 5, 0.4), bev: 0.2, ol: 1 });
    S.ell(9.2, 5.8, 1.9, 1.2, { m: 'gold', l: 0.5, g: gSph(8.6, 5.4, 1.9, 1.2, 0.6), ol: 1 });
  };
  /* p-price: a chain snapped through its middle link, still throwing sparks */
  ICON['p-price'] = function (S) {
    var T = xf(16, 16, -0.74), Lc = inv(16, 16, -0.74), a = 6, b = 4, th = 2.3, h = a - b, BB = [0, 0, 32, 32];
    var cap = function (u, v, uc, r) { var du = Math.max(0, Math.abs(u - uc) - h); return du * du + v * v <= r * r; };
    var link = function (uc, lo, hi) { return function (x, y) { var q = Lc(x, y), du = q[0] - uc; if (du < lo || du > hi) return false; return cap(q[0], q[1], uc, b) && !cap(q[0], q[1], uc, b - th); }; };
    var o = function (l) { return { m: 'steel', l: l, g: gLin(4, 4, 28, 28, 0.44), bev: 0.28, dz: 3, ol: 1 }; };
    S.shape(link(-11, -9, 9), BB, o(0.62));
    S.shape(link(11, -9, 9), BB, o(0.56));
    S.shape(link(-3.8, -9, 0.9), BB, o(0.66));
    S.shape(link(3.8, -0.9, 9), BB, o(0.58));
    [[-3, -2.85], [-3, 2.85], [3, -2.85], [3, 2.85]].forEach(function (q) { var p = P(T(q[0], q[1])); S.px(p[0], p[1], 'e3'); });
    var c = P(T(0, 0)); glint(S, c[0], c[1], 2, 'hw', 'g5');
    [[1.6, -4.2, 'e4'], [-1.4, 4.4, 'g4'], [3.4, 3.6, 'e3'], [-3.2, -4.4, 'e3']].forEach(function (s) { var p = P(T(s[0], s[1])); dot(S, p[0], p[1], s[2]); });
  };
  /* d-active: a crimson d20 by a lit candle - the game is still on */
  ICON['d-active'] = function (S) {
    var cx = 23.4;
    S.ell(cx, 28.6, 6.8, 2.2, { m: 'gold', l: 0.5, g: gSph(cx - 2, 27.6, 7, 3, 0.6), bev: 0.2, ol: 1 });
    S.poly(path([[20, 12.6], ['q', cx, 14, 26.8, 12.6], [26.8, 28], [20, 28]]), { m: 'linen', l: 0.8, g: gCyl(cx, 12, cx, 29, 3.4, 0.5), dz: 3.4, ol: 1 });
    S.seg(20.2, 13.6, 20, 18.4, 1.15, 1, { m: 'linen', l: 0.98 });
    S.seg(26.4, 13.4, 26.6, 16, 1, 0.9, { m: 'linen', l: 0.6 });
    S.rect(cx - 0.5, 10.6, 1, 2.2, { fx: 'k1' });
    S.poly(path([[cx, 2], ['q', cx + 3.4, 6.6, cx + 2.3, 9.4], ['q', cx, 12.2, cx - 2.3, 9.4], ['q', cx - 3.4, 6.6, cx, 2]]), { m: 'ember', l: 0.6, g: gLin(cx, 3, cx, 11, -0.3), ol: 1 });
    S.poly(path([[cx, 5.4], ['q', cx + 1.9, 8, cx + 1.3, 9.6], ['q', cx, 11, cx - 1.3, 9.6], ['q', cx - 1.9, 8, cx, 5.4]]), { m: 'holy', l: 0.7, on: 'any' });
    S.rect(cx - 0.5, 8.6, 1, 1.6, { fx: 'hw', on: 'any' });
    symD20(S, 10.4, 19.8, 10.4, 'blood', 'g4');
  };
  /* d-retired: a closed book, gold-tooled, its ribbon still marking the last page */
  ICON['d-retired'] = function (S) {
    var T = xf(15.4, 15.2, -0.1), Lc = inv(15.4, 15.2, -0.1), M = function (list) { return mapPts(T, list); }, BB = [0, 0, 32, 32];
    S.poly(M([[-8.6, -11.8], [11.4, -11.8], [11.4, 12.8], [-8.6, 12.8]]), { m: 'linen', l: 0.8, dz: 3, ol: 1,
      tex: function (x, y) { var q = Lc(x + 0.5, y + 0.5); return (Math.floor((q[0] > 9.6 ? q[0] : q[1]) + 40) % 2) ? -0.2 : 0.04; } });
    S.poly(M([[2.4, 9.6], [5.6, 9.6], [5.8, 15.8], [4.1, 14.2], [2.4, 15.8]]), { m: 'blood', l: 0.62, g: gLin(3, 10, 6, 16, 0.3), bev: 0.14, ol: 1 });
    var cid = S.poly(M([[-10.8, -13.6], [9.6, -13.6], [9.6, 11.2], [-10.8, 11.2]]), { m: 'moon', l: 0.5, bev: 0.22, dz: 4, ol: 1 });
    S.poly(M([[-10.8, -13.6], [-7.6, -13.6], [-7.6, 11.2], [-10.8, 11.2]]), { m: 'moon', l: 0.3, bev: 0.24, on: [cid] });
    [-8.4, -1.4, 5.6].forEach(function (v) { S.poly(M([[-10.8, v], [-7.6, v], [-7.6, v + 1.3], [-10.8, v + 1.3]]), { m: 'moon', l: 0.74, on: [cid] }); });
    S.shape(function (x, y) { var q = Lc(x, y), u = q[0], v = q[1]; return u > -5.6 && u < 7.6 && v > -11.6 && v < 9.2 && !(u > -4.6 && u < 6.6 && v > -10.6 && v < 8.2); }, BB, { m: 'gold', l: 0.55, on: [cid] });
    S.poly(M([[1, -5.6], [4.4, -1.2], [1, 3.2], [-2.4, -1.2]]), { m: 'gold', l: 0.62, bev: 0.25, on: [cid] });
    var e = P(T(0.2, -3)); S.px(e[0], e[1], 'hw');
    [[9.6, -13.6, -1, 1], [9.6, 11.2, -1, -1]].forEach(function (c) { S.poly(M([[c[0], c[1]], [c[0] + 4.4 * c[2], c[1]], [c[0], c[1] + 4.4 * c[3]]]), { m: 'gold', l: 0.6, bev: 0.25, ol: 1 }); });
  };
  /* d-building: a quill standing in a pot of ink - still being written */
  ICON['d-building'] = function (S) {
    var pot = path([[5.4, 22.4], ['q', 5.2, 18.2, 9.6, 17.8], [15.8, 17.8], ['q', 20.2, 18.2, 20, 22.4], [20, 27.2], ['q', 19.8, 29.8, 16.6, 29.8], [8.8, 29.8], ['q', 5.6, 29.8, 5.4, 27.2]]);
    S.poly(pot, { m: 'moon', l: 0.44, g: gSph(10, 21, 11, 10, 0.6), bev: 0.12, dz: 3, ol: 1 });
    S.rect(9.4, 14.8, 6.6, 3.4, { m: 'moon', l: 0.36, g: gCyl(12.7, 14, 12.7, 19, 3.3, 0.5), ol: 1 });
    S.rect(8.4, 14, 8.6, 1.8, { m: 'gold', l: 0.58, g: gCyl(12.7, 13, 12.7, 17, 4.3, 0.5), bev: 0.2, ol: 1 });
    S.rell(12.7, 14.4, 3, 0.8, 0, { fx: 'ol', on: 'any' });
    var q0 = [12.6, 16], q1 = [28.4, 1.6], len = Math.sqrt(Math.pow(q1[0] - q0[0], 2) + Math.pow(q1[1] - q0[1], 2)), ang = Math.atan2(q1[1] - q0[1], q1[0] - q0[0]);
    var Lc = inv(q0[0], q0[1], ang), T = xf(q0[0], q0[1], ang), clip = function (y) { return y < 14.6; };
    var vane = function (x, y) {
      var q = Lc(x, y), t = q[0] / len; if (t < 0.26 || t > 1.03 || !clip(y)) return false;
      var s = Math.sin(Math.PI * Math.min(1, (t - 0.26) / 0.77)), w = q[1] < 0 ? 4.3 * Math.pow(s, 0.7) : 2.6 * Math.pow(s, 0.9), bb = q[0] - Math.abs(q[1]) * 0.9;
      if ((q[1] < 0 && Math.abs(bb - len * 0.62) < 0.5) || (q[1] > 0 && Math.abs(bb - len * 0.78) < 0.5)) return false;
      return Math.abs(q[1]) <= w;
    };
    S.shape(function (x, y) { if (!clip(y)) return false; var q = Lc(x, y); return q[0] >= 0 && q[0] <= len * 0.3 && Math.abs(q[1]) <= 0.7; }, [8, 6, 22, 16], { m: 'linen', l: 0.86, ol: 1 });
    S.shape(vane, [8, 0, 32, 20], { m: 'linen', l: 0.72, g: function (x, y) { var q = Lc(x, y); return q[1] < 0 ? 0.1 : -0.18; }, ol: 1,
      tex: function (x, y) { var q = Lc(x + 0.5, y + 0.5); return (Math.floor((q[0] - Math.abs(q[1]) * 0.9) / 1.5) % 2) ? 0.1 : -0.08; } });
    var s0 = T(len * 0.26, 0), s1 = T(len * 0.99, 0);
    S.seg(s0[0], s0[1], s1[0], s1[1], 0.55, 0.4, { m: 'linen', l: 0.96, on: 'any' });
    S.pxl([7, 20, 7, 21, 7, 22], 'm4'); S.px(8, 19, 'm5');
  };
  /* d-explore: a folded map, a red trail dotted across it to an X */
  ICON['d-explore'] = function (S) {
    var X = [2.2, 11.2, 20.6, 29.6], Y = [8.2, 5, 8.2, 5], H = 19.6, sh = [0.25, -0.1, 0.2], ids = [];
    var pan = function (x) { return x < X[1] ? 0 : x < X[2] ? 1 : 2; }, pl = function (x) { return sh[pan(x)]; };
    for (var i = 0; i < 3; i++) ids.push(S.poly([[X[i], Y[i]], [X[i + 1], Y[i + 1]], [X[i + 1], Y[i + 1] + H], [X[i], Y[i] + H]],
      { m: 'linen', l: 0.7 + sh[i], bev: 0.14, dz: 4, ol: 1 }));
    S.ell(8.2, 14.6, 4.6, 3.4, { m: 'leaf', l: 0.66, g: pl, dz: 4, on: ids });
    S.ell(25, 22.2, 3.4, 2.4, { m: 'leaf', l: 0.66, g: pl, dz: 4, on: ids });
    S.ell(15.4, 21.6, 3.4, 2.2, { m: 'moon', l: 0.62, g: pl, dz: 4, on: ids });
    [[4.2, 24.4, 6.4, 22.6], [8.2, 21.2, 10.4, 19.6], [12.2, 18.4, 14.2, 16.8], [16, 15.6, 18.2, 14.6], [19.8, 13.8, 21.4, 13]].forEach(function (d) {
      S.seg(d[0], d[1], d[2], d[3], 0.62, 0.62, { m: 'blood', l: pan(d[0]) === 1 ? 0.42 : 0.62, on: ids });
    });
    S.shape(function (x, y) { var a = x - 25, b = y - 11.6; return Math.abs(a) <= 3.1 && Math.abs(b) <= 3.1 && (Math.abs(a - b) < 1.15 || Math.abs(a + b) < 1.15); }, [20, 7, 30, 17], { m: 'blood', l: 0.66, ol: 1 });
  };
  /* s-bold: a fist raised, bracer strapped on - the bold hero walks straight in */
  ICON['s-bold'] = function (S) {
    [[[3.4, 4.2], [6, 6.8]], [[28.6, 4.2], [26, 6.8]], [[2.4, 11.4], [5.4, 11.8]], [[29.6, 11.4], [26.6, 11.8]]].forEach(function (r) { S.seg(r[0][0], r[0][1], r[1][0], r[1][1], 0.8, 0.5, { m: 'holy', l: 0.7, ol: 1 }); });
    S.poly([[10.8, 19], [21.4, 19], [22.4, 31], [9.8, 31]], { m: 'skin', l: 0.5, g: gCyl(16.1, 18, 16.1, 31, 6, 0.5), ol: 1 });
    S.poly([[10, 21.8], [22.2, 21.8], [22.8, 31], [9.4, 31]], { m: 'wood', l: 0.5, g: gCyl(16.1, 21, 16.1, 31, 6.6, 0.55), bev: 0.14, dz: 3, ol: 1 });
    S.rect(9.6, 25.6, 13, 1, { m: 'wood', l: 0.16, on: 'any' });
    [[12, 23], [19, 23], [12, 28], [19, 28]].forEach(function (p) { S.px(p[0], p[1], 'g5'); S.px(p[0] + 1, p[1], 'g3'); S.px(p[0], p[1] + 1, 'g3'); S.px(p[0] + 1, p[1] + 1, 'g1'); });
    S.rect(10.2, 19.4, 11.8, 2.6, { m: 'blood', l: 0.55, bev: 0.2, ol: 1 });
    S.poly(path([[8.2, 11.4], [23.8, 10.6], ['q', 24.6, 17.6, 21.8, 20.2], [10.4, 20.2], ['q', 7.6, 17, 8.2, 11.4]]), { m: 'skin', l: 0.46, g: gSph(14, 12, 10, 9, 0.5), dz: 3, ol: 1 });
    [[10.4, 6.6], [14.1, 5.2], [17.8, 5.6], [21.4, 7.2]].forEach(function (f) {
      S.seg(f[0], f[1] + 1.9, f[0], 14.8, 1.9, 1.9, { m: 'skin', l: 0.62, g: gAdd(gCyl(f[0], f[1], f[0], 15, 1.9, 0.45), gLin(0, f[1], 0, 15, 0.3)), dz: 3, ol: 1 });
      S.rect(f[0] - 1.2, f[1] + 4.4, 2.4, 1, { m: 'skin', l: 0.14, on: 'any' });
    });
    /* The thumb curls up from the heel of the hand and across the front of the first two fingers: thick at the
       base, tapering to a nail, and never outside the fist's own outline. */
    S.seg(10.8, 16.6, 16.4, 14.4, 2.7, 2.3, { m: 'skin', l: 0.68, g: gCyl(10.8, 16.6, 16.4, 14.4, 2.5, 0.5), dz: 3, ol: 1 });
    S.pxl([16, 13, 17, 13], 'sk6'); S.px(13, 5, 'sk6'); S.px(9, 6, 'sk6');
  };
  /* s-shadow: a magnifying glass with an eye looking back through it */
  ICON['s-shadow'] = function (S) {
    var cx = 12.8, cy = 12.8;
    S.seg(20.4, 20.4, 28.4, 28.4, 2.1, 1.8, { m: 'wood', l: 0.5, g: gCyl(20.4, 20.4, 28.4, 28.4, 2, 0.55), ol: 1, tex: function (x, y) { return ((x + y) % 3 === 0) ? -0.14 : 0; } });
    S.seg(18.6, 18.6, 21.4, 21.4, 2.3, 2.3, { m: 'gold', l: 0.55, g: gCyl(18.6, 18.6, 21.4, 21.4, 2.3, 0.6), bev: 0.1, ol: 1 });
    S.ell(cx, cy, 8.8, 8.8, { m: 'night', l: 0.5, g: gLin(cx - 7, cy - 7, cx + 7, cy + 7, 0.6), dz: 3 });
    S.ring(cx, cy, 8.4, 10.8, { m: 'gold', l: 0.55, g: gSph(cx - 2, cy - 2, 11, 11, 0.6), bev: 0.2, ol: 1 });
    var eid = S.shape(function (x, y) { var u = (x - cx) / 6.6, v = (y - cy - 0.4) / 3.9; return Math.abs(u) <= 1 && Math.abs(v) <= 1 - u * u; }, [cx - 8, cy - 6, cx + 8, cy + 6],
      { m: 'linen', l: 0.7, g: gSph(cx - 1.5, cy - 1, 6, 4, 0.4), dz: 3, ol: 1 });
    S.ell(cx + 0.4, cy + 0.4, 2.9, 2.9, { m: 'ember', l: 0.62, g: gSph(cx - 0.4, cy - 0.4, 2.9, 2.9, 0.7), dz: 3, on: [eid] });
    S.ell(cx + 0.4, cy + 0.4, 1.2, 1.2, { fx: 'ol', on: 'any' });
    S.px(12, 12, 'hw'); S.pxl([7, 10, 8, 9], 'm3'); S.px(9, 8, 'm4');
  };
  /* s-strategist: a chess knight on a gold-banded base */
  ICON['s-strategist'] = function (S) {
    S.poly(path([[6.6, 25], [25.6, 25], ['q', 27, 27, 26, 30.4], [6.2, 30.4], ['q', 5.2, 27, 6.6, 25]]), { m: 'moon', l: 0.5, g: gLin(6, 25, 26, 30, 0.4), bev: 0.18, dz: 4, ol: 1 });
    var head = path([[13.8, 1.6], [15.6, 5], ['q', 21.4, 5.2, 23.4, 11], ['q', 24.8, 17, 22.8, 23.4], [10.4, 23.4], ['q', 9.6, 19.6, 12, 16.6], [9.6, 15.6], [5.8, 14.6],
      ['q', 3.6, 13.8, 4.2, 11.8], [6.6, 9.4], ['q', 8.6, 6.6, 11.6, 5.4], [12.2, 4]]);
    var hin = pip(head), hid = S.poly(head, { m: 'moon', l: 0.64, g: gSph(11, 9, 13, 16, 0.5), bev: 0.2, dz: 3, ol: 1 });
    S.shape(function (x, y) { return y > 5 && hin(x, y) && !hin(x + 2.6, y); }, [12, 4, 26, 24], { m: 'moon', l: 0.36, on: [hid], tex: function (x, y) { return (y % 2) ? -0.12 : 0.08; } });
    S.poly([[9.4, 21.4], [22.8, 21.4], [23.4, 25.4], [8.8, 25.4]], { m: 'gold', l: 0.6, g: gLin(9, 21, 23, 25, 0.3), bev: 0.2, ol: 1 });
    S.pxl([11, 8, 12, 8], 'm0'); S.px(5, 12, 'm1'); S.pxl([6, 14, 7, 14], 'm1'); S.px(9, 7, 'm5');
  };
  /* s-silver: a silver coin with a crescent struck on it, catching the light */
  ICON['s-silver'] = function (S) {
    var cx = 14.6, cy = 16.8, rx = 10.6, ry = 11.8, BB = [cx - rx - 1, cy - ry - 1, cx + rx + 1, cy + ry + 1];
    S.ell(cx + 2.2, cy + 0.4, rx, ry, { m: 'steel', l: 0.34, g: gLin(cx, cy - 10, cx + 12, cy + 10, 0.2), tex: function (x, y) { return (y % 2) ? -0.16 : 0.06; }, dz: 4, ol: 1 });
    /* raised rim, recessed field, raised crescent: bevel signs do the relief */
    var fid = S.ell(cx, cy, rx, ry, { m: 'iron', l: 0.72, g: gSph(cx - 3, cy - 4, rx + 2, ry + 2, 0.4), bev: 0.2, dz: 4, ol: 1 });
    var inner = S.shape(function (x, y) { var a = (x - cx) / (rx - 2.4), b = (y - cy) / (ry - 2.4); return a * a + b * b <= 1; }, BB, { m: 'iron', l: 0.4, g: gSph(cx - 2, cy - 3, rx, ry, 0.34), bev: -0.24, dz: 4, on: [fid] });
    S.shape(function (x, y) { var a = x - (cx - 0.6), b = y - (cy + 0.2), c = x - (cx + 1.8), d = y - (cy - 1.8); return a * a + b * b <= 30 && c * c + d * d > 22; }, BB, { m: 'iron', l: 0.96, bev: 0.32, dz: 4, on: [inner] });
    glint(S, 21, 8, 3, 'hw', 'm5');
    glint(S, 6, 24, 1, 'm5', 'm4');
  };
  /* t-strike: two blades crossed, straight at her */
  ICON['t-strike'] = function (S) {
    symSword(S, [27.4, 28.2], [4.4, 4.2], -0.06);
    symSword(S, [4.6, 28.2], [27.6, 4.2], 0);
    glint(S, 16, 12, 2, 'hw', 'g5');
  };
  /* t-trick: a goblet flung mid-throw, its wine leaving the lip - look over there */
  ICON['t-trick'] = function (S) {
    var cx = 11.8, cy = 18.4, ang = 0.62, T = xf(cx, cy, ang), Lc = inv(cx, cy, ang), BB = [0, 0, 32, 32];
    var ax0 = T(0, -10), ax1 = T(0, 8), gc = gCyl(ax0[0], ax0[1], ax1[0], ax1[1], 6.4, 0.55);
    /* stem and foot as one piece, then the bowl, then the open rim with wine still in it */
    S.shape(function (x, y) { var q = Lc(x, y), u = q[0], v = q[1], f = (v - 6.2) / 1.7; return (v > -1.6 && v < 6.2 && Math.abs(u) <= (Math.abs(v - 2) < 1.1 ? 2 : 1.35)) || (u * u / 30 + f * f <= 1); }, BB,
      { m: 'gold', l: 0.56, g: gc, bev: 0.18, dz: 3, ol: 1 });
    S.shape(function (x, y) { var q = Lc(x, y), t = (q[1] + 9.4) / 8.6; return t >= 0 && t <= 1 && Math.abs(q[0]) <= 6.4 * Math.sqrt(Math.max(0, 1 - t * t)); }, BB, { m: 'gold', l: 0.62, g: gc, bev: 0.14, dz: 3, ol: 1 });
    var m0 = T(0, -9.4);
    S.rell(m0[0], m0[1], 6.5, 2.1, ang, { m: 'gold', l: 0.86, ol: 1 });
    S.rell(m0[0], m0[1], 5.1, 1.2, ang, { m: 'blood', l: 0.3, on: 'any' });
    /* the wine thrown clear of the far lip, breaking into drops */
    var st = T(0.6, -12), c1 = [st[0] + 3.8, st[1] - 4.8], e = [st[0] + 9.6, st[1] - 3.4], pts = [];
    for (var i = 0; i <= 10; i++) { var t = i / 10 * 0.62, w = 1 - t; pts.push([w * w * st[0] + 2 * w * t * c1[0] + t * t * e[0], w * w * st[1] + 2 * w * t * c1[1] + t * t * e[1], 1.9 - 0.9 * t]); }
    S.shape(function (x, y) { for (var j = 0; j < pts.length; j++) { var dx = x - pts[j][0], dy = y - pts[j][1]; if (dx * dx + dy * dy <= pts[j][2] * pts[j][2]) return true; } return false; }, [0, 0, 32, 26],
      { m: 'blood', l: 0.56, g: gLin(st[0] - 2, st[1] - 3, e[0], e[1] + 3, 0.36), bev: 0.2, dz: 3, ol: 1 });
    [[0.82, 1.35], [1, 1.15]].forEach(function (d) { var t = d[0], w = 1 - t; S.ell(w * w * st[0] + 2 * w * t * c1[0] + t * t * e[0], w * w * st[1] + 2 * w * t * c1[1] + t * t * e[1], d[1], d[1], { m: 'blood', l: 0.64, ol: 1 }); });
    [[e[0] - 2.6, e[1] - 3.2, 1], [e[0] + 0.6, e[1] + 4.2, 1]].forEach(function (d) { S.ell(d[0], d[1], d[2], d[2], { m: 'blood', l: 0.7, ol: 1 }); });
    var hl = P(T(-3.8, -6.6)); S.px(hl[0], hl[1], 'hw');
  };
  /* t-shield: a round shield raised, iron-rimmed, a crimson quartering */
  ICON['t-shield'] = function (S) {
    var cx = 15.2, cy = 16.4, rx = 12.6, ry = 13.6, BB = [cx - rx - 1, cy - ry - 1, cx + rx + 1, cy + ry + 1];
    var planks = function (x, y) { return ((x - 2) % 5 === 0 ? -0.3 : 0) + (hash(x, y >> 2, 21) - 0.5) * 0.16; };
    S.ell(cx + 2, cy + 0.2, rx, ry, { m: 'wood', l: 0.22, bev: 0.1, ol: 1 });
    var fid = S.ell(cx, cy, rx, ry, { m: 'wood', l: 0.56, g: gSph(cx - 3, cy - 4, rx + 3, ry + 3, 0.5), ol: 1, tex: planks });
    S.shape(function (x, y) { var a = (x - cx) / (rx - 1.8), b = (y - cy) / (ry - 1.8); return a * a + b * b <= 1 && ((x < cx) === (y < cy)); }, BB, { m: 'blood', l: 0.5, g: gSph(cx - 3, cy - 4, rx + 3, ry + 3, 0.5), on: [fid], tex: planks });
    S.shape(function (x, y) { var a = (x - cx) / rx, b = (y - cy) / ry, c = (x - cx) / (rx - 2), d = (y - cy) / (ry - 2); return a * a + b * b <= 1 && c * c + d * d > 1; }, BB,
      { m: 'steel', l: 0.55, g: gSph(cx - 4, cy - 5, rx + 2, ry + 2, 0.6), bev: 0.14, on: [fid] });
    for (var k = 0; k < 8; k++) { var a = k * Math.PI / 4 + Math.PI / 8; S.px(Math.floor(cx + Math.cos(a) * (rx - 1)), Math.floor(cy + Math.sin(a) * (ry - 1)), 'i4'); }
    S.ell(cx, cy, 4.2, 4.4, { m: 'steel', l: 0.55, g: gSph(cx - 1.2, cy - 1.4, 4.2, 4.4, 0.85), ol: 1 });
    S.px(Math.floor(cx - 1.6), Math.floor(cy - 1.8), 'hw');
  };

  /* ---- companions (c-), keepsakes (k-), outfits (o-): objects only ---- */
  /* c-alone: one set of footprints, a left and a right, walking away up the icon: each a sole pressed into the earth,
     five toes above it, the big toe on the inner side */
  ICON['c-alone'] = function (S) {
    function print(cx, cy, ang, side) {
      var L = inv(cx, cy, ang - Math.PI / 2);          /* local s runs toe-ward (up), t across; side -1 = a left foot */
      var sole = function (s, t) {
        var a = (s - 2.2) / 4, b = (t - side * 0.5) / 3.1, h = (s + 4.6) / 2.6, k = t / 2.3;
        if (a * a + b * b <= 1 || h * h + k * k <= 1) return true;
        return s > -4.6 && s < 2.2 && t > -2.3 + (side < 0 ? 0 : 0.9 * (1 - Math.abs(s + 1.2) / 3.4)) && t < 2.3 - (side > 0 ? 0 : 0.9 * (1 - Math.abs(s + 1.2) / 3.4));
      };
      var inP = function (x, y) { var q = L(x, y); return sole(q[0], q[1]); };
      var bb = [cx - 11, cy - 11, cx + 11, cy + 11];
      var id = S.shape(inP, bb, { m: 'wood', l: 0.3, g: gSph(cx + 1.5, cy + 2, 7, 9, -0.3), dz: 4, ol: 1 });
      /* the lip of the print catches the light on the side away from it */
      S.shape(function (x, y) { return inP(x, y) && !inP(x + 1.1, y + 1.1); }, bb, { m: 'wood', l: 0.62, on: [id] });
      [[8.9, side * 1.9, 1.35, 1.2], [9.2, -side * 0.5, 0.95, 0.9], [8.8, -side * 2.1, 0.85, 0.85], [7.9, -side * 3.3, 0.75, 0.75], [6.7, -side * 4.1, 0.7, 0.7]].forEach(function (tq) {
        var p = [cx + Math.cos(ang - Math.PI / 2) * tq[0] - Math.sin(ang - Math.PI / 2) * tq[1], cy + Math.sin(ang - Math.PI / 2) * tq[0] + Math.cos(ang - Math.PI / 2) * tq[1]];
        S.ell(p[0], p[1], tq[2], tq[3] * 1.1, { m: 'wood', l: 0.3, ol: 1 });
      });
    }
    print(10, 21.4, -0.1, -1);
    print(22, 10.6, 0.12, 1);
    S.px(3, 29, 'lt3'); S.px(29, 27, 'lt2'); S.px(27, 2, 'lt3');
  };
  /* c-familiar: a small raven on a twig, moonlight catching its feathers */
  ICON['c-familiar'] = function (S) {
    S.seg(2.4, 26.6, 29.4, 24, 1.55, 1.25, { m: 'wood', l: 0.56, g: gCyl(2.4, 26.6, 29.4, 24, 1.5, 0.5), ol: 1, tex: function (x, y) { return hash(x, y, 107) > 0.8 ? -0.14 : 0; } });
    S.seg(24.6, 24.6, 28.6, 29.6, 0.9, 0.7, { m: 'wood', l: 0.42, ol: 1 });
    S.poly([[10.4, 18.2], [2.6, 25.4], [5, 27.4], [13.4, 21.6]], { m: 'moon', l: 0.2, g: gLin(3, 26, 13, 19, 0.2), bev: 0.14, ol: 1 });
    var body = S.rell(15.4, 16.8, 8.6, 5.9, -0.36, { m: 'moon', l: 0.3, g: gSph(13, 13, 10, 7, 0.4), ol: 1 });
    S.shape(function (x, y) { var q = inv(14, 18, -0.3)(x, y), u = q[0], v = q[1]; return v > -2.6 && v < 3.8 && u > -9 && u < 5 - Math.abs(v) * 0.4; }, [3, 10, 24, 26],
      { m: 'moon', l: 0.14, on: [body], tex: function (x, y) { var q = inv(14, 18, -0.3)(x + 0.5, y + 0.5); return Math.floor(q[0] + 30) % 3 === 0 ? 0.16 : 0; } });
    S.ell(21.4, 10.4, 4.5, 4.3, { m: 'moon', l: 0.34, g: gSph(20, 8.8, 4.5, 4.3, 0.5), ol: 1 });
    S.poly([[24.6, 8.8], [30.4, 11], [25.2, 12.8]], { m: 'iron', l: 0.36, g: gLin(25, 9, 29, 12, 0.3), bev: 0.2, ol: 1 });
    S.px(22, 9, 'm5'); S.px(23, 9, 'ol');
    [[15, 22.4, 14.2, 25.6], [18.4, 21.8, 18.6, 25]].forEach(function (l) { S.seg(l[0], l[1], l[2], l[3], 0.55, 0.55, { m: 'iron', l: 0.5, ol: 1 }); });
    S.pxl([18, 6, 19, 6, 20, 6, 17, 7, 13, 11, 11, 12, 9, 13], 'm3'); S.pxl([19, 7, 21, 6], 'm4');
  };
  /* c-beast: a broad paw, four toe pads and their claws */
  ICON['c-beast'] = function (S) {
    var pad = path([[16, 15.6], ['q', 23.8, 15, 25, 22], ['q', 25.8, 27.6, 21.2, 28.2], ['q', 18.2, 28.4, 16, 26.6], ['q', 13.8, 28.4, 10.8, 28.2], ['q', 6.2, 27.6, 7, 22], ['q', 8.2, 15, 16, 15.6]]);
    S.poly(pad, { m: 'wood', l: 0.5, g: gSph(13.6, 19.4, 10.4, 8.4, 0.6), bev: 0.16, ol: 1 });
    [[5.8, 12.6, 2.7, 3.5, -0.62], [11.2, 7.4, 2.9, 3.9, -0.2], [20.8, 7.4, 2.9, 3.9, 0.2], [26.2, 12.6, 2.7, 3.5, 0.62]].forEach(function (t) {
      var ux = Math.sin(t[4]), uy = -Math.cos(t[4]), b = [t[0] + ux * (t[3] - 0.4), t[1] + uy * (t[3] - 0.4)], tp = [t[0] + ux * (t[3] + 3.2), t[1] + uy * (t[3] + 3.2)];
      S.poly([[b[0] - uy * 1.3, b[1] + ux * 1.3], [tp[0], tp[1]], [b[0] + uy * 1.3, b[1] - ux * 1.3]], { m: 'linen', l: 0.72, g: gLin(b[0], b[1], tp[0], tp[1], 0.3), ol: 1 });
      S.rell(t[0], t[1], t[2], t[3], t[4], { m: 'wood', l: 0.52, g: gSph(t[0] - 1, t[1] - 1.2, t[2], t[3], 0.6), bev: 0.16, ol: 1 });
    });
    S.pxl([12, 18, 13, 17], 'lt4'); S.px(10, 5, 'sk5'); S.px(19, 5, 'sk5');
  };
  /* c-mount: an iron horseshoe, nail holes along its groove */
  ICON['c-mount'] = function (S) {
    var cx = 16, cy = 15;
    var edges = function (y) { var t = Math.max(0, y - cy); return [6.2 + t * 0.05, 11.6 - t * 0.12]; };
    var shoe = function (x, y) {
      if (y > 29.2) return false;
      var dx = Math.abs(x - cx);
      if (y <= cy) { var a = dx / 11.6, b = (y - cy) / 12.4, c = dx / 6.2, d = (y - cy) / 7.2; return a * a + b * b <= 1 && c * c + d * d > 1; }
      var e = edges(y); return dx >= e[0] && dx <= e[1];
    };
    var sid = S.shape(shoe, [2, 1, 30, 31], { m: 'steel', l: 0.58, g: gSph(12, 9, 14, 16, 0.55), bev: 0.2, ol: 1 });
    var mid = function (x, y) {
      var dx = Math.abs(x - cx);
      if (y <= cy) { var a = dx / 8.9, b = (y - cy) / 9.8; return Math.abs(a * a + b * b - 1) < 0.1; }
      var e = edges(y); return Math.abs(dx - (e[0] + e[1]) / 2) < 0.55;
    };
    S.shape(function (x, y) { return mid(x, y) && y < 27.6; }, [2, 1, 30, 31], { m: 'steel', l: 0.2, on: [sid] });
    [[-0.95, 0], [-0.52, 0], [-0.08, 0]].forEach(function (h) {
      [-1, 1].forEach(function (sd) { var a = Math.PI / 2 + sd * (Math.PI / 2 + h[0]); S.px(Math.floor(cx + Math.cos(a) * 8.9), Math.floor(cy - Math.abs(Math.sin(a)) * 9.8), 'ol'); });
    });
    [[-1, 20.4], [1, 20.4], [-1, 25.4], [1, 25.4]].forEach(function (h) { var e = edges(h[1]); S.px(Math.floor(cx + h[0] * (e[0] + e[1]) / 2), h[1], 'ol'); });
    S.pxl([8, 6, 9, 5, 10, 4], 'i4'); S.px(9, 4, 'hw');
  };
  /* c-spirit: a wisp, a small cold flame floating free, flickering, shedding sparks */
  ICON['c-spirit'] = function (S) {
    function wisp(sc, dx, dy) {
      return function (x, y) {
        var t = (y - 2.4 - dy) / (25.4 * sc); if (t < 0 || t > 1) return false;
        var sway = 3.2 * Math.sin(t * Math.PI * 1.25 + 0.6) * (1 - t) * (1 - t) - (1 - t) * 1.2;
        var w = 8.4 * sc * Math.pow(Math.sin(Math.PI * Math.pow(t, 0.62) * 0.96 + 0.04), 1.05) * (0.52 + 0.48 * t);
        var nib = t < 0.5 && Math.abs(x - 16 - dx - sway + (0.5 - t) * 7) < 1.1 * (0.5 - t) * 4 ? 1 : 0;
        return Math.abs(x - 16 - dx - sway) <= w || nib;
      };
    }
    var outer = wisp(1, 0, 0);
    S.shape(function (x, y) { return !outer(x, y) && (outer(x - 2, y) || outer(x + 2, y) || outer(x, y - 2) || outer(x, y + 2)); }, [1, 1, 31, 31],
      { m: 'moon', l: 0.3, noOl: 1, tex: function (x, y) { return bay(x, y) > 0.5 ? -3 : 0; } });
    S.shape(outer, [0, 0, 32, 32], { m: 'moon', l: 0.5, g: gLin(16, 28, 16, 3, -0.3), dz: 3, ol: 1 });
    S.shape(wisp(0.72, 0.4, 7), [0, 0, 32, 32], { m: 'moon', l: 0.76, on: 'any' });
    S.shape(wisp(0.44, 0.6, 14.2), [0, 0, 32, 32], { m: 'moon', l: 0.97, on: 'any' });
    S.ell(16.4, 23.4, 1.6, 1.8, { fx: 'hw', on: 'any' });
    dot(S, 24, 7, 'm5'); dot(S, 26, 14, 'm4'); dot(S, 6, 11, 'm4'); dot(S, 8, 4, 'm5'); dot(S, 25, 26, 'm3'); glint(S, 5, 20, 1, 'm5', 'm4');
  };
  /* k-locket: a silver locket open on its hinge, a pressed red flower inside, the lid swung aside */
  ICON['k-locket'] = function (S) {
    var bx = 19.8, by = 19.4;
    [[21.6, 0.8], [21, 2.8], [20.4, 4.8], [20.2, 6.8]].forEach(function (c, i) { S.rell(c[0], c[1] + 0.6, i % 2 ? 0.8 : 1.1, i % 2 ? 1.2 : 0.8, 0, { m: 'iron', l: 0.7, ol: 1 }); });
    S.ring(bx, 9.4, 1.1, 2.2, { m: 'iron', l: 0.66, bev: 0.2, ol: 1 });
    var lid = S.rell(7.4, 18.6, 3.4, 8.6, 0.1, { m: 'iron', l: 0.62, g: gLin(4, 12, 11, 26, 0.36), bev: 0.18, ol: 1 });
    S.rell(7.6, 18.6, 1.9, 7, 0.1, { m: 'iron', l: 0.86, g: gLin(6, 12, 9, 26, 0.3), on: [lid] });
    var bid = S.ell(bx, by, 7.8, 9.2, { m: 'iron', l: 0.62, g: gSph(bx - 2.4, by - 3.2, 9, 10.4, 0.5), bev: 0.2, ol: 1 });
    S.ell(bx, by, 5.8, 7.2, { m: 'linen', l: 0.7, g: gLin(bx - 5, by - 7, bx + 5, by + 7, -0.34), on: 'any' });
    S.ring(bx, by, 5.8, 6.6, { m: 'iron', l: 0.16, on: 'any' });
    S.rect(10.6, 15.6, 2.4, 6.4, { m: 'gold', l: 0.62, bev: 0.28, ol: 1 });
    S.pxl([11, 17, 11, 20], 'g2');
    S.seg(bx + 0.2, by + 1.4, bx - 1.2, by + 6, 0.5, 0.5, { m: 'green', l: 0.44, on: 'any' });
    S.rell(bx + 1.4, by + 4.2, 1.4, 0.8, -0.5, { m: 'green', l: 0.6, on: 'any' });
    for (var k = 0; k < 5; k++) { var a = k * Math.PI * 2 / 5 - Math.PI / 2; S.ell(bx + Math.cos(a) * 2.1, by - 1 + Math.sin(a) * 2.1, 1.5, 1.5, { m: 'blood', l: 0.6, g: gSph(bx - 1.2, by - 2.2, 3.4, 3.4, 0.45), on: 'any' }); }
    S.ell(bx, by - 1, 1, 1, { m: 'gold', l: 0.75, on: 'any' });
    S.px(15, 12, 'hw'); S.px(16, 11, 'i4'); S.px(6, 11, 'hw');
  };
  /* k-coin: an old gold coin with a square hole through it, strung on a knotted leather cord */
  ICON['k-coin'] = function (S) {
    var cx = 16, cy = 19, r = 10.8;
    S.seg(15.2, 2.8, 12.6, 9.2, 0.75, 0.75, { m: 'wood', l: 0.34, ol: 1 });
    var cid = S.ell(cx, cy, r, r, { m: 'gold', l: 0.56, g: gSph(cx - 2, cy - 2.4, r + 1, r + 1, 0.62), bev: 0.16, ol: 1 });
    S.ring(cx, cy, 8, 8.9, { m: 'gold', l: 0.26, on: [cid] });
    S.shape(function (x, y) { var dx = Math.abs(x - cx), dy = Math.abs(y - cy); return dx <= 5 && dy <= 5 && !(dx <= 3.6 && dy <= 3.6); }, [cx - 6, cy - 6, cx + 6, cy + 6], { m: 'gold', l: 0.74, bev: -0.3, on: [cid] });
    S.shape(function (x, y) { return Math.abs(x - cx) <= 3 && Math.abs(y - cy) <= 3; }, [cx - 4, cy - 4, cx + 4, cy + 4], { erase: 1 });
    [[-7, -1], [7, -1], [-1, 6.6], [-1, -8]].forEach(function (m) { S.rect(cx + m[0] - 0.5, cy + m[1] - 0.5, 1.4, 2.6, { m: 'gold', l: 0.28, on: [cid] }); });
    S.seg(16.8, 2.8, 17.4, 15.6, 0.8, 0.75, { m: 'wood', l: 0.6, g: gCyl(16.8, 2.8, 17.4, 15.6, 0.8, 0.5), ol: 1 });
    S.ell(16, 2.6, 1.9, 1.4, { m: 'wood', l: 0.52, g: gSph(15.4, 2.2, 1.9, 1.4, 0.6), ol: 1 });
    S.px(10, 12, 'hw'); S.px(9, 13, 'g5'); S.px(11, 11, 'g5');
  };
  /* k-ring: a gold ring set with a red stone */
  ICON['k-ring'] = function (S) {
    var cx = 16, cy = 20.6, BB = [2, 8, 30, 30];
    var band = function (x, y) { var a = (x - cx) / 11.2, b = (y - cy) / 7.8, c = (x - cx) / 8.2, d = (y - cy) / 5; return a * a + b * b <= 1 && c * c + d * d > 1; };
    S.shape(function (x, y) { return band(x, y) && y < cy; }, BB, { m: 'gold', l: 0.3, g: gLin(cx, cy - 8, cx, cy, -0.12), ol: 1 });
    S.shape(function (x, y) { return band(x, y) && y >= cy; }, BB, { m: 'gold', l: 0.6, g: gSph(cx - 3, cy + 1, 12, 7, 0.55), bev: 0.16, ol: 1 });
    S.shape(function (x, y) { return band(x, y) && Math.abs(y - cy) < 2.2 && x < cx; }, BB, { m: 'gold', l: 0.52, g: gLin(cx - 12, cy, cx - 7, cy, 0.3), ol: 1 });
    S.poly([[cx - 4.6, 12.8], [cx + 4.6, 12.8], [cx + 2.6, 16.2], [cx - 2.6, 16.2]], { m: 'gold', l: 0.56, bev: 0.24, ol: 1 });
    [[-3.8, 1], [3.8, -1]].forEach(function (p) { S.seg(cx + p[0], 12.6, cx + p[0] * 0.9, 7.8, 0.75, 0.6, { m: 'gold', l: 0.72, ol: 1 }); });
    var gid = S.ell(cx, 8.8, 4.6, 4.1, { m: 'blood', l: 0.55, g: gSph(cx - 1.6, 7.4, 4.8, 4.2, 0.85), ol: 1 });
    S.poly([[cx - 1.6, 6.2], [cx + 1.6, 6.2], [cx + 2.6, 8.8], [cx, 11], [cx - 2.6, 8.8]], { m: 'blood', l: 0.72, g: gLin(cx - 2, 6, cx + 2, 11, 0.3), on: [gid] });
    S.pxl([15, 6, 14, 7], 'e4'); S.px(15, 7, 'hw');
    glint(S, 23, 25, 1, 'hw', 'g5');
  };
  /* k-journal: a worn leather journal, its strap wound round and buckled */
  ICON['k-journal'] = function (S) {
    var T = xf(16, 16.6, -0.14), Lc = inv(16, 16.6, -0.14), M = function (l) { return mapPts(T, l); }, BB = [0, 0, 32, 32];
    S.poly(M([[-8.4, -12.2], [11.8, -12.2], [11.8, 12.6], [-8.4, 12.6]]), { m: 'linen', l: 0.78, dz: 3, ol: 1,
      tex: function (x, y) { var q = Lc(x + 0.5, y + 0.5); return (Math.floor((q[0] > 10 ? q[0] : q[1]) + 40) % 2) ? -0.2 : 0.04; } });
    var cid = S.poly(M([[-10.8, -13.8], [10.2, -13.8], [10.2, 11.6], [-10.8, 11.6]]), { m: 'wood', l: 0.56, g: gLin(-2, -12, 12, 14, 0.36), bev: 0.2, dz: 3, ol: 1,
      tex: function (x, y) { return (hash(x, y, 111) > 0.86 ? -0.14 : 0) + (vnoise(x * 0.3, y * 0.3, 113) > 0.7 ? 0.1 : 0); } });
    S.shape(function (x, y) { var q = Lc(x, y), u = q[0], v = q[1], e = Math.min(u + 10.8, 10.2 - u, v + 13.8, 11.6 - v); return e > 1.4 && e < 2.2 && (Math.floor(u + v + 40) % 2 === 0); }, BB, { m: 'wood', l: 0.94, on: [cid] });
    S.poly(M([[-10.8, -13.8], [-8, -13.8], [-8, 11.6], [-10.8, 11.6]]), { m: 'wood', l: 0.3, bev: 0.2, on: [cid] });
    var s0 = M([[-12, -1.8], [12.8, -1.8], [12.8, 1.8], [-12, 1.8]]);
    S.poly(s0, { m: 'wood', l: 0.3, g: gLin(-12, 0, 12, 0, 0.1), bev: 0.22, ol: 1 });
    S.poly(M([[1.2, -3], [6.6, -3], [6.6, 3], [1.2, 3]]), { m: 'gold', l: 0.62, bev: 0.3, ol: 1 });
    S.poly(M([[2.6, -1.5], [5.2, -1.5], [5.2, 1.5], [2.6, 1.5]]), { m: 'wood', l: 0.2, ol: 1 });
    var pr = M([[3.9, -1.4], [3.9, 1.4]]); S.seg(pr[0][0], pr[0][1], pr[1][0], pr[1][1], 0.45, 0.45, { m: 'gold', l: 0.9, on: 'any' });
    var tl = M([[12.4, 1.8], [13.2, 4.4]]);
    S.seg(tl[0][0], tl[0][1], tl[1][0], tl[1][1], 1.3, 1.05, { m: 'wood', l: 0.36, ol: 1 });
  };
  /* k-letter: a folded letter closed with a red wax seal */
  ICON['k-letter'] = function (S) {
    var T = xf(16, 16.4, -0.1), M = function (l) { return mapPts(T, l); };
    var lid = S.poly(M([[-13, -9.4], [13, -9.4], [13, 9.4], [-13, 9.4]]), { m: 'linen', l: 0.74, g: gLin(3, 6, 29, 27, 0.3), bev: 0.14, dz: 3, ol: 1 });
    S.poly(M([[-13, 9.4], [0, -0.6], [13, 9.4]]), { m: 'linen', l: 0.62, g: gLin(3, 20, 29, 27, 0.2), on: [lid], ol: 1 });
    S.poly(M([[-13, -9.4], [13, -9.4], [0, 2.8]]), { m: 'linen', l: 0.88, g: gLin(3, 6, 29, 14, 0.2), on: [lid], ol: 1 });
    S.poly(M([[-10.4, 6.6], [-5.6, 3], [-4.8, 3.8], [-9.4, 7.4]]), { m: 'linen', l: 0.46, on: [lid] });
    var c = T(0, 2.4), sid = S.shape(function (x, y) { var dx = x - c[0], dy = y - c[1], a = Math.atan2(dy, dx); return dx * dx + dy * dy <= Math.pow(4.4 + 0.5 * Math.sin(a * 5 + 1), 2); }, [c[0] - 6, c[1] - 6, c[0] + 6, c[1] + 6],
      { m: 'blood', l: 0.52, g: gSph(c[0] - 1.2, c[1] - 1.4, 5, 5, 0.7), ol: 1 });
    S.ring(c[0], c[1], 2.2, 2.9, { m: 'blood', l: 0.26, on: [sid] });
    S.rect(c[0] - 0.5, c[1] - 1.5, 1, 3, { m: 'blood', l: 0.3, on: [sid] }); S.rect(c[0] - 1.5, c[1] - 0.5, 3, 1, { m: 'blood', l: 0.3, on: [sid] });
    S.px(Math.floor(c[0] - 2), Math.floor(c[1] - 3), 'e4');
    S.pxl([Math.floor(c[0] + 3), Math.floor(c[1] + 4), Math.floor(c[0] + 4), Math.floor(c[1] + 3)], 'b2');
  };
  /* k-toy: a little carved wooden horse on a wheeled board */
  ICON['k-toy'] = function (S) {
    S.rect(4.6, 23, 22.8, 2.6, { m: 'wood', l: 0.42, bev: 0.2, ol: 1 });
    [[8.4, 27], [23.6, 27]].forEach(function (w) {
      S.ell(w[0], w[1], 2.9, 2.9, { m: 'wood', l: 0.3, g: gSph(w[0] - 1, w[1] - 1, 3, 3, 0.6), ol: 1 });
      S.ell(w[0], w[1], 0.9, 0.9, { m: 'gold', l: 0.66, on: 'any' });
    });
    [[9.4, 16.6, 9.6, 23.2], [12.4, 17, 12.8, 23.2], [19.4, 17, 19.2, 23.2], [22.2, 16.6, 22.8, 23.2]].forEach(function (l, i) { S.seg(l[0], l[1], l[2], l[3], 1.2, 1.05, { m: 'pine', l: i === 1 || i === 2 ? 0.34 : 0.5, ol: 1 }); });
    S.seg(5.8, 12.4, 3.4, 18.8, 1.2, 0.8, { m: 'wood', l: 0.4, ol: 1 });
    var bid = S.poly(path([[6.2, 11.6], [21.6, 11], ['q', 24, 12.4, 23.6, 15.2], ['q', 23, 18, 20.4, 18.2], [9.2, 18.2], ['q', 6, 17.8, 5.8, 14.6]]), { m: 'pine', l: 0.56, g: gSph(12, 12.6, 12, 6, 0.5), bev: 0.14, ol: 1,
      tex: function (x, y) { return (x + y * 3) % 7 === 0 ? -0.1 : 0; } });
    S.poly([[11.4, 10.8], [17.6, 10.6], [18, 15.6], [11.2, 15.8]], { m: 'blood', l: 0.52, bev: 0.2, on: [bid], ol: 1 });
    S.rect(11.6, 13.6, 6.2, 0.8, { m: 'gold', l: 0.62, on: 'any' });
    S.poly([[19.6, 12.2], [22.6, 4.4], [25.2, 3], [27.2, 4.2], [30.2, 8.6], [28.8, 10.4], [25.4, 8.8], [24, 13.4]], { m: 'pine', l: 0.6, g: gSph(24, 6, 7, 6, 0.5), bev: 0.14, ol: 1 });
    S.poly([[23.2, 4.4], [23.8, 0.9], [25.6, 3.4]], { m: 'pine', l: 0.52, ol: 1 });
    S.shape(function (x, y) { return x > 20.4 && x < 23.6 && y > 3.6 && y < 12.4 && (x - 21) * 2.1 + y > 11.4 && (x - 21) * 2.1 + y < 15.4; }, [19, 3, 25, 13], { m: 'wood', l: 0.36, on: 'any', tex: function (x, y) { return y % 2 ? -0.12 : 0.1; } });
  };
  /* o-plate: a steel breastplate, ridged down the middle, riveted, its neckline edged in gold */
  ICON['o-plate'] = function (S) {
    var body = path([[9.6, 3.4], ['q', 16, 9.2, 22.4, 3.4], [26.6, 5], ['q', 23.4, 10, 25, 15.6], ['q', 25.6, 21.8, 23, 24.6], [9, 24.6], ['q', 6.4, 21.8, 7, 15.6], ['q', 8.6, 10, 5.4, 5]]);
    var bin = pip(body), bid = S.poly(body, { m: 'steel', l: 0.56, g: gAdd(gRidge(16, 6, 16, 25, 0.14), gSph(13, 12, 13, 15, 0.5)), bev: 0.2, dz: 3, ol: 1 });
    S.shape(function (x, y) { return bin(x, y) && Math.abs(x - 16) < 0.6 && y > 7; }, [14, 6, 18, 25], { m: 'steel', l: 0.95, on: [bid] });
    S.poly(path([[9.6, 3.4], ['q', 16, 9.2, 22.4, 3.4], [22.4, 5], ['q', 16, 10.8, 9.6, 5]]), { m: 'gold', l: 0.6, bev: 0.2, on: [bid] });
    [[[8.4, 24.4], [23.6, 24.4], [24.6, 27.6], [7.4, 27.6]], [[7.4, 27.4], [24.6, 27.4], [25.6, 30.4], [6.4, 30.4]]].forEach(function (l, i) { S.poly(l, { m: 'steel', l: 0.5 - i * 0.08, g: gSph(13, 25, 14, 6, 0.4), bev: 0.22, ol: 1 }); });
    [[9, 8], [23, 8], [8, 20], [24, 20], [10, 26], [22, 26], [9, 29], [23, 29]].forEach(function (r) { S.px(r[0], r[1], 'i4'); });
    S.pxl([11, 11, 12, 10, 12, 12], 'i4'); S.px(11, 10, 'hw');
  };
  /* o-leather: a leather jerkin, laced up the front, stitched at the seams */
  ICON['o-leather'] = function (S) {
    var body = path([[8.2, 2.4], [12.6, 2.4], ['q', 16, 9.8, 19.4, 2.4], [23.8, 2.4], [24.6, 7.4], ['q', 22.6, 10.4, 23.8, 14.4], [25.6, 29.6], [6.4, 29.6], [8.2, 14.4], ['q', 9.4, 10.4, 7.4, 7.4]]);
    var bin = pip(body), bid = S.poly(body, { m: 'wood', l: 0.54, g: gSph(12.6, 12, 16, 20, 0.5), bev: 0.18, dz: 3, ol: 1,
      tex: function (x, y) { return (hash(x, y, 117) > 0.86 ? -0.12 : 0) + (vnoise(x * 0.3, y * 0.3, 119) > 0.68 ? 0.1 : 0); } });
    S.shape(function (x, y) { return bin(x, y) && Math.abs(x - 16) < 0.6 && y > 8; }, [14, 6, 18, 30], { m: 'wood', l: 0.12, on: [bid] });
    for (var y = 10; y < 26; y += 3) { S.seg(14.2, y, 17.8, y + 2, 0.45, 0.45, { m: 'pine', l: 0.62, on: 'any' }); S.seg(17.8, y, 14.2, y + 2, 0.45, 0.45, { m: 'pine', l: 0.52, on: 'any' }); }
    S.shape(function (x, y) { if (!bin(x, y)) return false; return !(bin(x - 1.6, y) && bin(x + 1.6, y) && bin(x, y - 1.6) && bin(x, y + 1.6)) && (Math.floor(x + y) % 2 === 0) && y > 4; }, [5, 2, 27, 31], { m: 'wood', l: 0.86, on: [bid] });
    S.rect(7, 26.4, 18, 1.2, { m: 'wood', l: 0.3, on: [bid] });
    [[10.4, 20.4], [21.6, 20.4]].forEach(function (p) { S.rect(p[0] - 1.4, p[1] - 1.4, 2.8, 2.8, { m: 'gold', l: 0.6, bev: 0.3, ol: 1 }); });
  };
  /* o-chain: a shirt of iron mail, its neck and sleeves bound in leather */
  ICON['o-chain'] = function (S) {
    var body = path([[11.4, 2.6], ['q', 16, 7, 20.6, 2.6], [25.8, 4], [30.4, 12.6], [26, 15], [24.4, 12.4], [24.6, 29.6], [7.4, 29.6], [7.6, 12.4], [6, 15], [1.6, 12.6], [6.2, 4]]);
    var bid = S.poly(body, { m: 'iron', l: 0.5, g: gSph(12, 11, 16, 20, 0.5), dz: 3, ol: 1,
      tex: function (x, y) { var r = y % 2, c = (x + (Math.floor(y / 2) % 2) * 1) % 2; return r === 0 ? (c === 0 ? 0.26 : -0.02) : (c === 0 ? -0.22 : 0.06); } });
    S.poly(path([[11.4, 2.6], ['q', 16, 7, 20.6, 2.6], [21.6, 4.2], ['q', 16, 9, 10.4, 4.2]]), { m: 'wood', l: 0.5, bev: 0.2, on: [bid], ol: 1 });
    [[[1.6, 12.6], [6, 15], [5.2, 16.2], [0.9, 13.8]], [[30.4, 12.6], [26, 15], [26.8, 16.2], [31.1, 13.8]]].forEach(function (c) { S.poly(c, { m: 'wood', l: 0.44, bev: 0.2, ol: 1 }); });
    S.shape(function (x, y) { return y > 28 && y < 29.8 && x > 7.4 && x < 24.6 && x % 2 === 0; }, [7, 27, 25, 31], { m: 'iron', l: 0.2, on: [bid] });
  };
  /* o-robes: flowing mage's robes, wide sleeves, a gold-trimmed collar and sash */
  ICON['o-robes'] = function (S) {
    var body = path([[12.4, 2.2], ['q', 16, 5.6, 19.6, 2.2], [22.8, 3.4], [29.6, 13.4], ['q', 30.6, 17.8, 27.6, 19.4], [23.2, 13.6], [25.4, 29.8], ['q', 16, 31, 6.6, 29.8], [8.8, 13.6], [4.4, 19.4], ['q', 1.4, 17.8, 2.4, 13.4], [9.2, 3.4]]);
    var bin = pip(body), bid = S.poly(body, { m: 'moon', l: 0.42, g: gSph(12, 11, 18, 22, 0.55), dz: 3, ol: 1,
      tex: function (x, y) { return (y > 16 && [11, 14, 18, 21].indexOf(x) >= 0) ? -0.2 : (y > 16 && [12, 19].indexOf(x) >= 0 ? 0.1 : 0); } });
    S.poly(path([[12.4, 2.2], ['q', 16, 5.6, 19.6, 2.2], [20.4, 3.6], [16, 12.4], [11.6, 3.6]]), { m: 'moon', l: 0.12, on: [bid] });
    [[12.4, 2.2, 16, 12.4], [19.6, 2.2, 16, 12.4]].forEach(function (l) { S.seg(l[0], l[1], l[2], l[3], 0.75, 0.75, { m: 'gold', l: 0.66, on: 'any' }); });
    S.shape(function (x, y) { return bin(x, y) && y >= 14.6 && y < 16.8; }, [6, 13, 26, 18], { m: 'gold', l: 0.56, bev: 0.2, on: [bid] });
    S.seg(17.4, 16, 18.6, 22.6, 0.6, 0.5, { m: 'gold', l: 0.5, on: 'any' }); S.seg(18.4, 16, 20.4, 21.8, 0.55, 0.45, { m: 'gold', l: 0.6, on: 'any' });
    S.shape(function (x, y) { return bin(x, y) && ((x < 8 && y > 15.4 - (8 - x) * 0.1) || (x > 24 && y > 15.4 - (x - 24) * 0.1)) && !bin(x + (x < 16 ? -1.4 : 1.4), y + 1.4); }, [1, 12, 31, 21], { m: 'gold', l: 0.6, on: [bid] });
    S.shape(function (x, y) { return bin(x, y) && !bin(x, y + 1.4) && y > 27; }, [5, 26, 27, 31], { m: 'gold', l: 0.52, on: [bid] });
  };
  /* o-cloak: a hooded travelling cloak hung by its hood from a wooden peg, a brooch at one shoulder */
  ICON['o-cloak'] = function (S) {
    S.seg(12.6, 2.8, 19.4, 2.1, 1.15, 1, { m: 'wood', l: 0.56, g: gCyl(12.6, 2.8, 19.4, 2.1, 1.1, 0.5), ol: 1 });
    var cl = path([[16, 2.4], ['q', 21.8, 3.4, 21.6, 9.4], ['q', 22.4, 11.6, 23.4, 12.4], ['q', 26, 20, 28.8, 29.6], ['q', 25.2, 28.4, 22.6, 30.4], ['q', 19.4, 28.8, 16.2, 30.4], ['q', 13, 28.8, 9.8, 30.4], ['q', 7, 28.4, 3.2, 29.6], ['q', 6, 20, 8.6, 12.4], ['q', 9.6, 11.6, 10.4, 9.4], ['q', 10.2, 3.4, 16, 2.4]]);
    var cin = pip(cl), cid = S.poly(cl, { m: 'leaf', l: 0.6, g: gSph(12, 10, 18, 22, 0.5), dz: 3, ol: 1,
      tex: function (x, y) { return (y > 15 && [9, 13, 20, 24].indexOf(x) >= 0) ? -0.24 : (y > 15 && [10, 21].indexOf(x) >= 0 ? 0.1 : 0); } });
    S.shape(function (x, y) { var d = Math.abs(y - (12.2 + Math.pow((x - 16) / 5.4, 2) * 1.6)); return cin(x, y) && d < 0.6 && Math.abs(x - 16) < 6.4; }, [8, 10, 24, 16], { m: 'leaf', l: 0.3, on: [cid] });
    S.shape(function (x, y) { return cin(x, y) && x > 16.6 && x < 17.8 && y > 13.4; }, [15, 13, 19, 31], { m: 'leaf', l: 0.26, on: [cid] });
    S.shape(function (x, y) { return cin(x, y) && !cin(x - 1.4, y - 1) && y > 3; }, [2, 2, 30, 31], { m: 'leaf', l: 0.92, on: [cid] });
    S.ell(11.4, 16.6, 2.5, 2.4, { m: 'gold', l: 0.58, g: gSph(10.6, 15.8, 2.5, 2.4, 0.7), bev: 0.24 });
    S.px(11, 16, 'b3'); S.px(10, 15, 'g5');
  };
  /* o-common: a plain linen shirt, laced at the neck, a leather belt and buckle */
  ICON['o-common'] = function (S) {
    var body = path([[11.6, 2.6], ['q', 16, 6.2, 20.4, 2.6], [25.4, 4.2], [30.2, 19.4], [26.2, 20.6], [23.6, 12], [24.2, 29.4], [7.8, 29.4], [8.4, 12], [5.8, 20.6], [1.8, 19.4], [6.6, 4.2]]);
    var bid = S.poly(body, { m: 'linen', l: 0.7, g: gSph(12, 10, 16, 20, 0.45), bev: 0.12, dz: 3, ol: 1,
      tex: function (x, y) { return ((x === 12 || x === 20) && y > 22) || ((x === 4 || x === 27) && y > 14) ? -0.18 : 0; } });
    S.poly([[13.4, 3.6], [18.6, 3.6], [16, 11.4]], { m: 'linen', l: 0.3, on: [bid] });
    [5.4, 7.8].forEach(function (y) { S.seg(14.4, y, 17.6, y + 1.6, 0.45, 0.45, { m: 'wood', l: 0.5, on: 'any' }); S.seg(17.6, y, 14.4, y + 1.6, 0.45, 0.45, { m: 'wood', l: 0.4, on: 'any' }); });
    S.rect(7.6, 19.6, 16.8, 3.2, { m: 'wood', l: 0.42, g: gLin(8, 20, 24, 22, 0.2), bev: 0.24, ol: 1 });
    S.rect(13.8, 19, 4.4, 4.4, { m: 'gold', l: 0.6, bev: 0.3, ol: 1 });
    S.rect(15, 20.2, 2, 2, { m: 'wood', l: 0.3, on: 'any' });
    S.seg(20.4, 22.6, 21.4, 27, 0.7, 0.6, { m: 'wood', l: 0.36, ol: 1 });
  };

  /* ---- shop items (i-) and portrait moods (v-) ---- */
  /* a soft round glow about (cx, cy) out to radius R, behind everything drawn so far: dithered thinner toward
     its edge, casting no outline, and kept 2px clear of the drawing so its outline stays whole */
  function aura(S, cx, cy, R, m, l0, l1, dens) {
    var W = S.w, H = S.h, add = [], x, y, i, j, k;
    for (y = 0; y < H; y++) for (x = 0; x < W; x++) {
      var t = 1 - Math.sqrt((x + 0.5 - cx) * (x + 0.5 - cx) + (y + 0.5 - cy) * (y + 0.5 - cy)) / R;
      if (t <= 0 || t * dens <= bay(x, y)) continue;
      var near = false;
      for (j = -1; j <= 1 && !near; j++) for (k = -1; k <= 1; k++) {
        var xx = x + k, yy = y + j; if (xx >= 0 && yy >= 0 && xx < W && yy < H && S.rp[yy * W + xx] >= 0 && !S.no[yy * W + xx]) { near = true; break; }
      }
      if (!near) add.push([x, y, t]);
    }
    for (i = 0; i < add.length; i++) S.rect(add[i][0], add[i][1], 1, 1, { m: m, l: lerp(l1, l0, add[i][2]), noOl: 1, dz: 4 });
  }
  /* a tapered stroke along a quadratic curve, widest a little after its start (a claw digs in, then drags) */
  function slashPts(p0, c, p1, w) {
    var L = [], R = [], N = 16;
    for (var i = 0; i <= N; i++) {
      var t = i / N, u = 1 - t, x = u * u * p0[0] + 2 * u * t * c[0] + t * t * p1[0], y = u * u * p0[1] + 2 * u * t * c[1] + t * t * p1[1];
      var dx = 2 * u * (c[0] - p0[0]) + 2 * t * (p1[0] - c[0]), dy = 2 * u * (c[1] - p0[1]) + 2 * t * (p1[1] - c[1]), l = Math.sqrt(dx * dx + dy * dy) || 1;
      var ww = w * Math.pow(Math.sin(Math.PI * Math.pow(t, 0.75)), 0.85);
      L.push([x - dy / l * ww, y + dx / l * ww]); R.unshift([x + dy / l * ww, y - dx / l * ww]);
    }
    return L.concat(R);
  }
  /* i-tincture: a small vial of dark glass, corked and tied off, a dark red draught glinting inside */
  ICON['i-tincture'] = function (S) {
    var cx = 16, cy = 18, ang = 0.1, T = xf(cx, cy, ang), Lc = inv(cx, cy, ang), M = function (l) { return mapPts(T, path(l)); };
    var body = M([[-2.3, -8.4], ['q', -2.2, -4.4, -5.8, -2.8], ['q', -8.3, -1.6, -8.1, 2.8], [-7.7, 9.2], ['q', -7.5, 12.4, -4.4, 12.6], [4.4, 12.6],
      ['q', 7.5, 12.4, 7.7, 9.2], [8.1, 2.8], ['q', 8.3, -1.6, 5.8, -2.8], ['q', 2.2, -4.4, 2.3, -8.4]]);
    var bin = pip(body), gid = S.poly(body, { m: 'moon', l: 0.34, g: gSph(cx - 3, cy - 2, 10, 13, 0.5), bev: 0.12, dz: 3, ol: 1 });
    /* the draught, level in the world while the vial leans; its surface catches a line of light */
    var lvl = 19.2, wet = function (x, y) { return bin(x, y) && bin(x - 1.5, y) && bin(x + 1.5, y) && bin(x, y + 1.4) && y > lvl; };
    S.shape(wet, [4, 14, 28, 32], { m: 'blood', l: 0.12, dz: 8, on: [gid] });
    S.shape(function (x, y) { var u = Lc(x, y)[0]; return wet(x, y) && u > -4.4 && u < -2.8; }, [4, 14, 28, 32], { m: 'blood', l: 0.36, dz: 8, on: 'any' });
    S.shape(function (x, y) { return wet(x, y) && y < lvl + 1; }, [4, 14, 28, 32], { m: 'blood', l: 0.4, dz: 8, on: [gid] });
    /* the glass: a long cold highlight down the left, a rim of light down the right */
    var h0 = T(-5.4, -0.6), h1 = T(-5.6, 8.6);
    S.seg(h0[0], h0[1], h1[0], h1[1], 0.5, 0.5, { m: 'moon', l: 0.86, dz: 6, on: [gid] });
    S.shape(function (x, y) { return bin(x, y) && !bin(x + 1.2, y) && y > 14 && y < 29; }, [4, 12, 28, 32], { m: 'moon', l: 0.55, dz: 6, on: [gid] });
    var sh = P(T(-4.2, -2.6)); S.px(sh[0], sh[1], 'm5');
    /* the lip, the twine wrap, the cork */
    var lp = T(0, -8.6); S.rell(lp[0], lp[1], 3.2, 1.2, ang, { m: 'moon', l: 0.6, bev: 0.24, ol: 1 });
    var w0 = T(-2.6, -6.4), w1 = T(2.6, -6.2); S.seg(w0[0], w0[1], w1[0], w1[1], 0.62, 0.62, { m: 'linen', l: 0.72, ol: 1 });
    var k0 = T(2.2, -6.2), k1 = T(4.2, -3.8); S.seg(k0[0], k0[1], k1[0], k1[1], 0.5, 0.45, { m: 'linen', l: 0.6, ol: 1 });
    S.poly(mapPts(T, [[-2.2, -9.4], [2.2, -9.4], [2.8, -14.4], [-2.8, -14.4]]), { m: 'pine', l: 0.46, g: gLin(12, 4, 20, 8, 0.4), bev: 0.2, ol: 1,
      tex: function (x, y) { return hash(x, y, 131) > 0.72 ? -0.18 : 0; } });
    var ct = T(0, -14.4); S.rell(ct[0], ct[1], 2.8, 1, ang, { m: 'pine', l: 0.8, ol: 1 });
    /* the dark red glint, where the light comes through the draught */
    var gl = P(T(4.4, 5.4)); S.px(gl[0], gl[1], 'b3'); S.px(gl[0], gl[1] + 1, 'b2'); S.px(gl[0] - 1, gl[1], 'b2');
  };
  /* i-charm: a carved bone charm on a knotted cord, a rune scratched into it */
  ICON['i-charm'] = function (S) {
    S.seg(5.4, 1.4, 13.6, 10.6, 0.78, 0.78, { m: 'wood', l: 0.5, g: gCyl(5.4, 1.4, 13.6, 10.6, 0.8, 0.5), ol: 1 });
    S.seg(26.6, 1.4, 18.4, 10.6, 0.78, 0.78, { m: 'wood', l: 0.36, g: gCyl(26.6, 1.4, 18.4, 10.6, 0.8, 0.5), ol: 1 });
    S.ell(9.6, 6, 1.9, 1.9, { m: 'blood', l: 0.58, g: gSph(9, 5.3, 1.9, 1.9, 0.8), ol: 1 });
    S.ell(22.4, 6, 1.5, 1.5, { m: 'gold', l: 0.56, g: gSph(22, 5.5, 1.5, 1.5, 0.8), ol: 1 });
    /* the bone: a long curved fang, grained, its root end broad and its point hooked */
    var bone = path([[11.2, 13.2], ['q', 16, 10.8, 20.8, 13.2], ['q', 22.4, 20.2, 20.6, 25.2], ['q', 19.4, 28.8, 16.6, 31], ['q', 16.2, 28.6, 14.8, 26.2], ['q', 11.4, 20.6, 11.2, 13.2]]);
    var bin = pip(bone), bid = S.poly(bone, { m: 'linen', l: 0.66, g: gSph(14, 16, 8, 13, 0.46), bev: 0.14, dz: 4, ol: 1 });
    S.shape(function (x, y) { return bin(x, y) && !bin(x + 1.3, y + 0.6); }, [8, 10, 24, 32], { m: 'linen', l: 0.28, dz: 5, on: [bid] });
    /* the cord's loop through the hole at the root */
    S.ell(16, 13.9, 1.2, 1.1, { fx: 'ol', on: [bid] });
    S.ring(16, 12.2, 1.5, 2.5, { m: 'wood', l: 0.5, g: gSph(15, 11, 3, 3, 0.5), ol: 1 });
    /* a rune scratched deep: the stave and its two raised arms (algiz), each cut with a lit lower lip */
    /* three runes scratched down it, each cut dark with its lower lip catching the light */
    var cuts = {};
    [[['x.x', 'xx.', 'x..', 'x..'], 15, 16], [['x.x', '.x.', '.x.'], 15, 21], [['xx', 'x.x', 'xx.', 'x..'], 16, 25]].forEach(function (g) {
      g[0].forEach(function (row, j) { for (var i = 0; i < row.length; i++) if (row.charAt(i) === 'x') cuts[(g[1] + i) + ',' + (g[2] + j)] = 1; });
    });
    Object.keys(cuts).forEach(function (k) {
      var p = k.split(',').map(Number); S.px(p[0], p[1], 'lt1');
      if (!cuts[(p[0] + 1) + ',' + (p[1] + 1)] && !cuts[p[0] + ',' + (p[1] + 1)]) S.px(p[0], p[1] + 1, 'pa');
    });
    S.px(13, 15, 'pa');
  };
  /* i-blessing: an open palm raised, a soft golden light gathered in it */
  ICON['i-blessing'] = function (S) {
    var skin = function (l) { return { m: 'skin', l: l, g: gSph(13, 14, 11, 14, 0.42), dz: 3, ol: 1 }; };
    S.rect(10.4, 27.4, 11.2, 4.6, { m: 'linen', l: 0.62, g: gCyl(16, 27, 16, 32, 5.6, 0.5), bev: 0.16, ol: 1 });
    S.poly(path([[10, 15.4], ['q', 9.6, 24.2, 12.4, 27.8], [19.8, 27.8], ['q', 22.8, 24.4, 22.6, 15.4], [21.8, 13], [10.8, 13]]), skin(0.56));
    [[11.2, 7.2, 1.62], [14.6, 4.4, 1.7], [18, 5.2, 1.66], [21.1, 8.8, 1.5]].forEach(function (f, i) {
      S.seg(f[0], f[1], f[0] + (i - 1.5) * 0.3, 16.4, f[2], f[2] + 0.1, skin(0.6 - i * 0.03));
      S.rect(f[0] - 0.9, f[1] + 3.8, 1.8, 0.9, { m: 'skin', l: 0.3, on: 'any' });
    });
    S.seg(10.6, 22.2, 5.4, 15.2, 1.95, 1.7, skin(0.62));
    S.px(5, 14, 'sk6');
    /* the light pooled in the palm, and its creases */
    S.ell(16.2, 20.4, 3.8, 3.4, { m: 'holy', l: 0.62, g: gSph(16.2, 20.4, 3.8, 3.4, 0.5), on: 'any', dz: 3 });
    S.ell(16.2, 20.4, 1.6, 1.5, { fx: 'hw', on: 'any' });
    S.seg(12.2, 17.4, 14.2, 25, 0.4, 0.4, { m: 'skin', l: 0.22, on: 'any' });
    aura(S, 16, 16, 14.6, 'brass', 0.62, 0.24, 1.35);
    glint(S, 26, 5, 1, 'hw', 'g4'); dot(S, 5, 8, 'g5');
  };
  /* v-battle (Battle Ready): a sword drawn and raised, light running off its edge */
  ICON['v-battle'] = function (S) {
    var p = [10.4, 28.6], t = [23.4, 1.6], dx = t[0] - p[0], dy = t[1] - p[1], L = Math.sqrt(dx * dx + dy * dy), ux = dx / L, uy = dy / L;
    var at = function (s, w) { return [p[0] + ux * s - uy * w, p[1] + uy * s + ux * w]; };
    var b0 = at(8.2, 0), f0 = at(10.6, 0), f1 = at(L - 7.6, 0);
    S.seg(b0[0], b0[1], t[0], t[1], 2.7, 0.5, { m: 'steel', l: 0.72, g: gRidge(b0[0], b0[1], t[0], t[1], 0.24), dz: 4, ol: 1 });
    S.seg(f0[0], f0[1], f1[0], f1[1], 0.5, 0.5, { m: 'iron', l: 0.22, on: 'any' });
    var c0 = at(7.4, -6.4), c1 = at(7.4, 6.4);
    S.seg(c0[0], c0[1], c1[0], c1[1], 1.5, 1.5, { m: 'gold', l: 0.56, g: gCyl(c0[0], c0[1], c1[0], c1[1], 1.5, 0.6), bev: 0.1, ol: 1 });
    [c0, c1].forEach(function (c) { S.ell(c[0], c[1], 1.7, 1.7, { m: 'gold', l: 0.62, g: gSph(c[0] - 0.5, c[1] - 0.5, 1.7, 1.7, 0.7), ol: 1 }); });
    var h0 = at(2, 0), h1 = at(6, 0);
    S.seg(h0[0], h0[1], h1[0], h1[1], 1.4, 1.4, { m: 'blood', l: 0.5, g: gCyl(h0[0], h0[1], h1[0], h1[1], 1.4, 0.5), ol: 1,
      tex: function (x, y) { return ((x + y) % 3 === 0) ? -0.22 : 0.06; } });
    S.ell(p[0], p[1], 2.2, 2.2, { m: 'gold', l: 0.62, g: gSph(p[0] - 0.7, p[1] - 0.7, 2.2, 2.2, 0.75), ol: 1 });
    var gl = P(at(L - 9.4, -0.6)); glint(S, gl[0], gl[1], 3, 'hw', 'g5');
    S.px(Math.floor(p[0] - 1), Math.floor(p[1] - 1), 'g5');
  };
  /* v-quiet (Quiet Power): a crescent moon, two small stars in its hollow */
  ICON['v-quiet'] = function (S) {
    var cx = 14.6, cy = 16.2, R = 12.6, ex = cx + 6.6, ey = cy - 3.8, er = 10.6, BB = [0, 0, 32, 32];
    var cres = function (x, y) { var a = x - cx, b = y - cy, c = x - ex, d = y - ey; return a * a + b * b <= R * R && c * c + d * d > er * er; };
    var id = S.shape(cres, BB, { m: 'moon', l: 0.72, g: gSph(cx - 4, cy - 4, R + 3, R + 3, 0.5), bev: 0.14, dz: 3, ol: 1 });
    /* the inner edge in soft shadow, a few old craters */
    S.shape(function (x, y) { return cres(x, y) && !cres(x + 1.4, y - 1); }, BB, { m: 'moon', l: 0.44, on: [id] });
    [[6.4, 18.6, 1.6], [9.8, 25, 1.3], [5.6, 12, 1.1]].forEach(function (c) { S.ell(c[0], c[1], c[2], c[2], { m: 'moon', l: 0.48, dz: 6, on: [id] }); });
    glint(S, 23, 20, 2, 'm5', 'm3'); glint(S, 26, 8, 1, 'hw', 'm4'); dot(S, 19, 13, 'm4');
  };
  /* v-brooding (Brooding): a low storm cloud, rain under it and one fork of lightning */
  ICON['v-brooding'] = function (S) {
    var puffs = [[8.6, 14.6, 5.2], [15.4, 10.4, 6.8], [22.8, 12.8, 5.8], [26.8, 16.6, 3.8], [5, 18, 3.6]];
    var cl = function (x, y) {
      if (y > 21.4) return false;
      if (y > 14 && x > 3.4 && x < 29.4) return true;
      for (var i = 0; i < puffs.length; i++) { var dx = x - puffs[i][0], dy = y - puffs[i][1]; if (dx * dx + dy * dy <= puffs[i][2] * puffs[i][2]) return true; }
      return false;
    };
    [[9, 22.6, 7.4, 27.2], [12.6, 23.4, 11.2, 27.4], [22.2, 22.8, 20.6, 27.8], [25.6, 22.6, 24.4, 26]].forEach(function (r) { S.seg(r[0], r[1], r[2], r[3], 0.5, 0.5, { m: 'moon', l: 0.6, ol: 1 }); });
    S.poly([[18.6, 19.4], [14, 25.2], [17, 25.2], [13.8, 31.2], [21.4, 23.2], [18.2, 23.2], [21.2, 19.4]], { m: 'holy', l: 0.66, g: gLin(14, 31, 21, 19, -0.2), ol: 1 });
    var cid = S.shape(cl, [0, 0, 32, 23], { m: 'moon', l: 0.34, g: gAdd(gSph(12, 6, 16, 14, 0.5), gLin(0, 8, 0, 22, 0.3)), dz: 3, ol: 1,
      tex: function (x, y) { return vnoise(x * 0.4, y * 0.4, 141) > 0.62 ? -0.08 : 0; } });
    /* the lit crowns of the puffs, and the heavy underside */
    S.shape(function (x, y) { return cl(x, y) && !cl(x + 1.2, y - 1.6); }, [0, 0, 32, 23], { m: 'moon', l: 0.66, dz: 6, on: [cid] });
    S.shape(function (x, y) { return cl(x, y) && !cl(x, y + 1.6); }, [0, 0, 32, 23], { m: 'moon', l: 0.08, dz: 6, on: [cid] });
    S.px(14, 5, 'm5');
  };
  /* v-cocky (Cocky Grin): a grinning face at a jaunty tilt, one brow cocked high over a wide eye, the other eye
     half shut, and a crooked grin hooked up to one side with one gold tooth catching the light */
  ICON['v-cocky'] = function (S) {
    var cx = 15.8, cy = 17.6, ang = -0.12, T = xf(cx, cy, ang), Lc = inv(cx, cy, ang), BB = [0, 0, 32, 32];
    var RX = 11.6, RY = 12.8;
    var face = function (x, y) { var q = Lc(x, y); return (q[0] * q[0]) / (RX * RX) + (q[1] * q[1]) / (RY * RY) <= 1; };
    /* his hair: cropped close at the sides, swept up into a quiff at the front */
    var quiff = function (x, y) {
      var q = Lc(x, y), u = q[0], v = q[1], a = (u + 1.8) / 7.4, b = (v + 13.4) / 3.3, c = (u - 4.4) / 5.2, d = (v + 12.2) / 2.4;
      return (face(x, y) && v < -10.9 + 0.03 * u * u) || (a * a + b * b <= 1 && v < -10.4 + u * 0.14) || (c * c + d * d <= 1 && v < -10.6);
    };
    var hid = S.shape(quiff, BB, { m: 'wood', l: 0.42, g: gAdd(gSph(cx - 4, cy - 14, 9, 5, 0.5), gLin(cx - 6, cy - 16, cx + 6, cy - 9, 0.2)), dz: 5, ol: 1,
      tex: function (x, y) { return ((x - y * 2 + 64) % 5 === 0) ? -0.16 : 0; } });
    var fid = S.shape(function (x, y) { return face(x, y) && !quiff(x, y); }, BB, { m: 'skin', l: 0.6, g: gSph(cx - 3.4, cy - 4.6, 14, 15, 0.5), bev: 0.1, dz: 5, ol: 1 });
    S.shape(function (x, y) { return face(x, y) && !quiff(x, y) && !face(x + 1.2, y + 1.6); }, BB, { m: 'skin', l: 0.26, dz: 5, on: [fid] });
    var hl = P(T(-4.6, -14.6)); S.px(hl[0], hl[1], 'lt4'); S.px(hl[0] + 1, hl[1], 'lt3');
    var TT = function (l) { return path(l).map(function (p) { return T(p[0], p[1]); }); }, dk = { m: 'wood', l: 0.08, on: [fid] };
    /* the brows: ours-left flung up in a high arch, ours-right low and level over a lazy lid */
    S.line(TT([[-9.4, -3.8], ['q', -6.4, -10.2, -1.4, -7.2]]), 1, dk);
    S.line(TT([[1.8, -5.2], [5.4, -5.8], [9, -5.2]]), 0.9, dk);
    /* the wide eye with a glint in it; the half-shut one under a level lid */
    S.shape(function (x, y) { var q = Lc(x, y), a = (q[0] + 5) / 1.7, b = (q[1] + 1.2) / 2.3; return a * a + b * b <= 1; }, BB, { fx: 'ol', on: [fid] });
    S.shape(function (x, y) { var q = Lc(x, y), a = (q[0] - 5.4) / 2.2, b = (q[1] + 1.2) / 1.5; return a * a + b * b <= 1 && q[1] > -1.3; }, BB, { fx: 'ol', on: [fid] });
    S.line(TT([[2.8, -1.9], [8, -2.1]]), 0.5, { m: 'wood', l: 0.3, on: [fid] });
    /* a small nose */
    S.line(TT([[0.6, -0.4], [1.4, 1.8]]), 0.5, { m: 'skin', l: 0.26, on: [fid] });
    var nz = P(T(0.2, 1.6)); S.px(nz[0], nz[1], 'skin:4');
    /* the grin: low and flat at ours-left, hooked up hard at ours-right, open on a row of teeth, one of them gold */
    var up = function (u) { var t = clamp((u + 7.2) / 15.6, 0, 1); return 5.6 - 4.4 * Math.pow(t, 1.6); };
    var gap = function (u) { var t = (u + 7.2) / 15.6; return t <= 0 || t >= 1 ? 0 : 4.4 * Math.pow(Math.sin(Math.PI * t), 0.7) * (0.28 + 0.72 * t); };
    var dOf = function (x, y) { var q = Lc(x, y); return [q[0], q[1] - up(q[0])]; };
    var mouth = function (x, y) { var q = dOf(x, y); return q[0] > -7.2 && q[0] < 8.4 && q[1] >= -0.6 && q[1] <= gap(q[0]) + 0.6; };
    var mid = S.shape(mouth, BB, { fx: 'ol', on: [fid] });
    S.shape(function (x, y) { var q = dOf(x, y); return mouth(x, y) && q[1] > 1.6 && q[1] < gap(q[0]) - 0.3; }, BB, { m: 'blood', l: 0.2, dz: 8, on: [mid] });
    var tid = S.shape(function (x, y) { var q = dOf(x, y); return mouth(x, y) && q[0] > -4.6 && q[0] < 7.4 && q[1] > 0.2 && q[1] < 1.9; }, BB, { m: 'linen', l: 0.9, g: gLin(10, 18, 26, 18, 0.16), dz: 8, on: [mid] });
    var gid = S.shape(function (x, y) { var q = dOf(x, y); return mouth(x, y) && q[0] > 2.1 && q[0] < 4.5 && q[1] > 0.2 && q[1] < 2.5; }, BB, { m: 'gold', l: 0.7, g: gLin(18, 16, 21, 21, 0.36), dz: 8, on: [mid, tid] });
    [-2.2, 0, 6].forEach(function (u) { S.shape(function (x, y) { var q = dOf(x, y); return Math.abs(q[0] - u) < 0.5 && q[1] > 0.9 && q[1] < 1.9; }, BB, { m: 'linen', l: 0.4, on: [tid] }); });
    /* the lower lip catching the light, the dimple at the high corner */
    S.shape(function (x, y) { var q = dOf(x, y); return q[0] > -5.2 && q[0] < 7 && q[1] - gap(q[0]) > 0.6 && q[1] - gap(q[0]) < 1.5; }, BB, { m: 'skin', l: 0.84, on: [fid] });
    S.line(TT([[9.4, -0.6], [10, 1.4]]), 0.5, { m: 'skin', l: 0.22, on: [fid] });
    var e = P(T(-5.6, -2.2)); S.px(e[0], e[1], 'hw');
    var g = P(T(2.8, up(2.8) + 0.9)); S.px(g[0], g[1], 'hw');
    var gp = P(T(10.8, 7.2)); glint(S, gp[0], gp[1], 2, 'hw', 'g5');
    S.px(Math.floor(cx - 7), Math.floor(cy - 9), 'sk6');
  };
  /* v-noble (Noble): a gold crown on red velvet, a ruby and two sapphires set in its band */
  ICON['v-noble'] = function (S) {
    S.ell(16, 16.4, 10, 7.6, { m: 'blood', l: 0.34, g: gSph(13, 12, 11, 9, 0.5), dz: 3, ol: 1, tex: function (x, y) { return ((x + y) % 3 === 0) ? -0.1 : 0; } });
    var tips = [[4.8, 11.4], [10.6, 8.2], [16, 4.8], [21.4, 8.2], [27.2, 11.4]];
    var crown = path([[5.4, 27.2], ['q', 16, 29.6, 26.6, 27.2], [27.2, 11.4], [23.4, 17.2], [21.4, 8.2], [18.4, 16.2], [16, 4.8], [13.6, 16.2], [10.6, 8.2], [8.6, 17.2], [4.8, 11.4]]);
    var cin = pip(crown), cid = S.poly(crown, { m: 'gold', l: 0.52, g: gAdd(gSph(12, 12, 17, 18, 0.46), gRidge(16, 4, 16, 28, 0.04)), bev: 0.2, dz: 3, ol: 1 });
    S.shape(function (x, y) { return cin(x, y) && y > 20.4; }, [3, 19, 29, 30], { m: 'gold', l: 0.66, g: gSph(12, 20, 16, 10, 0.4), bev: 0.2, on: [cid] });
    S.shape(function (x, y) { return cin(x, y) && y > 20.4 && y < 21.4; }, [3, 19, 29, 30], { m: 'gold', l: 0.2, on: [cid] });
    S.shape(function (x, y) { var yb = 26.4 + Math.pow((x - 16) / 11, 2) * -0.6 + (1 - Math.pow((x - 16) / 11, 2)) * 1.1; return cin(x, y) && y > yb && y < yb + 1; }, [3, 24, 29, 30], { m: 'gold', l: 0.24, on: [cid] });
    tips.forEach(function (t) { S.ell(t[0], t[1] - 1.2, 1.75, 1.75, { m: 'gold', l: 0.72, g: gSph(t[0] - 0.6, t[1] - 1.8, 1.75, 1.75, 0.8), ol: 1 }); });
    S.poly([[16, 20.6], [18.6, 23.8], [16, 27], [13.4, 23.8]], { m: 'blood', l: 0.58, g: gLin(14, 21, 18, 27, 0.5), bev: 0.2, ol: 1 });
    [[9.6, 24], [22.4, 24]].forEach(function (g) { S.poly([[g[0], g[1] - 1.9], [g[0] + 1.5, g[1]], [g[0], g[1] + 1.9], [g[0] - 1.5, g[1]]], { m: 'green', l: 0.56, g: gLin(g[0] - 1, g[1] - 2, g[0] + 1, g[1] + 2, 0.5), on: [cid] }); });
    S.px(15, 22, 'e4'); S.px(9, 23, 'g4'); S.px(21, 23, 'g4');
    S.px(15, 2, 'hw'); S.px(9, 6, 'g5'); S.pxl([7, 21, 8, 21], 'g5');
  };
  /* v-wild (Wild): three claw slashes torn through, raw at the edges */
  ICON['v-wild'] = function (S) {
    var marks = [[[13.4, 1.8], [11.2, 14.2], [3.6, 25]], [[20.4, 3], [18.4, 16.4], [10, 28.8]], [[27.6, 5.2], [25.6, 18.2], [17.4, 30.6]]];
    marks.forEach(function (m, i) {
      var pts = slashPts(m[0], m[1], m[2], 2.5 - i * 0.12), sin = pip(pts);
      var id = S.poly(pts, { m: 'blood', l: 0.56, g: gLin(m[0][0], m[0][1], m[2][0], m[2][1], 0.28), bev: 0.22, dz: 3, ol: 1 });
      var core = slashPts([m[0][0] + 0.5, m[0][1] + 1.2], [m[1][0] + 0.7, m[1][1] + 0.4], [m[2][0] + 0.8, m[2][1] - 0.6], 1.05);
      S.poly(core, { m: 'blood', l: 0.04, on: [id] });
      S.shape(function (x, y) { return sin(x, y) && !sin(x - 1.1, y - 0.6); }, bbox(pts), { m: 'blood', l: 0.3, on: [id] });
    });
    S.px(12, 7, 'e4'); S.px(19, 9, 'e4'); S.px(26, 11, 'e4');
  };
  /* v-mysterious (Mysterious): a deep hood, only a glint of eyes in its dark */
  ICON['v-mysterious'] = function (S) {
    var hood = path([[16, 1.8], ['q', 23.8, 3.2, 24.4, 12.4], ['q', 24.8, 18.2, 27.4, 22], ['q', 30.2, 25.8, 30.6, 31], [1.4, 31], ['q', 1.8, 25.8, 4.6, 22], ['q', 7.2, 18.2, 7.6, 12.4], ['q', 8.2, 3.2, 16, 1.8]]);
    var hin = pip(hood), hid = S.poly(hood, { m: 'moon', l: 0.4, g: gSph(11, 9, 18, 22, 0.5), dz: 3, ol: 1,
      tex: function (x, y) { return (y > 18 && [9, 22].indexOf(x) >= 0) ? -0.2 : (y > 20 && [10, 23].indexOf(x) >= 0 ? 0.1 : 0); } });
    S.shape(function (x, y) { return hin(x, y) && !hin(x - 1.2, y - 1); }, [0, 0, 32, 32], { m: 'moon', l: 0.84, on: [hid] });
    /* the opening: a pointed arch of black, its fabric rim lit on one side */
    var arch = function (x, y, g) { var dx = Math.abs(x - 16); if (y > 22 + g || y < 6.2 - g) return false; return dx <= (5.2 + g) * Math.sqrt(Math.max(0, Math.min(1, (y - 6.2 + g) / (9.4 + g)))); };
    S.shape(function (x, y) { return arch(x, y, 1.2); }, [8, 3, 24, 25], { m: 'moon', l: 0.24, on: [hid] });
    S.shape(function (x, y) { return arch(x, y, 1.2) && x < 16 && !arch(x + 1.1, y + 0.8, 1.2); }, [8, 3, 24, 25], { m: 'moon', l: 0.66, on: [hid] });
    S.shape(function (x, y) { return arch(x, y, 0); }, [8, 3, 24, 25], { m: 'night', l: 0.04, on: [hid] });
    S.pxl([13, 15, 18, 15], 'm5'); S.pxl([13, 16, 18, 16], 'm2');
    /* the cloak closed at the throat */
    S.seg(16, 23.4, 16, 31, 0.5, 0.5, { m: 'moon', l: 0.1, on: [hid] });
    S.ell(16, 23.6, 1.7, 1.5, { m: 'gold', l: 0.6, g: gSph(15.4, 23, 1.7, 1.5, 0.8), ol: 1 });
    S.px(10, 4, 'm5');
  };
  /* v-warm (Warm): a small hearth fire on two crossed logs, throwing sparks */
  ICON['v-warm'] = function (S) {
    [[4.6, 28.6, 25.2, 23.6, 0.46], [27.4, 28.6, 6.8, 23.6, 0.34]].forEach(function (l) {
      S.seg(l[0], l[1], l[2], l[3], 2.2, 2, { m: 'wood', l: l[4], g: gCyl(l[0], l[1], l[2], l[3], 2.2, 0.5), ol: 1,
        tex: function (x, y) { return (hash(x, y, 151) > 0.8 ? -0.16 : 0) + ((x + y) % 5 === 0 ? -0.1 : 0); } });
      S.ell(l[0], l[1], 1.7, 2, { m: 'pine', l: 0.7, g: gSph(l[0] - 0.4, l[1] - 0.6, 1.7, 2, 0.5), ol: 1 });
      S.px(Math.floor(l[0]), Math.floor(l[1]), 'lt3');
    });
    var cx = 16.2;
    var outer = path([[cx + 0.6, 2], ['q', cx + 2.6, 6.6, cx + 6.2, 10.6], ['q', cx + 9.6, 15.4, cx + 7.4, 20.8], ['q', cx + 4.8, 25.6, cx, 25.6], ['q', cx - 4.8, 25.6, cx - 7.2, 21],
      ['q', cx - 9.6, 16.2, cx - 6.2, 11.2], ['q', cx - 4.4, 9, cx - 4, 5.8], ['q', cx - 1.4, 8.6, cx - 1, 11.2], ['q', cx + 1.4, 7, cx + 0.6, 2]]);
    S.poly(outer, { m: 'ember', l: 0.52, g: gLin(cx, 25, cx, 3, 0.36), bev: 0.1, ol: 1 });
    var inner = path([[cx + 0.4, 8.6], ['q', cx + 2.4, 12.4, cx + 4.4, 15.4], ['q', cx + 6, 19.8, cx + 3.4, 22.8], ['q', cx, 24.6, cx - 3.4, 22.8], ['q', cx - 5.8, 19.6, cx - 3.4, 15.8], ['q', cx - 1.6, 13.6, cx + 0.4, 8.6]]);
    S.poly(inner, { m: 'holy', l: 0.52, g: gLin(cx, 24, cx, 9, 0.3), on: 'any' });
    S.ell(cx, 20.8, 2.2, 2.6, { fx: 'hw', on: 'any' });
    dot(S, 25, 5, 'e4'); dot(S, 7, 7, 'g5'); dot(S, 26, 12, 'e3'); dot(S, 23, 2, 'g4');
  };

  /* ---- the Blackfen paths: f- which way through the bog ---- */
  /* dark bog water across the lower part of a symbol, from y0 down, with a faint lit ripple line or two */
  function bogWater(S, y0, sheen) {
    var wid = S.shape(function (x, y) { var t = (x - 16) / 15.6, top = y0 + t * t * 2.4; return y >= top && y < 30.4 - t * t * 1.6; }, [0, y0 - 1, 32, 31], { m: 'night', l: 0.32, g: gLin(0, y0, 0, 31, 0.26), dz: 4, ol: 1 });
    [[y0 + 2.2, 5, 13], [y0 + 4.6, 17, 27], [y0 + 7.2, 8, 15]].forEach(function (r) { S.seg(r[1], r[0], r[2], r[0], 0.45, 0.45, { fx: sheen || 'm2', on: [wid] }); });
    return wid;
  }
  /* f-wisp: three pale blue-green will-o'-wisps hanging over black water, their light on it */
  ICON['f-wisp'] = function (S) {
    var wid = bogWater(S, 20.4, 'm2');
    [[9.2, 9.4, 2.6], [18.4, 5.6, 2.2], [24.4, 13, 1.9]].forEach(function (w, i) {
      aura(S, w[0], w[1], w[2] + 3.4, 'moon', 0.46, 0.2, 0.8);
      S.ell(w[0], w[1], w[2], w[2] * 1.15, { m: 'moon', l: 0.72, fx: 'y5', ol: 1 });
      S.ell(w[0] - 0.3, w[1] - 0.3, w[2] * 0.62, w[2] * 0.72, { fx: 'y4', on: 'any' });
      S.px(Math.floor(w[0] - 0.6), Math.floor(w[1] - 0.8), 'hw');
      /* the tail each one trails, and its light broken on the water below */
      S.seg(w[0] + 0.4, w[1] + w[2], w[0] + 1.4, w[1] + w[2] + 2.6, 0.55, 0.3, { fx: 'y5', noOl: 1 });
      for (var k = 0; k < 3; k++) S.seg(w[0] - 1.6 + k * 0.4, 22.6 + k * 2.4, w[0] + 1.6 - k * 0.4, 22.6 + k * 2.4, 0.45, 0.45, { fx: k ? 'y5' : 'y4', on: [wid] });
    });
    /* reeds at the edges */
    [[2.6, 21, 1.6, 12], [4.4, 21.4, 5.2, 14.4], [29, 21.2, 29.8, 13.4]].forEach(function (r) { S.seg(r[0], r[1], r[2], r[3], 0.6, 0.4, { m: 'leaf', l: 0.3, ol: 1 }); });
  };
  /* f-dry: a line of dry tussocks stepping across the black marsh, big and near to small and far */
  ICON['f-dry'] = function (S) {
    var wid = S.shape(function (x, y) { var a = (x - 16) / 15.4, b = (y - 21) / 9.6; return a * a + b * b <= 1 && y > 12.4; }, [0, 11, 32, 31], { m: 'night', l: 0.34, g: gLin(0, 12, 0, 31, 0.3), dz: 4, ol: 1 });
    [[15.6, 5, 11], [19.4, 20, 27], [27, 9, 14]].forEach(function (r) { S.seg(r[1], r[0], r[2], r[0], 0.45, 0.45, { fx: 'm2', on: [wid] }); });
    [[25.2, 14.2, 1.9], [19.6, 18, 2.4], [12.8, 22.2, 3], [5.8, 27, 3.6]].forEach(function (t, i) {
      var cx = t[0], cy = t[1], r = t[2];
      S.ell(cx, cy + r * 0.2, r * 1.05, r * 0.46, { m: 'wood', l: 0.36, g: gLin(cx - r, cy, cx + r, cy, 0.2) });
      /* the clump: pale dry blades springing up from the mound, a tuft taller than it is wide */
      var n = 4 + i;
      for (var k = 0; k < n; k++) {
        var f = k / (n - 1) - 0.5, u = f * r * 0.95, lean = f * r * 0.55, len = r * (1.25 + 0.4 * Math.cos(f * 3));
        var bx = cx + u + lean, by = cy - len - (k % 2) * r * 0.3;
        S.seg(cx + u * 0.7, cy + r * 0.1, bx, by, 0.7, 0.3, { m: 'gold', l: 0.56 + (k % 2 ? 0.2 : 0) });
        S.px(Math.floor(bx), Math.floor(by), k % 2 ? 'g5' : 'g4');
      }
      S.px(Math.floor(cx - r * 0.2), Math.floor(cy - r * 1.1), 'g5');
    });
  };
  /* f-shortcut: the bog's black heart, a skull risen half out of black water and bubbles coming up round it */
  ICON['f-shortcut'] = function (S) {
    var wid = S.shape(function (x, y) { var t = (x - 16) / 15.6; return y >= 18.6 + t * t * 2 && y < 30.6 - t * t * 1.6; }, [0, 17, 32, 31], { m: 'dark', l: 0.1, g: gLin(0, 18, 0, 31, 0.2), dz: 4, ol: 1 });
    /* the skull: the dome and brow above the water, the eye pits and nose, the teeth just breaking the surface */
    var skull = function (x, y) { var a = (x - 16) / 8.6, b = (y - 13.4) / 8.4; return a * a + b * b <= 1 && y < 19.6; };
    var sid = S.shape(skull, [6, 4, 26, 21], { m: 'linen', l: 0.72, g: gSph(13, 9.4, 10, 10, 0.5), dz: 4, ol: 1 });
    S.shape(function (x, y) { return skull(x, y) && !skull(x + 1.3, y + 1.3); }, [6, 4, 26, 21], { m: 'linen', l: 0.34, on: [sid] });
    [[12.4, 14.2], [19.6, 14.2]].forEach(function (e) { S.ell(e[0], e[1], 2.3, 2.1, { fx: 'ol', on: [sid] }); S.px(Math.floor(e[0]) + 1, Math.floor(e[1]) - 1, 'v2'); });
    S.poly([[16, 16.2], [17.2, 18.2], [14.8, 18.2]], { fx: 'ol', on: [sid] });
    S.rect(12.6, 18.6, 6.8, 1.6, { m: 'linen', l: 0.6, on: 'any' });
    [13.6, 15.6, 17.6].forEach(function (x) { S.px(Math.floor(x) + 1, 19, 'hb'); });
    S.shape(function (x, y) { var a = (x - 16) / 9.6; return y >= 19.4 && y < 20.4 && a * a < 1; }, [5, 19, 27, 21], { fx: 'v1', on: [wid] });
    /* the bubbles, and a crack across the brow */
    [[25.6, 14.6, 1.6], [23.2, 9.6, 1.2], [26.6, 6, 0.9], [7, 16.4, 1.1]].forEach(function (b) { S.ring(b[0], b[1], b[2] - 0.55, b[2] + 0.35, { m: 'green', l: 0.5, ol: 1 }); S.px(Math.floor(b[0] - b[2] * 0.4), Math.floor(b[1] - b[2] * 0.5), 'v3'); });
    S.line([[15.4, 5.6], [16.4, 7.4], [15.6, 9.2]], 0.42, { m: 'linen', l: 0.2, on: [sid] });
    S.px(12, 7, 'hw');
  };
  /* ---- Old Yost's copper: a full round coin face with a raised rim, for a spinning coin ---- */
  addRamp('copper', ['lt1', 'e1', 'e2', 'e3', 'e4']);
  function coinFace(S) {
    var C0 = 16, R = 15;
    S.ell(C0, C0, R, R, { m: 'copper', l: 0.3, g: gSph(C0 - 4, C0 - 4, 18, 18, 0.24), dz: 3, ol: 1 });
    /* the raised rim: bright toward the light, dark away from it, and the shadow it throws into the field */
    S.ring(C0, C0, 12.6, R, { m: 'copper', l: 0.62, g: gLin(5, 5, 27, 27, 0.5), dz: 4, on: 'any' });
    S.ring(C0, C0, 11.8, 12.6, { m: 'copper', l: 0.14, g: gLin(5, 5, 27, 27, -0.3), dz: 4, on: 'any' });
    /* the beaded border just inside it */
    for (var i = 0; i < 26; i++) {
      var a = i / 26 * 2 * Math.PI, x = C0 + Math.cos(a) * 10.9, y = C0 + Math.sin(a) * 10.9;
      S.px(Math.floor(x), Math.floor(y), (Math.cos(a) + Math.sin(a)) < 0 ? 'e3' : 'e2');
    }
    /* a touch of green patina in the rim's hollows, where old copper keeps it */
    [[5, 21], [24, 26], [26, 9], [9, 5]].forEach(function (p) { S.px(p[0], p[1], 'v2'); });
  }
  /* raised relief on the coin: a bright shape, its upper left edge catching the light, a shadow off its lower right */
  function coinRelief(S, test, bb, l) {
    var id = S.shape(test, bb, { m: 'copper', l: l || 0.7, dz: 5, on: 'any' });
    S.shape(function (x, y) { return test(x, y) && !test(x - 1, y - 1); }, bb, { m: 'copper', l: 0.98, on: [id] });
    S.shape(function (x, y) { return !test(x, y) && test(x - 1, y - 1); }, [bb[0], bb[1], bb[2] + 1, bb[3] + 1], { fx: 'lt1', on: 'any' });
    return id;
  }
  /* coin-heads: a crowned head in profile, facing right: a strong nose and chin, the hair at the back of the head */
  ICON['coin-heads'] = function (S) {
    coinFace(S);
    var head = pip(path([[10.6, 10.6], [19.2, 10], ['q', 20.2, 10.8, 20.2, 12], [23.2, 14.4], [21.2, 15.2], [21.8, 16], [21, 16.6], ['q', 21.8, 18.2, 20.6, 18.8],
      ['q', 19.6, 19.4, 18.4, 19.2], [18.6, 21.4], [20.2, 23.4], [11.2, 23.4], [12.8, 21], ['q', 10.8, 19.8, 10.4, 18], ['q', 8.8, 15, 9.6, 12.8]]));
    var crown = pip([[10.4, 10.8], [10.6, 6.4], [12.8, 8.4], [15, 5.2], [17.2, 8.4], [19.4, 6.4], [19.6, 10.4]]);
    var hid = coinRelief(S, head, [8, 8, 25, 24], 0.72);
    /* the hair: the back of the head down to the nape, darker, in strands */
    S.shape(function (x, y) { return head(x, y) && x < 15.4 - (y - 10) * 0.12 && y < 19.4; }, [8, 9, 17, 20], { m: 'copper', l: 0.42, on: [hid], tex: function (x, y) { return (x + y) % 3 === 0 ? -0.16 : 0; } });
    coinRelief(S, crown, [9, 4, 21, 11], 0.82);
    S.rect(10.6, 9.4, 9, 1, { m: 'copper', l: 0.36, on: 'any' });
    [[10.6, 5.6], [15, 4.4], [19.4, 5.6]].forEach(function (b) { S.px(Math.floor(b[0]), Math.floor(b[1]), 'e4'); });
    /* the eye under its brow, the ear, the cut of the bust */
    S.px(19, 13, 'lt1'); S.px(18, 12, 'copper:1'); S.px(19, 12, 'copper:1');
    S.ring(15.6, 15.2, 0.5, 1.4, { m: 'copper', l: 0.36, on: [hid] });
    S.line([[12, 22.4], [19.4, 22.4]], 0.45, { m: 'copper', l: 0.44, on: [hid] });
  };
  /* coin-tails: the Hollow Stump, the old broken stump the tavern is named for: splintered high on one side, the dark
     of its hollow showing at the top, a knot-hole, its roots spread in the earth, one new leaf */
  ICON['coin-tails'] = function (S) {
    coinFace(S);
    var stump = pip([[9.2, 21.8], [10, 17], [10.2, 12.6], [11, 8.6], [12.6, 11.4], [14, 12.6], [16.4, 12.2], [18, 10.8], [19, 12.6], [21.6, 13.4], [22, 17], [22.8, 21.8]]);
    var roots = function (x, y) { return y > 19.6 && y < 22.8 && ((x > 6.2 && x < 10.4 && y > 22.8 - (10.4 - x) * 0.55) || (x > 21.6 && x < 25.8 && y > 22.8 - (x - 21.6) * 0.55) || (Math.abs(x - 15.4) < 2.4 && y < 23.6)); };
    var sid = coinRelief(S, function (x, y) { return stump(x, y) || roots(x, y); }, [5, 7, 27, 24], 0.68);
    /* the hollow: the black of the rotted heart, open at the broken top */
    S.ell(16.4, 13.4, 4.2, 1.5, { fx: 'lt1', on: [sid] });
    S.ell(16.8, 13.2, 2.6, 0.9, { fx: 'k1', on: 'any' });
    S.ell(13.4, 17.6, 1.2, 1.6, { fx: 'lt1', on: [sid] });
    /* the bark in long ridges */
    [[11.8, 14.4, 11.4, 20.8], [17.6, 15.4, 17.8, 21], [20.4, 15, 20.8, 20.6]].forEach(function (g) { S.seg(g[0], g[1], g[2], g[3], 0.4, 0.4, { m: 'copper', l: 0.4, on: [sid] }); });
    /* a new leaf from the top of the splinter */
    S.poly([[11.2, 8.8], [8.4, 5.6], [11.8, 6.4]], { m: 'copper', l: 0.86, on: 'any' }); S.px(9, 6, 'e4');
    S.line([[6.4, 23.6], [25.6, 23.6]], 0.45, { m: 'copper', l: 0.5, on: 'any' });
  };

  function symbolKeys() { return SYMBOLS.slice(); }
  /* like classIconURL, for a choice symbol key; an unknown key gets the Adventurer d20 */
  function symbolURL(key, size, opts) {
    var s = String(key == null ? '' : key).trim().toLowerCase(), sk = SYMBOLS.indexOf(s) >= 0 ? s : 'other', glow = !!(opts && opts.glow);
    var k = Math.max(1, Math.ceil((+size || 128) / 32)), ck = sk + '|' + k + (glow ? '|g' : '');
    if (urlCache[ck]) return urlCache[ck];
    var u = upscale(iconMaster(sk, glow), k).toDataURL('image/png');
    urlCache[ck] = u;
    return u;
  }

  /* =====================================================================
     2. THE IRON MATRON - 192x144, a three-quarter figure leaning on a long bar.
     Locked description: tall, broad-shouldered woman, stained leather apron over dark wool,
     gray-brown hair tied back in a loose knot, barrel-hauling arms, a thin old scar across the
     back of one forearm, a damp bar towel over her right shoulder, a wooden cup of something
     dark slid toward the viewer. No race cues: ears under hair, plain features, human skin
     that keeps its hue in every state (only the light changes).
     She is in her late fifties and bone-tired, not dead: heavy lids, bags, a flat mouth,
     sloped shoulders, her weight on the bar. The bar runs the full width: a dim back shelf,
     an empty tankard, a few coins and a guttering candle that pools light on the wood.
     Each part (shelf, body, towel, head, bar, forearms) is rendered once per state and
     variant and cached as pixels. A frame is composited only when her pose or the flame
     changes, and a flame-only change repaints just the candle's corner.
     ===================================================================== */
  var MW = 192, MH = 144, MCX = 96.5;                 /* canvas, and her centre line (pixel column 96) */
  var LFIRE = norm3(0.82, -0.32, 0.52), LUP = norm3(-0.12, 0.78, 0.6), LDIM = norm3(0.15, -0.75, 0.62);
  function addRamp(n, keys) { if (RID[n] == null) { RID[n] = RNAMES.length; RNAMES.push(n); RAMPS.push(keys.map(pc)); } }
  addRamp('hairM', ['k1', 'lt1', 'hb', 'mu', 'pa2']);      /* gray-brown: brown in the shadows, gray in the light */
  addRamp('glass', ['m0', 'v1', 'v2', 'v3']);
  addRamp('glassC', ['m0', 'm1', 'm2', 'm3']);
  addRamp('wax', ['lt3', 'lt4', 'pa2', 'pa', 'hw']);
  addRamp('waxC', ['m1', 'm2', 'm3', 'm4', 'm5']);
  /* the counter top: the wood ramp carried on into candle-lit tones (at 2/3 the light it matches 'wood' step for step) */
  addRamp('woodLit', ['k1', 'lt1', 'lt2', 'lt3', 'lt4', 'sk5', 'g4']);
  addRamp('woodLitC', ['m0', 'ol', 'k1', 'lt1', 'lt2', 'lt2', 'lt2']);
  addRamp('woodLitD', ['m0', 'm1', 'w1', 'w2', 'i1', 'i1', 'i1']);
  var MAPS = {
    barkeep: null,
    matron: { skin: 'skinC', hair: 'hairC', hairM: 'hairC', wool: 'woolC', wood: 'woodC', woodLit: 'woodLitC', linen: 'linenC', gold: 'goldC', pine: 'woodC', iron: 'steel', glass: 'glassC', wax: 'waxC' },
    fallen: { skin: 'skinD', hair: 'hairD', hairM: 'hairD', wool: 'woolD', wood: 'woodD', woodLit: 'woodLitD', linen: 'linenC', gold: 'goldC', pine: 'woodD', iron: 'steel', glass: 'glassC', wax: 'woodD' }
  };
  MAPS.hurt = MAPS.matron;
  var M_STATES = ['barkeep', 'matron', 'hurt', 'fallen'];
  /* per state: body shift (dx, dy), squared shoulders (sq), slump (sl), weight on her right arm (ln),
     head offset (hx, hy) and roll (ha) */
  var MFIG = {
    barkeep: { dx: 0, dy: 0, sq: 0, sl: 1, ln: 1, hx: 0, hy: 1, ha: -0.05 },
    matron: { dx: 0, dy: -1, sq: 2, sl: 0, ln: 0, hx: 0, hy: -1, ha: 0 },
    hurt: { dx: 2, dy: -1, sq: 2, sl: 0, ln: 0, hx: 3, hy: -4, ha: 0.1 },
    fallen: { dx: 0, dy: 10, sq: 0, sl: 2, ln: 0, hx: -1, hy: 14, ha: -0.12 }
  };
  var M_TILT = [0, 0.065, 0.13];                      /* the idle head loll: rest, on the way, full */
  function offP(list, dx, dy) { return list.map(function (p) { return p[0] === 'q' ? ['q', p[1] + dx, p[2] + dy, p[3] + dx, p[4] + dy] : [p[0] + dx, p[1] + dy]; }); }

  function drawBody(S, st) {
    var F = MFIG[st], cold = st === 'matron' || st === 'hurt', fallen = st === 'fallen', warm = st === 'barkeep';
    var L = fallen ? LDIM : cold ? LUP : LFIRE, dx = F.dx, dy = F.dy, sq = F.sq, sl = F.sl, ox = MCX + dx, lnL = -1.4 * F.ln, lnR = 0.8 * F.ln;
    function tp(list) { return path(offP(list, ox, dy)); }
    var torsoLight = function (x, y) {
      if (warm) return 0.3 * (x - ox) / 34;
      if (cold) return 0.2 * (y - 70 - dy) / 44 - 0.02;
      return -0.05;
    };
    var woolTex = function (x, y) { return ((x * 3 + y * 5) % 7 === 0 ? -0.08 : 0) + (hash(x, y, 3) > 0.9 ? 0.06 : 0); };
    S.layer = 0;
    /* the neck, slimmer than it was, reaching up under the jaw so a moving head never opens a gap */
    var nid = S.poly(tp([[-4.4, 33], [4.4, 33], [5.2, 45], [7.8, 52], [-7.8, 52], [-5.2, 45]]), { m: 'skin', l: warm ? 0.36 : cold ? 0.44 : 0.3, g: gCyl(ox, 33 + dy, ox, 52 + dy, 5, 0.35, [L[0], 0, L[2]]), ol: 1 });
    S.shape(function (x, y) { var yy = y - dy; return yy > 41.5 && yy < 45.5 && Math.abs(x - ox) < 5.2; }, [ox - 7, 40 + dy, ox + 7, 47 + dy], { m: 'skin', l: warm ? 0.2 : cold ? 0.62 : 0.16, on: 'any' });
    /* two cords of the neck, faint: she is not young */
    S.line(tp([[-2.6, 45.4], [-3.4, 49.6]]), 0.45, { m: 'skin', l: warm ? 0.16 : 0.24, on: nid });
    S.line(tp([[2.6, 45.4], [3.4, 49.6]]), 0.45, { m: 'skin', l: 0.3, on: nid });
    /* dark wool dress: broad, sloping shoulders and a scooped neckline */
    var yS = 57.6 + sl * 1.4 - sq;
    var tid = S.poly(tp([[-9.9, 46.6], ['q', 0, 52.8, 9.9, 46.6], [14.6, 48.6 - sq + lnR * 0.5], [20.2 + sq, 51.6 - sq + sl + lnR], [24.4 + sq, yS + lnR], [25 + sq, 115], [-25 - sq, 115], [-24.4 - sq, yS + lnL], [-20.2 - sq, 51.6 - sq + sl + lnL], [-14.6, 48.6 - sq + lnL * 0.5]]), { m: 'wool', l: 0.42, g: torsoLight, tex: woolTex, ol: 1 });
    /* the linen shift showing at the neckline */
    S.line(tp([[-9.2, 47.5], ['q', 0, 53.9, 9.2, 47.5]]), 0.55, { m: 'linen', l: warm ? 0.52 : 0.42, g: torsoLight, on: tid });
    /* upper arms in rolled wool sleeves */
    [[-1, -23.4, 60.8 + lnL, -27, 86.4], [1, 23.4, 61.4 + lnR, 27, 86.4]].forEach(function (a) {
      var s = a[0], x0 = ox + a[1] + s * sq, y0 = a[2] + dy - sq + sl * 1.2, x1 = ox + a[3] + s * sq * 0.5, y1 = a[4] + dy;
      S.ell(x0, y0, 8.2, 7.4, { m: 'wool', l: 0.46, g: gAdd(gSph(x0, y0, 8.2, 7.4, 0.45, L), torsoLight), tex: woolTex, ol: 1 });
      S.seg(x0, y0 + 1, x1, y1, 7.4, 6.5, { m: 'wool', l: 0.46, g: gAdd(gCyl(x0, y0, x1, y1, 7.2, 0.5, L), torsoLight), tex: woolTex, ol: 1 });
      S.seg(x1 - s * 0.5, y1 - 6.5, x1 + s * 0.3, y1 - 0.5, 7.3, 7, { m: 'wool', l: 0.62, g: gAdd(gCyl(x1, y1 - 7, x1, y1, 7.2, 0.45, L), torsoLight), ol: 1,
        tex: function (x, y) { return ((y - dy) % 3 === 0) ? -0.3 : 0; } });
    });
    /* the stained leather apron: bib, straps, pocket, the ties at her waist */
    var stain = function (x, y) {
      var n = vnoise(x * 0.23, y * 0.2, 11) * 0.7 + vnoise(x * 0.6, y * 0.5, 12) * 0.3;
      return (n > 0.62 ? -0.26 : n < 0.22 ? 0.08 : 0) + (hash(x, y, 13) > 0.95 ? 0.16 : 0);
    };
    var bust = function (x, y) { var yy = y - dy; return yy < 62 ? 0.03 : yy < 70 ? 0.1 * (1 - Math.abs(yy - 66) / 4) : yy < 73.5 ? -0.07 : 0; };
    S.poly(tp([[-12.6, 57.2], [12.6, 57.2], [13.4, 70], [12.4, 86.5], [16.6, 98], [19.4, 115], [-19.4, 115], [-16.6, 98], [-12.4, 86.5], [-13.4, 70]]),
      { m: 'wood', l: 0.46, g: gAdd(torsoLight, gSph(ox, 82 + dy, 21, 40, 0.3, L), fallen ? null : bust), tex: stain, bev: 0.12, ol: 1 });
    S.rect(ox - 12.6, 57.2 + dy, 25.2, 1.2, { m: 'wood', l: 0.74, g: torsoLight, on: 'any' });
    S.seg(ox - 11.2, 57.8 + dy, ox - 6.2, 46.8 + dy, 1.3, 1.3, { m: 'wood', l: 0.42, g: torsoLight, ol: 1 });
    S.seg(ox + 11.2, 57.8 + dy, ox + 6.2, 46.8 + dy, 1.3, 1.3, { m: 'wood', l: 0.64, g: torsoLight, ol: 1 });
    S.rect(ox - 9, 95 + dy, 18, 11, { m: 'wood', l: 0.38, g: torsoLight, tex: stain, ol: 1 });
    for (var sx = -8; sx < 8; sx += 2) S.px(Math.floor(ox + sx), 96 + dy, 'wood:4');
    S.seg(ox - 25, 88.4 + dy, ox - 12.4, 88.8 + dy, 0.7, 0.7, { m: 'wood', l: 0.3, ol: 1 });
    S.seg(ox + 12.4, 88.8 + dy, ox + 25, 88.4 + dy, 0.7, 0.7, { m: 'wood', l: 0.62, ol: 1 });
  }

  /* the damp bar towel over her right shoulder (the viewer's left); sw swings its hanging end */
  function drawTowel(S, sw) {
    var ox = MCX, tl = function (x) { return 0.3 * (x - ox) / 34; };
    S.layer = 0;
    S.poly(path(offP([[-24.8, 53], ['q', -20.2, 46.6, -11.2, 47.6], [-9.2, 50.8], ['q', -15.6, 49.8, -19.6, 54.4]], ox, 0)), { m: 'linen', l: 0.66, g: gSph(ox - 17, 49, 9, 5, 0.45, LFIRE), ol: 1,
      tex: function (x, y) { return ((x + y) % 4 === 0) ? -0.16 : 0; } });
    var tw = offP([[-20.4, 52.4], [-11.6, 50.6], [-10, 59], [-10.8, 67], [-10 + sw, 75.6], [-10.6 + sw, 81.4], [-19.2 + sw, 82.4], [-18.8 + sw * 0.5, 73], [-20.6, 63]], ox, 0);
    S.poly(tw, { m: 'linen', l: 0.5, g: gAdd(gLin(ox - 21, 62, ox - 10, 62, -0.28), tl), bev: 0.1, ol: 1,
      tex: function (x, y) {
        var f = (x - sw * (y - 60) / 22 + (y > 64 ? 1 : 0) + 8) % 4;
        var damp = vnoise(x * 0.4, y * 0.2, 21) > 0.58 ? -0.24 : 0;
        return (f < 1 ? -0.24 : f > 2.6 ? 0.08 : 0) + damp + ((y === 78 || y === 80) ? -0.34 : 0);
      } });
    for (var fx = -19; fx < -10; fx += 2) S.px(Math.floor(ox + fx + sw), 83, 'linen:1');
  }

  /* the head, drawn in its own frame (u across, v down; v 0 = the eye line, v 15 = the chin) so it can roll */
  function drawHead(S, st, eyes, ti, look) {
    var F = MFIG[st], cold = st === 'matron' || st === 'hurt', fallen = st === 'fallen', warm = st === 'barkeep';
    var L = fallen ? LDIM : cold ? LUP : LFIRE;
    var ang = F.ha + (M_TILT[ti] || 0), co = Math.cos(ang), si = Math.sin(ang);
    var PX = MCX + F.hx, PY = 44 + F.hy;               /* the pivot, under the chin */
    function T(u, v) { var w = v - 15; return [PX + u * co - w * si, PY + u * si + w * co]; }
    function U(x, y) { var ax = x - PX, ay = y - PY; return [ax * co + ay * si, -ax * si + ay * co + 15]; }
    function TT(list) { return path(list).map(function (p) { return T(p[0], p[1]); }); }
    function box(u0, v0, u1, v1) { return bbox([T(u0, v0), T(u1, v0), T(u0, v1), T(u1, v1)], 1.5); }
    var K = T(9.8, -15.2), kx = K[0], ky = K[1];
    var hairTex = function (x, y) {       /* strands converge on the knot; some catch the light gray, some stay brown */
      var a = Math.atan2(y + 0.5 - ky, x + 0.5 - kx), s = Math.floor(a * 11 + hash(x >> 2, y >> 2, 9) * 0.8), g = hash(s + 40, 1, 17);
      return (s % 3 === 0 ? -0.22 : s % 3 === 1 ? 0.07 : 0) + (g > 0.72 ? 0.16 : g < 0.2 ? -0.1 : 0) + (hash(x, y, 7) > 0.92 ? 0.12 : 0);
    };
    var knotTex = function (x, y) { var a = Math.atan2(y - ky, x - kx) * 2 + Math.sqrt((x - kx) * (x - kx) + (y - ky) * (y - ky)) * 0.9; return Math.floor(a) % 2 ? -0.18 : 0.06; };
    var C = T(0, -6.4), hairL = gSph(C[0], C[1] + 2, 12.5, 11, 0.5, L);
    S.layer = 0;
    /* the loose knot high at the back, a second lobe, strands escaping it */
    S.rell(kx, ky, 6.4, 5.4, ang, { m: 'hairM', l: 0.5, g: gSph(kx - 1, ky - 1, 6.4, 5.4, 0.5, L), ol: 1, tex: knotTex });
    var K2 = T(13.8, -11.8);
    S.rell(K2[0], K2[1], 3.4, 3, ang, { m: 'hairM', l: 0.42, g: gSph(K2[0] - 1, K2[1] - 1, 3.4, 3, 0.4, L), ol: 1, tex: knotTex });
    S.line(TT([[13.6, -19.4], [15.6, -20.4]]), 0.5, { m: 'hairM', l: 0.62, noOl: 1 });
    S.line(TT([[16, -9.6], [17.4, -7.6], [17.2, -5.6], [16.4, -4.6]]), 0.5, { m: 'hairM', l: 0.5, noOl: 1 });
    /* the crown, pulled back */
    S.rell(C[0], C[1], 10.9, 9.9, ang, { m: 'hairM', l: 0.46, g: hairL, tex: hairTex });
    /* her face: an oval with a soft jaw, the cheeks carrying down into a slight jowl, a rounded chin */
    var fc = T(0, 2.5);
    var faceId = S.poly(TT([[-7.3, -8.6], [7.3, -8.6], ['q', 9.1, -4.2, 8.7, 1.2], ['q', 8.5, 7, 7, 10.2], ['q', 5.8, 12.4, 3.6, 13.8], ['q', 2, 15.2, 0, 15.2], ['q', -2, 15.2, -3.6, 13.8], ['q', -5.8, 12.4, -7, 10.2], ['q', -8.5, 7, -8.7, 1.2], ['q', -9.1, -4.2, -7.3, -8.6]]),
      { m: 'skin', l: warm ? 0.49 : cold ? 0.5 : 0.56, g: gSph(fc[0], fc[1], 9.4, 11.5, warm ? 0.42 : cold ? 0.55 : 0.28, fallen ? LFIRE : L), ol: 1, olc: 'lt1', dz: 3.4 });
    /* hair in front: a centre parting, the hairline, the side sweeps over her ears */
    var hl = function (u) { return -8.4 + 0.036 * u * u + (Math.abs(u) < 1.1 ? 0.8 : 0); };
    S.shape(function (x, y) { var q = U(x, y), a = q[0] / 11, b = (q[1] + 6.4) / 10; return a * a + b * b <= 1 && q[1] < hl(q[0]); }, box(-12, -17, 12, 0), { m: 'hairM', l: 0.5, g: hairL, tex: hairTex });
    S.poly(TT([[-11.2, -7.6], [-6.8, -7.6], [-7.5, -4.4], [-8.1, -0.4], [-8.5, 3.6], [-9.2, 6.6], [-10.6, 7.2], [-11.6, 4.6], [-11.9, 0.4], [-11.8, -3.8]]), { m: 'hairM', l: 0.4, g: hairL, tex: hairTex, ol: 0 });
    S.poly(TT([[11.2, -7.6], [6.8, -7.6], [7.5, -4.4], [8.1, -0.4], [8.5, 3.6], [9.2, 6.6], [10.6, 7.2], [11.6, 4.6], [11.9, 0.4], [11.8, -3.8]]), { m: 'hairM', l: 0.46, g: hairL, tex: hairTex, ol: 0 });
    /* shade under the hairline so the hair sits on the head */
    S.shape(function (x, y) { var q = U(x, y), h = hl(q[0]); return q[1] >= h && q[1] < h + 1.1; }, box(-9, -10, 9, -4), { m: 'skin', l: warm ? 0.24 : 0.2, g: gSph(fc[0], fc[1], 9.4, 11.5, 0.3, L), on: faceId });
    /* loose strands falling round her face */
    S.line(TT([[-6.4, -8], [-7.8, -3], [-8.8, 2.6], [-9.6, 7.6], [-9.2, 10.6], [-8.2, 12]]), 0.5, { m: 'hairM', l: 0.72, noOl: 1 });
    S.line(TT([[6.2, -8.2], [8.2, -2.6], [9.4, 3.6], [10, 8.2], [9.4, 10.8]]), 0.5, { m: 'hairM', l: 0.8, noOl: 1 });
    S.line(TT([[-1.4, -8.4], [-3.2, -6.4], [-4.2, -3.8]]), 0.5, { m: 'hairM', l: 0.66, noOl: 1 });
    matronFace(S, st, eyes, T, look);
    return faceId;
  }

  /* face features as tiny stamps, one char per pixel ('.' leaves the shaded skin), placed on the rolled head */
  var FCOL = { o: 'ol', k: 'k1', x: 'lt1', q: 'lt2', l: 'lt3', p: 'lt4', b: 'skin:1', c: 'skin:2', d: 'skin:3', e: 'skin:4',
    h: 'hairM:1', g: 'hairM:2', j: 'hairM:3', w: 'pa2', v: 'mu', r: 'e1', n: 'm3', m: 'm5',
    /* menace: the red of her eyes (R and its hot core Y are where the glow blooms from), and her teeth */
    Z: 'b1', Q: 'b2', R: 'b3', Y: 'e4', T: 'pa2', U: 'hb' };
  var FACE = {
    barkeep: {
      /* heavy lids that droop at the outer corners, dull whites, bags and a crease under them; open, halfway, shut */
      eyeL: [[['.jgg..', 'j.ccc.', '.oooo.', 'kvkov.', '.bbbbc', 'b.ddc.'], ['.jgg..', 'j.ccc.', '.oooo.', 'koooo.', '.bbbbc', 'b.ddc.'], ['.jgg..', 'j.ccc.', '.cccc.', 'koooo.', '.bbbbc', 'b.ddc.']], 3, 3],
      eyeR: [[['..ggj.', '.ddd.j', '.oooo.', '.vokwk', 'dcccc.', '.ddc.c'], ['..ggj.', '.ddd.j', '.oooo.', '.ooook', 'dcccc.', '.ddc.c'], ['..ggj.', '.ddd.j', '.dddd.', '.ooook', 'dcccc.', '.ddc.c']], 2, 3],
      nose: [['...d.', '.c.d.', '.c...', '.bde.', 'bkcqc'], 2, 4],
      /* a flat mouth, one corner lower than the other; a darker upper lip, a hint of the lower */
      mouth: [['..xqrqx..', '.x.plp.c.', 'b..bbc...'], 4, 0],
      dots: [[-3.4, 6.2, 'b'], [-4.2, 7.3, 'b'], [-4.7, 8.5, 'b'], [3.4, 6.2, 'c'], [4.2, 7.3, 'c'], [4.7, 8.5, 'c'],
        [-6.4, 4.6, 'b'], [-6, 5.8, 'b'], [6.2, 4.6, 'c'], [5.8, 5.8, 'c'], [-7.6, 1.6, 'b'], [7.4, 1.6, 'c'], [0.4, 13.2, 'd'], [-0.6, 13.2, 'd']]
    },
    matron: {
      /* the lids lift, the brows come down: eyes up from under them with a cold glint */
      eyeL: [[['gg....', '..hh..', 'koooh.', '.nmon.', '..bbb.']], 3, 3],
      eyeR: [[['....gg', '..hh..', '.hoook', '.nomn.', '.bbb..']], 2, 3],
      nose: [['...d.', '.c.d.', '.c...', '.bde.', 'bkcqc'], 2, 4],
      mouth: [['.xkkkkkx.', '.b.....b.', '...ccc...'], 4, 0],
      dots: [[-3.8, 6.6, 'b'], [-4.6, 7.8, 'b'], [3.8, 6.6, 'c'], [4.6, 7.8, 'c'], [-3.9, 10.9, 'b'], [3.9, 10.9, 'c']]
    },
    hurt: {
      eyeL: [[['...gg.', '.gg...', '.oo...', '...oo.', '.oo...']], 3, 3],
      eyeR: [[['.gg...', '...gg.', '...oo.', '.oo...', '...oo.']], 2, 3],
      nose: [['.....', '.c...', '.c...', '.bde.', 'bkcqc'], 2, 4],
      mouth: [['.koooook.', '.owwwwwo.', '..ooooo..'], 4, 0],
      dots: [[-3.8, 6.6, 'b'], [3.8, 6.6, 'c']]
    },
    fallen: {
      eyeL: [[['.gggg.', '......', '..ccc.', '.kooo.', '......']], 3, 3],
      eyeR: [[['.gggg.', '......', '.ccc..', '.oook.', '......']], 2, 3],
      nose: [['.....', '.c...', '.c...', '.b.d.', '.kcq.'], 2, 4],
      mouth: [['...bkkkb.', '....kkk..', '.........'], 4, 0],
      dots: []
    }
  };
  /* her looks, laid over a state's face (barkeep and matron only; eyes open, head still):
     s (stare) - she has noticed you: the brows come down a row, the lids narrow, a faint cold glint;
     m (menace) - she means to finish you: brows hard down, eyes burning red, a cruel smile */
  var FACE_LOOK = {
    s: {
      barkeep: {
        eyeL: [[['......', '.jggg.', '.oooo.', 'kvonv.', '.qqbbc', 'b.ddc.']], 3, 3],
        eyeR: [[['......', '.gggj.', '.oooo.', '.vnowk', 'dcqqq.', '.ddc.c']], 2, 3]
      },
      matron: {
        eyeL: [[['......', 'gghh..', 'koooh.', '.omon.', '..qqb.']], 3, 3],
        eyeR: [[['......', '..hhgg', '.hoook', '.nomo.', '.bqq..']], 2, 3]
      }
    },
    m: {
      barkeep: {
        eyeL: [[['x.....', '.xkk..', '..kooo', '.QRYR.', '..qbb.', 'b.ddc.']], 3, 3],
        eyeR: [[['.....x', '..kkx.', 'oook..', '.RYRQ.', '.bbq..', '.ddc.c']], 2, 3],
        mouth: [['o.......o', '.oxkkkxo.', '..oTTTo..', '...ooo...', '...bcc...'], 4, 1]
      },
      matron: {
        eyeL: [[['x.....', '.xkk..', '..kooo', '.QRYR.', '..qbb.']], 3, 3],
        eyeR: [[['.....x', '..kkx.', 'oook..', '.RYRQ.', '.bbq..']], 2, 3],
        mouth: [['o.......o', '.okkkkko.', '..oTTTo..', '...ooo...', '...bcc...'], 4, 1]
      }
    },
    /* z (stunned) - something she did not see coming has struck her spell away: both brows fly up (the left higher,
       so she looks lost, not only shocked), the lids lift off wide whites and small pupils that do not quite meet,
       and her jaw drops into a round, open mouth. Two frames: wide open, and the lids half down for a dazed blink */
    z: {
      barkeep: {
        eyeL: [[['.hhh..', 'h...g.', '......', '.xoox.', 'xwwoqx', 'xvwwvx', '.xqqx.'], ['.hhh..', 'h...g.', '......', '......', 'xoooox', 'xvwovx', '.xqqx.']], 3, 5],
        eyeR: [[['......', '.hhhg.', 'h....g', '.xoox.', 'xqwwwx', 'xvwowx', '.xqqx.'], ['......', '.hhhg.', 'h....g', '......', 'xoooox', 'xvwowx', '.xqqx.']], 2, 5],
        mouth: [['.........', '...xqx...', '..xooox..', '..xoooq..', '...xox...', '...bcc...'], 4, 1]
      },
      matron: {
        eyeL: [[['.kkk..', 'k...h.', '......', '.xoox.', 'xmmonx', 'xnmmnx', '.xqqx.'], ['.kkk..', 'k...h.', '......', '......', 'xoooox', 'xnmonx', '.xqqx.']], 3, 5],
        eyeR: [[['......', '.kkkh.', 'k....h', '.xoox.', 'xnmmmx', 'xnmomx', '.xqqx.'], ['......', '.kkkh.', 'k....h', '......', 'xoooox', 'xnmomx', '.xqqx.']], 2, 5],
        mouth: [['.........', '...xkx...', '..xooox..', '..xooox..', '...xox...', '...bcc...'], 4, 1]
      }
    }
  };
  var STUN_ALT = 'The Iron Matron thrown off balance, her eyes wide and lost, her mouth fallen open, a purple and pale teal shimmer running over her';
  function matronFace(S, st, eyes, T, look) {
    var f = FACE[st] || FACE.barkeep, lk = look && FACE_LOOK[look] && FACE_LOOK[look][st];
    if (lk) f = { eyeL: lk.eyeL || f.eyeL, eyeR: lk.eyeR || f.eyeR, nose: f.nose, mouth: lk.mouth || f.mouth, dots: lk.dots || f.dots };
    S.glow = null;
    function at(u, v) { var p = T(u, v); return [Math.floor(p[0]), Math.floor(p[1])]; }
    function stamp(p, rows, o) {
      for (var j = 0; j < rows.length; j++) for (var i = 0; i < rows[j].length; i++) {
        var ch = rows[j].charAt(i); if (ch === '.') continue;
        var x = p[0] - o[0] + i, y = p[1] - o[1] + j;
        S.px(x, y, FCOL[ch] || ch);
        if (ch === 'R' || ch === 'Y') (S.glow = S.glow || []).push([x, y]);
      }
    }
    var e = Math.min(eyes || 0, f.eyeL[0].length - 1);
    stamp(at(-4.2, 0.3), f.eyeL[0][e], [f.eyeL[1], f.eyeL[2]]);
    stamp(at(4.2, 0.3), f.eyeR[0][e], [f.eyeR[1], f.eyeR[2]]);
    stamp(at(0, 5.3), f.nose[0], [f.nose[1], f.nose[2]]);
    stamp(at(0, 9.3), f.mouth[0], [f.mouth[1], f.mouth[2]]);
    f.dots.forEach(function (d) { var p = at(d[0], d[1]); S.px(p[0], p[1], FCOL[d[2]]); });
  }

  /* a big hand laid flat on the bar, palm down, fingers together toward the viewer; side -1 = her right hand (the
     viewer's left). Seen from above: the back of the hand and its knuckle line toward us, nails on the fingertips at
     the bar's edge, and the thumb lying along the inner side, pointing in toward her other hand. In local px, + is
     inward (toward her middle); the middle finger is the longest, the little finger (outermost) the shortest.
     grip (fallen) keeps her slumped hands, fingers hooked over the edge, exactly as they were */
  function handFlat(S, x, y, side, L, skinL, grip) {
    if (grip) return handGrip(S, x, y, side, L, skinL);
    var m = function (px, py) { return [x - side * px, y + py]; };
    var back = [m(-5.2, -2.4), m(1.2, -2.8), m(4.4, -0.8), m(5.8, 1.8), m(5.6, 4.9), m(2.6, 5.4), m(-2.6, 5.5), m(-6.4, 5.1), m(-7.2, 2.6), m(-6.6, -0.4)];
    S.poly(back, { m: 'skin', l: skinL + 0.06, g: gSph(x, y + 2, 7, 6, 0.5, L), ol: 1 });
    var fx0 = -6.8, fw = 2.95, tipY = y + 10.8;
    var tipOf = function (k, t) { return tipY - Math.abs(k - 2) * 0.8 - (k === 0 ? 1 : 0) - (t - 0.5) * (t - 0.5) * 3.2; };
    var fingers = function (px, py) {
      var u = -side * (px - x), k = Math.floor((u - fx0) / fw); if (k < 0 || k > 3) return false;
      return py > y + 4.2 && py < tipOf(k, (u - fx0) / fw - k);
    };
    var fid = S.shape(fingers, [x - 9, y + 3, x + 9, y + 15], { m: 'skin', l: skinL + 0.1, g: gSph(x, y + 8, 7, 4, 0.45, L) });
    S.shape(function (px, py) { var u = -side * (px - x), t = ((u - fx0) / fw) % 1; return (t < 0.24 || t > 0.97) && u > fx0 + 1 && u < fx0 + fw * 4 - 0.5; }, [x - 9, y + 5.8, x + 9, y + 15], { m: 'skin', l: 0.06, on: fid });
    /* where the fingers leave the hand: a soft band of shade, not a line */
    S.shape(function (px, py) { return py > y + 5.2 && py < y + 6.2; }, [x - 9, y + 5, x + 9, y + 7], { m: 'skin', l: skinL - 0.14, g: gSph(x, y + 8, 7, 4, 0.45, L), on: fid });
    /* the thumb, flat on the wood beside the first finger, its tip pointing in and forward */
    var t0 = m(4.6, 0.4), t1 = m(7.8, 5.8);
    S.seg(t0[0], t0[1], t1[0], t1[1], 1.75, 1.4, { m: 'skin', l: skinL + 0.04, g: gCyl(t0[0], t0[1], t1[0], t1[1], 1.6, 0.45, L), ol: 1 });
    var tn = m(7.6, 5.2); S.px(Math.floor(tn[0]), Math.floor(tn[1]), 'skin:4');
    for (var k = 0; k < 4; k++) {
      /* the knuckle line: a lit knuckle over each finger, a dip of shade between them; a nail at each tip */
      var q = m(fx0 + fw * (k + 0.5), 4.6); S.px(Math.floor(q[0]), Math.floor(q[1]), 'skin:4');
      if (k < 3) { var d = m(fx0 + fw * (k + 1), 4.8); S.px(Math.floor(d[0]), Math.floor(d[1]), 'skin:1'); }
      var nl = m(fx0 + fw * (k + 0.5), tipOf(k, 0.5) - y - 1.6); S.px(Math.floor(nl[0]), Math.floor(nl[1]), 'skin:4');
    }
  }
  /* her slumped hands in the fallen state (fingers hooked over the edge): unchanged */
  function handGrip(S, x, y, side, L, skinL) {
    var grip = true;
    var m = function (px, py) { return [x + side * px, y + py]; };
    var back = [m(-6, -2), m(2.4, -3.2), m(6.4, 0.2), m(6.8, 5.4), m(3.2, 7), m(-5.6, 7), m(-7.4, 3.6)];
    S.poly(back, { m: 'skin', l: skinL + 0.06, g: gSph(x, y + 2, 7, 6, 0.5, L), ol: 1 });
    var fx0 = -6.6, fw = 3.05, tipY = y + (grip ? 12.6 : 10.8);
    var fingers = function (px, py) {
      var u = side * (px - x), k = Math.floor((u - fx0) / fw); if (k < 0 || k > 3) return false;
      var t = (u - fx0) / fw - k, tip = tipY - Math.abs(k - 1.3) * 0.9 - (t - 0.5) * (t - 0.5) * 3.2;
      return py > y + 4.2 && py < tip;
    };
    var fid = S.shape(fingers, [x - 9, y + 3, x + 9, y + 15], { m: 'skin', l: skinL + 0.1, g: gSph(x + side * 0.5, y + 8, 7, 4, 0.45, L), ol: 1 });
    S.shape(function (px, py) { var u = side * (px - x), t = ((u - fx0) / fw) % 1; return (t < 0.26 || t > 0.97) && u > fx0 + 1 && u < fx0 + fw * 4 - 0.5; }, [x - 9, y + 5.8, x + 9, y + 15], { m: 'skin', l: 0.06, on: fid });
    var t0 = m(5.4, 0.8), t1 = m(8.4, 5.2);
    S.seg(t0[0], t0[1], t1[0], t1[1], 1.7, 1.4, { m: 'skin', l: skinL + 0.04, g: gCyl(t0[0], t0[1], t1[0], t1[1], 1.6, 0.45, L), ol: 1 });
    for (var k = 0; k < 4; k++) { var q = m(fx0 + fw * (k + 0.5), 5.2); S.px(Math.round(q[0]), Math.round(q[1]), 'skin:4'); }
  }

  /* her forearms, hands and the cup, over the bar; they stay planted while she breathes */
  function drawFront(S, st) {
    var F = MFIG[st], cold = st === 'matron' || st === 'hurt', fallen = st === 'fallen', warm = st === 'barkeep';
    var L = fallen ? LDIM : cold ? LUP : LFIRE, dx = F.dx, dy = F.dy, sq = F.sq, ox = MCX + dx;
    var skinL = warm ? 0.52 : cold ? 0.5 : 0.4;
    S.layer = 0;
    /* her right forearm (the viewer's left), braced, palm down on the bar; the old scar across its back */
    var e1 = [ox - 28.4 - sq, 85 + dy], w1 = fallen ? [ox - 20.6, 114.2] : [ox - 19.6 - sq * 0.5, 107.6 + dy];
    var fa1 = S.seg(e1[0], e1[1], w1[0], w1[1], 5.3, 4.2, { m: 'skin', l: skinL, g: gCyl(e1[0], e1[1], w1[0], w1[1], 5, 0.5, L), ol: 1 });
    var sc0 = lerp(e1[0], w1[0], 0.56), sc1 = lerp(e1[1], w1[1], 0.56);
    S.line([[sc0 - 3.6, sc1 + 1.8], [sc0 - 0.8, sc1 + 0.1], [sc0 + 2.2, sc1 - 1.1], [sc0 + 3.8, sc1 - 2.2]], 0.5, { m: 'skin', l: 0.99, on: fa1 });
    S.line([[sc0 - 3.2, sc1 + 2.8], [sc0 + 0, sc1 + 1.2], [sc0 + 3.4, sc1 - 0.9]], 0.45, { m: 'skin', l: 0.1, on: fa1 });
    handFlat(S, ox - 18 - sq * 0.5, fallen ? 114.4 : 107 + dy, -1, L, skinL, fallen);
    /* her left forearm (the viewer's right) */
    var e2 = [ox + 28.4 + sq, 85 + dy], w2 = warm ? [ox + 21.2, 103.8 + dy] : fallen ? [ox + 22, 114.2] : [ox + 23.6 + sq, 106.8 + dy];
    S.seg(e2[0], e2[1], w2[0], w2[1], 5.3, 4.2, { m: 'skin', l: skinL, g: gCyl(e2[0], e2[1], w2[0], w2[1], 5, 0.5, L), ol: 1 });
    /* the cup: wooden staves, two dark hoops, something dark in it */
    var cx = ox + 9.6 + (fallen ? -1 : 0), cy0 = 101.6, cy1 = 116.2;
    if (warm) S.poly(offP([[14.2, 99], [21.8, 98.4], [25.2, 102.6], [23.4, 109.6], [17.6, 111.4], [14.4, 106.4]], ox, dy), { m: 'skin', l: skinL + 0.06, g: gSph(ox + 19, 104 + dy, 6, 6, 0.5, L), ol: 1 });
    S.poly([[cx - 5.4, cy0], [cx + 5.4, cy0], [cx + 5, cy1], [cx - 5, cy1]], { m: 'pine', l: 0.4, g: gCyl(cx, cy0, cx, cy1, 5.4, 0.55, [L[0], 0, 0.8]), ol: 1,
      tex: function (x, y) { return ((x + 1) % 3 === 0 ? -0.14 : 0) + (hash(x, y, 41) > 0.88 ? -0.12 : 0); } });
    [cy0 + 3.2, cy1 - 3.4].forEach(function (hy2) { S.rect(cx - 5.6, hy2, 11.2, 1.2, { m: 'wood', l: 0.12, on: 'any', tex: function (x) { return x === Math.round(cx + 2) ? 0.3 : 0; } }); });
    S.ell(cx, cy0, 5.6, 1.9, { m: 'pine', l: 0.74, g: gSph(cx, cy0, 5.6, 1.9, 0.4, L), ol: 1 });
    S.ell(cx, cy0 + 0.2, 4.3, 1.2, { fx: 'ol', on: 'any' });
    S.px(Math.round(cx + (warm ? 1 : -1)), cy0, warm ? 'e1' : cold ? 'm2' : 'k1');
    if (warm) {
      /* fingers curled round the cup's side, pushing it forward: one block, creases in skin tone */
      S.poly(offP([[13.6, 102.6], [17.2, 102.4], [18, 106], [17.6, 110.4], [15.6, 112.2], [13.4, 111.6]], ox, dy), { m: 'skin', l: skinL + 0.12, g: gSph(ox + 16, 106 + dy, 3, 5, 0.4, L), ol: 1, olc: 'lt1', dz: 3.4 });
      var fx1 = Math.floor(ox + 14);
      S.pxl([fx1, 105 + dy, fx1 + 1, 105 + dy, fx1 + 2, 105 + dy, fx1, 108 + dy, fx1 + 1, 108 + dy, fx1 + 2, 108 + dy], 'skin:1');
      S.pxl([fx1, 103 + dy, fx1, 106 + dy, fx1, 109 + dy], 'skin:4');
    } else {
      handFlat(S, ox + 24 + sq, fallen ? 114.4 : 107 + dy, 1, L, skinL, fallen);
    }
  }

  /* ---- the long bar, its props and the candle; lvl 0-3 is how brightly the flame is burning ---- */
  var CNX = 150, CNY = 113;                            /* the candle's dish on the counter; the wick at (150, 102) */
  var M_FLAME_RECT = [CNX - 36, 91, CNX + 37, 122];    /* everything the flame touches: flame, glow, candle, pool of light */
  function mVign(x) { var d = Math.abs(x + 0.5 - MCX) / MCX; return -0.34 * d * d * d; }
  function drawBar(S, st, lvl) {
    var warm = st === 'barkeep', fallen = st === 'fallen', lit = !fallen;
    var gain = warm ? [0.3, 0.38, 0.46, 0.55][lvl] : 0, rad = [0.86, 0.93, 1, 1.07][lvl];
    function pool(x, y) { var a = (x - CNX) / (28 * rad), b = (y - CNY - 0.5) / (5.6 * rad), d = a * a + b * b; return d >= 1 ? 0 : (1 - d) * (1 - d); }
    function side(x) { return warm ? 0.03 * (x - MCX) / MCX : 0; }
    function ringStain(x, y) { var a = (x - 66) / 4.2, b = (y - 114) / 1.6, d = a * a + b * b; return d <= 1 && d >= 0.42 ? -0.1 : 0; }
    var grain = function (x, y) { var g = vnoise(x * 0.07, y * 0.9, 31); return (g > 0.64 ? -0.18 : g < 0.26 ? 0.08 : 0) + ((x * 7 + y * 13) % 37 === 0 ? -0.16 : 0) + ringStain(x, y); };
    var K = 2 / 3;                                      /* 'wood' light expressed on the longer 'woodLit' ramp */
    S.layer = 0;
    /* the counter top: two long planks seen from above, the far edge in shadow, the candle's pool on them */
    S.rect(0, 109, MW, 8, { m: 'woodLit', l: 0.44 * K, g: function (x, y) { return K * ((y - 109) * 0.024 + mVign(x) + side(x)) + gain * pool(x, y); }, tex: function (x, y) { return K * grain(x, y); } });
    S.rect(0, 109, MW, 1, { m: 'wood', l: 0.16, g: mVign, on: 'any' });
    S.rect(0, 112, MW, 1, { m: 'woodLit', l: 0.3 * K, g: function (x, y) { return K * (mVign(x) + side(x)) + gain * 0.8 * pool(x, y); }, on: 'any' });
    [9, 57, 131, 177].forEach(function (jx) { S.px(jx, 110, 'wood:1'); S.px(jx, 111, 'wood:1'); });
    [33, 84, 163].forEach(function (jx) { for (var jy = 113; jy < 117; jy++) S.px(jx, jy, 'wood:1'); });
    /* the front lip: a rounded edge catching the light, a shadow under it */
    S.rect(0, 117, MW, 2, { m: 'woodLit', l: 0.86 * K, g: function (x, y) { return K * ((y === 118 ? -0.24 : 0) + mVign(x) * 1.2 + side(x)) + gain * 0.5 * pool(x, 114); } });
    S.rect(0, 119, MW, 1, { m: 'wood', l: 0.08 });
    /* the front: upright planks, iron nails, darker toward the floor */
    S.rect(0, 120, MW, 24, { m: 'wood', l: 0.34, g: function (x, y) { return -0.24 * (y - 120) / 24 + mVign(x) * 1.3 + side(x); },
      tex: function (x, y) { var p = (x + 9) % 16, k = (x + 9) >> 4; return (p === 0 ? -0.42 : p === 1 ? 0.14 : 0) + (hash(k, 1, 37) - 0.5) * 0.14 + (hash(x, y >> 3, 33) > 0.85 ? -0.09 : 0) + (hash(x, 0, 35) > 0.8 ? 0.06 : 0); } });
    for (var nx = 7; nx < MW; nx += 16) { S.px(nx + 3, 123, 'iron:1'); S.px(nx + 3, 139, 'iron:1'); }
    /* an empty pewter tankard left at the far end */
    var tx = 40, tb = 113;
    var tk = S.poly([[tx - 4.4, tb - 11], [tx + 4.4, tb - 11], [tx + 4, tb], [tx - 4, tb]], { m: 'iron', l: 0.4, g: gAdd(gCyl(tx, tb - 11, tx, tb, 4.4, 0.6, [0.55, 0, 0.83]), function (x) { return mVign(x) * 0.6; }), ol: 1 });
    S.rect(tx - 4.4, tb - 8, 8.8, 1, { m: 'iron', l: 0.2, on: [tk] });
    S.rect(tx - 4.2, tb - 3, 8.4, 1, { m: 'iron', l: 0.2, on: [tk] });
    S.ell(tx, tb - 11, 4.6, 1.4, { m: 'iron', l: 0.74, ol: 1 });
    S.ell(tx, tb - 10.9, 3.3, 0.7, { fx: 'ol', on: 'any' });
    S.line([[tx - 4.2, tb - 9.2], [tx - 7.4, tb - 8.4], [tx - 7.6, tb - 4.2], [tx - 4.2, tb - 3.4]], 0.75, { m: 'iron', l: 0.34, ol: 1 });
    /* a few coins someone left */
    [[55, 114], [58, 115], [57, 113]].forEach(function (p, i) {
      S.pxl([p[0], p[1], p[0] + 1, p[1], p[0] + 2, p[1]], i === 2 ? 'gold:4' : 'gold:3');
      S.pxl([p[0], p[1] + 1, p[0] + 1, p[1] + 1, p[0] + 2, p[1] + 1], 'gold:1');
    });
    S.pxl([54, 115, 54, 116, 57, 116, 61, 116], 'wood:0');
    /* the candle: a guttering stub in an iron dish, wax run down its side */
    S.ell(CNX, CNY + 0.4, 6.2, 1.9, { m: 'iron', l: 0.36, g: gSph(CNX + 1.5, CNY - 0.6, 6.2, 1.9, 0.4, LFIRE), ol: 1 });
    S.ell(CNX, CNY - 0.2, 4.4, 1.1, { m: 'wax', l: lit ? 0.46 : 0.28, on: 'any' });
    S.rect(148, 103, 5, 10, { m: 'wax', l: lit ? 0.5 + gain * 0.4 : 0.34, g: function (x, y) { return (lit ? 0.34 : 0.1) * (107 - y) / 6 + 0.12 * (x - 150) / 2; }, ol: 1 });
    S.pxl([147, 106, 147, 107, 147, 108, 153, 104, 153, 105], lit ? 'wax:2' : 'wax:1');
    S.pxl([146, 112, 154, 112], 'wax:1');
    S.px(150, 102, lit ? 'k1' : 'ol');
    if (fallen) S.pxl([150, 100, 151, 98, 150, 96, 149, 95, 149, 93], 'hb');     /* snuffed: a thread of smoke */
  }

  /* one plank of bottles on the back wall, dim, the middle hidden behind her */
  function drawShelf(S) {
    var sy = 79;
    function fade(x) { var d = Math.abs(x + 0.5 - MCX) / MCX; return -0.26 * d * d; }
    S.layer = 0;
    S.rect(2, sy, MW - 4, 3, { m: 'wood', l: 0.26, g: function (x, y) { return fade(x) + (y === sy ? 0.14 : y === sy + 2 ? -0.14 : 0); } });
    [24, 168].forEach(function (bx) { S.rect(bx, sy + 3, 2, 3, { m: 'wood', l: 0.14 }); });
    function bottle(x, w, h, m, l) {
      var hw = w / 2, sh = sy - h * 0.6, top = sy - h;
      S.poly(path([[x - hw, sy], [x - hw, sh + 1.5], ['q', x - hw, sh - 1, x - 1, sh - 2], [x - 1, top + 1.5], [x + 1, top + 1.5], [x + 1, sh - 2], ['q', x + hw, sh - 1, x + hw, sh + 1.5], [x + hw, sy]]),
        { m: m, l: l, g: gAdd(gCyl(x, top, x, sy, hw, 0.45, [0.7, 0, 0.7]), fade), ol: 1 });
      S.rect(x - 1, top, 2, 1.5, { m: 'wood', l: 0.44 });
    }
    bottle(14, 5.6, 15, 'glass', 0.42);
    S.ell(29, sy - 4.6, 5, 4.8, { m: 'wood', l: 0.4, g: gAdd(gSph(29, sy - 5.6, 5, 4.8, 0.4, LFIRE), fade), ol: 1 });   /* a clay jug */
    S.rect(27.5, sy - 11.4, 3, 2.6, { m: 'wood', l: 0.3, ol: 1 });
    S.line([[33.4, sy - 8.6], [35.6, sy - 7], [34.4, sy - 3.4]], 0.6, { m: 'wood', l: 0.3, ol: 1 });
    bottle(42, 4.4, 10, 'glass', 0.36);
    bottle(156, 4.6, 11, 'glass', 0.4);
    bottle(167, 5.6, 16, 'glass', 0.44);
  }

  /* the flame: small hand-drawn frames, bottom row at the wick. a dim base, b outer, c mid, d bright, e core */
  var M_FLAMES = [
    ['..b..', '..c..', '.bdb.', '.cec.', '.cec.', '.bdb.', '..a..'],
    ['...b.', '..cb.', '..dc.', '.cec.', '.cec.', '.bdb.', '..a..'],
    ['.b...', '.bc..', '.cdb.', '.cec.', '.bdb.', '..a..'],
    ['..b..', '.bcb.', '.cec.', '.bdb.', '..a..'],
    ['..b..', '..b..', '..c..', '.bdb.', '.cec.', '.cec.', '.bdb.', '..a..'],
    ['..b..', '.bcb.', '..a..'],
    ['.bab.', '..a..']
  ];
  var M_FCOL = null;
  function mFlameCols(cold) {
    if (!M_FCOL) M_FCOL = [
      { a: P32[PI.e2], b: P32[PI.e3], c: P32[PI.g4], d: P32[PI.g5], e: P32[PI.hw], h: P32[PI.e1] },
      { a: P32[PI.m2], b: P32[PI.m3], c: P32[PI.m4], d: P32[PI.m5], e: P32[PI.hw], h: P32[PI.m1] }];
    return M_FCOL[cold ? 1 : 0];
  }
  function mFlameDraw(out, st, f, clip) {
    var rows = M_FLAMES[f], cols = mFlameCols(st !== 'barkeep'), n = rows.length, x0 = CNX - 2, yb = 101, i, j, yy;
    function on(ii, jj) { return jj >= 0 && jj < n && ii >= 0 && ii < 5 && rows[jj].charAt(ii) !== '.'; }
    for (j = -1; j <= n; j++) {
      yy = yb - (n - 1 - j); if (yy < clip[1] || yy >= clip[3]) continue;
      for (i = -1; i <= 5; i++) {
        var x = x0 + i, k = yy * MW + x;
        if (on(i, j)) { out[k] = cols[rows[j].charAt(i)]; continue; }
        /* a thin dithered glow hugging the flame, only over empty air */
        if (!out[k] && (on(i - 1, j) || on(i + 1, j) || on(i, j + 1)) && bay(x, yy) < 0.55) out[k] = cols.h;
      }
    }
  }
  /* which flame frame burns at time t (ms): a flicker, now and then a gutter; gust bends it after a blow */
  function mFlame(st, t, gust) {
    if (st === 'fallen') return { f: -1, lvl: 0 };
    if (t < 0) return { f: 0, lvl: 2 };
    var q = Math.floor(t / 80), cold = st !== 'barkeep';
    if (gust > 0.25) return { f: q % 2 ? 2 : 5, lvl: 1 };
    var gw = Math.floor(t / 9000), ge = t - gw * 9000 - (1500 + hash(gw, 1, 73) * 6000);
    if (!cold && hash(gw, 2, 74) > 0.35 && ge >= 0 && ge < 720) return { f: ge < 160 || ge > 560 ? 5 : 6, lvl: 0 };
    var n = vnoise(q * (cold ? 0.14 : 0.3), 0.5, 71) * 0.7 + hash(q, 3, 72) * (cold ? 0.12 : 0.3);
    if (n > 0.74) return { f: 4, lvl: 3 };
    if (n > 0.58) return { f: hash(q, 5, 75) > 0.5 ? 0 : 1, lvl: 3 };
    if (n > 0.42) return { f: hash(q, 6, 76) > 0.5 ? 2 : 0, lvl: 2 };
    if (n > 0.3) return { f: 3, lvl: 2 };
    return { f: 3, lvl: 1 };
  }

  /* ---- her idle life: a slow breath, blinks every 3 to 6 s, now and then a sigh or a loll of the head ---- */
  var M_REST = { by: 0, hy: 0, eyes: 0, ti: 0, sway: 0, key: '0,0,0,0,0' };
  function mBlink(e) { return e < 0 ? 0 : e < 70 ? 1 : e < 190 ? 2 : e < 300 ? 1 : 0; }
  function mPose(st, t) {
    var by = 0, hy = 0, eyes = 0, ti = 0, sway = 0;
    if (st === 'barkeep') {
      var ph = (t % 4800) / 4800;
      by = ph > 0.1 && ph < 0.52 ? -1 : 0;
      var bw = Math.floor(t / 4500), be = t - bw * 4500 - hash(bw, 3, 61) * 1500;
      eyes = mBlink(be);
      if (hash(bw, 5, 62) < 0.2) eyes = Math.max(eyes, mBlink(be - 340));
      /* one event in every 13 s window, sigh and loll in turn (the first a sigh), now and then skipped */
      var sw = Math.floor(t / 13000), se = t - sw * 13000 - (2200 + hash(sw, 9, 64) * 6500);
      if ((sw < 2 || hash(sw, 7, 63) > 0.2) && se >= 0) {
        if (sw % 2 === 0) {                              /* a long, weary sigh: up, hold, down and sag */
          if (se < 2800) {
            eyes = se < 900 ? 0 : se < 1300 ? 1 : se < 2250 ? 2 : 1;
            by = se < 300 ? -1 : se < 1250 ? -2 : se < 1400 ? -1 : se < 1550 ? 0 : se < 2300 ? 1 : 0;
            hy = se >= 1550 && se < 2300 ? 1 : 0;
          }
        } else if (se < 3600) {                          /* her head lolls to one side, a slow blink, then back */
          ti = se < 140 || se > 3460 ? 1 : 2;
          if (se > 1500 && se < 1820) eyes = se < 1570 || se > 1740 ? 1 : 2;
        }
      }
      var s = Math.sin(t / 5600 * 2 * Math.PI + 0.9);
      sway = s > 0.5 ? 1 : s < -0.5 ? -1 : 0;
    } else if (st === 'matron') {
      var ph2 = (t % 4200) / 4200;
      by = ph2 > 0.12 && ph2 < 0.5 ? -1 : 0;
    }
    return { by: by, hy: hy, eyes: eyes, ti: ti, sway: sway, key: by + ',' + hy + ',' + eyes + ',' + ti + ',' + sway };
  }

  /* flashes keep the shading: each colour maps by its brightness onto a short ramp */
  function lumLUT(keys, gamma) {
    var ramp = keys.map(pc);
    return PRGB.map(function (c) { var l = (c[0] * 0.3 + c[1] * 0.59 + c[2] * 0.11) / 255; return ramp[clamp(Math.floor(Math.pow(l, gamma || 0.75) * ramp.length * 1.2), 0, ramp.length - 1)]; });
  }
  var LUT_HURT = null, LUT_WHITE = null;
  function lutHurt() { return LUT_HURT || (LUT_HURT = lumLUT(['ol', 'b1', 'b2', 'e1', 'e2', 'e3', 'e4', 'g5'], 0.8)); }
  function lutWhite() { return LUT_WHITE || (LUT_WHITE = lumLUT(['i1', 'i2', 'i3', 'i4', 'm5', 'hw'], 0.6)); }

  function rimPass(S, a, layer, dx, dy, c, y0, y1) {
    var W = S.w, H = S.h, ci = pc(c), hit = [];
    for (var y = y0 || 0; y < (y1 || H); y++) for (var x = 0; x < W; x++) {
      var i = y * W + x; if (a[i] <= 0) continue;
      var nx = x + dx, ny = y + dy, out = nx < 0 || ny < 0 || nx >= W || ny >= H;
      if (!out) { var n = ny * W + nx; out = a[n] < 0 || (a[n] === 0 && (S.rp[n] < 0 || S.ly[n] !== layer)); }
      if (out) hit.push(i);
    }
    hit.forEach(function (i) { a[i] = ci; });
  }
  function mFlash(a) { var lw = lutWhite(); for (var i = 0; i < a.length; i++) if (a[i] > 0 && bay(i % MW, (i / MW) | 0) < 0.3) a[i] = lw[a[i]]; }
  /* the outline under her chin is a soft brown, not black */
  function mSoftJaw(S, a, fid) {
    for (var i = MW; i < a.length; i++) if (a[i] === 0 && S.rp[i] < 0 && S.id[i - MW] === fid && S.rp[i - MW] >= 0) a[i] = PI.lt1;
  }
  /* dither a part away toward the canvas's left and right edges */
  function mFadeEnds(a, w0, w1) {
    for (var y = 0; y < MH; y++) for (var x = 0; x < MW; x++) {
      var e = Math.min(x, MW - 1 - x); if (e >= w1) continue;
      if (bay(x, y) > (e - w0) / (w1 - w0)) a[y * MW + x] = -1;
    }
  }
  /* she sags sideways: shear the figure behind the bar, then fracture and erode it */
  function mFallenFx(a0) {
    var sh = new Int16Array(a0.length); sh.fill(-1);
    for (var y = 0; y < MH; y++) { var off = y < 110 ? Math.round((110 - y) * 0.075) : 0; for (var x = 0; x < MW; x++) { var tx = x + off; if (tx < MW && a0[y * MW + x] >= 0) sh[y * MW + tx] = a0[y * MW + x]; } }
    var cracks = [[[78, 52], [85, 62], [82, 72], [90, 84], [87, 96]], [[114, 56], [109, 67], [114, 79], [109, 90]], [[100, 34], [103, 40], [101, 46]]];
    cracks.forEach(function (ln) {
      for (var s = 1; s < ln.length; s++) {
        var ax = ln[s - 1][0], ay = ln[s - 1][1], bx = ln[s][0], by = ln[s][1], n = Math.max(Math.abs(bx - ax), Math.abs(by - ay));
        for (var j = 0; j <= n; j++) { var qx = Math.round(ax + (bx - ax) * j / n), qy = Math.round(ay + (by - ay) * j / n), qi = qy * MW + qx; if (sh[qi] > 0) sh[qi] = j % 3 === 0 ? PI.hw : PI.m4; if (sh[qi + 1] > 0 && j % 2) sh[qi + 1] = PI.m2; }
      }
    });
    for (var yy = 0; yy < MH; yy++) for (var xx = 0; xx < MW; xx++) {
      var i = yy * MW + xx; if (sh[i] < 0) continue;
      var edge = (xx > 0 && sh[i - 1] < 0) || (xx < MW - 1 && sh[i + 1] < 0) || (yy > 0 && sh[i - MW] < 0);
      if (edge && sh[i] === 0 && hash(xx, yy, 91) > 0.6) sh[i] = hash(xx, yy, 5) > 0.5 ? PI.m2 : -1;
    }
    return sh;
  }

  /* ---- parts: rendered once, kept as Uint32 pixels cropped to their bounding box (0 = clear) ---- */
  var mCache = {};
  function mMemo(key, fn) { var v = mCache[key]; if (v === undefined) { v = fn(); mCache[key] = v; } return v; }
  function mPartOf(a) {
    var x0 = MW, y0 = MH, x1 = 0, y1 = 0, x, y, v;
    for (y = 0; y < MH; y++) for (x = 0; x < MW; x++) if (a[y * MW + x] >= 0) { if (x < x0) x0 = x; if (x >= x1) x1 = x + 1; if (y < y0) y0 = y; if (y >= y1) y1 = y + 1; }
    if (x1 <= x0) return { px: new Uint32Array(0), bb: [0, 0, 0, 0], w: 0 };
    var w = x1 - x0, u = new Uint32Array(w * (y1 - y0));
    for (y = y0; y < y1; y++) for (x = x0; x < x1; x++) { v = a[y * MW + x]; if (v >= 0) u[(y - y0) * w + x - x0] = P32[v]; }
    return { px: u, bb: [x0, y0, x1, y1], w: w };
  }
  function mRim(st) {
    if (st === 'barkeep') return { dx: 1, dy: 0, c: 'e2', y0: 46, y1: 114 };
    if (st === 'matron' || st === 'hurt') return { dx: -1, dy: 0, c: 'm4', c2: 'm3', y0: 8, y1: 114 };
    return null;
  }
  function mCold(st) { return st === 'matron' || st === 'hurt'; }
  function mBody(st) {
    return mMemo('b' + st, function () {
      var S = new Spr(MW, MH); drawBody(S, st);
      var fid = st === 'fallen' ? drawHead(S, st, 0, 0) : -1;
      var a = S.render({ map: MAPS[st], rim: mRim(st), dz: 2.8 })[0];
      if (mCold(st)) rimPass(S, a, 0, 0, -1, 'm3', 6, 60);
      if (st === 'hurt') mFlash(a);
      if (st === 'fallen') { mSoftJaw(S, a, fid); a = mFallenFx(a); }
      return mPartOf(a);
    });
  }
  function mHead(st, eyes, ti, look) {
    if (st === 'fallen') return null;
    look = look && FACE_LOOK[look] && FACE_LOOK[look][st] ? look : '';
    return mMemo('h' + st + eyes + ti + look, function () {
      var S = new Spr(MW, MH), fid = drawHead(S, st, eyes, ti, look);
      var a = S.render({ map: MAPS[st], rim: mCold(st) ? mRim(st) : null, dz: 2.8 })[0];
      if (mCold(st)) rimPass(S, a, 0, 0, -1, 'm3', 6, 60);
      mSoftJaw(S, a, fid);
      if (st === 'hurt') mFlash(a);
      var part = mPartOf(a);
      part.glow = S.glow;                               /* where her eyes burn, for the bloom (menace only) */
      return part;
    });
  }
  /* the menace bloom: a soft 1-2 px red light round each burning eye pixel, dithered, strength lvl 1..3.
     Each colour it touches is lit additively toward red and snapped back to the palette. */
  var LUT_GLOW = null;
  function lutGlow() {
    if (LUT_GLOW) return LUT_GLOW;
    var set = ['ol', 'k1', 'lt1', 'lt2', 'lt3', 'lt4', 'sk5', 'b1', 'b2', 'b3', 'e1', 'e2', 'e3', 'e4'].map(pc);
    function near(r, g, b) {
      var best = set[0], bd = 1e12;
      for (var j = 0; j < set.length; j++) { var p = PRGB[set[j]], dr = r - p[0], dg = g - p[1], db = b - p[2], d = dr * dr * 3 + dg * dg * 4 + db * db * 2; if (d < bd) { bd = d; best = set[j]; } }
      return best;
    }
    LUT_GLOW = [0.34, 0.62].map(function (a) {
      return PRGB.map(function (c) { return near(c[0] * (1 - a * 0.35) + 225 * a, c[1] * (1 - a * 0.6) + 30 * a, c[2] * (1 - a * 0.6) + 30 * a); });
    });
    return LUT_GLOW;
  }
  function mEyeGlow(out, pts, ox, oy, lvl, clip) {
    if (!pts || !pts.length || lvl <= 0) return;
    var L = lutGlow(), idx = p32Index(), R = lvl >= 3 ? 2.6 : lvl >= 2 ? 2.2 : 1.6, dens = lvl >= 3 ? 1.25 : lvl >= 2 ? 1.05 : 0.8;
    var core = {}, i, x, y;
    for (i = 0; i < pts.length; i++) core[(pts[i][0] + ox) + ',' + (pts[i][1] + oy)] = 1;
    var x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
    pts.forEach(function (p) { x0 = Math.min(x0, p[0] + ox); x1 = Math.max(x1, p[0] + ox); y0 = Math.min(y0, p[1] + oy); y1 = Math.max(y1, p[1] + oy); });
    x0 = Math.max(clip[0], Math.floor(x0 - R)); x1 = Math.min(clip[2] - 1, Math.ceil(x1 + R)); y0 = Math.max(clip[1], Math.floor(y0 - R)); y1 = Math.min(clip[3] - 1, Math.ceil(y1 + R));
    for (y = y0; y <= y1; y++) for (x = x0; x <= x1; x++) {
      if (core[x + ',' + y]) continue;
      var d = 99;
      for (i = 0; i < pts.length; i++) { var dx = x - pts[i][0] - ox, dy = (y - pts[i][1] - oy) * 1.3, q = Math.sqrt(dx * dx + dy * dy); if (q < d) d = q; }
      if (d > R) continue;
      var t = 1 - (d - 0.6) / R, j = y * MW + x, v = out[j];
      if (!v || t * dens <= bay(x, y)) continue;
      var pi = idx[v]; if (pi == null) continue;
      out[j] = P32[L[d <= 1.2 ? 1 : 0][pi]];
    }
  }
  function mTowel(sw) {
    return mMemo('t' + sw, function () { var S = new Spr(MW, MH); drawTowel(S, sw); return mPartOf(S.render({ dz: 2.8 })[0]); });
  }
  function mFront(st) {
    return mMemo('f' + st, function () {
      var S = new Spr(MW, MH); drawFront(S, st);
      var a = S.render({ map: MAPS[st], dz: 2.8 })[0];
      if (st === 'barkeep') rimPass(S, a, 0, 1, 0, 'e2');
      if (mCold(st)) rimPass(S, a, 0, -1, 0, 'm3');
      if (st === 'hurt') mFlash(a);
      return mPartOf(a);
    });
  }
  function mBar(st, lvl) {
    if (st === 'hurt') st = 'matron';
    if (st !== 'barkeep') lvl = 0;                     /* only the warm flame throws a pool of light */
    return mMemo('r' + st + lvl, function () {
      var S = new Spr(MW, MH); drawBar(S, st, lvl);
      var a = S.render({ map: MAPS[st], dz: 2.4 })[0];
      mFadeEnds(a, 0, 7);
      return mPartOf(a);
    });
  }
  function mShelf(st) {
    if (st === 'hurt') st = 'matron';
    return mMemo('s' + st, function () {
      var S = new Spr(MW, MH); drawShelf(S);
      var a = S.render({ map: MAPS[st], dz: 2.4 })[0];
      mFadeEnds(a, 2, 16);
      return mPartOf(a);
    });
  }

  /* composite one frame (or just the clip rect) into a 192x144 buffer; the figure can be nudged (hits) or flashed */
  var M_FULL = [0, 0, MW, MH], P32_INDEX = null;
  function mBlit(dst, part, ox, oy, lut, clip) {
    if (!part || !part.w) return;
    if (lut && !P32_INDEX) { P32_INDEX = {}; for (var q = 0; q < P32.length; q++) P32_INDEX[P32[q]] = q; }
    var src = part.px, bb = part.bb, pw = part.w;
    var x0 = Math.max(bb[0] + ox, clip[0]), x1 = Math.min(bb[2] + ox, clip[2]), y0 = Math.max(bb[1] + oy, clip[1]), y1 = Math.min(bb[3] + oy, clip[3]);
    for (var y = y0; y < y1; y++) {
      var sr = (y - oy - bb[1]) * pw - ox - bb[0], dr = y * MW;
      for (var x = x0; x < x1; x++) {
        var v = src[sr + x]; if (!v) continue;
        if (lut) { var pi = P32_INDEX[v]; if (pi != null) v = P32[lut[pi]]; }
        dst[dr + x] = v;
      }
    }
  }
  /* ex (optional, from mountMatron): a moved front part, and what to draw under and over it */
  function mCompose(out, st, pose, fl, ox, oy, lut, clip, ex) {
    clip = clip || M_FULL;
    for (var y = clip[1]; y < clip[3]; y++) out.fill(0, y * MW + clip[0], y * MW + clip[2]);
    mBlit(out, mShelf(st), 0, 0, null, clip);
    mBlit(out, mBody(st), ox, oy + pose.by, lut, clip);
    if (st === 'barkeep') mBlit(out, mTowel(pose.sway), ox, oy + pose.by, lut, clip);
    var head = mHead(st, pose.eyes, pose.ti, pose.look);
    mBlit(out, head, ox, oy + pose.by + pose.hy, lut, clip);
    if (pose.glow && head) mEyeGlow(out, head.glow, ox, oy + pose.by + pose.hy, pose.glow, clip);
    mBlit(out, mBar(st, fl.lvl), 0, 0, null, clip);
    if (ex && ex.under) ex.under(out, clip, ox, oy, pose);
    mBlit(out, ex && ex.front ? ex.front : mFront(st), ox, oy, lut, clip);
    if (fl.f >= 0) mFlameDraw(out, st, fl.f, clip);
    if (ex && ex.over) ex.over(out, clip, ox, oy, pose);
    return out;
  }
  var MATRON_ALT = {
    barkeep: 'The Iron Matron behind her bar, a bar towel over her shoulder, sliding a wooden cup forward',
    matron: 'The Iron Matron, the warmth gone from her face, eyes catching a cold glint',
    hurt: 'The Iron Matron reeling from a blow',
    fallen: 'The Iron Matron slumping over the bar as the illusion fails'
  };
  function matronCanvas(state, opts) {
    opts = opts || {};
    var st = M_STATES.indexOf(state) >= 0 ? state : 'barkeep', k = Math.max(1, Math.round(+opts.scale || 3));
    var pose = opts.frame ? { by: -1, hy: 0, eyes: 0, ti: 0, sway: 1, key: 'f' } : M_REST;
    var buf = mCompose(new Uint32Array(MW * MH), st, pose, mFlame(st, -1, 0), 0, 0, null, null);
    var c = upscale(u32ToCanvas(buf, MW, MH), k);
    c.style.width = (MW * k) + 'px'; pixelated(c);
    c.setAttribute('role', 'img'); c.setAttribute('aria-label', MATRON_ALT[st]);
    return c;
  }
  /* idle-time render queue: this state's pose variants first, then the other states */
  function mWarmJobs(first) {
    var jobs = [];
    [first].concat(M_STATES.filter(function (s) { return s !== first; })).forEach(function (st) {
      jobs.push(function () { mShelf(st); mBody(st); mFront(st); mHead(st, 0, 0); });
      if (st === 'barkeep') {
        [0, 1, 2, 3].forEach(function (l) { jobs.push(function () { mBar(st, l); }); });
        [-1, 0, 1].forEach(function (s) { jobs.push(function () { mTowel(s); }); });
        [[1, 0], [2, 0], [0, 2], [1, 2], [2, 2], [0, 1]].forEach(function (v) { jobs.push(function () { mHead(st, v[0], v[1]); }); });
      } else jobs.push(function () { mBar(st, 0); });
    });
    return jobs;
  }

  /* =====================================================================
     2b. HER HANDS MOVE - the cup slid across the bar, and a spell cast between her palms.
     drawFrontX is drawFront with the cup and each forearm free to move. At the rest pose it lays
     down the same pixels as drawFront, and the rest pose itself still uses mFront, so the still
     art never changes. Poses are quantised (the slide and the lift in eighths, four loop phases)
     and each is rendered once and cached like the other parts. The spell, its motes and light,
     the burst and the dust are drawn straight into the frame after the parts.
     ===================================================================== */
  var CUP_HELD = [9.6, 116.2, 1], CUP_SERVED = [3.4, 118.8, 1.14];     /* from her centre line: x, base y, scale */
  var CAST_PH = [[0, 0, 0], [1, -1, 0.07], [1, 0, 0.07], [0, 1, 0]];  /* loop phases: hands out, a rolling bob, fingers flex */
  function cupAt(st, which) { var c = which === 'served' ? CUP_SERVED : CUP_HELD; return [MCX + MFIG[st].dx + c[0] - (st === 'fallen' ? 1 : 0), c[1], c[2]]; }
  function orbAt(st) { var F = MFIG[st]; return [MCX + F.dx, 76 + F.dy]; }
  function restL(st) { var F = MFIG[st]; return [MCX + F.dx + 24 + F.sq, 107 + F.dy]; }        /* her left hand, laid flat */
  /* the old scar across the back of her right forearm, turned by r as the forearm turns */
  function mScar(S, id, E, W, r) {
    var s0 = lerp(E[0], W[0], 0.56), s1 = lerp(E[1], W[1], 0.56), c = Math.cos(r), s = Math.sin(r);
    var R = function (p) { return [s0 + p[0] * c - p[1] * s, s1 + p[0] * s + p[1] * c]; };
    S.line([[-3.6, 1.8], [-0.8, 0.1], [2.2, -1.1], [3.8, -2.2]].map(R), 0.5, { m: 'skin', l: 0.99, on: id });
    S.line([[-3.2, 2.8], [0, 1.2], [3.4, -0.9]].map(R), 0.45, { m: 'skin', l: 0.1, on: id });
  }
  /* a band along a circular arc in a hand's own frame (u along the hand, v across it toward the spell): its centre
     line starts at P0 heading p0 (0 = along u, + turns toward +v) and bends k radians per pixel over len; off shifts it
     across, w0..w1 is its half-width, and its far end is rounded */
  function arcBand(P0, p0, k, len, off, w0, w1) {
    var Lh = [], Rh = [], n = 12, i;
    function at(s) {
      var p = p0 + k * s;
      if (Math.abs(k) < 1e-4) return [P0[0] + Math.cos(p0) * s, P0[1] + Math.sin(p0) * s, p];
      return [P0[0] + (Math.sin(p) - Math.sin(p0)) / k, P0[1] + (Math.cos(p0) - Math.cos(p)) / k, p];
    }
    for (i = 0; i <= n; i++) {
      var q = at(len * i / n), w = lerp(w0, w1, i / n), nx = -Math.sin(q[2]), ny = Math.cos(q[2]);
      Lh.push([q[0] + nx * (off - w), q[1] + ny * (off - w)]); Rh.unshift([q[0] + nx * (off + w), q[1] + ny * (off + w)]);
    }
    var e = at(len), dx = Math.cos(e[2]), dy = Math.sin(e[2]), cap = [];
    for (i = 1; i < 6; i++) {
      var an = -Math.PI / 2 + Math.PI * i / 6;
      cap.push([e[0] - dy * off + (dx * Math.cos(an) - dy * Math.sin(an)) * w1, e[1] + dx * off + (dy * Math.cos(an) + dx * Math.sin(an)) * w1]);
    }
    return Lh.concat(cap, Rh);
  }
  /* drop lone pixels and one-pixel spurs from a drawn region, so a small shape keeps a clean edge */
  function despur(S, bb) {
    var W = S.w, x0 = Math.max(1, Math.floor(bb[0])), y0 = Math.max(1, Math.floor(bb[1])), x1 = Math.min(W - 1, Math.ceil(bb[2])), y1 = Math.min(S.h - 1, Math.ceil(bb[3]));
    for (var pass = 0; pass < 2; pass++) for (var y = y0; y < y1; y++) for (var x = x0; x < x1; x++) {
      var i = y * W + x; if (S.rp[i] < 0) continue;
      var nb = (S.rp[i - 1] >= 0) + (S.rp[i + 1] >= 0) + (S.rp[i - W] >= 0) + (S.rp[i + W] >= 0);
      if (nb <= 1) { S.rp[i] = -1; S.fx[i] = -1; S.no[i] = 0; }
    }
  }
  /* her hand cupped round the spell, seen from the side at the size she is on a phone: a solid shape with the palm
     toward the spell, the fingers as three rows curled over it (dark creases between, no gaps), one thick thumb curled
     under it, a knuckle at the back. Drawn in its own frame from the wrist W: u along the hand, v across it toward the
     spell. a is the way the hand points; curl 0..1 closes it (0 open and straight, as it lifts off the bar);
     side -1 = her right hand */
  var CUP_O = [6.8, 11.6];                               /* the spell's centre in the hand's frame when it is closed */
  function castHand(S, W, a, side, curl, L, skinL, fs) {
    var A = [Math.cos(a), Math.sin(a)], N = [side * A[1], -side * A[0]], F = fs == null ? 1 : fs;
    function H(p) { return [W[0] + A[0] * p[0] * F + N[0] * p[1], W[1] + A[1] * p[0] * F + N[1] * p[1]]; }
    function poly(list) { return list.map(H); }
    var C0 = H([4.4, 0]), lit = gSph(C0[0], C0[1], 9, 9, 0.22, L);
    var bx = gLin(H([0, -3])[0], H([0, -3])[1], H([0, 3])[0], H([0, 3])[1], 0.16);   /* the back of the hand lighter than the palm */
    var o = function (lv, extra) { var r = { m: 'skin', l: skinL + lv, g: gAdd(lit, bx), dz: 7, ol: 1, olc: 'lt1' }; if (extra) for (var k in extra) r[k] = extra[k]; return r; };
    var cu = clamp(curl, 0, 1.2), R0 = Math.sqrt((8.2 - CUP_O[0]) * (8.2 - CUP_O[0]) + CUP_O[1] * CUP_O[1]);
    /* the thumb: from the heel of the palm, out and round under the spell */
    var TR = [1.6, 2.2], rT = Math.sqrt((TR[0] - CUP_O[0]) * (TR[0] - CUP_O[0]) + (TR[1] - CUP_O[1]) * (TR[1] - CUP_O[1]));
    S.poly(poly(arcBand(TR, lerp(0.55, 2.62, Math.min(1, cu)), -cu / rT, 6.6, 0, 1.6, 1.2)), o(0.02));
    /* the palm and the back of the hand, the knuckle standing up at the back */
    S.poly(poly(path([[-0.8, -2.6], [3.2, -2.9], [6.8, -3], ['q', 8.9, -3.2, 9.1, -1.2], [9.1, 2], ['q', 8.2, 2.9, 6, 2.8], [2.8, 2.8], ['q', 0.4, 2.8, -0.8, 1.6]])), o(0.06));
    var kn = H([7.6, -2.3]); S.ell(kn[0], kn[1], 1.4, 1.2, o(0.14, { ol: 0 }));
    /* three rows of finger over the top, the outermost first; the middle row reaches furthest */
    [[-1.9, 9.4, 0.12], [0, 10, 0.06], [1.9, 8.8, 0]].forEach(function (r) {
      S.poly(poly(arcBand([8.2, 0], 0.12 * Math.min(1, cu), cu / R0, r[1], r[0], 1.05, 0.95)), o(r[2]));
    });
    var bb = bbox(poly([[-2, -5], [12, -5], [12, 16], [-2, 16], [6, 18]]), 3);
    despur(S, bb);
    S.px(Math.floor(kn[0]), Math.floor(kn[1]), 'skin:4');
  }
  /* a forearm lifted off the bar and the hand turned up to hold the spell; u 0..1 is the lift (the hand swings up from
     hanging toward the bar, through pointing in, to cupped round the spell), k the loop phase; side -1 = her right hand */
  var CAST_LEAN = 0.18;
  function castArm(S, st, side, E, W0, u, k, L, skinL) {
    var q = orbAt(st), ph = CAST_PH[k] || CAST_PH[0], e = u * u * (3 - 2 * u);
    var W1 = [q[0] + side * (12.6 + ph[0] * 0.6), q[1] + 4.6 + ph[1] * side * 0.5];
    var W = [lerp(W0[0], W1[0], u), lerp(W0[1], W1[1], u) - Math.sin(Math.PI * u) * 3];
    var fa = S.seg(E[0], E[1], W[0], W[1], 5.3, lerp(4.2, 3.2, e), { m: 'skin', l: skinL, g: gCyl(E[0], E[1], W[0], W[1], 5, 0.5, L), ol: 1 });
    if (side < 0) mScar(S, fa, E, W, Math.atan2(W[1] - E[1], W[0] - E[0]) - Math.atan2(W0[1] - E[1], W0[0] - E[0]));
    var a1 = -Math.PI / 2 - side * CAST_LEAN; if (side > 0) a1 += 2 * Math.PI;
    /* on the way up the hand points partly at you, so it is drawn shorter until it turns in round the spell */
    castHand(S, W, lerp(Math.PI / 2, a1, e), side, lerp(0.12, 1, e) + ph[2] * 0.8, L, skinL, lerp(0.62, 1, e * e));
  }
  /* drawFront with free parts: sp.cup = [x, base, scale]; sp.l / sp.r = her left / right arm as
     ['rest'], ['grip'] (riding the cup), ['flat', x, y] or ['cast', u, k] */
  function drawFrontX(S, st, sp) {
    var F = MFIG[st], cold = st === 'matron' || st === 'hurt', fallen = st === 'fallen', warm = st === 'barkeep';
    var L = fallen ? LDIM : cold ? LUP : LFIRE, dy = F.dy, sq = F.sq, ox = MCX + F.dx;
    var skinL = warm ? 0.52 : cold ? 0.5 : 0.4;
    var ref = cupAt(st, 'held'), cx = sp.cup[0], cb = sp.cup[1], cs = sp.cup[2], moved = cx !== ref[0] || cb !== ref[1] || cs !== 1;
    var cy0 = moved ? cb - 14.6 * cs : 101.6, ix = Math.round(cx - ref[0]), iy = Math.round(cb - ref[1]);
    function gp(p) { return moved ? [cx + cs * (ox + p[0] - ref[0]), cb + cs * (p[1] + dy - ref[1])] : [ox + p[0], p[1] + dy]; }
    S.layer = 0;
    var e1 = [ox - 28.4 - sq, 85 + dy], w1 = fallen ? [ox - 20.6, 114.2] : [ox - 19.6 - sq * 0.5, 107.6 + dy];
    if (sp.r[0] === 'cast') castArm(S, st, -1, e1, w1, sp.r[1], sp.r[2], L, skinL);
    else {
      var fa1 = S.seg(e1[0], e1[1], w1[0], w1[1], 5.3, 4.2, { m: 'skin', l: skinL, g: gCyl(e1[0], e1[1], w1[0], w1[1], 5, 0.5, L), ol: 1 });
      mScar(S, fa1, e1, w1, 0);
      handFlat(S, ox - 18 - sq * 0.5, fallen ? 114.4 : 107 + dy, -1, L, skinL, fallen);
    }
    var e2 = [ox + 28.4 + sq, 85 + dy], lm = sp.l[0], grip = lm === 'grip' || (lm === 'rest' && warm);
    if (lm === 'cast') castArm(S, st, 1, e2, [ox + 23.6 + sq, 106.8 + dy], sp.l[1], sp.l[2], L, skinL);
    else {
      var w2 = grip ? gp([21.2, 103.8]) : lm === 'flat' ? [sp.l[1] - 0.4, sp.l[2] - 0.2] : fallen ? [ox + 22, 114.2] : [ox + 23.6 + sq, 106.8 + dy];
      S.seg(e2[0], e2[1], w2[0], w2[1], 5.3, 4.2, { m: 'skin', l: skinL, g: gCyl(e2[0], e2[1], w2[0], w2[1], 5, 0.5, L), ol: 1 });
    }
    if (grip) { var hc = gp([19, 104]); S.poly([[14.2, 99], [21.8, 98.4], [25.2, 102.6], [23.4, 109.6], [17.6, 111.4], [14.4, 106.4]].map(gp), { m: 'skin', l: skinL + 0.06, g: gSph(hc[0], hc[1], 6 * cs, 6 * cs, 0.5, L), ol: 1 }); }
    S.poly([[cx - 5.4 * cs, cy0], [cx + 5.4 * cs, cy0], [cx + 5 * cs, cb], [cx - 5 * cs, cb]], { m: 'pine', l: 0.4, g: gCyl(cx, cy0, cx, cb, 5.4 * cs, 0.55, [L[0], 0, 0.8]), ol: 1,
      tex: function (x, y) { x -= ix; y -= iy; return ((x + 1) % 3 === 0 ? -0.14 : 0) + (hash(x, y, 41) > 0.88 ? -0.12 : 0); } });
    [cy0 + 3.2 * cs, cb - 3.4 * cs].forEach(function (hy2) { S.rect(cx - 5.6 * cs, hy2, 11.2 * cs, 1.2, { m: 'wood', l: 0.12, on: 'any', tex: function (x) { return x === Math.round(cx + 2 * cs) ? 0.3 : 0; } }); });
    S.ell(cx, cy0, 5.6 * cs, 1.9 * cs, { m: 'pine', l: 0.74, g: gSph(cx, cy0, 5.6 * cs, 1.9 * cs, 0.4, L), ol: 1 });
    S.ell(cx, cy0 + 0.2, 4.3 * cs, 1.2 * cs, { fx: 'ol', on: 'any' });
    S.px(Math.round(cx + (warm ? 1 : -1)), cy0, warm ? 'e1' : cold ? 'm2' : 'k1');
    if (grip) {
      var fc = gp([16, 106]), fx1 = Math.floor(ox + 14);
      S.poly([[13.6, 102.6], [17.2, 102.4], [18, 106], [17.6, 110.4], [15.6, 112.2], [13.4, 111.6]].map(gp), { m: 'skin', l: skinL + 0.12, g: gSph(fc[0], fc[1], 3 * cs, 5 * cs, 0.4, L), ol: 1, olc: 'lt1', dz: 3.4 });
      var cr = function (c, r, col) { var q = moved ? gp([fx1 - ox + c + 0.5, r + 0.5]) : [fx1 + c, r + dy]; S.px(Math.floor(q[0]), Math.floor(q[1]), col); };
      [0, 1, 2].forEach(function (c) { cr(c, 105, 'skin:1'); cr(c, 108, 'skin:1'); });
      [103, 106, 109].forEach(function (r) { cr(0, r, 'skin:4'); });
    } else if (lm === 'flat') handFlat(S, sp.l[1], sp.l[2], 1, L, skinL, false);
    else if (lm === 'rest') handFlat(S, ox + 24 + sq, fallen ? 114.4 : 107 + dy, 1, L, skinL, fallen);
  }
  function spKey(a) { return a.map(function (v) { return typeof v === 'number' ? Math.round(v * 100) / 100 : v; }).join(','); }
  function mFrontX(st, sp) {
    var key = 'x' + st + '|' + spKey(sp.cup) + '|' + spKey(sp.l) + '|' + spKey(sp.r);
    return mMemo(key, function () {
      var S = new Spr(MW, MH); drawFrontX(S, st, sp);
      var a = S.render({ map: MAPS[st], dz: 2.8 })[0];
      /* a raised hand takes the spell's light, not the moon's: the cold rim stays on the forearms below the wrists */
      var castY = sp.l[0] === 'cast' || sp.r[0] === 'cast' ? orbAt(st)[1] + 12 : 0;
      if (st === 'barkeep') rimPass(S, a, 0, 1, 0, 'e2');
      if (mCold(st)) rimPass(S, a, 0, -1, 0, 'm3', castY);
      if (st === 'hurt') mFlash(a);
      var part = mPartOf(a); part.k = key; return part;
    });
  }

  /* ---- the spell and the slide's dust, drawn into a 192x144 frame (clip-aware, like the flame) ---- */
  var ORBITS = [[5, 11, 0.42, 1, 0], [5, 11, 0.42, 1, 1 / 3], [5, 11, 0.42, 1, 2 / 3], [4.6, 9.5, -0.55, -1, 0.15]];  /* rx, ry, tilt, turn, phase */
  var LUT_SPELL = null;
  function lutSpell() { return LUT_SPELL || (LUT_SPELL = lumLUT(['m2', 'm3', 'm4', 'm5'], 0.62)); }
  function p32Index() { if (!P32_INDEX) { P32_INDEX = {}; for (var q = 0; q < P32.length; q++) P32_INDEX[P32[q]] = q; } return P32_INDEX; }
  function mPut(out, clip, x, y, c) { x = Math.floor(x); y = Math.floor(y); if (x >= clip[0] && x < clip[2] && y >= clip[1] && y < clip[3]) out[y * MW + x] = c; }
  /* g 0..1 = how far the spell has gathered, p 0..1 = the pulse */
  /* keep: optional test (x, y) for frame pixels the glow must leave dark (the pixel round her hands, so their outline stays whole) */
  function mOrb(out, clip, qx, qy, g, p, keep) {
    if (g <= 0) return;
    var R = (4 + 2 * p) * g, Rc = (1.5 + 1.1 * p) * g, Rh = R + (4 + 2.6 * p) * g;
    var M5 = C('m5'), M4 = C('m4'), M3 = C('m3'), M2 = C('m2');
    for (var y = Math.floor(qy - Rh - 1); y <= qy + Rh + 1; y++) for (var x = Math.floor(qx - Rh - 1); x <= qx + Rh + 1; x++) {
      var dx = x + 0.5 - qx, dy = y + 0.5 - qy, d = Math.sqrt(dx * dx + dy * dy), b = bay(x, y), c;
      if (d <= Rc) c = M5;
      else if (d <= R) { var f = (d - Rc) / Math.max(0.01, R - Rc); c = f < 0.4 ? M5 : f < 0.75 ? M4 : b < 0.5 ? M4 : M3; }
      else if (d <= Rh) { if (keep && keep(x, y)) continue; var h = Math.pow(1 - (d - R) / (Rh - R), 1.7) * (0.6 + 0.3 * p); if (b < h * 0.4) c = M3; else if (b < h) c = M2; else continue; }
      else continue;
      mPut(out, clip, x, y, c);
    }
  }
  function mMote(o, T, g) {
    var ph = (o[3] * T / 1000 + o[4]) * 2 * Math.PI, sc = 1 + 1.6 * (1 - g), ex = Math.cos(ph) * o[0] * sc, ey = Math.sin(ph) * o[1] * sc, c = Math.cos(o[2]), s = Math.sin(o[2]);
    return [ex * c - ey * s, ex * s + ey * c, Math.sin(ph)];
  }
  /* motes on the far side of their orbit go behind the spell and her hands, the near side in front */
  function mMotes(out, clip, qx, qy, g, T, near, keep) {
    if (g <= 0) return;
    var cols = near ? [C('m5'), C('m4'), C('m3')] : [C('m4'), C('m3'), C('m3')];
    function put(x, y, c) { if (!keep || !keep(Math.floor(x), Math.floor(y))) mPut(out, clip, x, y, c); }
    ORBITS.forEach(function (o) {
      for (var j = 2; j >= 0; j--) {
        var m = mMote(o, T - j * 45, g); if ((m[2] >= 0) !== near) continue;
        put(qx + m[0], qy + m[1], cols[j]);
        if (!j && near) { put(qx + m[0] + 1, qy + m[1], cols[0]); put(qx + m[0], qy + m[1] - 1, cols[1]); }
      }
    });
  }
  /* a test for frame pixels on or within r of a part drawn at (ox, oy) */
  function partNear(part, ox, oy, r) {
    if (!part || !part.w) return null;
    var bb = part.bb, pw = part.w, src = part.px, ph = bb[3] - bb[1];
    return function (X, Y) {
      for (var j = -r; j <= r; j++) for (var i = -r; i <= r; i++) {
        var x = X + i - ox - bb[0], y = Y + j - oy - bb[1];
        if (x >= 0 && y >= 0 && x < pw && y < ph && src[y * pw + x]) return true;
      }
      return false;
    };
  }
  /* the snuff: the core pops into a ring and sixteen sparks fly out and die, tau 0..1 */
  function mBurst(out, clip, qx, qy, tau) {
    if (tau < 0 || tau >= 1) return;
    var M5 = C('m5'), M4 = C('m4'), M3 = C('m3'), e = easeOut(tau);
    if (tau < 0.3) {
      var rr = 1.5 + tau * 22;
      for (var i = 0; i < 28; i++) { var an = i / 28 * 2 * Math.PI; if (bay(i, 1) < tau * 2.4) continue; mPut(out, clip, qx + Math.cos(an) * rr, qy + Math.sin(an) * rr * 0.9, tau < 0.12 ? M5 : M4); }
      if (tau < 0.12) for (var yy = -1; yy <= 1; yy++) for (var xx = -1; xx <= 1; xx++) mPut(out, clip, qx + xx, qy + yy, M5);
    }
    for (var k = 0; k < 16; k++) {
      var a = k * Math.PI / 8 + (hash(k, 3, 81) - 0.5) * 0.5, d = 2 + (10 + hash(k, 4, 82) * 10) * e;
      var x = qx + Math.cos(a) * d, y = qy + Math.sin(a) * d * 0.85;
      if (tau > 0.6 && bay(Math.floor(x), Math.floor(y)) < (tau - 0.6) / 0.4) continue;
      mPut(out, clip, x, y, tau < 0.35 ? M5 : tau < 0.7 ? M4 : M3);
      if (tau < 0.55) mPut(out, clip, x - Math.cos(a) * 1.3, y - Math.sin(a) * 1.1, M4);
    }
  }
  /* wood dust scraped up round the cup's base as it stops: puffs out both sides, rises, thins, tau 0..1 */
  function mDust(out, clip, c, tau, cold) {
    if (tau < 0 || tau >= 1) return;
    var cols = cold ? [C('m5'), C('m4'), C('m3')] : [C('pa'), C('sk6'), C('sk5')], e = easeOut(tau);
    for (var i = 0; i < 18; i++) {
      var sd = i < 10 ? -1 : 1, x0 = c[0] + sd * (5 * c[2] + 0.4), y0 = c[1] - 0.7;
      var x = x0 + sd * (1 + hash(i, 1, 91) * 6.5) * e, y = y0 - (0.4 + hash(i, 2, 92) * 4.6) * e + tau * tau * 1.4;
      if (bay(Math.floor(x), Math.floor(y)) < tau * 1.2 - 0.2) continue;
      var col = cols[Math.min(2, Math.floor(tau * 2.2 + hash(i, 3, 93) * 0.9))];
      mPut(out, clip, x, y, col);
      if (tau < 0.45 && i % 3 === 0) { mPut(out, clip, x + sd, y, col); mPut(out, clip, x, y - 1, cols[Math.min(2, 1 + (i & 1))]); }
    }
  }
  /* the spell's light on a part as it was blitted: on her hands, a rim on the edges that face it and a
     sparse wash near it; on her face (o.under), only the lowest edge of the chin and jaw, lit from below */
  function mSpellLight(out, clip, part, pox, poy, qx, qy, amt, o) {
    if (!part || !part.w || amt <= 0) return;
    var src = part.px, bb = part.bb, pw = part.w, ph = bb[3] - bb[1], OL = P32[0], idx = p32Index(), lut = lutSpell();
    var M3 = C('m3'), M4 = C('m4'), M5 = C('m5');
    function at(x, y) { x -= bb[0]; y -= bb[1]; return x < 0 || y < 0 || x >= pw || y >= ph ? 0 : src[y * pw + x]; }
    for (var y = Math.max(bb[1], o.y0 || 0); y < Math.min(bb[3], o.y1 || MH); y++) for (var x = bb[0]; x < bb[2]; x++) {
      var v = src[(y - bb[1]) * pw + x - bb[0]]; if (!v || v === OL) continue;
      var X = x + pox, Y = y + poy; if (X < clip[0] || X >= clip[2] || Y < clip[1] || Y >= clip[3]) continue;
      if (o.under) { if (!at(x, y + 1) && Math.abs(X + 0.5 - qx) <= o.half) out[Y * MW + X] = amt > 0.2 ? M4 : M3; continue; }
      var dx = qx - (X + 0.5), dy = qy - (Y + 0.5), d = Math.sqrt(dx * dx + dy * dy); if (d > o.reach) continue;
      var f = amt * (1 - d / o.reach), n = at(x + Math.round(dx / d), y + Math.round(dy / d));
      if ((!n || n === OL) && f > (o.soft ? 0.2 : 0.12)) { out[Y * MW + X] = f > 0.55 && !o.soft ? M5 : f > 0.34 ? M4 : M3; continue; }
      if (o.wash && bay(X, Y) < f * o.wash) { var pi = idx[v]; if (pi != null) out[Y * MW + X] = P32[lut[pi]]; }
    }
  }

  /* ---- the stun's mythic light: a flickering rim and aura round her in FramedFantasy's purple and pale teal, and a
     shimmer that sweeps across her. Her silhouette (what shows of her above the counter, and her forearms) is found
     once per pose and kept with how far each pixel outside it lies, up to 3 px ---- */
  var ZMASK = {}, ZMASK_N = 0, LUT_MYTH = null, M_TOP = 109;
  function lutMyth() { return LUT_MYTH || (LUT_MYTH = lumLUT(['y1', 'y2', 'y3', 'y4'], 0.7)); }
  function mMark(M, part, dy) {
    if (!part || !part.w) return;
    var bb = part.bb, pw = part.w, src = part.px;
    for (var y = bb[1]; y < bb[3]; y++) {
      var Y = y + dy; if (Y < 0 || Y >= M_TOP) continue;
      for (var x = bb[0]; x < bb[2]; x++) if (src[(y - bb[1]) * pw + x - bb[0]]) M[Y * MW + x] = 1;
    }
  }
  function mStunMask(st, pose, front) {
    var key = st + '|' + pose.by + ',' + pose.hy + ',' + pose.eyes + ',' + pose.ti + ',' + (pose.look || '') + ',' + (st === 'barkeep' ? pose.sway : '') + '|' + (front && front.k || 'f');
    var got = ZMASK[key]; if (got) return got;
    if (ZMASK_N > 48) { ZMASK = {}; ZMASK_N = 0; }
    var M = new Uint8Array(MW * MH), x, y, i;
    mMark(M, mBody(st), pose.by);
    if (st === 'barkeep') mMark(M, mTowel(pose.sway), pose.by);
    mMark(M, mHead(st, pose.eyes, pose.ti, pose.look), pose.by + pose.hy);
    mMark(M, front, 0);
    var D = new Float32Array(MW * MH);
    for (i = 0; i < D.length; i++) D[i] = M[i] ? 0 : 9;
    for (y = 1; y < MH; y++) for (x = 1; x < MW - 1; x++) { i = y * MW + x; if (D[i]) D[i] = Math.min(D[i], D[i - 1] + 1, D[i - MW] + 1, D[i - MW - 1] + 1.4, D[i - MW + 1] + 1.4); }
    for (y = MH - 2; y >= 0; y--) for (x = MW - 2; x > 0; x--) { i = y * MW + x; if (D[i]) D[i] = Math.min(D[i], D[i + 1] + 1, D[i + MW] + 1, D[i + MW + 1] + 1.4, D[i + MW - 1] + 1.4); }
    /* edge: her outline (not the cut where the counter hides her); skin: the pixel just inside it; ring: the air round her */
    var edge = [], skin = [], ring = [], inside = [], s0 = 1e9, s1 = -1e9;
    function out(j, yy) { return yy < M_TOP && !M[j]; }
    for (y = 1; y < M_TOP; y++) for (x = 1; x < MW - 1; x++) {
      i = y * MW + x;
      if (M[i]) {
        inside.push(i); var s = x * 0.72 + y * 0.7; if (s < s0) s0 = s; if (s > s1) s1 = s;
        if (out(i - 1, y) || out(i + 1, y) || out(i - MW, y - 1) || out(i + MW, y + 1)) edge.push(i);
        else if (y > 1 && (out(i - 2, y) || out(i + 2, y) || out(i - 2 * MW, y - 2) || out(i + 2 * MW, y + 2))) skin.push(i);
      } else if (D[i] <= 3.2 && y < M_TOP - 1) ring.push(i, D[i]);
    }
    ZMASK_N++;
    return (ZMASK[key] = { edge: edge, skin: skin, ring: ring, inside: inside, s0: s0, s1: s1 });
  }
  /* zf from stunFx: lvl 0..1 (the flicker), ph (the drift of purple into teal round her), band 0..1 (the shimmer's
     place on its sweep, -1 = none). lut: a hit's flash, which the light takes too so the flash stays on top */
  function mStunTint(out, clip, st, pose, ox, oy, zf, lut, front) {
    var I = zf.lvl; if (I <= 0.01) return;
    var Z = mStunMask(st, pose, front), idx = p32Index(), k, i, x, y;
    var Y1 = PI.y1, Y2 = PI.y2, Y3 = PI.y3, Y4 = PI.y4, Y5 = PI.y5;
    function col(c) { return P32[lut ? lut[c] : c]; }
    function purple(X, Y) { return Math.sin(X * 0.19 - Y * 0.12 + zf.ph) + (bay(X, Y) - 0.5) * 0.9 > 0; }
    var ML = lutMyth();
    function wash(i, dens) {
      var X = i % MW + ox, Y = ((i / MW) | 0) + oy; if (X < clip[0] || X >= clip[2] || Y < clip[1] || Y >= clip[3] || Y >= M_TOP - 1) return;
      if (bay(X, Y) >= dens) return;
      var j = Y * MW + X, pi = idx[out[j]]; if (pi != null) out[j] = P32[ML[pi]];
    }
    if (!lut) {
      /* the shimmer: a soft slanting band crossing her from the top left, through a light-by-brightness map */
      if (zf.band >= 0) {
        var sb = lerp(Z.s0 - 12, Z.s1 + 12, zf.band);
        for (k = 0; k < Z.inside.length; k++) {
          i = Z.inside[k]; var d = Math.abs((i % MW) * 0.72 + ((i / MW) | 0) * 0.7 - sb); if (d > 6) continue;
          wash(i, Math.pow(1 - d / 6, 1.4) * 0.66 * I);
        }
      }
      /* the light just inside her outline */
      for (k = 0; k < Z.skin.length; k++) wash(Z.skin[k], I * 0.34);
    }
    /* the rim: her dark outline catches the light, pixel by pixel as it flickers */
    for (k = 0; k < Z.edge.length; k++) {
      i = Z.edge[k]; x = i % MW + ox; y = ((i / MW) | 0) + oy;
      if (x < clip[0] || x >= clip[2] || y < clip[1] || y >= clip[3] || y >= M_TOP - 1) continue;
      if (bay(x, y) < I * 0.62) out[y * MW + x] = col(purple(x, y) ? Y3 : Y4);
    }
    /* the aura: thinning out to 3 px round her, never over the counter */
    for (k = 0; k < Z.ring.length; k += 2) {
      i = Z.ring[k]; var dd = Z.ring[k + 1]; x = i % MW + ox; y = ((i / MW) | 0) + oy;
      if (x < clip[0] || x >= clip[2] || y < clip[1] || y >= clip[3] || y >= M_TOP - 1) continue;
      var b = bay(x, y), pu = purple(x, y);
      if (dd <= 1.05) { if (b < I * 0.66) out[y * MW + x] = col(pu ? Y3 : Y4); }
      else if (dd <= 2.05) { if (b < I * 0.4) out[y * MW + x] = col(pu ? Y2 : Y5); }
      else if (b < I * 0.2) out[y * MW + x] = col(pu ? Y1 : Y5);
    }
  }

  /* ---- the smite: Jan's unseen save. A mythic bolt (core #ef94ff, trail #b3f5ee) streaks in out of the dark past
     her shoulder, off the canvas at the upper left, and strikes the side of her head. At impact the moment holds for
     SM_STOP ms under a pale mythic flash, a ring and a burst of shards open in the two colours, and she is knocked
     back 4 px and eases back. Times are ms from the throw ---- */
  var SM_FLY = 450, SM_STOP = 70, SM_FLASH = 100, SM_KNOCK = 440, SM_BURST = 560;
  var LUT_SMITE = null;
  function lutSmite() { return LUT_SMITE || (LUT_SMITE = lumLUT(['y2', 'y3', 'y4', 'm5', 'hw'], 0.5)); }
  function smitePts(st) {
    var F = MFIG[st] || MFIG.matron;
    return { s: [-24, 4], c: [30, -6], p: [Math.round(MCX + F.hx - 9.5), 44 + F.hy - 16] };
  }
  function qBez(a, c, b, t) { var u = 1 - t; return [u * u * a[0] + 2 * u * t * c[0] + t * t * b[0], u * u * a[1] + 2 * u * t * c[1] + t * t * b[1]]; }
  /* the bolt in flight, f 0..1 of the way: out of the dark at the left edge, gathering speed as it comes */
  function mBolt(out, clip, B, f) {
    var Y1 = C('y1'), Y2 = C('y2'), Y3 = C('y3'), Y4 = C('y4'), Y5 = C('y5'), i;
    function at(g) { return qBez(B.s, B.c, B.p, Math.pow(clamp(g, 0, 1), 1.35)); }
    /* the trail: the last stretch of its path, three pixels and pale teal behind the head, thinning to deep teal and violet */
    for (i = 30; i >= 1; i--) {
      var g = f - i * 0.0095; if (g <= 0) continue;
      var q = at(g), w = i / 30;
      if (w > 0.72 && bay(Math.floor(q[0]), Math.floor(q[1])) > 0.55) continue;
      var col = w < 0.45 ? Y4 : w < 0.72 ? Y5 : Y1;
      mPut(out, clip, q[0], q[1], col);
      if (w < 0.6) mPut(out, clip, q[0] + 0.7, q[1] + 0.7, col);
      if (w < 0.3) mPut(out, clip, q[0] - 0.7, q[1] - 0.7, col);
    }
    for (i = 0; i < 6; i++) {                           /* motes it sheds */
      var gm = f - 0.07 - i * 0.045; if (gm <= 0) continue;
      var qm = at(gm), sd = Math.floor(f * 18);
      mPut(out, clip, qm[0] + (hash(i, sd, 171) - 0.5) * 7, qm[1] + (hash(i, sd, 172) - 0.5) * 7, i & 1 ? Y4 : Y3);
    }
    /* the head: a purple diamond in a deeper purple glow */
    var h = at(f), hx = Math.floor(h[0]), hy = Math.floor(h[1]);
    for (var j = -3; j <= 3; j++) for (var k = -3; k <= 3; k++) {
      var d = Math.abs(j) + Math.abs(k);
      if (d <= 2) mPut(out, clip, hx + k, hy + j, Y3);
      else if (d === 3 && bay(hx + k, hy + j) < 0.7) mPut(out, clip, hx + k, hy + j, Y2);
    }
  }
  /* the burst at the point P, tau ms after the hit-stop ends (tau < 0: the moment of impact, held) */
  function mSmiteBurst(out, clip, P, tau) {
    var Y2 = C('y2'), Y3 = C('y3'), Y4 = C('y4'), HW = C('hw'), T = Math.max(0, tau), k, i;
    if (T >= SM_BURST) return;
    var e = easeOut(T / SM_BURST);
    if (tau < 60) {                                      /* the strike: a bright star while the moment holds */
      for (k = -6; k <= 6; k++) { var a = Math.abs(k), c = a < 2 ? HW : a < 4 ? Y4 : Y3; mPut(out, clip, P[0] + k, P[1], c); mPut(out, clip, P[0], P[1] + k, c); }
      for (k = 1; k <= 3; k++) [[1, 1], [-1, -1], [1, -1], [-1, 1]].forEach(function (d) { mPut(out, clip, P[0] + d[0] * k, P[1] + d[1] * k, k < 2 ? HW : k < 3 ? Y4 : Y2); });
    }
    if (T < 360) {                                       /* two rings, purple inside pale teal, opening and thinning */
      var rr = 3 + 14 * easeOut(T / 360), fade = T / 360, n = Math.round(rr * 7);
      for (i = 0; i < n; i++) {
        var an = i / n * 2 * Math.PI, cx = Math.cos(an), cy = Math.sin(an) * 0.92;
        if (bay(i, 5) >= 1.15 - fade * 1.1) continue;
        mPut(out, clip, P[0] + cx * rr, P[1] + cy * rr, Y4);
        if (T < 200) mPut(out, clip, P[0] + cx * (rr + 1), P[1] + cy * (rr + 1), Y4);
        if (rr > 4) mPut(out, clip, P[0] + cx * (rr - 1.4), P[1] + cy * (rr - 1.4), Y3);
      }
    }
    for (k = 0; k < 20; k++) {                           /* shards: flung on along the bolt's line and fanning out */
      var a0 = 0.45 + (hash(k, 1, 181) - 0.5) * 2.8 + (k % 5 === 0 ? Math.PI : 0), sp = 8 + hash(k, 2, 182) * 20;
      var sx = P[0] + Math.cos(a0) * sp * e, sy = P[1] + Math.sin(a0) * sp * e + e * e * 4;
      if (T > SM_BURST * 0.5 && bay(Math.floor(sx), Math.floor(sy)) < (T / SM_BURST - 0.5) / 0.5) continue;
      mPut(out, clip, sx, sy, T < 120 ? HW : k & 1 ? Y3 : Y4);
      if (T < 380) { mPut(out, clip, sx - Math.cos(a0) * 1.2, sy - Math.sin(a0) * 1.2, k & 1 ? Y4 : Y3); mPut(out, clip, sx - Math.cos(a0) * 2.3, sy - Math.sin(a0) * 2.3, Y2); }
    }
  }

  function mountMatron(el, opts) {
    opts = opts || {};
    var reduced = opts.reduced != null ? !!opts.reduced : prefersReduced();
    var state = M_STATES.indexOf(opts.state) >= 0 ? opts.state : 'barkeep';
    var fixedK = opts.scale ? Math.max(1, Math.round(+opts.scale)) : 0;
    var c = mk(MW, MH), x = ctx2d(c), nat = mk(MW, MH), nx = ctx2d(nat), im = nx.createImageData(MW, MH), px = new Uint32Array(im.data.buffer);
    var arWas = el.style.aspectRatio || '';
    el.style.aspectRatio = MW + ' / ' + MH;            /* the host takes the art's shape, so the stage CSS never stretches her */
    c.style.display = 'block'; c.style.width = '100%'; c.style.height = 'auto'; c.style.maxWidth = '100%';
    pixelated(c); c.setAttribute('role', 'img'); c.setAttribute('aria-label', MATRON_ALT[state]);
    c.className = 'qoart-matron';
    el.appendChild(c);
    var alive = true, trans = null, hitT = -1, timer = 0, rafId = 0, k = 1, t0 = now(), frozen = null, lastPose = '', lastFlame = '';
    var bufB = new Uint32Array(MW * MH);
    /* ---- the cup and the cast: their state, and what they add to a frame (null = the still art) ---- */
    var cup = opts.cup === 'served' ? 'served' : 'held', cupA = null, dust = null, castOn = false, castT0 = 0, castU0 = 0, snuff = null;
    var PUSH = 700, PULL = 620, REACH = 260, HOLD = 70, RET = 380, LIFT = 420, DROP = 300, BURST = 250, DUST = 600;
    /* her looks: stare (she has noticed you) and menace (red eyes, a cruel smile); menace wins when both are on */
    var stareOn = false, menaceOn = false, menaceT0 = 0;
    /* and stunned (wins over both while it is on): wide eyes, lifted brows, an open mouth, a stagger, a mythic shimmer */
    var stunOn = false, stunT0 = 0, stunOffT = -1, STUN_OUT = 240;
    /* the smite in play: { t0 (wall clock), imp (impact), pts, res (resolves the promise), done, still (reduced) } */
    var smiteS = null;
    function smiteRes() { if (smiteS && !smiteS.done) { smiteS.done = true; try { smiteS.res(); } catch (e) { warn(e); } } }
    /* where the smite is at wall-clock time tn: f (the bolt's way, < 1 in flight), tau (ms after the hit-stop), the
       flash, her knock-back, and a key for the frame; null once it is over */
    function smitePhase(tn) {
      var S = smiteS; if (!S) return null;
      if (S.still) return { f: 1, tau: 140, flash: true, ox: 0, oy: 0, key: 'r' };
      var e = tn - S.imp;
      if (e < 0) return { f: (tn - S.t0) / SM_FLY, tau: -1, flash: false, ox: 0, oy: 0, key: 'f' + Math.floor((tn - S.t0) / 16) };
      smiteRes();
      var tau = e - SM_STOP;
      if (tau >= Math.max(SM_BURST, SM_KNOCK)) { smiteS = null; return null; }
      var kb = tau < 0 ? 4 : 4 * (1 - easeInOut(Math.min(1, tau / SM_KNOCK)));
      return { f: 1, tau: tau, flash: e < SM_FLASH, ox: Math.round(kb), oy: e < SM_STOP + 60 ? -1 : 0, key: 'b' + Math.floor(e / 16) };
    }
    function withSmite(ex, sm) {
      if (!sm) return ex;
      var P = smiteS.pts;
      return {
        key: (ex ? ex.key : 'f') + '|s' + sm.key,
        front: ex ? ex.front : null,
        under: ex ? ex.under : null,
        over: function (out, clip, ox, oy, pose) {
          if (ex && ex.over) ex.over(out, clip, ox, oy, pose);
          if (sm.f < 1) mBolt(out, clip, P, sm.f); else mSmiteBurst(out, clip, P.p, sm.tau);
        }
      };
    }
    function lookOf(st) { return st !== 'barkeep' && st !== 'matron' ? '' : stunOn ? 'z' : menaceOn ? 'm' : stareOn ? 's' : ''; }
    /* the bloom: it swells in over a quarter second, then breathes and flickers between two strengths */
    function glowAt(t) {
      if (reduced) return 2;
      var e = t - menaceT0;
      if (e < 90) return 1;
      if (e < 200) return 2;
      var b = Math.sin(e / 1400 * 2 * Math.PI) * 0.6 + (hash(Math.floor(e / 110), 7, 71) - 0.5) * 0.9;
      return b > 0.12 ? 3 : 2;
    }
    function withLook(p, st, t) {
      var lk = lookOf(st); if (!lk) return p;
      var g = lk === 'm' ? glowAt(t) : 0, ey = 0, ti = 0;
      if (lk === 'z') { var z = stunPose(t); ey = z.eyes; ti = z.ti; }
      return { by: p.by, hy: p.hy, eyes: ey, ti: ti, sway: p.sway, look: lk, glow: g, key: p.by + ',' + p.hy + ',' + ey + ',' + ti + ',' + p.sway + ',' + lk + g };
    }
    /* the stun as it plays: the blow throws her a few pixels aside and her head rocks over (0.7 s), then she sways,
       dazed, her head rolling slowly and a double blink now and then. Reduced motion: the head rolled, held still */
    function stunPose(t) {
      if (reduced) return { eyes: 0, ti: 1, ox: 0, oy: 0 };
      var e = Math.max(0, t - stunT0), ox = 0, oy = 0, ti = 0, eyes = 0;
      if (e < 260) { ox = Math.round(2.6 * Math.sin(Math.PI * e / 260)); oy = e < 150 ? -1 : 0; }
      else if (e < 620) ox = -Math.round(1.3 * Math.sin(Math.PI * (e - 260) / 360));
      if (e >= 50 && e < 520) ti = e < 110 || e > 440 ? 1 : 2;
      else if (e >= 520) {
        var s = Math.sin((e - 520) / 2600 * 2 * Math.PI);
        ti = s > 0.35 ? 1 : 0;
        if (e > 1400) ox = s < -0.7 ? -1 : 0;
      }
      var b = e < 780 ? -1 : (e - 780) % 3700;
      if (b >= 0 && b < 420) eyes = b < 110 || (b >= 230 && b < 330) ? 1 : 0;
      return { eyes: eyes, ti: ti, ox: ox, oy: oy };
    }
    /* the mythic tint's strength and phase this frame, or null: it flickers in steps and fades out after stun(false) */
    function stunFx(t) {
      if (state !== 'barkeep' && state !== 'matron') return null;
      var fade = 1;
      if (!stunOn) { if (stunOffT < 0 || reduced) return null; fade = 1 - (t - stunOffT) / STUN_OUT; if (fade <= 0) { stunOffT = -1; return null; } }
      if (reduced) return { lvl: 0.8, ph: 1.3, band: -1, key: 'r' };
      var e = Math.max(0, t - stunT0), step = Math.floor(e / 70);
      var lvl = (e < 140 ? 1 : 0.62 + 0.3 * Math.sin(e / 900 * 2 * Math.PI) + (hash(step, 5, 131) - 0.5) * 0.36) * fade;
      if (hash(step, 9, 132) < 0.08) lvl *= 0.45;
      var band = (e % 1500) / 1500;
      return { lvl: clamp(lvl, 0, 1), ph: e / 260, band: band, key: step + ',' + Math.floor(band * 40) + ',' + Math.round(fade * 6) };
    }
    function clk() { return frozen != null ? frozen : now() - t0; }      /* the idle clock, so _at() freezes these too */
    function q8(v) { return Math.round(v * 8) / 8; }
    function mixCup(a, b, m) { return m <= 0 ? a : m >= 1 ? b : [lerp(a[0], b[0], m), lerp(a[1], b[1], m), lerp(a[2], b[2], m)]; }
    function liftU(t) {
      if (castOn) return reduced ? 1 : castU0 + (1 - castU0) * easeOut(clamp((t - castT0) / LIFT, 0, 1));
      if (snuff) { var d = (t - snuff.t0) / DROP; return d >= 1 ? 0 : snuff.u0 * (1 - d * d); }
      return 0;
    }
    function gatherG(t) { return reduced ? 1 : easeOut(clamp((t - castT0 - 110) / 380, 0, 1)); }
    function flatAt(st, x) { var R = restL(st); return st !== 'barkeep' && x === R[0] ? ['rest'] : ['flat', x, R[1]]; }
    function nearCup(st, c) { var R = restL(st); return R[0] + Math.round(c[0] + 5.4 * c[2] + 8.4 - R[0]); }   /* her flat hand, just clear of it */
    /* where the cup is and what her left hand does: reach for it, slide it, let go, go back to rest */
    function cupPose(st, t) {
      var H = cupAt(st, 'held'), V = cupAt(st, 'served'), R = restL(st), A = cupA;
      if (A && st !== 'fallen') {
        var from = A.to === 'served' ? H : V, to = A.to === 'served' ? V : H, e = t - A.t0;
        if (e < A.reach) { var xa = nearCup(st, from); return [from, flatAt(st, R[0] + Math.round((xa - R[0]) * easeInOut(Math.max(0, e) / A.reach)))]; }
        e -= A.reach;
        if (e < A.move) return [mixCup(from, to, q8(A.to === 'served' ? easeOut(e / A.move) : easeInOut(e / A.move))), ['grip']];
        e -= A.move;
        if (e < HOLD) return [to, ['grip']];
        e -= HOLD;
        if (A.ret && e < RET) { var xb = nearCup(st, to); return [to, flatAt(st, xb + Math.round((R[0] - xb) * easeInOut(e / RET)))]; }
        cupA = null;
      }
      return cup === 'served' ? [V, st === 'barkeep' ? ['flat', R[0], R[1]] : ['rest']] : [H, ['rest']];
    }
    function busy() {
      if (reduced || frozen != null || !onScreen) return false;
      var t = clk();
      return castOn || !!cupA || (!!snuff && t - snuff.t0 < Math.max(DROP, BURST)) || (!!dust && t - dust.t0 < DUST) ||
        (stunOn && (state === 'barkeep' || state === 'matron')) || stunOffT >= 0 || !!smiteS;
    }
    /* the stun's shimmer rides on the frame's other additions, drawn last so a hit's flash still reads on top */
    function withStun(ex, st, t, lut) {
      var zf = stunFx(t); if (!zf) return ex;
      return {
        key: (ex ? ex.key : 'f') + '|z' + zf.key,
        front: ex ? ex.front : null,
        under: ex ? ex.under : null,
        over: function (out, clip, ox, oy, pose) {
          if (ex && ex.over) ex.over(out, clip, ox, oy, pose);
          mStunTint(out, clip, st, pose, ox, oy, zf, lut, ex && ex.front ? ex.front : mFront(st));
        }
      };
    }
    function motion(st, t) {
      if (snuff && t - snuff.t0 >= Math.max(DROP, BURST)) snuff = null;
      if (dust && t - dust.t0 >= DUST) dust = null;
      var u = st === 'fallen' ? 0 : q8(liftU(t)), cp = cupPose(st, t), kk = 0;
      if (castOn && u >= 1 && !reduced) kk = Math.max(0, Math.floor((t - castT0 - LIFT) / 250)) % 4;
      var arm = u > 0 ? ['cast', u, kk] : null, sp = { cup: cp[0], l: arm || cp[1], r: arm || ['rest'] };
      var still = !arm && !cupA && cup === 'held', cold = st !== 'barkeep', q = snuff ? snuff.q : orbAt(st === 'hurt' ? 'matron' : st);
      var g = 0, p = 0.6, T = 250, light = 0;
      if (castOn) { g = gatherG(t); light = g; if (!reduced) { p = 0.5 - 0.5 * Math.cos((t - castT0) / 1000 * 2 * Math.PI); T = t - castT0; } }
      var bt = snuff ? (t - snuff.t0) / BURST : 1;
      if (bt < 1) light = (1 - bt) * snuff.g;
      var dt = dust ? t - dust.t0 : -1, fx = g > 0 || bt < 1 || dt >= 0;
      if (still && !fx) return null;
      var front = still ? null : mFrontX(st, sp), F = MFIG[st];
      return {
        key: (front ? front.k : 'f') + (fx ? '|' + Math.floor(t / 33) : ''),
        front: front,
        under: function (out, clip, ox, oy) {
          if (g > 0) { var kp = partNear(front || mFront(st), ox, oy, 1); mMotes(out, clip, q[0] + ox, q[1] + oy, g, T, false, kp); mOrb(out, clip, q[0] + ox, q[1] + oy, g, p, kp); }
        },
        over: function (out, clip, ox, oy, pose) {
          if (light > 0) {                                /* a cold light on her hands, and faintly under her face */
            var lp = light * (0.65 + 0.35 * p);
            mSpellLight(out, clip, front || mFront(st), ox, oy, q[0] + ox, q[1] + oy, lp, { reach: 19, wash: 0, soft: 1 });
            mSpellLight(out, clip, mHead(st, pose.eyes, pose.ti, pose.look), ox, oy + pose.by + pose.hy, q[0] + ox, q[1] + oy, lp * 0.3, { under: 1, half: 8.5, y0: 38 + F.hy, y1: 48 + F.hy });
          }
          if (g > 0) mMotes(out, clip, q[0] + ox, q[1] + oy, g, T, true, partNear(front || mFront(st), ox, oy, 0));
          if (bt < 1) mBurst(out, clip, q[0] + ox, q[1] + oy, bt);
          if (dt >= 0 && dt < DUST) mDust(out, clip, cupAt(st, 'served'), dt / DUST, cold);
        }
      };
    }
    function setCup(c) {
      if ((c !== 'held' && c !== 'served') || c === cup) return;
      var t = clk(), warm = state === 'barkeep';
      cupA = null; cup = c; dust = null;                /* a slide already under way ends where it was going */
      if (reduced || state === 'fallen') { draw(); return; }
      var reach = c === 'served' && warm ? 0 : REACH;
      cupA = { to: c, t0: t, reach: reach, move: c === 'served' ? PUSH : PULL, ret: !(c === 'held' && warm) };
      if (c === 'served') dust = { t0: t + reach + PUSH };
      kick();
    }
    function setCast(on) {
      if (on === castOn) return;
      var t = clk();
      if (on) {
        if (state === 'fallen') return;
        castU0 = snuff ? liftU(t) : 0; snuff = null; castOn = true; castT0 = t;
        if (!reduced) warmMore(castJobs(state === 'hurt' ? 'matron' : state));
      } else {
        var u = liftU(t), g = gatherG(t);
        castOn = false;
        snuff = reduced ? null : { t0: t, u0: u, g: g, q: orbAt(state === 'hurt' ? 'matron' : state) };
      }
      if (reduced) { draw(); return; }
      kick();
    }
    /* idle-time renders of the poses these moves pass through */
    function cupJobs(st) {
      var H = cupAt(st, 'held'), V = cupAt(st, 'served'), R = restL(st), list = [], i, x;
      for (i = 1; i <= 8; i++) (function (m) { list.push(function () { mFrontX(st, { cup: mixCup(H, V, m), l: ['grip'], r: ['rest'] }); }); })(i / 8);
      for (x = nearCup(st, V); x <= R[0]; x++) (function (xx) { list.push(function () { mFrontX(st, { cup: V, l: flatAt(st, xx), r: ['rest'] }); }); })(x);
      return list;
    }
    function castJobs(st) {
      var list = [];
      [st, st === 'matron' ? 'hurt' : null].forEach(function (s) {
        if (!s) return;
        var cc = cupAt(s, cup), i;
        for (i = 1; i <= 8; i++) (function (u) { list.push(function () { var a = ['cast', u, 0]; mFrontX(s, { cup: cc, l: a, r: a }); }); })(i / 8);
        if (s === st) for (i = 1; i < 4; i++) (function (kq) { list.push(function () { var a = ['cast', 1, kq]; mFrontX(s, { cup: cc, l: a, r: a }); }); })(i);
      });
      return list;
    }
    function size() {
      var w = el.clientWidth || MW * 2; k = fixedK || Math.max(1, Math.round(w * dprCap() / MW));
      if (c.width !== MW * k) { c.width = MW * k; c.height = MH * k; noSmooth(x); }
      lastPose = ''; draw();
    }
    function paint(r) {
      if (!r) { nx.putImageData(im, 0, 0); x.clearRect(0, 0, c.width, c.height); x.drawImage(nat, 0, 0, c.width, c.height); return; }
      var w = r[2] - r[0], h = r[3] - r[1];
      nx.putImageData(im, 0, 0, r[0], r[1], w, h);
      x.clearRect(r[0] * k, r[1] * k, w * k, h * k);
      x.drawImage(nat, r[0], r[1], w, h, r[0] * k, r[1] * k, w * k, h * k);
    }
    function draw() {
      if (!alive) return;
      var tn = now(), t = frozen != null ? frozen : tn - t0, ox = 0, oy = 0, lut = null, st = state, gust = 0;
      if (hitT >= 0) {
        var h = (tn - hitT) / 380;
        if (h >= 1) hitT = -1;
        else {
          if (h < 0.14) lut = lutWhite();
          else if (h < 0.55) { if (state === 'matron' || state === 'hurt') { st = 'hurt'; if (h < 0.3) lut = lutHurt(); } else if (h < 0.3) lut = lutHurt(); }
          if (!reduced) { var a = (1 - h) * (1 - h); ox = Math.round(Math.sin(h * 40) * 3 * a); oy = Math.round(Math.cos(h * 31) * 1.4 * a); gust = 1 - h; }
        }
      }
      if (stunOn && lookOf(state)) { var zp = stunPose(t); ox += zp.ox; oy += zp.oy; }
      var sm = smitePhase(tn);
      if (sm) { if (sm.flash && !lut) lut = lutSmite(); ox += sm.ox; oy += sm.oy; }
      var pose = withLook(reduced ? M_REST : mPose(st, t), st, t), fl = mFlame(st, reduced ? -1 : t, gust), ex = withSmite(withStun(motion(st, t), st, t, lut), sm);
      var pk = st + '|' + pose.key + '|' + ox + ',' + oy + (lut ? (lut === LUT_WHITE ? 'w' : lut === LUT_SMITE ? 'y' : 'h') : '') + (ex ? '|' + ex.key : ''), fk = fl.f + ':' + fl.lvl;
      if (!trans && pk === lastPose) {                 /* only the flame moved: repaint its corner */
        if (fk === lastFlame) return;
        lastFlame = fk; mCompose(px, st, pose, fl, ox, oy, lut, M_FLAME_RECT, ex); paint(M_FLAME_RECT); return;
      }
      mCompose(px, st, pose, fl, ox, oy, lut, null, ex);
      if (trans) {
        var p = (tn - trans.t0) / 320;
        if (p >= 1) trans = null;
        else {
          mCompose(bufB, trans.from, withLook(M_REST, trans.from, t), mFlame(trans.from, t, 0), 0, 0, null, null, motion(trans.from, t));
          var e = easeInOut(p);
          for (var yy = 0; yy < MH; yy++) for (var xx = 0; xx < MW; xx++) if (bay(xx >> 1, yy >> 1) * 0.94 + 0.03 > e) px[yy * MW + xx] = bufB[yy * MW + xx];
        }
      }
      lastPose = trans ? '' : pk; lastFlame = fk;
      paint(null);
    }
    function loop() {
      rafId = 0;
      if (!alive) return;
      draw();
      if ((trans || hitT >= 0 || (smiteS && !smiteS.still) || busy()) && !doc.hidden) rafId = raf(loop);
    }
    function kick() { if (!rafId && alive && !doc.hidden) rafId = raf(loop); }
    var onScreen = true;
    function tick() {                                  /* the idle clock: about 12 fps, and nothing is drawn unless something changed */
      timer = 0; if (!alive) return;
      var on = !doc.hidden && onScreen;
      if (on && !rafId) { draw(); if (busy()) kick(); }
      timer = root.setTimeout(tick, on ? 80 : 400);
    }
    function vis() { if (!doc.hidden) { lastPose = ''; draw(); if (trans || hitT >= 0 || busy()) kick(); } }
    doc.addEventListener('visibilitychange', vis);
    var ro = null;
    if (root.ResizeObserver) { ro = new root.ResizeObserver(function () { size(); }); ro.observe(el); }
    else root.addEventListener('resize', size);
    var io = null;
    if (root.IntersectionObserver) { io = new root.IntersectionObserver(function (en) { onScreen = en[0] ? en[0].isIntersecting : true; if (onScreen) { draw(); if (busy()) kick(); } }); io.observe(c); }
    size();
    if (!reduced) timer = root.setTimeout(tick, 80);
    /* render the pose variants and the other states in idle time so no change ever costs a long frame;
       the moves this scene is about to need (the slide at the bar, the cast in the fight) go last */
    var jobs = mWarmJobs(state), ric = root.requestIdleCallback ? root.requestIdleCallback.bind(root) : function (f) { return root.setTimeout(f, 120); }, warming = false;
    if (!reduced) jobs = jobs.concat(state === 'matron' ? castJobs('matron') : state === 'barkeep' && cup === 'held' ? cupJobs('barkeep') : []);
    ['barkeep', 'matron'].forEach(function (s) { ['s', 'm'].forEach(function (lk) { jobs.push(function () { mHead(s, 0, 0, lk); }); }); });
    ['barkeep', 'matron'].forEach(function (s) { [[0, 0], [0, 1], [0, 2], [1, 0], [1, 1]].forEach(function (v) { jobs.push(function () { mHead(s, v[0], v[1], 'z'); }); }); });
    var MENACE_ALT = 'The Iron Matron, her eyes burning red, a cruel smile on her face';
    function label() { c.setAttribute('aria-label', !lookOf(state) ? MATRON_ALT[state] : stunOn ? STUN_ALT : menaceOn ? MENACE_ALT : MATRON_ALT[state]); }
    function prewarm() { if (!alive || !jobs.length) { warming = false; return; } warming = true; ric(function () { if (!alive) return; try { jobs.shift()(); } catch (e) { warn(e); } prewarm(); }); }
    function warmMore(list) { jobs = jobs.concat(list); if (!warming) prewarm(); }
    prewarm();
    return {
      canvas: c,
      getState: function () { return state; },
      setState: guard(function (s) {
        if (M_STATES.indexOf(s) < 0 || s === state) return;
        if (castOn && s !== 'matron' && s !== 'hurt') setCast(false);
        var from = state; state = s; label();
        if (reduced) { draw(); return; }
        trans = { from: from, t0: now() }; kick();
      }),
      /* 'served': her hand slides the cup to the front edge of the bar and comes back; 'held': she draws it back */
      setCup: guard(setCup),
      /* true: her hands lift and a cold spell gathers between them, looping; false: it snuffs out and they drop */
      cast: guard(function (on) { setCast(!!on); }),
      hit: guard(function () {
        if (castOn) setCast(false);                   /* a blow breaks the spell */
        hitT = now();
        if (reduced) { draw(); root.setTimeout(function () { hitT = -1; draw(); }, 160); return; }
        kick();
      }),
      /* true: she has noticed you - brows down a row, her eyes narrowed on you with a faint cold glint; she stops
         blinking and her head stops lolling. Barkeep and matron states; false puts her face back */
      stare: guard(function (on) {
        on = !!on; if (on === stareOn) return;
        stareOn = on; lastPose = ''; draw();
      }),
      /* true: her eyes burn red with a soft 1-2 px bloom that breathes and flickers (still under reduced motion),
         and her mouth sets in a cruel smile. Barkeep and matron states; wins over stare; false puts her face back */
      menace: guard(function (on) {
        on = !!on; if (on === menaceOn) return;
        menaceOn = on; if (on) menaceT0 = clk();
        label(); lastPose = ''; draw();
      }),
      /* true: she is thrown off - her eyes go wide and lost, her brows fly up and her mouth drops open; she staggers
         a few pixels, then sways dazed, and a flickering purple (#ef94ff) and pale teal (#b3f5ee) rim and shimmer
         run over her. Wins over menace and stare while on; a hit still flashes on top. false puts back the look she
         had (menace, stare or none) and the shimmer fades out in a quarter second. Barkeep and matron states.
         Reduced motion: the stunned face, her head rolled, and a still rim, no stagger, flicker or fade */
      stun: guard(function (on) {
        on = !!on; if (on === stunOn) return;
        var t = clk();
        stunOn = on;
        if (on) {
          /* straight after a smite's impact her knock-back is already the first lurch: start past it, into the rebound */
          var se = smiteS && !smiteS.still ? now() - smiteS.imp : -1;
          stunT0 = se >= -30 && se < 450 ? t - 260 : t; stunOffT = -1;
        } else stunOffT = t;
        label(); lastPose = ''; draw();
        if (!reduced) kick();
      }),
      /* the unseen save: a mythic bolt streaks in from the dark at the upper left (about 450 ms) and strikes the side of her
         head; at impact the moment holds 70 ms under a pale purple and teal flash, a ring and a burst of shards open in
         #ef94ff and #b3f5ee, and she is knocked back 4 px and eases back over about 0.45 s. It breaks a cast.
         Returns a Promise that resolves at the moment of impact (at once under reduced motion or on a hidden page,
         where one impact frame shows for about 0.2 s instead). stun(true) called at impact picks up her recoil */
      smite: guard(function () {
        var tn = now();
        if (smiteS && !smiteS.done) return smiteS.p;                   /* one already in flight: the same impact */
        if (castOn) setCast(false);
        var res = null, pr = new Promise(function (r) { res = r; });
        var still = reduced || doc.hidden || !onScreen;
        smiteS = { t0: tn, imp: tn + (still ? 0 : SM_FLY), pts: smitePts(state === 'hurt' ? 'matron' : state), res: res, done: false, still: still, p: pr };
        if (still) {
          var mine = smiteS;
          smiteRes(); draw();
          root.setTimeout(function () { if (smiteS === mine) { smiteS = null; lastPose = ''; draw(); } }, 220);
        } else {
          root.setTimeout(smiteRes, SM_FLY + 60);                      /* resolves on time even if a frame is late */
          lastPose = ''; kick();
        }
        return pr;
      }, function () { return Promise.resolve(); }),
      destroy: guard(function () {
        smiteRes(); smiteS = null;
        alive = false; if (timer) root.clearTimeout(timer); if (rafId) caf(rafId);
        doc.removeEventListener('visibilitychange', vis);
        if (io) io.disconnect();
        if (ro) ro.disconnect(); else root.removeEventListener('resize', size);
        if (c.parentNode) c.parentNode.removeChild(c);
        el.style.aspectRatio = arWas;
      }),
      /* QA: freeze the idle clock at t ms and draw that frame */
      _at: function (t) { frozen = Math.max(0, +t || 0); draw(); }
    };
  }

  /* =====================================================================
     3. THE TAVERN AT NIGHT - native 192x108 (landscape) or 108x192 (portrait).
     A crooked timber tavern alone in a black bog: warm windows, a swinging sign,
     chimney smoke, rain, drifting mist, fireflies; the door can swing open and flood
     the boardwalk with light. Static layers are built once; moving layers are drawn
     into the same native buffer each tick and scaled up nearest-neighbour.
     ===================================================================== */
  var TW = 84, TH = 66, TDX = 42, TDY = 62;          /* tavern sprite box, door base centre */
  var LMOON = norm3(0.55, -0.62, 0.56);
  var TAV = null;
  function C(k) { return P32[pc(k)]; }

  function drawTavern() {
    var S = new Spr(TW, TH), L = LMOON;
    var plasterTex = function (x, y) { var n = vnoise(x * 0.3, y * 0.3, 51); return (n > 0.66 ? -0.16 : n < 0.25 ? 0.08 : 0) + (hash(x, y, 52) > 0.95 ? -0.14 : 0); };
    var slateTex = function (x, y) { return ((y % 2 === 0) ? -0.16 : 0) + (((x + (y >> 1) * 2) % 4 === 0) ? -0.1 : 0) + (hash(x, y, 53) > 0.9 ? 0.1 : 0); };
    var stoneTex = function (x, y) { var r = y % 3 === 0, c = ((x + (y / 3 | 0) * 2) % 5) === 0; return (r || c) ? -0.3 : (hash(x, y, 54) > 0.8 ? 0.12 : 0); };
    var lit = function (amt) { return function (x) { return amt * (x - 42) / 42; }; };
    /* lean-to shed on the left, a barrel beside it */
    S.poly([[5, 51], [21, 44.5], [21, 60], [5, 60]], { m: 'plaster', l: 0.3, tex: plasterTex, ol: 1 });
    S.poly([[1.5, 52.5], [22, 42], [22.4, 44.6], [3, 54.6]], { m: 'slate', l: 0.5, tex: slateTex, bev: 0.2, ol: 1 });
    S.rect(8, 53, 4, 4, { m: 'win', l: 0.18, ol: 1 });
    S.rect(2, 56, 5, 5, { m: 'timber', l: 0.4, ol: 1, tex: function (x, y) { return y === 57 || y === 59 ? -0.3 : 0; } });
    /* stone footing */
    S.rect(18.5, 59.5, 47, 4.5, { m: 'stone', l: 0.46, tex: stoneTex, g: lit(0.15), ol: 1 });
    /* walls: ground floor, jettied upper floor, the gable */
    S.rect(21, 44.5, 42, 15.5, { m: 'plaster', l: 0.42, tex: plasterTex, g: lit(0.14), ol: 1 });
    S.rect(18.5, 30.5, 47, 13, { m: 'plaster', l: 0.46, tex: plasterTex, g: lit(0.14), ol: 1 });
    S.poly([[18.5, 31], [65.5, 31], [42, 8.5]], { m: 'plaster', l: 0.5, tex: plasterTex, g: lit(0.14), ol: 1 });
    /* chimney, behind the right roof slope */
    S.rect(54.5, 7, 6.5, 17, { m: 'stone', l: 0.5, tex: stoneTex, g: lit(0.3), ol: 1 });
    S.rect(53.5, 5.5, 8.5, 2, { m: 'stone', l: 0.72, ol: 1 });
    /* roof: the overhang either side of the gable, shingle ends catching the moon */
    S.poly([[11.5, 34.5], [42, 3.2], [42, 7.4], [17.8, 32.4]], { m: 'slate', l: 0.36, tex: slateTex, bev: 0.18, ol: 1 });
    S.poly([[72.5, 34.5], [42, 3.2], [42, 7.4], [66.2, 32.4]], { m: 'slate', l: 0.62, tex: slateTex, bev: 0.18, ol: 1 });
    S.seg(12.4, 33.4, 42, 4.4, 0.75, 0.75, { m: 'timber', l: 0.55, on: 'any' });
    S.seg(71.6, 33.4, 42, 4.4, 0.75, 0.75, { m: 'timber', l: 0.85, on: 'any' });
    /* timber frame */
    var tb = function (x, y, w, h, l) { S.rect(x, y, w, h, { m: 'timber', l: l == null ? 0.45 : l, g: lit(0.2), ol: 1 }); };
    tb(21, 44.5, 2, 15.5); tb(61, 44.5, 2, 15.5); tb(18, 42.6, 48, 2.4, 0.55); tb(21, 58.4, 42, 1.6, 0.3);
    tb(18.5, 30.2, 47, 1.8, 0.55); tb(18.5, 30.5, 2, 12.5); tb(63.5, 30.5, 2, 12.5); tb(36.2, 30.5, 1.6, 12.5); tb(46.2, 30.5, 1.6, 12.5);
    S.seg(22, 42.2, 27.4, 32.6, 0.7, 0.7, { m: 'timber', l: 0.45, ol: 1 }); S.seg(62, 42.2, 56.6, 32.6, 0.7, 0.7, { m: 'timber', l: 0.55, ol: 1 });
    S.seg(42, 9.5, 42, 30.4, 0.8, 0.8, { m: 'timber', l: 0.5, ol: 1 });
    S.seg(42, 16.5, 29, 30.2, 0.7, 0.7, { m: 'timber', l: 0.45, ol: 1 }); S.seg(42, 16.5, 55, 30.2, 0.7, 0.7, { m: 'timber', l: 0.6, ol: 1 });
    S.seg(24.4, 26.4, 59.6, 26.4, 0.7, 0.7, { m: 'timber', l: 0.5, ol: 1 });
    /* door frame and porch hood */
    tb(35.8, 47.2, 1.8, 12.8); tb(46.4, 47.2, 1.8, 12.8); tb(35.4, 46.4, 13.2, 1.6, 0.6);
    S.poly([[33.5, 46.8], [42, 43.4], [50.5, 46.8], [49.8, 47.8], [34.2, 47.8]], { m: 'slate', l: 0.5, bev: 0.2, ol: 1 });
    /* windows: frame, warm glass, mullions */
    var wins = [[25, 48.5, 8, 7], [51, 48.5, 8, 7], [23.5, 33.6, 7, 7], [53.5, 33.6, 7, 7], [39, 34.4, 5, 6]];
    wins.forEach(function (w) {
      S.rect(w[0] - 1, w[1] - 1, w[2] + 2, w[3] + 2, { m: 'timber', l: 0.4, ol: 1 });
      S.rect(w[0], w[1], w[2], w[3], { m: 'win', l: 0.62, g: function (x, y) { return -0.4 * (y - w[1]) / w[3] + 0.15 - 0.12 * Math.abs(x - w[0] - w[2] / 2) / w[2]; }, noOl: 1 });
      S.rect(w[0] + Math.floor(w[2] / 2), w[1], 1, w[3], { m: 'timber', l: 0.3, noOl: 1 });
      S.rect(w[0], w[1] + Math.floor(w[3] / 2), w[2], 1, { m: 'timber', l: 0.3, noOl: 1 });
      S.rect(w[0] - 1.4, w[1] + w[3] + 0.6, w[2] + 2.8, 1.2, { m: 'timber', l: 0.7, ol: 1 });
    });
    S.rect(38.5, 16.5, 7, 7, { m: 'timber', l: 0.45, ol: 1 });
    S.rect(39.5, 17.5, 5, 5, { m: 'win', l: 0.5, g: function (x, y) { return -0.3 * (y - 17.5) / 5; }, noOl: 1 });
    S.rect(41.5, 17.5, 1, 5, { m: 'timber', l: 0.3, noOl: 1 }); S.rect(39.5, 19.5, 5, 1, { m: 'timber', l: 0.3, noOl: 1 });
    /* lantern on a hook right of the door */
    S.seg(49.2, 46.5, 51.4, 46.5, 0.5, 0.5, { m: 'timber', l: 0.4, ol: 1 });
    S.rect(50.2, 47.2, 2.6, 3.4, { m: 'win', l: 0.9, ol: 1 });
    /* the sign's bracket, off the left corner post */
    S.seg(65.8, 37.6, 78.4, 37.6, 0.75, 0.75, { m: 'timber', l: 0.6, ol: 1 });
    S.seg(65.8, 41.8, 71.8, 37.8, 0.5, 0.5, { m: 'timber', l: 0.5, ol: 1 });
    var idx = S.render({ dz: 2.4, map: null })[0];
    /* warm spill: plaster and timber near the lit glass take the firelight (dithered falloff) */
    var src = [];
    wins.forEach(function (w) { src.push([w[0] + w[2] / 2, w[1] + w[3] / 2, 9]); });
    src.push([42, 20.5, 6], [51.5, 49, 8], [42, 55, 7]);
    var warmOf = {}, plaster = RAMP_SRC.plaster.map(pc), timber = RAMP_SRC.timber.map(pc);
    var wp = ['lt1', 'lt2', 'lt3', 'lt4'].map(pc), wt = ['k1', 'lt1', 'lt2', 'e1'].map(pc);
    plaster.forEach(function (c, i) { warmOf[c] = [wp, i]; });
    timber.forEach(function (c, i) { if (!warmOf[c]) warmOf[c] = [wt, i]; });
    var winPx = [];
    var winSet = RAMP_SRC.win.map(pc);
    for (var y = 0; y < TH; y++) for (var x = 0; x < TW; x++) {
      var i = y * TW + x, c = idx[i]; if (c < 0) continue;
      if (winSet.indexOf(c) >= 0 && S.rp[i] === RID.win) { winPx.push(i); continue; }
      var wo = warmOf[c]; if (!wo) continue;
      var best = 0;
      for (var s = 0; s < src.length; s++) { var d = Math.sqrt((x + 0.5 - src[s][0]) * (x + 0.5 - src[s][0]) + (y + 0.5 - src[s][1]) * (y + 0.5 - src[s][1])); best = Math.max(best, 1 - d / src[s][2]); }
      if (best > 0 && best * 1.25 > bay(x, y)) idx[i] = wo[0][clamp(Math.round(wo[1] * 0.5 + best * 2.6), 0, wo[0].length - 1)];
    }
    return { idx: idx, win: winPx, rp: S.rp, lm: S.lm };
  }

  /* the hanging sign in five swing poses: a board with a gilded stump */
  var SIGN = null;
  function signFrames() {
    if (SIGN) return SIGN;
    SIGN = [-0.2, -0.1, 0, 0.1, 0.2].map(function (a) {
      var S = new Spr(16, 14), px0 = 8, py0 = 0.5, T = xf(px0, py0, a);
      var ch1 = T(-3.6, 3.4), ch2 = T(3.6, 3.4);
      S.seg(px0 - 3.2, 0.5, ch1[0], ch1[1], 0.4, 0.4, { m: 'iron', l: 0.3 }); S.seg(px0 + 3.2, 0.5, ch2[0], ch2[1], 0.4, 0.4, { m: 'iron', l: 0.4 });
      var board = mapPts(T, [[-5.6, 3.2], [5.6, 3.2], [5.6, 11.2], [-5.6, 11.2]]);
      S.poly(board, { m: 'timber', l: 0.62, bev: 0.25, ol: 1, tex: function (x, y) { return (y % 3 === 0) ? -0.14 : 0; } });
      var stump = mapPts(T, [[-2.6, 9.6], [-2, 6], [-2.6, 5.3], [2.6, 5.3], [2, 6], [2.6, 9.6]]);
      S.poly(stump, { m: 'gold', l: 0.5, on: 'any' });
      var tp1 = T(-1.8, 5.6), tp2 = T(1.8, 5.6); S.seg(tp1[0], tp1[1], tp2[0], tp2[1], 0.45, 0.45, { m: 'gold', l: 0.9, on: 'any' });
      return S.render({ dz: 2.4 })[0];
    });
    return SIGN;
  }

  function buildScene(portrait) {
    var W = portrait ? 108 : 192, H = portrait ? 192 : 108;
    var hz = portrait ? 112 : 70;                            /* horizon */
    var door = portrait ? [54, 126] : [104, 84];             /* door base, where the boardwalk begins */
    var moon = portrait ? [80, 26, 7] : [160, 20, 7];
    var tav = TAV || (TAV = drawTavern());
    var ox = door[0] - TDX, oy = door[1] - TDY;
    var bg = new Int16Array(W * H), x, y, i;
    var sky = ['m0', 'm1', 'm2', 'm3'].map(pc);
    /* sky with a moon halo, stars, cloud bands */
    for (y = 0; y < hz; y++) for (x = 0; x < W; x++) {
      var dm = Math.sqrt((x - moon[0]) * (x - moon[0]) + (y - moon[1]) * (y - moon[1]));
      var v = Math.pow(y / hz, 1.3) * 1.7 + Math.max(0, 1 - dm / (portrait ? 30 : 36)) * 1.5;
      var lv = Math.floor(v), f = (v - lv - 0.5) * 3.2 + 0.5;
      if (f > bay(x, y)) lv++;
      bg[y * W + x] = sky[clamp(lv, 0, 3)];
      if (dm > 12 && y < hz * 0.62 && hash(x, y, 61) > 0.9972) bg[y * W + x] = hash(x, y, 62) > 0.6 ? pc('m4') : pc('m3');
    }
    for (y = 0; y < H; y++) for (x = 0; x < W; x++) {
      var dmn = Math.sqrt((x - moon[0]) * (x - moon[0]) + (y - moon[1]) * (y - moon[1]));
      if (dmn <= moon[2]) { var mx = (x - moon[0]) / moon[2], my = (y - moon[1]) / moon[2]; bg[y * W + x] = pc(mx + my < -0.6 ? 'm5' : (hash(x, y, 63) > 0.84 || (mx > 0.3 && my > 0.1)) ? 'm3' : 'm4'); }
    }
    /* clouds: flat-bottomed banks of puffs, lit on top by the moon */
    var puffs = [], rs = 17;
    function rnd() { rs = (rs * 16807) % 2147483647; return rs / 2147483647; }
    var banks = portrait ? [[18, 0.2, 5], [60, 0.75, 4], [92, 0.3, 4]] : [[16, 0.25, 7], [44, 0.7, 6], [58, 0.15, 4]];
    banks.forEach(function (bk) {
      var cxB = bk[1] * W, span = W * (portrait ? 0.9 : 0.55);
      for (var k = 0; k < bk[2]; k++) {
        var px = cxB + (rnd() - 0.5) * span, rx = 5 + rnd() * (portrait ? 8 : 11), ry = 2.5 + rnd() * 3.5;
        puffs.push([px, bk[0] - ry * 0.4 - rnd() * 2, rx, ry, bk[0] + 1.5]);
      }
    });
    var cloudAt = function (xx, yy) {
      for (var k = 0; k < puffs.length; k++) { var pf = puffs[k]; if (yy > pf[4]) continue; var ax = (xx - pf[0]) / pf[2], ay = (yy - pf[1]) / pf[3]; if (ax * ax + ay * ay <= 1) return true; }
      return false;
    };
    for (y = 1; y < hz - 4; y++) for (x = 0; x < W; x++) {
      if (!cloudAt(x + 0.5, y + 0.5)) continue;
      var dmc = Math.sqrt((x - moon[0]) * (x - moon[0]) + (y - moon[1]) * (y - moon[1]));
      var t1 = !cloudAt(x + 0.5, y - 0.5), t2 = !cloudAt(x + 0.5, y - 1.5), bot = !cloudAt(x + 0.5, y + 1.5);
      var col = t1 ? (dmc < 34 ? 'm4' : 'm3') : t2 ? (dmc < 40 ? 'm3' : 'm2') : bot ? 'm0' : (dmc < 26 ? 'm2' : 'm1');
      if (!t1 && !t2 && !bot && dmc < 40 && hash(x, y, 68) > 0.9) col = 'm2';
      bg[y * W + x] = pc(col);
    }
    /* far bog line: low scrub and dead trees in silhouette */
    for (x = 0; x < W; x++) {
      var top = hz - 1 - Math.round(vnoise(x * 0.12, 0, 66) * 3 + vnoise(x * 0.5, 1, 67) * 1.2);
      for (y = top; y < hz; y++) bg[y * W + x] = pc('m1');
    }
    var trees = portrait ? [[10, 16], [26, 9], [94, 13], [100, 7]] : [[14, 18], [30, 11], [48, 8], [150, 12], [172, 17], [185, 9]];
    trees.forEach(function (t, k) {
      var tx = t[0], th = t[1];
      for (var yy = 0; yy < th; yy++) { var xx = tx + Math.round(Math.sin(yy * 0.4 + k) * 0.6); bg[(hz - 1 - yy) * W + xx] = pc('m1'); if (yy < th * 0.4) bg[(hz - 1 - yy) * W + xx + 1] = pc('m1'); }
      for (var b = 0; b < 3; b++) {
        var by = hz - 1 - Math.round(th * (0.45 + b * 0.18)), dir = (b + k) % 2 ? 1 : -1, len = 3 + ((k + b) % 3);
        for (var s = 1; s <= len; s++) { var bxx = tx + dir * s, byy = by - Math.round(s * 0.6); if (bxx >= 0 && bxx < W && byy >= 0) bg[byy * W + bxx] = pc('m1'); }
      }
    });
    /* black water: ripples, the moon's broken path */
    for (y = hz; y < H; y++) {
      var dd = (y - hz) / (H - hz);
      for (x = 0; x < W; x++) {
        i = y * W + x; var c = pc(dd < 0.08 ? 'm1' : 'm0');
        var rip = hash(Math.floor(x / (3 + dd * 6)), y, 71);
        if (rip > 0.93 - dd * 0.05) c = pc('m1');
        var mw = 1.5 + (y - hz) * 0.22;
        if (Math.abs(x - moon[0]) < mw && hash(Math.floor(x / 2), y, 72) > 0.45 && (y % 2 === 0)) c = pc(Math.abs(x - moon[0]) < mw * 0.4 ? 'm4' : 'm3');
        bg[i] = c;
      }
    }
    /* the tavern's mound */
    var mrx = portrait ? 34 : 40;
    for (y = door[1] - 4; y < door[1] + 7; y++) for (x = door[0] - mrx - 2; x < door[0] + mrx + 2; x++) {
      if (x < 0 || x >= W || y >= H) continue;
      var e = Math.pow((x - door[0]) / mrx, 2) + Math.pow((y - door[1] - 1) / 5, 2);
      if (e <= 1) { var top2 = Math.pow((x - door[0]) / mrx, 2) + Math.pow((y - 1 - door[1] - 1) / 5, 2) > 1; bg[y * W + x] = pc(top2 ? 'm2' : (hash(x, y, 73) > 0.7 ? 'k1' : 'ol')); }
    }
    /* reflections of the lit windows, laid down before the tavern so its footing covers the top */
    var refl = [];
    tav.win.forEach(function (ti) { var tx = ti % TW + ox, ty = (ti / TW | 0) + oy; if (ty > oy + 30) refl.push(tx); });
    /* the tavern itself, leaning a little */
    var lean = 0.035;
    for (y = 0; y < TH; y++) for (x = 0; x < TW; x++) {
      var ci = tav.idx[y * TW + x]; if (ci < 0) continue;
      var gx = x + ox + Math.round((TDY - y) * lean), gy = y + oy;
      if (gx >= 0 && gx < W && gy >= 0 && gy < H) bg[gy * W + gx] = ci;
    }
    var winG = tav.win.map(function (ti) { var tx = ti % TW, ty = ti / TW | 0; return [tx + ox + Math.round((TDY - ty) * lean), ty + oy, tav.idx[ti]]; });
    /* the boardwalk to the door, planks widening toward us, posts in the water */
    var bw0 = 4.5, bw1 = portrait ? 22 : 17;
    var bwTest = function (xx, yy) { var t = (yy - door[1]) / (H - door[1]); var hw = bw0 + (bw1 - bw0) * t; return Math.abs(xx + 0.5 - door[0] - t * (portrait ? 0 : -6)) <= hw; };
    for (y = door[1] + 1; y < H; y++) {
      var tt = (y - door[1]) / (H - door[1]), plank = Math.floor(Math.pow(y - door[1], 0.72) * 1.3), plankPrev = Math.floor(Math.pow(y - 1 - door[1], 0.72) * 1.3);
      for (x = 0; x < W; x++) {
        if (!bwTest(x, y)) continue;
        var cx0 = door[0] + tt * (portrait ? 0 : -6), hw2 = bw0 + (bw1 - bw0) * tt, u = (x + 0.5 - cx0) / hw2;
        var col = plank !== plankPrev ? 'ol' : (u > 0.45 ? 'lt2' : u < -0.6 ? 'k1' : (hash(x, plank, 74) > 0.85 ? 'k1' : 'lt1'));
        if (plank !== plankPrev && Math.abs(u) > 0.92) col = 'k1';
        if (col === 'lt1' && u > 0.1 && bay(x, y) > 0.7) col = 'lt2';
        bg[y * W + x] = pc(col);
      }
      if (plank % 3 === 0 && plank !== plankPrev) {
        [-1, 1].forEach(function (sd) { var px = Math.round(cx0e(y) + sd * (bw0 + (bw1 - bw0) * tt + 1)); for (var k = -3; k < 2; k++) if (y + k > door[1] && y + k < H && px >= 0 && px < W) bg[(y + k) * W + px] = pc(k === -3 ? 'lt1' : 'k1'); });
      }
    }
    function cx0e(yy) { return door[0] + ((yy - door[1]) / (H - door[1])) * (portrait ? 0 : -6); }
    /* reeds and cattails in the corners */
    var reeds = portrait ? [[2, 24], [7, 18], [12, 28], [96, 20], [101, 26], [106, 16]] : [[3, 18], [8, 12], [13, 22], [20, 14], [168, 16], [175, 22], [181, 13], [188, 19]];
    reeds.forEach(function (r, k) {
      var rx = r[0], rh = r[1];
      for (var s = 0; s < 3; s++) {
        var bx = rx + s * 2 - 2, hgt = rh - s * 3 - (k % 2) * 2;
        for (var yy = 0; yy < hgt; yy++) { var xx = bx + Math.round(Math.sin(yy * 0.12 + k + s) * (yy / hgt) * 2), py = H - 1 - yy; if (xx >= 0 && xx < W && py >= 0) bg[py * W + xx] = pc(yy > hgt - 3 && s === 1 ? 'm2' : 'ol'); }
        if (s === 1) { var topx = bx + Math.round(Math.sin((hgt - 5) * 0.12 + k + s) * 2); for (var q = 0; q < 3; q++) { var ty2 = H - hgt + 2 + q; if (topx >= 0 && topx < W && ty2 >= 0) { bg[ty2 * W + topx] = pc(q === 0 ? 'lt1' : 'k1'); if (topx + 1 < W) bg[ty2 * W + topx + 1] = pc('ol'); } } }
      }
    });
    var tmask = new Uint8Array(W * H);
    for (y = 0; y < TH; y++) for (x = 0; x < TW; x++) { if (tav.idx[y * TW + x] < 0) continue; var mx2 = x + ox + Math.round((TDY - y) * lean), my2 = y + oy; if (mx2 >= 0 && mx2 < W && my2 >= 0 && my2 < H) tmask[my2 * W + mx2] = 1; }
    var u32 = new Uint32Array(W * H);
    for (i = 0; i < W * H; i++) u32[i] = P32[bg[i]];
    var doorG = { x0: 38 + ox + Math.round((TDY - 55) * lean), x1: 46 + ox + Math.round((TDY - 55) * lean), y0: 48 + oy, y1: 60 + oy };
    var sign = [ox + 72.4 + Math.round((TDY - 38) * lean), oy + 38];
    var chim = [ox + 57.8 + Math.round((TDY - 5) * lean), oy + 4.5];
    return { tmask: tmask, W: W, H: H, hz: hz, door: door, doorG: doorG, moon: moon, bg: u32, idx: bg, win: winG, refl: refl, sign: sign, chim: chim, bwTest: bwTest, portrait: portrait,
      lantern: [ox + 51 + Math.round((TDY - 48) * lean), oy + 48] };
  }

  function mountScene(el, kind, opts) {
    opts = opts || {};
    var reduced = opts.reduced != null ? !!opts.reduced : prefersReduced();
    var pmode = opts.portrait == null ? 'auto' : opts.portrait;
    var coverMode = el.clientHeight > 0;           /* a sized box is filled (cover); otherwise the scene sets its own height */
    function wantPortrait() {
      if (pmode === true || pmode === false) return pmode;
      var w = el.clientWidth, h = el.clientHeight;
      if (coverMode && w && h) return w / h < 0.95;
      return (root.innerWidth || 800) < (root.innerHeight || 600) * 0.9;
    }
    var sc = buildScene(wantPortrait());
    var c = mk(sc.W, sc.H), x = ctx2d(c), nat = mk(sc.W, sc.H), nx = ctx2d(nat);
    var im = nx.createImageData(sc.W, sc.H), buf = new Uint32Array(im.data.buffer);
    c.className = 'qoart-scene';
    c.setAttribute('role', 'img');
    c.setAttribute('aria-label', 'A crooked two-storey tavern alone in a black bog at night, rain falling, its windows lit warm');
    c.style.display = 'block'; c.style.width = '100%';
    pixelated(c);
    el.appendChild(c);
    var alive = true, rafId = 0, last = 0, t0 = now(), openT = -1, openRes = null, visible = true, opened = false, dbgOpen = null;
    /* rain, fireflies and smoke state */
    var drops = [], flies = [], puffs = [], splash = [];
    function resetDrops() {
      drops = [];
      var n = Math.round(sc.W * sc.H / 240);
      for (var k = 0; k < n; k++) drops.push({ x: Math.random() * (sc.W + 40), y: Math.random() * sc.H, v: 170 + Math.random() * 90, l: 4 + (Math.random() < 0.35 ? 2 : 0), b: Math.random() < 0.14, stop: sc.hz + Math.random() * (sc.H - sc.hz) });
      flies = [];
      var fz = sc.portrait ? [[12, 160], [20, 140], [92, 150], [100, 172], [30, 176], [84, 132], [14, 120], [96, 118]] : [[20, 88], [34, 96], [12, 76], [160, 92], [176, 84], [184, 100], [58, 90], [146, 80], [124, 100]];
      fz.forEach(function (p, k) { flies.push({ x: p[0], y: p[1], ph: k * 1.7, sp: 0.5 + (k % 3) * 0.23, r: 3 + (k % 4) }); });
    }
    resetDrops();
    function size() {
      var w = el.clientWidth || sc.W * 3, h = el.clientHeight;
      var p = wantPortrait();
      if (p !== sc.portrait) { sc = buildScene(p); c.width = sc.W; c.height = sc.H; nat.width = sc.W; nat.height = sc.H; im = nx.createImageData(sc.W, sc.H); buf = new Uint32Array(im.data.buffer); resetDrops(); }
      var scale = coverMode && h > 0 ? Math.max(w / sc.W, h / sc.H) : w / sc.W;
      var k = clamp(Math.round(scale * dprCap()), 1, 4);
      if (c.width !== sc.W * k || c.height !== sc.H * k) { c.width = sc.W * k; c.height = sc.H * k; noSmooth(x); }
      if (coverMode) { c.style.height = '100%'; c.style.objectFit = 'cover'; } else { c.style.height = 'auto'; c.style.objectFit = ''; }
      render(now());
    }
    function put(px, py, col) { px |= 0; py |= 0; if (px >= 0 && py >= 0 && px < sc.W && py < sc.H) buf[py * sc.W + px] = col; }
    function render(t) {
      var W = sc.W, H = sc.H, s = (t - t0) / 1000, k;
      buf.set(sc.bg);
      /* window flicker: each pane breathes on its own, firelight never steady */
      var wr = RAMP_SRC.win.map(pc);
      for (k = 0; k < sc.win.length; k++) {
        var wv = sc.win[k], lvl = wr.indexOf(wv[2]);
        var fl = vnoise(s * (reduced ? 0 : 2.2) + (wv[0] >> 3) * 3.1, (wv[1] >> 3) * 1.7, 81);
        var nl = clamp(lvl + (fl > 0.68 ? 1 : fl < 0.3 ? -1 : 0), 0, wr.length - 1);
        put(wv[0], wv[1], P32[wr[nl]]);
      }
      /* their reflections, shivering on the water */
      var rr = ['e1', 'e2', 'e3'].map(C);
      for (k = 0; k < sc.refl.length; k += 3) {
        var rx = sc.refl[k];
        for (var ry = sc.door[1] + 8; ry < Math.min(H, sc.door[1] + 8 + 12); ry += 2) {
          var jit = Math.round((vnoise(ry * 0.5, s * (reduced ? 0 : 3) + k, 82) - 0.5) * 4);
          if (!sc.bwTest(rx + jit, ry) && hash(rx, ry + Math.floor(s * 6), 83) > 0.55 && !sc.tmask[ry * W + rx + jit]) put(rx + jit, ry, rr[(ry - sc.door[1]) < 10 && hash(rx, ry, 86) > 0.6 ? 1 : 0]);
        }
      }
      /* the door, opening */
      var op = dbgOpen != null ? dbgOpen : openT < 0 ? (opened ? 1 : 0) : clamp((t - openT) / 1200, 0, 1);
      drawDoor(op, s);
      /* the sign swings in the weather */
      var sf = signFrames(), sa = reduced ? 2 : clamp(Math.round(2 + Math.sin(s * 1.9) * 1.6 + Math.sin(s * 0.7) * 0.5), 0, 4), sb = sf[sa];
      for (var sy = 0; sy < 14; sy++) for (var sx = 0; sx < 16; sx++) { var v = sb[sy * 16 + sx]; if (v >= 0) put(Math.round(sc.sign[0]) + sx - 8, Math.round(sc.sign[1]) + sy, P32[v]); }
      /* chimney smoke */
      if (!reduced) {
        if (puffs.length < 9 && Math.random() < 0.06) puffs.push({ x: sc.chim[0], y: sc.chim[1], t: s, r: 1 });
        for (k = puffs.length - 1; k >= 0; k--) {
          var pf = puffs[k], age = s - pf.t;
          if (age > 5) { puffs.splice(k, 1); continue; }
          var pxc = sc.chim[0] + age * 4 + Math.sin(age * 1.3 + k) * 1.5, pyc = sc.chim[1] - age * 5.5, pr = 1 + age * 0.7, dens = 0.75 * (1 - age / 5);
          for (var yy = -pr; yy <= pr; yy++) for (var xx = -pr; xx <= pr; xx++) {
            if (xx * xx + yy * yy > pr * pr) continue;
            var gx = Math.round(pxc + xx), gy = Math.round(pyc + yy);
            if (gx < 0 || gy < 0 || gx >= W || gy >= H) continue;
            if (dens * (1 - (xx * xx + yy * yy) / (pr * pr + 0.01) * 0.6) > bay(gx, gy)) put(gx, gy, C(age < 1.2 ? 'm3' : 'm2'));
          }
        }
      }
      /* mist over the water, drifting */
      var bands = sc.portrait ? [[sc.hz - 4, 12, 3], [sc.door[1] + 14, 16, -2.2], [H - 20, 14, 1.6]] : [[sc.hz - 3, 10, 3], [sc.door[1] + 6, 12, -2], [H - 14, 10, 1.4]];
      var near = op > 0.2 ? op : 0;
      bands.forEach(function (bd, bi) {
        for (var my = Math.max(0, bd[0]); my < Math.min(H, bd[0] + bd[1]); my++) {
          var prof = Math.sin(Math.PI * (my - bd[0]) / bd[1]);
          for (var mx = 0; mx < W; mx++) {
            var mxs = mx + s * bd[2] * (reduced ? 0 : 1), n = vnoise(mxs * 0.045, my * 0.28 + bi * 7, 84 + bi) * 0.65 + vnoise(mxs * 0.16, my * 0.6, 87 + bi) * 0.35;
            var dens = (n - 0.44) * 2.6 * prof;
            if (sc.tmask[my * W + mx] && my < sc.door[1] - 2) continue;
            if (dens > bay(mx, my) * 0.9 + 0.08) {
              var dd = Math.abs(mx - sc.door[0]) + Math.abs(my - sc.door[1]) * 0.6;
              put(mx, my, near && dd < 30 * near ? C(dens > 0.7 ? 'e2' : 'e1') : C(dens > 0.62 ? 'm3' : 'm2'));
            }
          }
        }
      });
      /* fireflies */
      for (k = 0; k < flies.length; k++) {
        var f = flies[k], fx = f.x + Math.sin(s * f.sp + f.ph) * f.r, fy = f.y + Math.sin(s * f.sp * 1.3 + f.ph * 2) * f.r * 0.6;
        var glow = reduced ? 0.8 : Math.max(0, Math.sin(s * (1.1 + f.sp) + f.ph * 3));
        if (glow > 0.25) { put(fx, fy, C(glow > 0.7 ? 'g5' : 'v3')); if (glow > 0.75) { put(fx - 1, fy, C('v2')); put(fx + 1, fy, C('v2')); put(fx, fy - 1, C('v2')); put(fx, fy + 1, C('v2')); } }
      }
      /* rain */
      var rc = C('m3'), rc2 = C('m2'), rb = C('m4');
      for (k = 0; k < drops.length; k++) {
        var d = drops[k];
        for (var q = 0; q < d.l; q++) put(d.x + q * 0.45, d.y - q, q === 0 ? (d.b ? rb : rc) : (q < 2 && d.b ? rc : rc2));
      }
      for (k = splash.length - 1; k >= 0; k--) { var sp = splash[k]; put(sp.x - 1, sp.y, rc2); put(sp.x + 1, sp.y, rc2); if (sp.a < 0.05) put(sp.x, sp.y - 1, rc); }
      nx.putImageData(im, 0, 0);
      x.clearRect(0, 0, c.width, c.height);
      x.drawImage(nat, 0, 0, c.width, c.height);
    }
    function drawDoor(op, s) {
      var d = sc.doorG, W = sc.W, H = sc.H, dw = d.x1 - d.x0, dh = d.y1 - d.y0, e = easeOut(clamp(op / 0.55, 0, 1));
      var inner = ['e2', 'e3', 'e4', 'g4', 'g5', 'hw'].map(C), plank = ['k1', 'lt1', 'lt2', 'lt3'].map(C);
      var panelW = op > 0 ? Math.max(1, Math.round(dw * (1 - e))) : dw;
      for (var yy = d.y0; yy < d.y1; yy++) for (var xx = d.x0; xx < d.x1; xx++) {
        var arch = yy === d.y0 && (xx === d.x0 || xx === d.x1 - 1);
        if (arch) continue;
        var u = xx - d.x0, v = (yy - d.y0) / dh;
        if (u < panelW && op < 1) {
          var ed = u === panelW - 1 && op > 0;
          put(xx, yy, ed ? plank[3] : (u % 3 === 2 ? plank[0] : (u === 0 ? plank[1] : plank[op > 0 ? 2 : 1])));
          if ((yy === d.y0 + 3 || yy === d.y1 - 3) && u < panelW - 1) put(xx, yy, C('i1'));
        } else {
          var lv = 2 + v * 2.4 - Math.abs(u - dw / 2) / dw * 1.6 + op;
          var li = Math.floor(lv), fr2 = lv - li; if (fr2 > bay(xx, yy)) li++;
          put(xx, yy, inner[clamp(li, 0, inner.length - 1)]);
        }
      }
      if (op <= 0) { for (var lx = d.x0 + 1; lx < d.x1 - 1; lx++) put(lx, d.y1, C(hash(lx, 0, Math.floor(s * 4)) > 0.4 ? 'e2' : 'e1')); return; }
      /* the flood: warm light pouring out onto the boardwalk and the water */
      var f = easeOut(clamp((op - 0.12) / 0.88, 0, 1)), reach = f * (H - d.y1) * 0.92, ramp = ['e1', 'e2', 'e3', 'g4', 'g5'].map(C);
      for (var y = d.y1; y < Math.min(H, d.y1 + reach); y++) {
        var t = (y - d.y1) / Math.max(1, reach), hw = dw * 0.5 + (y - d.y1) * (sc.portrait ? 0.34 : 0.62), cxl = (d.x0 + d.x1) / 2 + (y - d.y1) * (sc.portrait ? 0 : -0.1);
        for (var x2 = Math.floor(cxl - hw); x2 <= Math.ceil(cxl + hw); x2++) {
          if (x2 < 0 || x2 >= W) continue;
          var side = Math.abs(x2 + 0.5 - cxl) / hw; if (side > 1) continue;
          var onWalk = sc.bwTest(x2, y);
          var I = Math.pow(1 - t, 1.6) * (1 - side * side * 0.85) * f * (onWalk ? 1.3 : 0.5);
          if (!onWalk && (y % 2) && hash(x2 >> 1, y, 85) < 0.5) continue;
          var lvF = I * 5, lvI = Math.floor(lvF), frF = (lvF - lvI - 0.5) * 2.4 + 0.5; if (frF > bay(x2, y)) lvI++;
          if (lvI > 0) put(x2, y, ramp[clamp(lvI - 1, 0, 4)]);
        }
      }
      /* a halo of warmth round the doorway */
      for (var hy = d.y0 - 5; hy < d.y1 + 1; hy++) for (var hx = d.x0 - 5; hx < d.x1 + 5; hx++) {
        if (hx >= d.x0 && hx < d.x1 && hy >= d.y0 && hy < d.y1) continue;
        var dd = Math.max(d.x0 - hx, hx - d.x1 + 1, d.y0 - hy, 0);
        if (dd > 0 && dd <= 5 && f * (1 - dd / 6) * 0.8 > bay(hx, hy)) {
          var cur = hy >= 0 && hx >= 0 && hx < W && hy < H ? sc.idx[hy * W + hx] : -1;
          if (cur >= 0 && cur !== pc('m0') && cur !== pc('m1')) put(hx, hy, C(dd < 2 ? 'e3' : 'e2'));
        }
      }
    }
    function step(dt) {
      for (var k = 0; k < drops.length; k++) {
        var d = drops[k]; d.y += d.v * dt; d.x -= d.v * 0.45 * dt;
        if (d.y > d.stop) { if (splash.length < 40) splash.push({ x: Math.round(d.x), y: Math.round(d.stop), a: 0 }); d.y = -Math.random() * 20; d.x = Math.random() * (sc.W + 40); d.stop = sc.hz + Math.random() * (sc.H - sc.hz); }
      }
      for (var j = splash.length - 1; j >= 0; j--) { splash[j].a += dt; if (splash[j].a > 0.1) splash.splice(j, 1); }
    }
    function loop(t) {
      rafId = 0;
      if (!alive) return;
      if (doc.hidden || !visible) return;
      if (t - last >= 32) { step(Math.min(0.05, (t - last) / 1000)); last = t; render(t); }
      if (openT >= 0 && t - openT >= 1200) { openT = -1; opened = true; render(t); if (openRes) { var r = openRes; openRes = null; r(); } }
      if (!reduced || openT >= 0) rafId = raf(loop);
    }
    function kick() { if (!rafId && alive) { last = now(); rafId = raf(loop); } }
    function vis() { if (!doc.hidden) kick(); }
    doc.addEventListener('visibilitychange', vis);
    var io = null;
    if (root.IntersectionObserver) { io = new root.IntersectionObserver(function (en) { visible = en[0] ? en[0].isIntersecting : true; if (visible) kick(); }); io.observe(el); }
    var ro = null;
    if (root.ResizeObserver) { ro = new root.ResizeObserver(function () { size(); }); ro.observe(el); }
    else root.addEventListener('resize', size);
    size();
    if (!reduced) kick();
    return {
      canvas: c,
      open: function () {
        return new Promise(function (resolve) {
          try {
            if (opened || !alive) { resolve(); return; }
            if (reduced) { opened = true; render(now()); resolve(); return; }
            if (openT >= 0) { var prev = openRes; openRes = function () { if (prev) prev(); resolve(); }; return; }
            openT = now(); openRes = resolve; kick();
          } catch (e) { warn(e); resolve(); }
        });
      },
      isOpen: function () { return opened; },
      destroy: guard(function () {
        alive = false; if (rafId) caf(rafId);
        doc.removeEventListener('visibilitychange', vis);
        if (io) io.disconnect();
        if (ro) ro.disconnect(); else root.removeEventListener('resize', size);
        if (openRes) { var r = openRes; openRes = null; r(); }
        if (c.parentNode) c.parentNode.removeChild(c);
      }),
      _render: function (openFrac, sec) { dbgOpen = openFrac == null ? null : openFrac; if (sec) for (var q = 0; q < Math.round(sec * 30); q++) step(1 / 30); render(t0 + (sec || 0) * 1000); dbgOpen = null; }
    };
  }
  /* =====================================================================
     4. PIXEL PARTICLES - an overlay drawn on a cell grid (2-4 CSS px per cell),
     six-step colour ramps, alpha in eighths, pooled at 900 cells, rAF only while alive.
     ===================================================================== */
  var PR_SRC = {
    gold: ['hw', 'g5', 'g4', 'g3', 'g2', 'g1'],
    ember: ['g5', 'e4', 'e3', 'e2', 'e1', 'k1'],
    holy: ['hw', 'm5', 'g5', 'g4', 'g3', 'g2'],
    ice: ['hw', 'm5', 'm4', 'm3', 'm2', 'm1'],
    blood: ['e4', 'e3', 'b3', 'b2', 'b1', 'k1'],
    shadow: ['mu', 'hb', 'w3', 'w2', 'w1', 'ol']
  };
  var PR_NAMES = Object.keys(PR_SRC);
  var PR = PR_NAMES.map(function (n) { return PR_SRC[n].map(function (k) { return P32[pc(k)] & 0x00ffffff; }); });
  function rampId(n) { var i = PR_NAMES.indexOf(n); return i < 0 ? 0 : i; }
  var PN = 900;
  var K = { mote: 0, rise: 1, burst: 2, streak: 3, spark: 4 };

  function particles(target, opts) {
    opts = opts || {};
    var cell = clamp(Math.round(+opts.cell || 3), 2, 4);
    var reduced = opts.reduced != null ? !!opts.reduced : prefersReduced();
    var isCanvas = target && target.tagName === 'CANVAS', cv = isCanvas ? target : mk(1, 1), own = !isCanvas;
    if (own) {
      cv.className = 'qoart-particles';
      var st = cv.style; st.position = 'absolute'; st.left = '0'; st.top = '0'; st.width = '100%'; st.height = '100%'; st.pointerEvents = 'none';
      try { if (root.getComputedStyle(target).position === 'static') target.style.position = 'relative'; } catch (e) { /* ignore */ }
      target.appendChild(cv);
    }
    cv.setAttribute('aria-hidden', 'true');
    pixelated(cv);
    var cx = cv.getContext('2d'), Wc = 1, Hc = 1, cssW = 1, cssH = 1, im = null, px = null;
    var P = { x: new Float32Array(PN), y: new Float32Array(PN), vx: new Float32Array(PN), vy: new Float32Array(PN), age: new Float32Array(PN), ttl: new Float32Array(PN),
      del: new Float32Array(PN), ph: new Float32Array(PN), gr: new Float32Array(PN), dr: new Float32Array(PN), rp: new Uint8Array(PN), kd: new Uint8Array(PN), sz: new Uint8Array(PN), on: new Uint8Array(PN), len: new Uint8Array(PN) };
    var top = 0, live = 0, fx = [], cont = [], rafId = 0, last = 0, alive = true;
    function size() {
      var r = cv.getBoundingClientRect ? cv.getBoundingClientRect() : null;
      cssW = Math.max(1, (r && r.width) || cv.clientWidth || (target && target.clientWidth) || 300);
      cssH = Math.max(1, (r && r.height) || cv.clientHeight || (target && target.clientHeight) || 150);
      var w = Math.ceil(cssW / cell), h = Math.ceil(cssH / cell);
      if (w !== Wc || h !== Hc || !im) { Wc = w; Hc = h; cv.width = w; cv.height = h; im = cx.createImageData(w, h); px = new Uint32Array(im.data.buffer); }
    }
    size();
    function slot() {
      if (top < PN) return top++;
      for (var i = 0; i < PN; i++) if (!P.on[i]) return i;
      var oldest = 0; for (var j = 1; j < PN; j++) if (P.age[j] / P.ttl[j] > P.age[oldest] / P.ttl[oldest]) oldest = j;
      return oldest;                                                   /* full: the most spent cell gives way */
    }
    function spawn(kd, x, y, vx, vy, ttl, rp, o) {
      var i = slot(); if (!P.on[i]) live++;
      P.on[i] = 1; P.kd[i] = kd; P.x[i] = x; P.y[i] = y; P.vx[i] = reduced ? 0 : vx; P.vy[i] = reduced ? 0 : vy;
      P.age[i] = 0; P.ttl[i] = reduced ? Math.min(ttl, 0.4) : ttl; P.del[i] = reduced ? 0 : ((o && o.del) || 0); P.rp[i] = rp; P.ph[i] = Math.random() * 6.283;
      P.gr[i] = reduced ? 0 : ((o && o.gr) || 0); P.dr[i] = (o && o.dr) || 0; P.sz[i] = (o && o.sz) || 1; P.len[i] = (o && o.len) || 0;
      return i;
    }
    /* write one cell; alpha snapped to eighths; brighter/more opaque cells win. OFY moves an effect that was
       drawn at a place on the page which has since scrolled (see shift). */
    var OFY = 0;
    function plot(gx, gy, rgb, a) {
      gy += OFY;
      if (gx < 0 || gy < 0 || gx >= Wc || gy >= Hc) return;
      var A = Math.round(clamp(a, 0, 1) * 8); if (A <= 0) return;
      var i = gy * Wc + gx, cur = px[i] >>> 24, na = Math.round(A * 255 / 8);
      if (na < cur) return;
      px[i] = ((na << 24) | rgb) >>> 0;
    }
    function tone(rp, t) { var r = PR[rp]; return r[clamp(Math.floor(t * r.length), 0, r.length - 1)]; }
    function draw() {
      if (!px) return;
      px.fill(0);
      var i, e;
      for (e = 0; e < fx.length; e++) { OFY = Math.round((fx[e].oy || 0) / cell); fx[e].draw(); }
      OFY = 0;
      for (i = 0; i < top; i++) {
        if (!P.on[i] || P.age[i] < P.del[i]) continue;
        var t = (P.age[i] - P.del[i]) / P.ttl[i], kd = P.kd[i], a, col;
        var gx = Math.floor(P.x[i] / cell), gy = Math.floor(P.y[i] / cell);
        if (kd === K.mote) {
          a = Math.min(1, t / 0.18) * Math.min(1, (1 - t) / 0.3) * (0.55 + 0.45 * Math.sin(P.ph[i] + P.age[i] * 5));
          col = tone(P.rp[i], 0.15 + (1 - a) * 0.5);
          plot(gx, gy, col, a);
          if (a > 0.8) { var hc = tone(P.rp[i], 0.55); plot(gx - 1, gy, hc, 0.35); plot(gx + 1, gy, hc, 0.35); plot(gx, gy - 1, hc, 0.35); plot(gx, gy + 1, hc, 0.35); }
        } else if (kd === K.streak) {
          a = Math.min(1, (1 - t) / 0.2);
          var n = P.len[i] || 4, sp = Math.sqrt(P.vx[i] * P.vx[i] + P.vy[i] * P.vy[i]) || 1, ux = P.vx[i] / sp, uy = P.vy[i] / sp;
          for (var q = 0; q < n; q++) plot(Math.floor((P.x[i] - ux * q * cell) / cell), Math.floor((P.y[i] - uy * q * cell) / cell), tone(P.rp[i], 0.2 + q / n * 0.6), a * (0.75 - q / n * 0.55));
        } else {
          a = t < 0.65 ? 1 : Math.max(0, (1 - t) / 0.35);
          col = tone(P.rp[i], Math.pow(t, 0.8) * 0.95);
          plot(gx, gy, col, a);
          if (P.sz[i] > 1) { plot(gx + 1, gy, col, a); plot(gx, gy + 1, col, a); plot(gx + 1, gy + 1, tone(P.rp[i], Math.min(0.99, t + 0.2)), a); }
          if (t < 0.3 && kd !== K.spark) { var hc2 = tone(P.rp[i], 0.45); plot(gx - 1, gy, hc2, 0.25); plot(gx + 1, gy, hc2, 0.25); plot(gx, gy - 1, hc2, 0.25); plot(gx, gy + 1, hc2, 0.25); }
        }
      }
      cx.putImageData(im, 0, 0);
    }
    function step(dt) {
      var i;
      for (i = 0; i < top; i++) {
        if (!P.on[i]) continue;
        P.age[i] += dt;
        if (P.age[i] < P.del[i]) continue;
        var kd = P.kd[i], la = P.age[i] - P.del[i];
        if (kd === K.mote) { P.x[i] += (P.vx[i] + (reduced ? 0 : Math.sin(P.ph[i] + la * 1.6) * 10)) * dt; P.y[i] += P.vy[i] * dt; }
        else if (kd === K.rise) { P.x[i] += (P.vx[i] + (reduced ? 0 : Math.sin(P.ph[i] + la * 4.5) * 16)) * dt; P.y[i] += P.vy[i] * dt; P.vy[i] *= Math.pow(0.55, dt); }
        else {
          if (P.dr[i]) { var k = Math.pow(P.dr[i], dt); P.vx[i] *= k; P.vy[i] *= k; }
          P.vy[i] += P.gr[i] * dt; P.x[i] += P.vx[i] * dt; P.y[i] += P.vy[i] * dt;
        }
        if (la >= P.ttl[i] || (kd === K.streak && P.y[i] > cssH + 20)) { P.on[i] = 0; live--; }
      }
      if (live <= 0) { live = 0; top = 0; }
      for (i = fx.length - 1; i >= 0; i--) if (!fx[i].step(dt)) fx.splice(i, 1);
      for (i = 0; i < cont.length; i++) cont[i].tick(dt);
    }
    function busy() { return live > 0 || fx.length > 0 || cont.length > 0; }
    function loop(t) {
      rafId = 0;
      if (!alive || doc.hidden) return;
      var dt = Math.min(0.05, (t - last) / 1000); last = t;
      var t0 = now();
      step(dt); draw();
      if (now() - t0 > 8 && top > 400) { for (var i = 0; i < top; i += 3) if (P.on[i] && P.kd[i] !== K.streak) { P.on[i] = 0; live--; } }  /* over budget: thin the crowd */
      if (busy()) rafId = raf(loop);
      else { px.fill(0); cx.putImageData(im, 0, 0); }
    }
    function kick() { if (!rafId && alive && !doc.hidden) { last = now(); rafId = raf(loop); } }
    function vis() { if (!doc.hidden && busy()) kick(); }
    doc.addEventListener('visibilitychange', vis);
    var ro = null;
    if (root.ResizeObserver) { ro = new root.ResizeObserver(function () { size(); if (!rafId) draw(); }); ro.observe(own ? target : cv); }
    else root.addEventListener('resize', size);

    /* ---- effect objects drawn procedurally each frame ---- */
    function ringFx(x, y, rp, maxR, delay) {
      var age = -delay, dur = reduced ? 0.35 : 0.75;
      return {
        step: function (dt) { age += dt; return age < dur; },
        draw: function () {
          if (age < 0) return;
          var t = age / dur, r = (reduced ? maxR : maxR * easeOut(t)) / cell, c0 = x / cell, c1 = y / cell, a = 1 - t * t;
          var steps = Math.max(12, Math.round(r * 6.3));
          for (var k = 0; k < steps; k++) {
            var an = k / steps * 6.2832, gx = Math.floor(c0 + Math.cos(an) * r), gy = Math.floor(c1 + Math.sin(an) * r);
            if (t > 0.35 && bay(gx, gy) > (1 - t) * 1.5) continue;
            plot(gx, gy, tone(rp, t * 0.7), a);
            if (t < 0.4) plot(Math.floor(c0 + Math.cos(an) * (r - 1)), Math.floor(c1 + Math.sin(an) * (r - 1)), tone(rp, 0.5 + t), a * 0.6);
          }
        }
      };
    }
    function crackFx(x, y, rp, reach, arms, ttl) {
      var segs = [], speed = reduced ? 1e6 : 900, age = 0;
      function arm(x0, y0, ang, len, born, depth) {
        var px0 = x0, py0 = y0, n = Math.max(3, Math.round(len / 14)), acc = born;
        for (var s = 0; s < n; s++) {
          ang += (Math.random() - 0.5) * 0.9;
          var l = len / n * (0.7 + Math.random() * 0.6), px1 = px0 + Math.cos(ang) * l, py1 = py0 + Math.sin(ang) * l;
          segs.push([px0, py0, px1, py1, acc, acc + l / speed, depth]);
          acc += l / speed;
          if (depth < 2 && Math.random() < 0.28) arm(px1, py1, ang + (Math.random() < 0.5 ? -1 : 1) * (0.5 + Math.random() * 0.6), len * 0.45, acc, depth + 1);
          px0 = px1; py0 = py1;
        }
      }
      for (var a = 0; a < arms; a++) arm(x, y, a / arms * 6.2832 + Math.random() * 0.6, reach * (0.7 + Math.random() * 0.5), 0, 0);
      return {
        step: function (dt) { age += dt; return age < ttl; },
        draw: function () {
          var fade = age > ttl * 0.6 ? 1 - (age - ttl * 0.6) / (ttl * 0.4) : 1;
          for (var s = 0; s < segs.length; s++) {
            var g = segs[s]; if (age < g[4]) continue;
            var f = clamp((age - g[4]) / Math.max(1e-4, g[5] - g[4]), 0, 1), xe = lerp(g[0], g[2], f), ye = lerp(g[1], g[3], f);
            var ax = Math.floor(g[0] / cell), ay = Math.floor(g[1] / cell), bx = Math.floor(xe / cell), by = Math.floor(ye / cell);
            var n = Math.max(Math.abs(bx - ax), Math.abs(by - ay)) || 1, fresh = age - g[5] < 0.12;
            for (var k = 0; k <= n; k++) {
              var gx = Math.round(ax + (bx - ax) * k / n), gy = Math.round(ay + (by - ay) * k / n);
              plot(gx, gy, tone(rp, fresh && k === n ? 0 : 0.18 + g[6] * 0.2), fade * (g[6] ? 0.8 : 1));
              if (!g[6] && fade > 0.5) plot(gx + 1, gy, tone(rp, 0.6), 0.3 * fade);
            }
          }
        }
      };
    }
    /* shards: a rect cut into jittered triangles that fall away from the impact;
       with a source (canvas/img) the shards carry that picture */
    function shatterFx(x, y, rp, n, rect, source) {
      var R = rect, cols = Math.max(2, Math.round(Math.sqrt(n / 2 * R.w / R.h))), rows = Math.max(2, Math.round(n / 2 / cols));
      var gw = R.w / cols, gh = R.h / rows, pts = [], i, j;
      for (j = 0; j <= rows; j++) for (i = 0; i <= cols; i++) {
        var edge = i === 0 || j === 0 || i === cols || j === rows;
        pts.push([R.x + i * gw + (edge ? 0 : (Math.random() - 0.5) * gw * 0.7), R.y + j * gh + (edge ? 0 : (Math.random() - 0.5) * gh * 0.7)]);
      }
      var tex = null, maxD = Math.sqrt(R.w * R.w + R.h * R.h);
      if (source) tex = sampleSource(source);
      var shards = [];
      for (j = 0; j < rows; j++) for (i = 0; i < cols; i++) {
        var a = pts[j * (cols + 1) + i], b = pts[j * (cols + 1) + i + 1], c = pts[(j + 1) * (cols + 1) + i], d = pts[(j + 1) * (cols + 1) + i + 1];
        var tris = (i + j) % 2 ? [[a, b, d], [a, d, c]] : [[a, b, c], [b, d, c]];
        tris.forEach(function (tr) {
          var mx = (tr[0][0] + tr[1][0] + tr[2][0]) / 3, my = (tr[0][1] + tr[1][1] + tr[2][1]) / 3;
          var dx = mx - x, dy = my - y, dist = Math.sqrt(dx * dx + dy * dy) || 1;
          var sp = 60 + 220 * (1 - dist / maxD) + Math.random() * 60;
          shards.push({ v: tr.map(function (p) { return [p[0] - mx, p[1] - my]; }), ox: mx, oy: my, x: mx, y: my, vx: dx / dist * sp * 0.7 + (Math.random() - 0.5) * 40, vy: dy / dist * sp * 0.4 - 90 - Math.random() * 60,
            rot: 0, vr: (Math.random() - 0.5) * 6, del: dist / 1400, shade: Math.random() });
        });
      }
      var age = 0, dur = reduced ? 0.4 : 1.9;
      return {
        step: function (dt) {
          age += dt;
          if (!reduced) shards.forEach(function (s) { if (age < s.del) return; s.vy += 1100 * dt; s.x += s.vx * dt; s.y += s.vy * dt; s.rot += s.vr * dt; });
          return age < dur;
        },
        draw: function () {
          var fadeAll = reduced ? 1 - age / dur : (age > dur - 0.5 ? (dur - age) / 0.5 : 1);
          shards.forEach(function (s) {
            var cs = Math.cos(s.rot), sn = Math.sin(s.rot), V = s.v.map(function (p) { return [s.x + p[0] * cs - p[1] * sn, s.y + p[0] * sn + p[1] * cs]; });
            var x0 = Math.floor(Math.min(V[0][0], V[1][0], V[2][0]) / cell), x1 = Math.ceil(Math.max(V[0][0], V[1][0], V[2][0]) / cell);
            var y0 = Math.floor(Math.min(V[0][1], V[1][1], V[2][1]) / cell), y1 = Math.ceil(Math.max(V[0][1], V[1][1], V[2][1]) / cell);
            if (x1 < 0 || y1 < 0 || x0 >= Wc || y0 >= Hc) return;
            var inT = pip(V), lit = age >= s.del;
            for (var gy = Math.max(0, y0); gy <= Math.min(Hc - 1, y1); gy++) for (var gx = Math.max(0, x0); gx <= Math.min(Wc - 1, x1); gx++) {
              var qx = (gx + 0.5) * cell, qy = (gy + 0.5) * cell; if (!inT(qx, qy)) continue;
              var edge = !inT(qx - cell, qy) || !inT(qx, qy - cell), edgeB = !inT(qx + cell, qy) || !inT(qx, qy + cell);
              var col, a = fadeAll;
              if (tex) {
                var lx = qx - s.x, ly = qy - s.y, sx = s.ox + lx * cs + ly * sn, sy = s.oy - lx * sn + ly * cs;
                var tx = Math.floor(sx / cell), ty = Math.floor(sy / cell);
                var tv = tx >= 0 && ty >= 0 && tx < tex.w && ty < tex.h ? tex.d[ty * tex.w + tx] : 0;
                if (!(tv >>> 24)) continue;
                col = tv & 0x00ffffff; a *= (tv >>> 24) / 255;
                if (lit && edge) col = tone(rp, 0.1); else if (lit && edgeB) col = tone(rp, 0.85);
              } else {
                col = edge ? tone(rp, 0.05) : edgeB ? tone(rp, 0.9) : tone(rp, 0.35 + s.shade * 0.3);
                if (!lit) continue;
              }
              plot(gx, gy, col, a);
            }
          });
        }
      };
    }
    function sampleSource(src) {
      try {
        var cr = cv.getBoundingClientRect(), sr = src.getBoundingClientRect();
        var off = mk(Wc, Hc), oc = off.getContext('2d');
        oc.imageSmoothingEnabled = true;
        var nw = src.naturalWidth || src.videoWidth || src.width, nh = src.naturalHeight || src.videoHeight || src.height;
        var dx = (sr.left - cr.left) / cell, dy = (sr.top - cr.top) / cell, dw = sr.width / cell, dh = sr.height / cell;
        var fit = ''; try { fit = root.getComputedStyle(src).objectFit; } catch (e) { fit = ''; }
        if (fit === 'cover' && nw && nh) {
          var sc = Math.max(sr.width / nw, sr.height / nh), vw = sr.width / sc, vh = sr.height / sc;
          oc.drawImage(src, (nw - vw) / 2, (nh - vh) / 2, vw, vh, dx, dy, dw, dh);
        } else oc.drawImage(src, dx, dy, dw, dh);
        var d = oc.getImageData(0, 0, Wc, Hc);
        return { w: Wc, h: Hc, d: new Uint32Array(d.data.buffer) };
      } catch (e) { warn(e); return null; }
    }

    var api = {
      canvas: cv,
      emit: guard(function (kind, x, y, o) {
        o = o || {};
        if (!alive) return;
        size();
        var rp = rampId(o.ramp || (kind === 'rain' ? 'ice' : kind === 'shatter' ? 'ice' : 'gold')), n, k, spread = +o.spread;
        x = +x || 0; y = +y || 0;
        if (kind === 'motes') {
          n = o.n || 20; spread = spread || 60;
          for (k = 0; k < n; k++) { var an = Math.random() * 6.283, rr = Math.sqrt(Math.random()) * spread; spawn(K.mote, x + Math.cos(an) * rr, y + Math.sin(an) * rr * 0.7, (Math.random() - 0.5) * 8, -(8 + Math.random() * 18), 2 + Math.random() * 2.2, rp, { del: Math.random() * 0.5 }); }
        } else if (kind === 'rise') {
          n = o.n || 24; spread = spread || 40;
          for (k = 0; k < n; k++) spawn(K.rise, x + (Math.random() - 0.5) * spread, y + (Math.random() - 0.5) * 6, (Math.random() - 0.5) * 24, -(80 + Math.random() * 120), 0.9 + Math.random() * 0.9, rp, { del: Math.random() * 0.35, sz: Math.random() < 0.2 ? 2 : 1 });
        } else if (kind === 'burst') {
          n = o.n || 40;
          var cone = (o.spread != null ? +o.spread : 360) * Math.PI / 180, dir = (o.dir != null ? +o.dir : -90) * Math.PI / 180, spd = +o.speed || 1;
          for (k = 0; k < n; k++) { var a2 = cone >= 6.28 ? Math.random() * 6.283 : dir + (Math.random() - 0.5) * cone, v = (90 + Math.random() * 230) * spd; spawn(K.burst, x, y, Math.cos(a2) * v, Math.sin(a2) * v, 0.55 + Math.random() * 0.5, rp, { gr: 260, dr: 0.12, sz: Math.random() < 0.3 ? 2 : 1 }); }
        } else if (kind === 'ring') {
          n = Math.max(1, Math.min(4, o.n || 1));
          for (k = 0; k < n; k++) fx.push(ringFx(x, y, rp, (spread || 90) * (1 - k * 0.18), k * 0.14));
          if (!reduced) for (k = 0; k < 14; k++) { var a3 = k / 14 * 6.283; spawn(K.spark, x, y, Math.cos(a3) * 160, Math.sin(a3) * 160, 0.35, rp, { dr: 0.05 }); }
        } else if (kind === 'crack') {
          fx.push(crackFx(x, y, rp, spread || 120, Math.max(2, Math.min(12, o.n || 6)), +o.ttl || 1.5));
          if (!reduced) for (k = 0; k < 10; k++) { var a4 = Math.random() * 6.283, v4 = 40 + Math.random() * 120; spawn(K.spark, x, y, Math.cos(a4) * v4, Math.sin(a4) * v4, 0.4, rp, { gr: 200, dr: 0.2 }); }
        } else if (kind === 'shatter') {
          var src = o.source || null, rect = o.rect || null;
          if (!rect && src && src.getBoundingClientRect) { var cr = cv.getBoundingClientRect(), sr = src.getBoundingClientRect(); rect = { x: sr.left - cr.left, y: sr.top - cr.top, w: sr.width, h: sr.height }; }
          if (!rect) { var sp2 = spread || Math.min(cssW, cssH) * 0.6; rect = o.full ? { x: 0, y: 0, w: cssW, h: cssH } : { x: x - sp2 / 2, y: y - sp2 / 2, w: sp2, h: sp2 }; }
          fx.push(shatterFx(x, y, rp, Math.max(6, Math.min(120, o.n || 40)), rect, src));
          if (!reduced) for (k = 0; k < 18; k++) { var a5 = Math.random() * 6.283, v5 = 80 + Math.random() * 200; spawn(K.spark, x, y, Math.cos(a5) * v5, Math.sin(a5) * v5 - 60, 0.5, rp, { gr: 500, dr: 0.3 }); }
        } else if (kind === 'rain') {
          n = o.n || 40;
          var ra = ((o.dir != null ? +o.dir : 18) + 90) * Math.PI / 180;
          if (!reduced) for (k = 0; k < n; k++) { var vr = 520 + Math.random() * 260; spawn(K.streak, Math.random() * (cssW + 120) - 60, -Math.random() * cssH * 0.6, Math.cos(ra) * vr, Math.sin(ra) * vr, 3, rp, { len: 3 + (Math.random() * 3 | 0) }); }
        } else return;
        kick();
      }),
      start: guard(function (kind, o) {
        o = o || {};
        if (!alive || reduced || (kind !== 'rain' && kind !== 'motes' && kind !== 'rise')) return function () {};
        size();
        var rp = rampId(o.ramp || (kind === 'rain' ? 'ice' : kind === 'rise' ? 'ember' : 'gold')), acc = 0, on = true;
        var ra = ((o.dir != null ? +o.dir : 18) + 90) * Math.PI / 180;
        var c = { tick: function (dt) {
          if (!on) return;
          var area = cssW * cssH / 100000, rate;
          if (kind === 'rain') rate = (o.n || 30) * area * 3.2;
          else if (kind === 'motes') rate = (o.n || 16) * area / 3.2;
          else rate = (o.n || 12) * (cssW / 400);
          acc += rate * dt;
          while (acc >= 1) {
            acc -= 1;
            if (kind === 'rain') { var vr = 520 + Math.random() * 260; spawn(K.streak, Math.random() * (cssW + 160) - 80, -10 - Math.random() * 30, Math.cos(ra) * vr, Math.sin(ra) * vr, 3, rp, { len: 3 + (Math.random() * 3 | 0) }); }
            else if (kind === 'motes') spawn(K.mote, Math.random() * cssW, cssH * (0.15 + Math.random() * 0.85), (Math.random() - 0.5) * 8, -(8 + Math.random() * 20), 2.6 + Math.random() * 2.4, rp);
            else spawn(K.rise, (o.x != null ? +o.x : cssW / 2) + (Math.random() - 0.5) * (o.spread || cssW * 0.6), o.y != null ? +o.y : cssH, (Math.random() - 0.5) * 24, -(80 + Math.random() * 120), 1 + Math.random(), rp, { sz: Math.random() < 0.2 ? 2 : 1 });
          }
        } };
        cont.push(c); kick();
        return function stop() { on = false; var i = cont.indexOf(c); if (i >= 0) cont.splice(i, 1); };
      }, function () { return function () {}; }),
      clear: guard(function () { P.on.fill(0); top = 0; live = 0; fx.length = 0; cont.length = 0; if (px) { px.fill(0); cx.putImageData(im, 0, 0); } }),
      /* The page scrolled under the overlay: everything alive moves with it, so a ring stays on the tile it came from. */
      shift: guard(function (dx, dy) { if (!dy && !dx) return; for (var i = 0; i < top; i++) if (P.on[i]) { P.x[i] += dx; P.y[i] += dy; } for (var e = 0; e < fx.length; e++) fx[e].oy = (fx[e].oy || 0) + dy; }),
      destroy: guard(function () {
        alive = false; if (rafId) caf(rafId);
        doc.removeEventListener('visibilitychange', vis);
        if (ro) ro.disconnect(); else root.removeEventListener('resize', size);
        if (own && cv.parentNode) cv.parentNode.removeChild(cv);
      }),
      _step: function (sec) { for (var q = 0; q < Math.round(sec * 60); q++) step(1 / 60); draw(); if (rafId) { caf(rafId); rafId = 0; } }
    };
    return api;
  }

  root.QOArt = {
    version: VERSION,
    palette: PHEX.slice(),
    classKey: guard(classKey, 'other'),
    classIcon: guard(classIcon, function () { return mk(32, 32); }),
    classIconURL: guard(classIconURL, ''),
    classList: guard(classList, []),
    symbolURL: guard(symbolURL, function (key, size) { try { return classIconURL('other', size); } catch (e) { return ''; } }),
    symbolKeys: guard(symbolKeys, []),
    matronCanvas: guard(matronCanvas, function () { return mk(MW, MH); }),
    particles: guard(particles, function () { var n = function () {}; return { emit: n, start: function () { return n; }, clear: n, shift: n, destroy: n }; }),
    ramps: PR_NAMES.slice(),
    reducedMotion: prefersReduced,
    mountScene: guard(mountScene, function () { return { open: function () { return Promise.resolve(); }, destroy: function () {} }; }),
    mountMatron: guard(mountMatron, function () { return { setState: function () {}, hit: function () {}, destroy: function () {} }; })
  };
})(typeof window !== 'undefined' ? window : this);
